/**
 * Cross-reference & defined-terms document review (AIVA-7506).
 *
 * Durable anchors, one rule's find/fix pair (broken_reference — reference target exists,
 * the walking-skeleton rule), and the two shared sweep/anchor entry points
 * the backend's `word_docreview_scan` / `word_docreview_anchor_findings`
 * tools dispatch to. Deliberately a separate file from `wordDocument.ts`
 * (mirroring `changeLog.ts`/`changeVerify.ts`) rather than one function per
 * rule per file — this grows to hold all 14 rules' find/fix pairs.
 *
 * Anchors are dropped ONLY on findings that survive backend evaluation, not
 * during the raw sweep — the sweep functions below (`findBrokenReference`,
 * and its siblings in later phases) never touch the document; only
 * `anchorFindings` does, and only for the candidates the caller already
 * decided are real.
 */

import { generateUuidV4 } from "../../../../src/telemetry/correlation";
import { logOfficeError } from "../../../../src/telemetry";
import type { FindingCreate } from "../../../../src/docReview/services/api";
import { headingLevelFrom, readWordDocumentMap, tocFromParagraphs, withTrackingForced } from "./wordDocument";
import type { TocEntry } from "./wordDocument";
import { footnoteLabel, footnoteSource, loadFootnoteBodies, loadFootnotes, loadFootnoteTexts } from "./footnotes";
import { profiledStep } from "./scanProfiler";
import { reconsideredCategories, suppressingCategory, type ExceptionCategory } from "./termExceptions";

/** Every anchor this feature inserts carries this tag prefix — the cleanup
 * sweep at the end of a review session deletes exactly these and nothing
 * the user's own document controls. */
export const ANCHOR_TAG_PREFIX = "AIVA_";

/** Office.js errors (e.g. "GeneralException") carry an unhelpfully terse
 * `.message` equal to their `.code` — the actual detail lives in
 * `.debugInfo`, which `runHandler`'s plain `err.message` capture drops on
 * the floor. Append it so the error that reaches the backend (and this
 * conversation, when copied from the network tab) is diagnosable without a
 * console breakpoint. */
function describeOfficeError(err: unknown): string {
  const message = err instanceof Error ? err.message : String(err);
  const debugInfo = (err as { debugInfo?: unknown } | null)?.debugInfo;
  return debugInfo ? `${message} | debugInfo=${JSON.stringify(debugInfo)}` : message;
}

export interface FixResult {
  applied: boolean;
  /** True when the anchor was missing or its live text no longer matches
   * the fingerprint captured at find-time — the document moved since the
   * issue was found, so nothing was written. */
  stale?: boolean;
  reason?: string;
  /** Whether Track Changes was actually in force for the write (mirrors
   * `withTrackingForced`'s own honesty about hosts that refuse the mode). */
  tracked?: boolean;
  /** AIVA-8280: the id Word assigned to the comment this fix inserted, for
   * fixes that resolve by commenting. Persisted on the finding so a later
   * scan can ask "is that exact comment still in the document?" — the only
   * way to tell a resolved comment fix from one the operator has since
   * undone, since a comment leaves the flagged text untouched and so the
   * rule's own condition stays true forever either way.
   *
   * Matching by id rather than by the comment's TEXT is what makes this
   * usable: several findings of the same rule routinely carry identical
   * `commentText` (every broken_reference says "section reference not
   * found"), so one surviving comment would otherwise vouch for all of
   * them. Absent when the host would not return an id, or for a fix that
   * writes no comment — both fall back to the text comparison. */
  commentId?: string;
}

// ---------------------------------------------------------------------------
// Durable anchors
// ---------------------------------------------------------------------------

/** Normalise whitespace and curly/straight quote variants before comparing
 * live text to a captured fingerprint — the same drift `highlightWordText`
 * and `executeReviewEdits` already tolerate elsewhere in this file tree. */
function normalizeForCompare(text: string): string {
  return text
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

/** Curly-to-straight quotes ONLY — deliberately not whitespace-collapsing
 * like `normalizeForCompare`, so every character stays at the SAME index as
 * the original text. defined_not_used/used_not_defined/alphabetical_order/missing_defined_term's defined-term patterns all require a
 * literal straight quote, but Word's AutoCorrect turns typed/pasted quotes
 * into curly ones by default — a document authored anywhere other than
 * directly typed into these regexes' own test fixtures (an LLM-drafted
 * document being the common real case) reads as having NO defined terms at
 * all otherwise, silently, not as an error. */
function straightenQuotes(text: string): string {
  return text.replace(/[‘’]/g, "'").replace(/[“”]/g, '"');
}

/** Words that may follow a plural possessive inside one quoted term: `‘Sellers’ Representative’`. */
const TERM_CONTINUATION = /^ [A-Z][\w-]*(?: (?:[A-Z][\w-]*|of|and|the|for|in|to|on|or|by))*$/;
/** The longest single-quoted term read, quotes included. */
const MAX_SINGLE_QUOTED_TERM = 82;

/** An opening single quote: after a space or bracket, before a letter. `Seller's` is not one. */
function isOpeningSingleQuote(text: string, i: number): boolean {
  const before = text[i - 1];
  return (
    (text[i] === "‘" || text[i] === "'") &&
    (before === undefined || /[\s([]/.test(before)) &&
    /[A-Za-z]/.test(text[i + 1] ?? "")
  );
}

/** A closing single quote: not followed by a letter or digit, so `Seller’s` is an apostrophe. */
function isClosingSingleQuote(text: string, j: number): boolean {
  return (text[j] === "’" || text[j] === "'") && !/[\p{L}\p{N}]/u.test(text[j + 1] ?? "") && /\S/.test(text[j - 1] ?? "");
}

/** Where the single-quoted term opened at `open` closes, or -1. The first
 *  closing quote wins unless only more capitalised words follow it to a later
 *  one: `‘Sellers’ Representative’` is one term, `‘Seller’ and the Buyers’` is not. */
function singleQuoteClose(text: string, open: number): number {
  let close = -1;
  const end = Math.min(text.length, open + MAX_SINGLE_QUOTED_TERM);
  for (let j = open + 1; j < end; j += 1) {
    const c = text[j] ?? "";
    if ('"“”‘\r\n'.includes(c)) break;
    if (isClosingSingleQuote(text, j)) {
      if (close !== -1 && !TERM_CONTINUATION.test(text.slice(close + 1, j))) break;
      close = j;
    }
  }
  return close;
}

/** `text` with each single-quoted term (UK style, `‘Business Day’`) given
 *  straight double quotes; nothing else changes, and every index is kept. A
 *  single-quoted span inside double quotes is a quotation within a quotation,
 *  not a term, and is left alone. */
function doubleQuoteSingleQuotedTerms(text: string): string {
  if (!/[‘']/.test(text)) return text;
  const out = text.split("");
  let inDouble = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i];
    const close = !inDouble && isOpeningSingleQuote(text, i) ? singleQuoteClose(text, i) : -1;
    if (c === '"') inDouble = !inDouble;
    else if (c === "“" || c === "”") inDouble = c === "“";
    else if (close !== -1) {
      out[i] = '"';
      out[close] = '"';
      i = close;
    }
    i += 1;
  }
  return out.join("");
}

/** `straightenQuotes` for defined-term patterns: single-quoted terms read as double-quoted ones. */
function straightenTermQuotes(text: string): string {
  return straightenQuotes(doubleQuoteSingleQuotedTerms(text));
}

/** AIVA-9190: the one list of phrases that tie a quoted term to its meaning.
 *  Every check that recognises a definition builds its pattern from these three. */
const DEFINITION_VERB = String.raw`means?|shall\s+mean|refers\s+to|includes|shall\s+include|(?:is|are)\s+defined\s+in|\(?as\s+(?:such\s+term\s+is\s+)?defined\s+in`;
const DEFINITION_MEANING_VERB = String.raw`(?:shall\s+)?ha(?:s|ve)\s+the\s+(?:respective\s+)?meanings?`;
const DEFINITION_MEANING_SOURCE = String.raw`\s+(?:given|set\s+(?:forth|out)|ascribed|assigned|attributed|specified|provided|stated|described|contained)(?:\s+(?:to|in))?`;

/** Re-derive a defined-term match's `matchText`/`term` from the ORIGINAL
 * (un-straightened) text, at the same position a match found against a
 * straightened copy occupies. Needed because anchoring searches the LIVE
 * document, which still has its real curly quotes — storing the
 * straightened strings would make `body.search` unable to find them again.
 * Every one of these patterns wraps its captured group in exactly one pair
 * of quotes, and `straightenQuotes` never changes any character's length or
 * position, so the first quote inside `match[0]` locates the group in the
 * original text too, regardless of which specific pattern produced it. */
function originalMatchAndTerm(originalText: string, match: RegExpMatchArray): { matchText: string; term: string } {
  const whole = match[0];
  const start = match.index ?? 0;
  const matchText = originalText.slice(start, start + whole.length);
  const group = match[1] ?? "";
  // The term's own opening quote: a match may quote another name first, and the
  // term may end in punctuation inside its quotes ("Seller Parties.").
  const quoteOffset = whole.lastIndexOf(`"${group}`);
  if (quoteOffset === -1 || !group) return { matchText, term: group.trim() };
  const termStart = start + quoteOffset + 1;
  return { matchText, term: originalText.slice(termStart, termStart + group.length).trim() };
}

/** Whether `liveText` still matches the fingerprint captured when the
 * finding was anchored. `false` means "go ahead and write"; a caller must
 * never treat a failed *read* (anchor not found) as `isStale`'s job to
 * report — that is `resolveAnchor` returning `null`, a distinct case. */
export function isStale(liveText: string, expected: string): boolean {
  return normalizeForCompare(liveText) !== normalizeForCompare(expected);
}

/** Insert a hidden, durable anchor at `range` and capture its current text
 * as the staleness fingerprint. Caller must already be inside a `Word.run`. */
export async function insertAnchorAtRange(
  context: Word.RequestContext,
  range: Word.Range,
): Promise<{ tag: string; fingerprint: string }> {
  range.load("text");
  await context.sync();
  const fingerprint = range.text;
  const cc = range.insertContentControl();
  const tag = `${ANCHOR_TAG_PREFIX}${generateUuidV4()}`;
  cc.tag = tag;
  cc.title = "AIVA review anchor";
  cc.appearance = Word.ContentControlAppearance.hidden;
  cc.cannotDelete = false;
  await context.sync();
  return { tag, fingerprint };
}

/** Re-find a previously-inserted anchor by tag. `null` means the read
 * failed or the anchor genuinely no longer exists — never conflate this
 * with `isStale` returning `false`. */
export async function resolveAnchor(
  context: Word.RequestContext,
  tag: string,
): Promise<Word.ContentControl | null> {
  const cc = context.document.contentControls.getByTag(tag).getFirstOrNullObject();
  cc.load("isNullObject,text");
  await context.sync();
  if (cc.isNullObject) return resolveFootnoteAnchor(context, tag);
  return cc;
}

/** AIVA-9042: an anchor placed inside a footnote. Asked only once the
 *  document-wide lookup has missed — whether that lookup already reaches into
 *  footnotes is host-dependent, so this costs nothing where it does. `null`
 *  when no footnote holds the tag either. */
async function resolveFootnoteAnchor(context: Word.RequestContext, tag: string): Promise<Word.ContentControl | null> {
  const bodies = await loadFootnoteBodies(context);
  if (bodies.length === 0) return null;
  const candidates = bodies.map((body) => {
    const cc = body.contentControls.getByTag(tag).getFirstOrNullObject();
    cc.load("isNullObject,text");
    return cc;
  });
  await context.sync();
  return candidates.find((cc) => !cc.isNullObject) ?? null;
}

/**
 * Verify-after-fix (AIVA-7506 US12): re-read an anchor's live state in a
 * fresh round trip, independent of whatever `Word.run` just wrote it —
 * genuine confirmation the write actually persisted, not just that the API
 * call didn't throw. `null` means the anchor no longer resolves at all.
 *
 * Only meaningful for fixes that write a specific value to check against
 * (bracket_heading_mismatch's replaced text, inconsistent_formatting's underline) — a comment-only fix (broken_reference/defined_term_section_mismatch/defined_not_used/
 * numbering_order) has nothing further to verify: a successful `insertComment` already
 * is the confirmation.
 */
export function readAnchorState(anchorTag: string): Promise<{ text: string; underline: string } | null> {
  return Word.run(async (context) => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return null;
    const range = cc.getRange();
    range.load("text,font/underline");
    await context.sync();
    return { text: range.text, underline: (range.font.underline as unknown as string) ?? "Unknown" };
  });
}

/** A finding's own text-level identity — `matchText`/`occurrenceIndex` as
 * the anchor step originally searched for it, carried on every finding's
 * `fix_params`, plus the fingerprint captured if it ever anchored at all
 * (absent when it never did). */
export interface FindingLocationHint {
  readonly matchText?: string;
  readonly occurrenceIndex?: number;
  readonly expected?: string;
}

export interface SelectFindingResult {
  selected: boolean;
  /** Which route located it: the durable anchor, or the text fallback. */
  via?: "anchor" | "text";
  /** Located by text, and that text no longer matches `expected` — the
   * document moved since the finding was made. The selection still
   * happened (a scroll writes nothing), but the caller must say so rather
   * than let a fix be applied against it blind. */
  moved?: boolean;
}

/** Ordinals — in Word's own non-overlapping, case-insensitive search order —
 * of every occurrence of `needle` in `text` that stands as its own
 * word/phrase rather than inside a longer alphanumeric run.
 *
 * The boundary test is `labelAppearsIn`'s and rules.py's: not alphanumeric
 * on either side. That is also the space every `occurrenceIndex` in this
 * file is counted in (see `findBrokenReference`'s `seenCount`), so an
 * ordinal from here and an `occurrenceIndex` from a sweep mean the same
 * thing. Ordinals rather than string offsets, so a per-paragraph `search()`
 * result can be indexed by the same number. */
function* occurrenceScan(
  text: string,
  needle: string,
): Generator<{ ordinal: number; offset: number; bounded: boolean }> {
  const haystack = text.toLowerCase();
  const target = needle.toLowerCase();
  if (!target) return;
  let from = 0;
  let ordinal = 0;
  for (;;) {
    const at = haystack.indexOf(target, from);
    if (at === -1) return;
    const before = at === 0 ? "" : haystack[at - 1]!;
    const after = haystack[at + target.length] ?? "";
    // THE boundary rule, in one place. Not alphanumeric on either side —
    // `labelAppearsIn`'s test and the one Word's own `matchWholeWord` was
    // found to use (AIVA-8055, AIVA-8296). A second opinion about word
    // boundaries anywhere in this file is how occurrence indexes drift, so
    // there is deliberately only this one.
    yield { ordinal, offset: at, bounded: !/[a-z0-9]/.test(before) && !/[a-z0-9]/.test(after) };
    ordinal += 1;
    // Non-overlapping, exactly how Word's own search counts occurrences —
    // an overlapping walk here would shift every later ordinal and index
    // into the wrong hit.
    from = at + target.length;
  }
}

function boundedOccurrenceOrdinals(text: string, needle: string): number[] {
  const ordinals: number[] = [];
  for (const hit of occurrenceScan(text, needle)) if (hit.bounded) ordinals.push(hit.ordinal);
  return ordinals;
}

/** Only reached when the whole-word search found nothing at all: the same
 * search with whole-word OFF, narrowed back down to genuinely word-bounded
 * hits in string space.
 *
 * Word's whole-word semantics are defined over alphanumeric runs, so a
 * `matchText` carrying punctuation ("Section 9 (Termination)") can match
 * zero times under `matchWholeWord: true` while plainly sitting in the
 * document — a confirmed cause of a finding that never anchored. The
 * string-space boundary filter is what stops this from reviving the exact
 * substring bug whole-word was added for (AIVA-8055): "Section 2" must
 * never resolve onto "Section 24". */
async function locateLooseOccurrence(
  context: Word.RequestContext,
  matchText: string,
  occurrenceIndex: number,
): Promise<Word.Range | null> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items/text");
  await context.sync();

  // Document order, accumulating word-bounded occurrences until the
  // requested one is reached — the same document-order counting the sweeps
  // mint `occurrenceIndex` with.
  let seen = 0;
  for (const paragraph of paragraphs.items) {
    const ordinals = boundedOccurrenceOrdinals(paragraph.text ?? "", matchText);
    if (seen + ordinals.length <= occurrenceIndex) {
      seen += ordinals.length;
      continue;
    }
    const ordinal = ordinals[occurrenceIndex - seen]!;
    const hits = paragraph.search(matchText, { matchCase: false, matchWholeWord: false });
    hits.load("items");
    await context.sync();
    return hits.items[ordinal] ?? null;
  }
  return null;
}

/** The Nth word-bounded occurrence of `matchText`, found the same way the
 * anchor step originally found it — `wordBoundedHits` (AIVA-8547) — so a
 * finding that once anchored cleanly resolves identically here. When that
 * narrowing cannot be verified, the occurrence is found inside its own
 * paragraph, as the anchor step does (AIVA-8300, `queueParagraphFallback`).
 * Only a host that hands back no paragraph text at all keeps the previous
 * behaviour: whole-word first, and the punctuation-tolerant fallback above
 * when that finds nothing.
 *
 * `null` when the occurrence genuinely is not there — never a
 * nearest-guess, because a wrong location here is a fix written in the
 * wrong place. */
async function locateOccurrenceByText(
  context: Word.RequestContext,
  matchText: string,
  occurrenceIndex: number,
): Promise<Word.Range | null> {
  const search = queueWordBoundedSearch(context.document.body, matchText);
  const paragraphs = loadOptionalParagraphs(context);
  await context.sync();
  const paragraphTexts = paragraphTextsOf(paragraphs);
  const verified = verifiedWordBoundedHits(search, paragraphTexts);
  if (verified !== null) {
    return verified[occurrenceIndex] ?? locateFootnoteOccurrence(context, matchText, occurrenceIndex - verified.length);
  }
  // AIVA-8300: jump-to-location and anchor repair resolve an unverifiable
  // count the way the anchor step now does — inside the occurrence's own
  // paragraph — or they would disagree with it about where the finding is.
  if (paragraphs && paragraphTexts.length > 0) {
    const fallback = queueParagraphFallback(paragraphs, paragraphTexts, matchText, occurrenceIndex);
    if (fallback) {
      await context.sync();
      return paragraphFallbackHit(fallback);
    }
    const bodyHits = wordBoundedOrdinals(paragraphTexts, matchText).bounded.length;
    return locateFootnoteOccurrence(context, matchText, occurrenceIndex - bodyHits);
  }
  const exact = search.strict;
  if (exact.items.length > 0) return exact.items[occurrenceIndex] ?? null;
  return locateLooseOccurrence(context, matchText, occurrenceIndex);
}

/** AIVA-9042: an occurrence past the main text's last hit, numbered on into
 *  the footnotes as the anchor step numbers it (`anchorInFootnotes`). Without
 *  this, a footnote finding whose anchor was lost could be neither shown nor
 *  repaired. `null` for a negative index — the occurrence is in the main text
 *  and was not found there. */
async function locateFootnoteOccurrence(
  context: Word.RequestContext,
  matchText: string,
  footnoteOccurrence: number,
): Promise<Word.Range | null> {
  if (footnoteOccurrence < 0) return null;
  const [hit] = await footnoteOccurrenceHits(context, [{ matchText, footnoteOccurrence }]);
  return hit ?? null;
}

/**
 * "Jump to location" (AIVA-7506 US11 follow-up): scroll the document to a
 * finding and select it, so the operator can see the actual text before
 * deciding to apply or skip. Read-only — no write, no tracked change,
 * nothing to verify — so it needs none of the staleness/tracking machinery
 * every fix function carries.
 *
 * AIVA-8209: the stored `anchor_tag` lives in Cosmos while the content
 * control it names lives in the document, and the two come apart routinely
 * — a re-scan's `cleanupAllAnchors()`, "Done reviewing", an edit that
 * replaces the anchored range, or an anchor step that never landed at all.
 * The card then rendered exactly as usual and the click did nothing
 * whatsoever, which reads as "the cards stopped working" rather than "this
 * location is gone". So the anchor is now the FIRST way back to the
 * location, not the only one: when it no longer resolves, the finding's own
 * `matchText`/`occurrenceIndex` re-locate it.
 *
 * Deliberately still read-only. It does NOT re-anchor, because inserting a
 * content control is a document write with its own tracking/nesting rules,
 * and it must never re-baseline `expected` — a fix applied against a
 * silently-refreshed fingerprint is the one outcome worse than a dead
 * click. A relocation that no longer matches `expected` is reported as
 * `moved` and left for the staleness guard to refuse.
 */
export function selectFindingLocation(
  anchorTag: string | null,
  hint: FindingLocationHint = {},
): Promise<SelectFindingResult> {
  return Word.run(async (context) => {
    if (anchorTag) {
      const cc = await resolveAnchor(context, anchorTag);
      if (cc) {
        cc.getRange().select(Word.SelectionMode.select);
        await context.sync();
        return { selected: true, via: "anchor" };
      }
    }
    const matchText = hint.matchText?.trim();
    if (!matchText) return { selected: false };
    const hit = await locateOccurrenceByText(context, matchText, hint.occurrenceIndex ?? 0);
    if (!hit) {
      console.warn(
        `[docReview] select: could not relocate matchText=${JSON.stringify(matchText)} occurrenceIndex=${hint.occurrenceIndex ?? 0}`,
      );
      return { selected: false };
    }
    hit.load("text");
    hit.select(Word.SelectionMode.select);
    await context.sync();
    return { selected: true, via: "text", moved: hint.expected ? isStale(hit.text, hint.expected) : false };
  });
}

/**
 * Run `body` with Track Changes forced off, restoring whatever the document
 * had afterwards — the save/restore shape `withTrackingForced` has, in the
 * opposite direction.
 *
 * `insertContentControl()` throws "GeneralException" while tracking is on,
 * and a hidden anchor is plumbing rather than a user-visible edit, so it
 * must not be recorded as a revision either way. Shared by every path that
 * inserts an anchor so the two can never drift on which mode they restore.
 */
async function withTrackingDisabled<T>(context: Word.RequestContext, body: () => Promise<T>): Promise<T> {
  context.document.load("changeTrackingMode");
  await context.sync();
  const previousMode = context.document.changeTrackingMode;
  if (previousMode !== Word.ChangeTrackingMode.off) {
    context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
    await context.sync();
  }
  try {
    return await body();
  } finally {
    if (previousMode !== Word.ChangeTrackingMode.off) {
      context.document.changeTrackingMode = previousMode;
      await context.sync();
    }
  }
}

/**
 * The `AIVA_*` anchor already wrapping `range`, if there is one — so a
 * repeated repair reuses it instead of inserting a second control over the
 * same words, which Word rejects outright ("GeneralException"). Someone
 * else's content control (a template field, a form control) is deliberately
 * NOT adopted: its tag would never resolve as one of ours.
 */
async function existingAnchorAround(
  context: Word.RequestContext,
  range: Word.Range,
): Promise<{ anchorTag: string; fingerprint: string } | null> {
  const parent = range.parentContentControlOrNullObject;
  parent.load("isNullObject,tag,text");
  await context.sync();
  if (parent.isNullObject) return null;
  const tag = parent.tag ?? "";
  return tag.startsWith(ANCHOR_TAG_PREFIX) ? { anchorTag: tag, fingerprint: parent.text } : null;
}

export interface RepairAnchorResult {
  /** The anchor the finding can now be fixed through — reused if one
   * already covered the location, freshly inserted otherwise. `null` means
   * the text could not be re-located at all, which is the one honest
   * "this finding has no location any more". */
  anchorTag: string | null;
  /** The located range's CURRENT text. Usable as `expected` ONLY by a
   * finding that never had one (see `applyFinding`) — adopting it for a
   * finding that already carries a fingerprint would re-baseline the
   * staleness guard onto whatever the document says now, which is exactly
   * how a fix gets written over text the operator never reviewed. */
  fingerprint: string | null;
  /** An `AIVA_*` control already wrapped the location and was reused. */
  reused?: boolean;
}

/**
 * Re-establish a finding's anchor from its own text identity (AIVA-8209
 * Layer 2), so a fix can still be applied after the content control has
 * gone — a re-scan's `cleanupAllAnchors()`, "Done reviewing", an operator
 * edit over the anchored range, or an anchor step that never landed in the
 * first place.
 *
 * This is the WRITE counterpart to `selectFindingLocation`, and deliberately
 * shares its `locateOccurrenceByText` resolver rather than repeating the
 * anchor step's own search: jumping to a finding and fixing it must agree
 * on where that finding is, or the operator reviews one location and writes
 * to another. It also means a finding whose `matchText` carries punctuation
 * ("Section 9 (Termination)") — which `anchorChunkBatched`'s whole-word-only
 * search can never match, and which is a confirmed cause of a finding that
 * never anchored — is repairable here even though it was unanchorable there.
 *
 * Idempotent on purpose. A transient apply failure writes no status, so the
 * operator can (and does) click Apply again; without the reuse check below,
 * the second attempt would call `insertContentControl` on a range this
 * function already wrapped and throw "GeneralException", turning a
 * retryable hiccup into a permanently broken finding.
 */
export async function repairFindingAnchor(matchText: string, occurrenceIndex = 0): Promise<RepairAnchorResult> {
  const needle = matchText.trim();
  if (!needle) return { anchorTag: null, fingerprint: null };
  try {
    return await Word.run((context) =>
      withTrackingDisabled(context, async () => {
        const hit = await locateOccurrenceByText(context, needle, occurrenceIndex);
        if (!hit) {
          console.warn(
            `[docReview] repair: could not relocate matchText=${JSON.stringify(needle)} occurrenceIndex=${occurrenceIndex}`,
          );
          return { anchorTag: null, fingerprint: null };
        }
        const existing = await existingAnchorAround(context, hit);
        if (existing) return { ...existing, reused: true };
        const { tag, fingerprint } = await insertAnchorAtRange(context, hit);
        return { anchorTag: tag, fingerprint, reused: false };
      }),
    );
  } catch (err) {
    // Same defensive shape as `anchorOneSequential`: a thrown Office error
    // here means this one finding stays unrepaired, which the caller
    // reports honestly — it must never surface as an unhandled rejection.
    logOfficeError(err, "word-docreview-repair-anchor");
    console.warn(`[docReview] repair: threw for matchText=${JSON.stringify(needle)}:`, err);
    return { anchorTag: null, fingerprint: null };
  }
}

/**
 * Locate and anchor each candidate finding the caller has already decided
 * is real. `matchText` is re-found via a live text search (the same
 * `body.search` pattern `executeReviewEdits` uses) — findings are never
 * anchored during the raw sweep, only here, after evaluation.
 *
 * `occurrenceIndex` (default 0) picks which hit to anchor when the same
 * `matchText` occurs more than once in the document — e.g. inconsistent_formatting (formatting
 * consistency) can flag the SECOND mention of "Section 10.01" while the
 * first stays fine. Omitting it anchors the first occurrence, which is
 * correct whenever the text is unique.
 */
export async function anchorFindings(
  findings: Array<{ index: number; matchText: string; occurrenceIndex?: number }>,
): Promise<Array<{ index: number; anchorTag: string | null; fingerprint: string | null }>> {
  try {
    return await profiledStep(
      "anchor: all findings",
      () => anchorFindingsInner(findings),
      (out) => `${out.filter((a) => a.anchorTag).length} of ${findings.length} marked`,
    );
  } catch (err) {
    logOfficeError(err, "word-docreview-anchor-findings");
    throw new Error(describeOfficeError(err));
  }
}

type AnchorResult = { index: number; anchorTag: string | null; fingerprint: string | null };

/**
 * A word-bounded search, queued but not yet synced — see `wordBoundedHits`.
 *
 * AIVA-8547: `matchWholeWord: true` is NOT a word-boundary filter for a phrase.
 * Word ignores it whenever the search text contains a space, which every
 * reference this file searches for does ("Section 2"). Confirmed against desktop
 * Word: "Section 2" with whole-word ON returns the same five hits as with it
 * OFF, including the start of "Section 21.04", "Section 20.04" and "Section 24".
 * Every `occurrenceIndex` in this file is counted with `occurrenceScan`'s
 * alphanumeric boundary, so indexing that list put a "Section 2" finding on
 * "Section 21.04" — a second card on a reference that already had its own — and
 * made inconsistent_formatting read one reference's underline as another's.
 *
 * So both searches are queued in the SAME round trip: the whole-word one (the
 * behaviour every caller had before) and a plain one that `wordBoundedHits`
 * narrows with `occurrenceScan` itself.
 *
 * The narrowed list is THE list every sweep that indexes by `occurrenceIndex`
 * shares with the anchor step. It keeps the "Section 2" inside "Section 2.05"
 * ("." is a boundary): `documentOccurrenceCounter` counts that one too, and
 * dropping it on one side only is how AIVA-8537 corrected every index twice and
 * landed one reference late (AIVA-8300). `.items` is read once per collection,
 * because an Office.js proxy returns a fresh array on each access.
 */
interface WordBoundedSearch {
  readonly matchText: string;
  readonly strict: Word.RangeCollection;
  readonly loose: Word.RangeCollection;
}

function queueWordBoundedSearch(body: Word.Body, matchText: string): WordBoundedSearch {
  // AIVA-8925: one cheap property, not every one. A one-letter text ("a") has
  // thousands of plain hits, and loading each one's text, style and hyperlink
  // cost ~70s on a 26-page contract. Every caller loads what it then reads.
  const strict = body.search(matchText, { matchCase: false, matchWholeWord: true });
  strict.load("items/isEmpty");
  const loose = body.search(matchText, { matchCase: false, matchWholeWord: false });
  loose.load("items/isEmpty");
  return { matchText, strict, loose };
}

/**
 * Which of Word's plain-search hits for `needle` are word-bounded, as ordinals
 * into that hit list — plus how many hits the list should have in total.
 *
 * Built on `occurrenceScan`, the same walk that mints every `occurrenceIndex`,
 * so the two cannot disagree about a boundary. `total` is what makes the result
 * checkable: a plain case-insensitive search returns every non-overlapping
 * occurrence in document order, which is exactly what `occurrenceScan` walks —
 * verified hit for hit against desktop Word, punctuation-ended phrases ("Section
 * 9 (Termination)", "(a)") included. Pure, so testable without a host.
 */
export function wordBoundedOrdinals(
  paragraphTexts: readonly string[],
  needle: string,
): { total: number; bounded: number[] } {
  let total = 0;
  const bounded: number[] = [];
  for (const text of paragraphTexts) {
    for (const hit of occurrenceScan(text, needle)) {
      if (hit.bounded) bounded.push(total);
      total += 1;
    }
  }
  return { total, bounded };
}

/**
 * The word-bounded hits of a queued search, in document order — the list every
 * `occurrenceIndex` in this file indexes. Call after the sync that loaded both
 * the search and `paragraphTexts`.
 *
 * Falls back to the whole-word list exactly as callers used it before when the
 * narrowing cannot be verified: no paragraph text to count against (an older
 * host, or a test double), or a hit count that disagrees with the document's own
 * text — a `{ REF }` field can render differently to `paragraph.text` than to
 * `body.search`. Where we cannot prove the narrowed list right, nothing changes.
 */
function wordBoundedHits(search: WordBoundedSearch, paragraphTexts: readonly string[]): Word.Range[] {
  return verifiedWordBoundedHits(search, paragraphTexts) ?? search.strict.items;
}

/** Each key's `wordBoundedHits` — the per-text hit lists a sweep indexes. */
function wordBoundedHitLists(
  searchesByKey: Map<string, WordBoundedSearch>,
  paragraphTexts: readonly string[],
): Map<string, Word.Range[]> {
  const out = new Map<string, Word.Range[]>();
  for (const [key, search] of searchesByKey) out.set(key, wordBoundedHits(search, paragraphTexts));
  return out;
}

/** `wordBoundedHits` when it could be verified against the document's own text,
 *  `null` when it could not. A caller with a fallback of its own (the anchor
 *  step's punctuation retry) needs to tell "checked, and there is genuinely no
 *  bounded hit" apart from "could not check" — only the second may fall back,
 *  or the substring hit this exists to exclude comes straight back. */
function verifiedWordBoundedHits(search: WordBoundedSearch, paragraphTexts: readonly string[]): Word.Range[] | null {
  if (paragraphTexts.length === 0) return null;
  const loose = search.loose.items;
  const { total, bounded } = wordBoundedOrdinals(paragraphTexts, search.matchText);
  if (total !== loose.length) return null;
  return bounded.map((i) => loose[i]!);
}

/** Pure: where the `occurrenceIndex`th word-bounded occurrence of `needle`
 *  sits — which paragraph, which of that paragraph's own matches it is, and
 *  how many matches the paragraph has in all — or null when the document has
 *  fewer. The same document-order count `documentOccurrenceCounter` mints
 *  indexes with, kept per paragraph. */
export function paragraphOccurrence(
  paragraphTexts: readonly string[],
  needle: string,
  occurrenceIndex: number,
): { paragraphIndex: number; ordinal: number; matches: number } | null {
  let seen = 0;
  for (const [paragraphIndex, text] of paragraphTexts.entries()) {
    const hits = [...occurrenceScan(text, needle)];
    const bounded = hits.filter((hit) => hit.bounded);
    if (seen + bounded.length > occurrenceIndex) {
      return { paragraphIndex, ordinal: bounded[occurrenceIndex - seen]!.ordinal, matches: hits.length };
    }
    seen += bounded.length;
  }
  return null;
}

/** A search queued inside the one paragraph that holds a finding's
 *  occurrence — see `queueParagraphFallback`. */
interface ParagraphFallback {
  readonly search: Word.RangeCollection;
  readonly matchText: string;
  readonly ordinal: number;
  readonly matches: number;
}

/**
 * AIVA-8300 (reopened on staging): the fallback for when the whole-document
 * narrowing cannot be verified — `verifiedWordBoundedHits` returned null.
 *
 * That happens whenever Word's hit count and the paragraph text disagree
 * ANYWHERE in the document. A Table of Contents or a cross-reference field can
 * read one way to `body.search` and another to `paragraph.text`, and either is
 * enough. The fallback used to be Word's raw whole-word list, which for a
 * phrase still holds the "Section 5" inside "Section 50" and the TOC's own
 * entry — so every index, counted without those, landed on the wrong one: the
 * fix wrote its link into the TOC.
 *
 * Instead the occurrence is found paragraph by paragraph, the way
 * `locateLooseOccurrence` always has: which paragraph holds it comes from the
 * text, and only THAT paragraph is searched. A disagreement elsewhere — in the
 * TOC — can no longer move it. Queued only; the caller syncs. Null when the
 * text has no such occurrence or the paragraph is not there.
 */
function queueParagraphFallback(
  paragraphs: Word.ParagraphCollection,
  paragraphTexts: readonly string[],
  matchText: string,
  occurrenceIndex: number,
): ParagraphFallback | null {
  const at = paragraphOccurrence(paragraphTexts, matchText, occurrenceIndex);
  const paragraph = at ? paragraphs.items[at.paragraphIndex] : undefined;
  if (!at || !paragraph) return null;
  const search = paragraph.search(matchText, { matchCase: false, matchWholeWord: false });
  search.load("items");
  return { search, matchText, ordinal: at.ordinal, matches: at.matches };
}

/** The hit a synced `queueParagraphFallback` search found — or null, and a
 *  warning, when even that one paragraph's count disagrees with its text: then
 *  no hit in it can be trusted, and declining beats pointing somewhere
 *  plausible. */
function paragraphFallbackHit(fallback: ParagraphFallback | null): Word.Range | null {
  if (!fallback) return null;
  const items = fallback.search.items;
  if (items.length !== fallback.matches) {
    console.warn(
      `[docReview] anchor: paragraph search disagrees with its text for matchText=${JSON.stringify(fallback.matchText)} ` +
        `(text has ${fallback.matches}, Word returned ${items.length}); left unanchored rather than guessed.`,
    );
    return null;
  }
  return items[fallback.ordinal] ?? null;
}

type AnchorRequest = { index: number; matchText: string; occurrenceIndex?: number };

/** An anchor result, plus — when the main text had no hit at the requested
 *  occurrence — how many hits it DID have, so the occurrence can be carried on
 *  into the footnotes (AIVA-9042, see footnotes.ts). Internal to this file:
 *  `bodyHits` never leaves `anchorFindingsInner`. */
type BodyAnchorResult = AnchorResult & { bodyHits?: number };

// How many findings' search+anchor calls get batched into one round trip of
// `context.sync()` calls. Each finding used to cost 3 sequential syncs of its
// own (search, fingerprint load, content-control insert) — for a real
// document with 20-30 findings that's 60-90 Office.js round trips, the
// dominant cost of "mark each finding" per a Langfuse trace. Batching a whole
// chunk behind 3 syncs TOTAL (not 3 per finding) is the same "one sweep,
// not N round trips" pattern `readWordDocumentMap` already uses. Chunked
// rather than one giant batch so a single invalid search string can only
// poison its own chunk's fallback, not the whole findings list.
const ANCHOR_CHUNK_SIZE = 25;

/** Word rejects a longer search string — for the whole batch, not just that
 *  one search — so a text this long can never be anchored. A link this long is
 *  not swept, and the anchor step never puts one in a batch (AIVA-9179). */
const WORD_SEARCH_MAX_LENGTH = 255;

/** Fast path: search, load fingerprints, and insert every content control
 * for a whole chunk behind exactly 3 `context.sync()` calls, regardless of
 * chunk size. Any single bad search string throws for the WHOLE chunk (Word
 * rejects an invalid search argument for the batch, not just its own
 * request) — callers must catch and fall back to `anchorOneSequential` for
 * chunks where this happens. */
async function anchorChunkBatched(context: Word.RequestContext, chunk: AnchorRequest[]): Promise<BodyAnchorResult[]> {
  // A plain search matches as a substring, so "Section 2" also hits inside
  // "Section 24", "Section 20", etc. (anything sharing that numeric prefix).
  // That silently corrupts occurrence counting/ordering for any document with
  // more than 9 numbered sections, and anchors a finding to a fragment of a
  // completely different reference. Every matchText here comes from a regex
  // bounded by non-word characters at both ends, so a real word boundary is
  // always what is meant.
  // AIVA-8547: word-bounded by `wordBoundedHits`, not by matchWholeWord alone —
  // Word ignores that option for a phrase, so "Section 2" still hit inside
  // "Section 21.04". See `queueWordBoundedSearch`.
  const searches = chunk.map((f) => queueWordBoundedSearch(context.document.body, f.matchText));
  // Rides the search round trip — one collection load for the whole chunk, not
  // one per finding — so the hits can be narrowed and checked against the
  // document's own text. See `wordBoundedHits` and `occurrenceCountDisagrees`.
  const paragraphs = loadOptionalParagraphs(context);
  await profiledStep(`  anchor: searches (${chunk.length} findings)`, () => context.sync());
  const paragraphTexts = paragraphTextsOf(paragraphs);

  // Word-bounded hits in document order — including the "Section 2" inside
  // "Section 2.05", since "." is a word boundary, which `documentOccurrenceCounter`
  // counts too (AIVA-8300). `.items` is read ONCE per search: an Office.js proxy
  // returns a fresh array each access.
  const verifiedByIndex = searches.map((s) => verifiedWordBoundedHits(s, paragraphTexts));
  const itemsByIndex = searches.map((s, i) => verifiedByIndex[i] ?? s.strict.items);
  const hitsFor = (i: number): Word.Range[] => itemsByIndex[i] ?? [];

  // AIVA-8300: an unverifiable count with paragraph text to go on is resolved
  // inside the occurrence's own paragraph, never from Word's raw list — see
  // `queueParagraphFallback`. Every such search rides one extra round trip.
  const fallbacks = new Map<number, ParagraphFallback | null>();
  if (paragraphs && paragraphTexts.length > 0) {
    chunk.forEach((f, i) => {
      if (verifiedByIndex[i] !== null) return;
      occurrenceCountDisagrees(paragraphTexts, f.matchText, searches[i]!.strict.items.length, "anchor");
      fallbacks.set(i, queueParagraphFallback(paragraphs, paragraphTexts, f.matchText, f.occurrenceIndex ?? 0));
    });
    if ([...fallbacks.values()].some(Boolean)) {
      await profiledStep(`  anchor: paragraph fallbacks (${fallbacks.size})`, () => context.sync());
    }
  }

  // AIVA-8296: a finding with no hit AT ALL falls back to the plain search,
  // unfiltered — but only when the narrowing above could not be verified (no
  // paragraph text to check against). That is the known case of a matchText
  // ending in ")" ("Section 9 (Termination)"), which the whole-word search never
  // matches, so the finding would never anchor: Apply then fails "missing_args"
  // and jump-to-location stays disabled. A VERIFIED empty list means there is no
  // word-bounded occurrence, and falling back would re-admit exactly the
  // substring hit the narrowing excluded. The plain search is already loaded,
  // so this costs no extra round trip.
  const looseByIndex = new Map<number, Word.RangeCollection>();
  chunk.forEach((f, i) => {
    if (verifiedByIndex[i] === null && hitsFor(i).length === 0 && f.matchText.trim()) {
      looseByIndex.set(i, searches[i]!.loose);
    }
  });

  // AIVA-9042: how many main-text hits each finding had, for a miss — see
  // BodyAnchorResult. Recorded only where the text was found nowhere at the
  // requested occurrence, so it changes nothing for a hit.
  const bodyHits = new Map<number, number>();
  const hits: Array<Word.Range | null> = chunk.map((f, i) => {
    if (fallbacks.has(i)) {
      const hit = paragraphFallbackHit(fallbacks.get(i) ?? null);
      if (!hit) bodyHits.set(i, wordBoundedOrdinals(paragraphTexts, f.matchText).bounded.length);
      if (!hit) {
        console.warn(
          `[docReview] anchor: no hit for matchText=${JSON.stringify(f.matchText)} occurrenceIndex=${f.occurrenceIndex ?? 0} (resolved within its own paragraph)`,
        );
      }
      return hit;
    }
    const strictItems = hitsFor(i);
    // Checked on the STRICT path only — a zero-hit strict result is the known
    // punctuation case the loose retry below exists for, not a counting
    // disagreement — and REPORTED, deliberately not acted on.
    //
    // Declining here would cover every rule at once, which is the tempting
    // part. It is not done because this step runs against the LIVE document
    // and compares two things Word need not agree about on a real contract: a
    // `{ REF }` field's rendered text can present differently to
    // `paragraph.text` than to `body.search`, and legal templates are full of
    // them. A false decline here would empty the panel across every rule at
    // once — "the checker stopped finding anything" — which is a worse and
    // far less diagnosable failure than the one it guards against.
    //
    // So the warning is the product here: it names the matchText, so a
    // document where our counting really does disagree with Word can be
    // diagnosed from a log instead of from a screenshot of the wrong
    // highlight. inconsistent_formatting, where a wrong index FABRICATES a
    // finding by reading another range's formatting, does decline — one rule,
    // one clear reason.
    if (strictItems.length > 0) {
      occurrenceCountDisagrees(paragraphTexts, f.matchText, strictItems.length, "anchor");
    }
    // Only a ZERO-hit strict result falls back — see `needsLooseRetry` above.
    const items = strictItems.length === 0 ? (looseByIndex.get(i)?.items ?? strictItems) : strictItems;
    const hit = items[f.occurrenceIndex ?? 0];
    if (!hit) bodyHits.set(i, items.length);
    if (hit && strictItems.length === 0) {
      console.info(
        `[docReview] anchor: strict whole-word search found nothing for matchText=${JSON.stringify(f.matchText)}; anchored via non-whole-word fallback`,
      );
    }
    if (!hit) {
      // Confirmed real cause of a finding stuck with no anchor at all
      // (Apply then fails "missing_args", jump-to-location stays
      // disabled): either the search found nothing (matchWholeWord with a
      // phrase ending in punctuation, e.g. "Section 9 (Termination)", is a
      // suspect — Word's whole-word boundary semantics are for
      // alphanumeric runs, not a trailing ")"), or fewer hits existed than
      // the occurrenceIndex expected (a real occurrence-counting mismatch).
      // Logged here, at the exact moment the anchor step gives up, rather
      // than only surfacing three tool calls later as a generic "missing
      // args" on whatever Apply happens to be clicked first.
      console.warn(
        `[docReview] anchor: no hit for matchText=${JSON.stringify(f.matchText)} occurrenceIndex=${f.occurrenceIndex ?? 0} (found ${items.length} total match(es), incl. non-whole-word fallback)`,
      );
    }
    return hit ?? null;
  });
  hits.forEach((hit) => hit?.load("text"));
  await profiledStep("  anchor: read hit text", () => context.sync());

  const anchors: Array<{ tag: string; fingerprint: string } | null> = hits.map((hit) => {
    if (!hit) return null;
    const fingerprint = hit.text;
    const cc = hit.insertContentControl();
    const tag = `${ANCHOR_TAG_PREFIX}${generateUuidV4()}`;
    cc.tag = tag;
    cc.title = "AIVA review anchor";
    cc.appearance = Word.ContentControlAppearance.hidden;
    cc.cannotDelete = false;
    return { tag, fingerprint };
  });
  await profiledStep("  anchor: insert markers", () => context.sync());

  return chunk.map((f, i) => {
    const anchor = anchors[i];
    const missed = bodyHits.get(i);
    return {
      index: f.index,
      anchorTag: anchor?.tag ?? null,
      fingerprint: anchor?.fingerprint ?? null,
      ...(missed === undefined ? {} : { bodyHits: missed }),
    };
  });
}

/** Slow-path fallback, one finding at a time — identical shape to this
 * function's pre-batching form. Only reached for a chunk whose batched
 * search threw, so a single bad finding's search string is isolated to
 * (at most) `ANCHOR_CHUNK_SIZE` neighbors, never the whole findings list. */
async function anchorOneSequential(context: Word.RequestContext, finding: AnchorRequest): Promise<BodyAnchorResult> {
  try {
    // Word-bounded exactly as the batched path is — see anchorChunkBatched and
    // `queueWordBoundedSearch` (AIVA-8547).
    const search = queueWordBoundedSearch(context.document.body, finding.matchText);
    const paragraphs = loadOptionalParagraphs(context);
    await context.sync();
    const paragraphTexts = paragraphTextsOf(paragraphs);
    const verified = verifiedWordBoundedHits(search, paragraphTexts);
    // AIVA-8300: an unverifiable count resolves inside its own paragraph — the
    // same as the batched path, so a chunk that fell back here anchors the same.
    if (verified === null && paragraphs && paragraphTexts.length > 0) {
      const fallback = queueParagraphFallback(paragraphs, paragraphTexts, finding.matchText, finding.occurrenceIndex ?? 0);
      if (fallback) await context.sync();
      const located = paragraphFallbackHit(fallback);
      if (!located) {
        console.warn(
          `[docReview] anchor: no hit for matchText=${JSON.stringify(finding.matchText)} occurrenceIndex=${finding.occurrenceIndex ?? 0} (resolved within its own paragraph)`,
        );
        const bodyHits = wordBoundedOrdinals(paragraphTexts, finding.matchText).bounded.length;
        return { index: finding.index, anchorTag: null, fingerprint: null, bodyHits };
      }
      const { tag, fingerprint } = await insertAnchorAtRange(context, located);
      return { index: finding.index, anchorTag: tag, fingerprint };
    }
    let items = verified ?? search.strict.items;
    // Same zero-hit fallback anchorChunkBatched does, for the same punctuation
    // reason and under the same "could not verify" condition — kept in step so
    // a chunk that fell back to this slow path can never anchor FEWER (or
    // different) findings than the batched path would have.
    if (verified === null && items.length === 0 && finding.matchText.trim()) {
      const loose = search.loose;
      if (loose.items.length > 0) {
        console.info(
          `[docReview] anchor: strict whole-word search found nothing for matchText=${JSON.stringify(finding.matchText)}; anchored via non-whole-word fallback`,
        );
        items = loose.items;
      }
    }
    const hit = items[finding.occurrenceIndex ?? 0];
    if (!hit) {
      // See anchorChunkBatched's own comment on this — same diagnostic,
      // this is just the slow-path fallback for a chunk whose batched
      // search threw.
      console.warn(
        `[docReview] anchor: no hit for matchText=${JSON.stringify(finding.matchText)} occurrenceIndex=${finding.occurrenceIndex ?? 0} (found ${items.length} total match(es), incl. non-whole-word fallback)`,
      );
      return { index: finding.index, anchorTag: null, fingerprint: null, bodyHits: items.length };
    }
    const { tag, fingerprint } = await insertAnchorAtRange(context, hit);
    return { index: finding.index, anchorTag: tag, fingerprint };
  } catch (err) {
    // This is the OTHER way a finding ends up with no anchor at all — not
    // "the search found nothing" (that's logged above), but a thrown
    // exception from body.search itself (an invalid/too-long search string)
    // or from insertContentControl (e.g. "GeneralException" when the range
    // is already wrapped by another content control). Previously swallowed
    // completely, with no trace of which finding or why.
    console.warn(
      `[docReview] anchor: threw for matchText=${JSON.stringify(finding.matchText)} occurrenceIndex=${finding.occurrenceIndex ?? 0}:`,
      err,
    );
    return { index: finding.index, anchorTag: null, fingerprint: null };
  }
}

/** A finding the main text had no hit for, and which occurrence it is once
 *  the main text's own hits are counted off (AIVA-9042). */
type FootnoteAnchorRequest = { request: AnchorRequest; footnoteOccurrence: number };

/** Each `footnoteOccurrence`-th word-bounded occurrence of `matchText` across
 *  the footnotes, footnote by footnote, with its text loaded — or null where
 *  there is none, or where that footnote's search disagrees with its own text.
 *  Empty when the document has no readable footnotes. Shared by the anchor
 *  step and by jump-to-location/anchor repair, so all three agree on where a
 *  footnote occurrence is. */
async function footnoteOccurrenceHits(
  context: Word.RequestContext,
  needles: ReadonlyArray<{ matchText: string; footnoteOccurrence: number }>,
): Promise<Array<Word.Range | null>> {
  const bodies = await loadFootnoteBodies(context);
  if (bodies.length === 0) return [];
  for (const body of bodies) body.load("text");
  await context.sync();
  const texts = bodies.map((body) => straightenQuotes(body.text ?? ""));

  const plans = needles.map(({ matchText, footnoteOccurrence }) => {
    const needle = straightenQuotes(matchText);
    let remaining = footnoteOccurrence;
    for (const [k, text] of texts.entries()) {
      const { total, bounded } = wordBoundedOrdinals([text], needle);
      const ordinal = bounded[remaining];
      if (ordinal !== undefined) {
        const search = bodies[k]!.search(matchText, { matchCase: false, matchWholeWord: false });
        search.load("items");
        return { matchText, search, total, ordinal, footnote: k + 1 };
      }
      remaining -= bounded.length;
    }
    return null;
  });
  await context.sync();

  const hits = plans.map((plan, i) => {
    if (!plan) {
      console.warn(
        `[docReview] anchor: no hit for matchText=${JSON.stringify(needles[i]!.matchText)} in the main text or the footnotes`,
      );
      return null;
    }
    if (plan.search.items.length !== plan.total) {
      console.warn(
        `[docReview] anchor: footnote ${plan.footnote} search disagrees with its text for matchText=${JSON.stringify(plan.matchText)} ` +
          `(text has ${plan.total}, Word returned ${plan.search.items.length}); left unanchored rather than guessed.`,
      );
      return null;
    }
    return plan.search.items[plan.ordinal] ?? null;
  });
  hits.forEach((hit) => hit?.load("text"));
  await context.sync();
  return hits;
}

/**
 * AIVA-9042 — anchor findings whose occurrence lies past the main text's last
 * hit: occurrence `bodyHits + k` is the k-th word-bounded occurrence across the
 * footnotes, footnote by footnote (the convention footnotes.ts documents).
 *
 * Which footnote holds it is read from the footnotes' own text, so only that
 * one footnote is searched. A footnote whose search count disagrees with its
 * text is declined, not guessed — the rule the main text's paragraph fallback
 * already follows (`paragraphFallbackHit`). Quotes are compared straightened,
 * as every sweep compares them.
 */
async function anchorInFootnotes(
  context: Word.RequestContext,
  requests: FootnoteAnchorRequest[],
): Promise<AnchorResult[]> {
  const unanchored = (r: FootnoteAnchorRequest): AnchorResult => ({ index: r.request.index, anchorTag: null, fingerprint: null });
  const hits = await footnoteOccurrenceHits(
    context,
    requests.map((r) => ({ matchText: r.request.matchText, footnoteOccurrence: r.footnoteOccurrence })),
  );
  if (hits.length === 0) return requests.map(unanchored);

  const results = hits.map((hit, i): AnchorResult => {
    if (!hit) return unanchored(requests[i]!);
    const cc = hit.insertContentControl();
    const tag = `${ANCHOR_TAG_PREFIX}${generateUuidV4()}`;
    cc.tag = tag;
    cc.title = "AIVA review anchor";
    cc.appearance = Word.ContentControlAppearance.hidden;
    cc.cannotDelete = false;
    return { index: requests[i]!.request.index, anchorTag: tag, fingerprint: hit.text };
  });
  await context.sync();
  return results;
}

/** The main-text misses that may be footnote occurrences: the requested
 *  occurrence is at or past the number of hits the main text had. A miss
 *  INSIDE the main text's count (a declined paragraph search) stays a miss. */
function footnoteRequestsFor(results: BodyAnchorResult[], requests: AnchorRequest[]): FootnoteAnchorRequest[] {
  const byIndex = new Map(requests.map((r) => [r.index, r]));
  const out: FootnoteAnchorRequest[] = [];
  for (const result of results) {
    const request = byIndex.get(result.index);
    if (result.anchorTag !== null || result.bodyHits === undefined || !request) continue;
    const footnoteOccurrence = (request.occurrenceIndex ?? 0) - result.bodyHits;
    if (footnoteOccurrence >= 0) out.push({ request, footnoteOccurrence });
  }
  return out;
}

function anchorFindingsInner(findings: AnchorRequest[]): Promise<AnchorResult[]> {
  return Word.run((context) =>
    // Tracking forced off for the whole batch — see `withTrackingDisabled`,
    // shared with `repairFindingAnchor` so both anchor-inserting paths
    // restore the document's mode identically.
    withTrackingDisabled(context, async () => {
      const out: BodyAnchorResult[] = [];
      // Empty/whitespace-only match text is skipped without even calling
      // search — same guard as before batching existed.
      const usable: AnchorRequest[] = [];
      for (const finding of findings) {
        // AIVA-9179: Word rejects a search over its limit, and one rejected
        // search fails the whole batched sync — every other finding in the
        // chunk then falls back to one search at a time, which is what pushed
        // anchoring past its time limit on a run-in-clause document. Such a
        // text can never be found, so it is left unanchored here, alone.
        if (finding.matchText.length > WORD_SEARCH_MAX_LENGTH) {
          console.warn(
            `[docReview] anchor: matchText is ${finding.matchText.length} characters, over Word's search limit; ` +
              `not anchored: ${JSON.stringify(finding.matchText.slice(0, 60))}…`,
          );
          out.push({ index: finding.index, anchorTag: null, fingerprint: null });
        } else if (finding.matchText.trim()) usable.push(finding);
        else out.push({ index: finding.index, anchorTag: null, fingerprint: null });
      }

      for (let i = 0; i < usable.length; i += ANCHOR_CHUNK_SIZE) {
        const chunk = usable.slice(i, i + ANCHOR_CHUNK_SIZE);
        try {
          out.push(...(await anchorChunkBatched(context, chunk))); // NOSONAR - chunks share one Word context and must sync in order
        } catch (err) {
          // One finding's search text being invalid (exceeding Word's
          // search-string limits, or otherwise rejected by body.search)
          // fails the whole batched sync for this chunk — must never sink
          // anchoring for every OTHER finding, so fall back to one at a
          // time, but only for this chunk. Logged so it's visible WHY the
          // batch fell back at all, not just silently retried — a batch
          // failure this size (up to 25 findings) previously left no trace
          // of the actual thrown error anywhere.
          console.warn(
            `[docReview] anchor: batched search threw, falling back to sequential for ${chunk.length} finding(s):`,
            err,
          );
          for (const finding of chunk) out.push(await anchorOneSequential(context, finding)); // NOSONAR - sequential on purpose, a rejected anchor must not affect the next
        }
      }
      return withFootnoteAnchors(context, out, usable);
    }),
  );
}

/** AIVA-9042: the main-text pass's results, with every miss that may be a
 *  footnote occurrence retried in the footnotes — and `bodyHits` dropped, so
 *  the result is exactly the shape callers always got. A footnote pass that
 *  throws leaves those findings unanchored, never the main text's. */
async function withFootnoteAnchors(
  context: Word.RequestContext,
  results: BodyAnchorResult[],
  requests: AnchorRequest[],
): Promise<AnchorResult[]> {
  const plain = results.map(({ index, anchorTag, fingerprint }): AnchorResult => ({ index, anchorTag, fingerprint }));
  const footnoteRequests = footnoteRequestsFor(results, requests);
  if (footnoteRequests.length === 0) return plain;
  try {
    const footnoteResults = new Map((await anchorInFootnotes(context, footnoteRequests)).map((r) => [r.index, r]));
    return plain.map((r) => footnoteResults.get(r.index) ?? r);
  } catch (err) {
    logOfficeError(err, "word-docreview-anchor-footnotes");
    console.warn("[docReview] anchor: footnote pass threw; footnote findings left unanchored:", err);
    return plain;
  }
}

/** Delete every `AIVA_*` content control in the document, keeping its
 * contents. Run at the start of a new scan (guards against anchors left by
 * an abandoned prior session) and when the operator finishes reviewing. */
/**
 * True when the document cannot be written to — Word's "Viewing" mode, a file
 * opened read-only, or one under Protected View.
 *
 * Every other Word skill defers its write to a pill the user clicks
 * (`emitPills` in handlers.ts), so a read-only document simply never gets
 * written to and nothing fails mid-turn. The docreview scan is the ONE
 * exception: it writes IMMEDIATELY and unconditionally, before it reads
 * anything, because `cleanupAllAnchors()` has to clear stale anchors before
 * the sweeps run. In Viewing mode that delete throws, the scan dies before a
 * single rule is swept, and the turn surfaces a generic "the document scan
 * failed - please try again" - advice that cannot work, since every retry in
 * Viewing mode fails identically. Hence this pre-flight check: refuse with
 * something actionable instead of failing on a write the user can't see.
 *
 * `Document.isReadOnly` is WordApiDesktop 1.4 — desktop-only, same gate
 * `supportsBookmarks()` uses. On Word on the web (and any host without it)
 * this returns false and the scan proceeds exactly as it does today: the
 * check is a targeted improvement to the desktop message, never a new way
 * to block a host that was working before.
 */
export async function isDocumentReadOnly(): Promise<boolean> {
  try {
    if (!Office.context.requirements.isSetSupported("WordApiDesktop", "1.4")) return false;
  } catch {
    return false;
  }
  try {
    return await Word.run(async (context) => {
      context.document.load("isReadOnly");
      await context.sync();
      return context.document.isReadOnly === true;
    });
  } catch (err) {
    // Never let the guard itself be what breaks a scan that would have
    // worked: an unreadable flag degrades to "writable" and the scan proceeds
    // exactly as it did before this check existed. Logged rather than
    // swallowed, because a host that starts throwing here would otherwise
    // silently reinstate the original Viewing-mode failure with no trace.
    logOfficeError(err, "word-docreview-is-document-read-only");
    return false;
  }
}

export function cleanupAllAnchors(): Promise<number> {
  return Word.run(async (context) => {
    const ccs = context.document.contentControls;
    ccs.load("items/tag");
    await context.sync();
    const stale = ccs.items.filter((c) => (c.tag ?? "").startsWith(ANCHOR_TAG_PREFIX));
    for (const cc of stale) cc.delete(true);
    await context.sync();
    return stale.length + (await cleanupFootnoteAnchors(context, new Set(stale.map((c) => c.tag))));
  });
}

/** AIVA-9042: the review's anchors inside footnotes — skipping any tag the
 *  document-wide pass already deleted, in case this host's document-wide
 *  collection reaches into footnotes too. */
async function cleanupFootnoteAnchors(context: Word.RequestContext, alreadyDeleted: ReadonlySet<string>): Promise<number> {
  const bodies = await loadFootnoteBodies(context);
  if (bodies.length === 0) return 0;
  try {
    const collections = bodies.map((body) => {
      const controls = body.contentControls;
      controls.load("items/tag");
      return controls;
    });
    await context.sync();
    const stale = collections
      .flatMap((controls) => controls.items)
      .filter((c) => (c.tag ?? "").startsWith(ANCHOR_TAG_PREFIX) && !alreadyDeleted.has(c.tag));
    for (const cc of stale) cc.delete(true);
    await context.sync();
    return stale.length;
  } catch (err) {
    // A scan starts with this cleanup: a footnote that refuses it must not
    // stop the review of everything else. The anchor left behind is hidden
    // and cleared by the next scan that can.
    logOfficeError(err, "word-docreview-cleanup-footnote-anchors");
    return 0;
  }
}

// ---------------------------------------------------------------------------
// Shared deterministic-evaluation helpers (AIVA-7506 frontend rework)
//
// The 10 rules that need no LLM judgment (missing_hyperlink, broken_reference, bracket_heading_mismatch, inconsistent_formatting, exhibit_schedule_mismatch's
// unambiguous case, defined_term_section_mismatch, shortened_hyperlink_text, defined_not_used, alphabetical_order, numbering_order, missing_defined_term, ambiguous_reference) now evaluate here,
// client-side, right after their own find<Rule> sweep — ported 1:1 from the
// Python rules.py this replaces (see the deleted
// apps/backend/ai/integrations/word/doc_review/rules.py for the original).
// `word.docreview.scan`'s handler runs find, then evaluate, then POSTs the
// resulting findings straight to the bulk-create endpoint — never through
// the LLM. Only topic_mismatch, exhibit_schedule_mismatch's ambiguous case, hyperlink_target_mismatch, and used_not_defined still need a
// judgment call; see their own sections below for the frontend-side
// filtering/bucketing that narrows what crosses to Python.
// ---------------------------------------------------------------------------

/** Mirrors the backend's (now-deleted) `EvaluatedFinding` — the pre-merge
 * shape every evaluate<Rule> function builds, before `toFindingCreate`
 * folds `matchText`/`occurrenceIndex` into `fixParams` the same way the
 * Python persistence layer used to. */
interface RawFinding {
  ruleId: string;
  matchText: string;
  problem: string;
  comment: string;
  fixHandler: string;
  fixParams?: Record<string, unknown>;
  confidence?: number | null;
  occurrenceIndex?: number;
}

function toFindingCreate(f: RawFinding): FindingCreate {
  const fix_params: Record<string, unknown> = { ...f.fixParams, matchText: f.matchText };
  if (f.occurrenceIndex !== undefined) fix_params.occurrenceIndex = f.occurrenceIndex;
  return {
    rule_id: f.ruleId,
    host: "word",
    problem: f.problem,
    comment: f.comment,
    fix_handler: f.fixHandler,
    fix_params,
    confidence: f.confidence ?? null,
  };
}

// A numeric section/article label, e.g. "10", "10.01" — as opposed to a
// lettered Exhibit/Schedule label like "B". Mirrors rules.py's
// `_NUMERIC_LABEL`.
const NUMERIC_LABEL = /^\d+(?:\.\d+)*$/;

// A reference/hyperlink text's own leading category word, captured — mirrors
// rules.py's `_HAS_REFERENCE_WORD`. Shared by inconsistent_formatting (grouping the formatting
// majority per category) and shortened_hyperlink_text (excluding an already-full reference from
// "looks shortened").
const HAS_REFERENCE_WORD = /^(section|article|clause|exhibit|schedule)/i;

/** "Section"/"Article"/"Exhibit"/"Schedule"/"clause" out of a reference's own
 * leading word, lowercased — e.g. "Section 5.2" and "Exhibit B" belong to
 * different categories, since a document can legitimately format one type
 * differently from another. Falls back to "" (its own bucket) for a
 * reference matching neither prefix. Mirrors rules.py's
 * `_reference_category`. */
function referenceCategory(matchText: string): string {
  const match = HAS_REFERENCE_WORD.exec(matchText);
  return match?.[1]?.toLowerCase() ?? "";
}

/** Whether a reference's own category always targets a heading EXPLICITLY
 * titled with that same prefix ("SCHEDULE 2 — ...", "EXHIBIT A — ..."),
 * never a bare-numbered one ("2. Definitions"). Section/Article/Clause
 * references genuinely do target bare-numbered headings, so only
 * Schedule/Exhibit need this distinction — without it, a numeric-labelled
 * Schedule/Exhibit reference (e.g. "Schedule 2") used to resolve through the
 * same bare-digit heading index Section references use, silently colliding
 * with an unrelated, same-numbered Section (e.g. "Schedule 2" resolving
 * against "2. Definitions") whenever both happened to share a number. */
function isExplicitPrefixCategory(matchText: string): boolean {
  const category = referenceCategory(matchText);
  return category === "schedule" || category === "exhibit";
}

/** The key every occurrenceIndex `seenCount` map must use — never the raw
 *  matchText. The generic anchor step (anchorChunkBatched/anchorOneSequential)
 *  always searches with `matchCase: false`, so "EXHIBIT B" and "Exhibit B"
 *  are the SAME occurrence sequence to a live search, even though they're
 *  different literal strings. A seenCount keyed by raw matchText silently
 *  split them into two separate counts — confirmed via a real regression
 *  test for exhibit_schedule_mismatch, whose own pattern deliberately captures multiple case
 *  variants as first-class alternatives (a common real shape: an ALL-CAPS
 *  "EXHIBIT B" heading alongside Title-Case "Exhibit B" body mentions) —
 *  but the same gap exists anywhere a pattern carries the `i` flag,
 *  including broken_reference's own numeric reference pattern this file's every other
 *  occurrenceIndex ultimately derives from. The STORED/returned matchText
 *  keeps its own real casing; only the counting key is folded. */
function occurrenceCountKey(matchText: string): string {
  return matchText.toLowerCase();
}

/** Offsets, ascending, of every occurrence of `needle` in `text` that a live
 *  `body.search(needle, { matchWholeWord: true })` would return.
 *
 *  The sibling of `boundedOccurrenceOrdinals`: one records WHERE Word's hits
 *  are, the other WHICH of its non-overlapping matches they are. Both read
 *  `occurrenceScan`, so they cannot disagree about what a word boundary is —
 *  and that disagreement is exactly the defect class this file keeps
 *  reopening. See `documentOccurrenceCounter`. */
function boundedOccurrenceOffsets(text: string, needle: string): number[] {
  const offsets: number[] = [];
  for (const hit of occurrenceScan(text, needle)) if (hit.bounded) offsets.push(hit.offset);
  return offsets;
}

/** How many hits a live `body.search(matchText, { matchWholeWord: true })`
 *  SHOULD return, computed from the document's own paragraph text.
 *
 *  The point of comparison for `occurrenceCountDisagrees`. Kept beside
 *  `boundedOccurrenceOffsets` because it is that function summed — if the two
 *  ever drift apart, the check below stops meaning anything. */
function expectedBoundedTotal(paragraphTexts: readonly string[], matchText: string): number {
  let total = 0;
  for (const text of paragraphTexts) {
    for (const hit of occurrenceScan(text, matchText)) if (hit.bounded) total += 1;
  }
  return total;
}

/** Whether Word disagrees with us about how many times `matchText` occurs —
 *  i.e. whether an `occurrenceIndex` for it can still be trusted.
 *
 *  ## Why this exists at all
 *
 *  `documentOccurrenceCounter` removes the five known reasons the counter and
 *  the live search disagreed (see its docstring). It cannot remove the SIXTH,
 *  because the sixth is by definition one nobody has thought of: both sides
 *  rest on "Word's whole-word boundary is alphanumeric", established by
 *  experiment in AIVA-8055 and AIVA-8296. If that is wrong for some character
 *  nobody has tried — an en dash, a non-breaking space, a ligature — every
 *  index for that text is off and the finding lands on the wrong words again.
 *
 *  So rather than trust the rule, this asks Word and checks. Our count and
 *  Word's count are of the same thing; if they differ at all, our model of
 *  Word is wrong for this text, and the honest response is to decline the
 *  finding rather than point somewhere plausible-looking.
 *
 *  Declining is not a cost-free default, so it is deliberately narrow: it
 *  fires only on a real numeric disagreement for one specific text, leaving
 *  every other finding in the run untouched. The alternative — emitting it
 *  anyway — is what AIVA-8544 was reported as, and what AIVA-8547 already
 *  rejected in its own form ("a borrowed value looks like a real vote").
 *
 *  Logged, never silent: a tripped check is the early warning for leak six,
 *  and a mismatch that only ever shows up as a missing finding is a mismatch
 *  nobody can diagnose. */
function occurrenceCountDisagrees(
  paragraphTexts: readonly string[],
  matchText: string,
  liveHitCount: number,
  where: string,
): boolean {
  // Nothing loaded to compare against — an older host, or a caller that did
  // not load paragraphs. Same posture as every other capability probe in this
  // file (`typeof body.search !== "function"`): fall back to the previous
  // behaviour rather than let a missing input read as a disagreement and
  // decline every finding in the document.
  if (paragraphTexts.length === 0) return false;
  const expected = expectedBoundedTotal(paragraphTexts, matchText);
  if (expected === liveHitCount) return false;
  // Deliberately states only the FACT, not the consequence: the two callers
  // respond differently (inconsistent_formatting declines the finding, the
  // shared anchor step reports and proceeds — see each for why), and a message
  // that named one of them would be wrong half the time it is printed.
  console.warn(
    `[docReview] ${where}: occurrence count disagrees with Word for matchText=${JSON.stringify(matchText)} ` +
      `(the document's own text says ${expected}, body.search returned ${liveHitCount}), so no occurrence ` +
      `index for this text can be trusted. This is the AIVA-8544 class of defect; the matchText above is the lead.`,
  );
  return true;
}

/** Queue a load of the document's own paragraph text, when the host can supply
 *  it. `null` when it cannot — an older Word build, or a test double that
 *  answers `body.search` but nothing else.
 *
 *  Optional by design. `occurrenceCountDisagrees` is a safety net, never a
 *  prerequisite for finding anything, so a host that cannot hand paragraphs
 *  back gets the unchecked behaviour rather than an empty panel. Same posture
 *  as `countBodyOccurrences` degrading when `body.search` is missing.
 *
 *  Caller must `context.sync()` before reading `paragraphTextsOf`. */
function loadOptionalParagraphs(context: Word.RequestContext): Word.ParagraphCollection | null {
  const paragraphs = context.document.body.paragraphs;
  if (typeof paragraphs?.load !== "function") return null;
  paragraphs.load("items/text");
  return paragraphs;
}

/** The loaded paragraph text, or `[]` when the host supplied none. */
function paragraphTextsOf(paragraphs: Word.ParagraphCollection | null): string[] {
  return paragraphs?.items?.map((p) => p.text ?? "") ?? [];
}

/** The matchTexts whose live hit count Word disagrees with us about, and whose
 *  occurrence indexes are therefore not safe to use. See
 *  `occurrenceCountDisagrees`. */
function distrustedMatchTexts(
  paragraphTexts: readonly string[],
  rangesByText: Map<string, Word.Range[]>,
  where: string,
): Set<string> {
  const distrusted = new Set<string>();
  for (const [text, ranges] of rangesByText) {
    if (occurrenceCountDisagrees(paragraphTexts, text, ranges.length, where)) distrusted.add(text);
  }
  return distrusted;
}

/** Mints the `occurrenceIndex` every sweep in this file carries, by counting
 *  the LITERAL `matchText` across the document's own paragraph text.
 *
 *  ## Why not count as the sweep scans
 *
 *  Every sweep finds its matches with a regex, and a regex is greedy: it
 *  produces "Section 3.01" and nothing else. The anchor step then asks WORD
 *  for "Section 3", and Word's whole-word boundary is any non-alphanumeric
 *  character — so Word counts the "Section 3" sitting inside "Section 3.01"
 *  as a hit, and returns a longer list than the sweep ever counted. The
 *  finding's index then points into the wrong place in that list.
 *
 *  That disagreement has been patched five times, each time for a different
 *  reason the counter could not see: heading paragraphs (AIVA-8065), Table of
 *  Contents entries (AIVA-8180), letter case (AIVA-8300), the review's own
 *  comments (AIVA-8296), and dotted labels (AIVA-8544, this one). Every one
 *  of them is the same shape — the counter modelled the document, the search
 *  read it, and the two disagreed.
 *
 *  ## What this does instead
 *
 *  It does not model anything. It counts occurrences of the literal string
 *  the anchor step will search for, in the document's own text, using the
 *  boundary rule `boundedOccurrenceOffsets` shares with the locator. What
 *  produced the match — which regex, which paragraph style, which case — is
 *  not consulted and cannot cause a disagreement. A heading, a TOC entry and
 *  a review comment are all just text that Word's search will find, so they
 *  are all just text that this counts. The five leaks above close together,
 *  and a sixth of the same shape has nowhere to enter.
 *
 *  Offsets rather than "the Nth match this sweep produced", because two
 *  different labels can start at the same paragraph and only the offset says
 *  which live hit is which.
 *
 *  Costs no Word round trip at all: it reads the paragraph text the sweep has
 *  already loaded. Per distinct matchText it walks the document once and
 *  caches the result, so a sweep over a 10-page contract (170 paragraphs, ~32
 *  distinct reference texts) is a few thousand in-memory `indexOf` calls. */
function documentOccurrenceCounter(paragraphTexts: readonly string[]) {
  // key → per-paragraph bounded offsets, plus the running total before each
  // paragraph. Built once per distinct matchText, on first use.
  const byKey = new Map<string, { offsets: number[][]; countBefore: number[] }>();

  function tableFor(matchText: string) {
    const key = occurrenceCountKey(matchText);
    const cached = byKey.get(key);
    if (cached) return cached;
    const offsets = paragraphTexts.map((text) => boundedOccurrenceOffsets(text, matchText));
    const countBefore: number[] = [];
    let running = 0;
    for (const paragraphOffsets of offsets) {
      countBefore.push(running);
      running += paragraphOffsets.length;
    }
    const table = { offsets, countBefore };
    byKey.set(key, table);
    return table;
  }

  return {
    /** The occurrenceIndex of the match sitting at `offset` in
     *  `paragraphIndex`, or `null` when Word's own search would not return
     *  that position at all.
     *
     *  `null` is not a failure to be papered over: it means the sweep's regex
     *  matched something a whole-word search will not find — a label inside a
     *  longer alphanumeric run, say — so there is no live hit to anchor to and
     *  no honest index to give. Every caller drops the match rather than
     *  guessing one, for the same reason AIVA-8547 stopped borrowing a nearby
     *  range: a wrong location reads as a real finding in the wrong place,
     *  which is worse than no finding at all. */
    at(paragraphIndex: number, offset: number, matchText: string): number | null {
      const { offsets, countBefore } = tableFor(matchText);
      const within = offsets[paragraphIndex];
      if (!within) return null;
      const ordinal = within.indexOf(offset);
      if (ordinal === -1) return null;
      return countBefore[paragraphIndex]! + ordinal;
    },
  };
}

// A heading's own leading number, e.g. "10.01" out of "10.01 Payment Terms".
// Mirrors rules.py's `_HEADING_NUMBER` — shared by every rule that needs to
// resolve a heading to its number (or vice versa).
const HEADING_NUMBER = /^\s*(\d+(?:\.\d+)*)/;

/** The word a heading may put in front of its own number — "Section 4. Fees
 *  and Payment" rather than "4.01  Fees". `HEADING_NUMBER` cannot see past it
 *  (it anchors on a leading digit), and it must NOT be taught to: thirteen
 *  other call sites depend on exactly that meaning, among them Word's own
 *  rendered `listNumber` and the numbered-item filter, where accepting a word
 *  would turn every titled heading into a list item.
 *
 *  Deliberately NOT "schedule"/"exhibit"/"annex". Those live in their own
 *  label namespace, resolved by `letteredTargetExists` against heading TEXT —
 *  folding them in here would make "Schedule 1 — Fee Schedule" contribute the
 *  number "1" and quietly satisfy a reference to Section 1. */
const SECTION_WORD_PREFIX = /^\s*(?:section|article|clause)\s+/i;

/** A heading's own number, whether it leads with the digits ("4.01  Fees") or
 *  spells the category first ("Section 4. Fees and Payment"). `null` when the
 *  heading carries no number at all. */
function headingOwnNumber(text: string): string | null {
  const direct = HEADING_NUMBER.exec(text);
  if (direct?.[1]) return direct[1];
  const withoutWord = text.replace(SECTION_WORD_PREFIX, "");
  if (withoutWord === text) return null;
  return HEADING_NUMBER.exec(withoutWord)?.[1] ?? null;
}

/** "10.01(a)" -> "10.01" — mirrors rules.py's `_strip_subclause_letter`. */
function stripSubclauseLetter(label: string): string {
  return label.replace(/\([a-z0-9]+\)$/i, "");
}

function numericTargetExists(baseNumber: string, index: HeadingIndex): boolean {
  if (index.headingNumbers.has(baseNumber)) return true;
  // EXACT match only, and deliberately BEFORE the prefix walk below rather
  // than folded into `headingNumbers`.
  //
  // Folding them in is the obvious version and it is wrong: the prefix walk
  // treats "a heading whose number is a prefix of mine" as proof I exist, so
  // adding the top-level "12" would make a reference to the non-existent
  // Section 12.05 resolve against Section 12 and stop being reported. That
  // trades this rule's false positives for false NEGATIVES — a broken
  // reference shipping silently in a contract, which is the worse half of the
  // trade and the one nobody would notice. The prefix walk keeps seeing
  // exactly the numbers it saw before.
  if (index.sectionWordNumbers.has(baseNumber)) return true;
  for (const hn of index.headingNumbers) {
    if (baseNumber === hn || baseNumber.startsWith(`${hn}.`)) return true;
  }
  return false;
}

/** Whether `needle` (already lowercased) appears in `heading` as its own
 * word/phrase, not merely as a substring — a plain `.includes()` check would
 * wrongly match "exhibit b" inside "exhibit b1" or "exhibit b-2", treating a
 * DIFFERENT exhibit's heading as evidence this one exists. Bounded on both
 * sides by "not alphanumeric", mirroring rules.py's `_label_appears_in`. */
function labelAppearsIn(needle: string, heading: string): boolean {
  const pattern = new RegExp(`(?<![a-z0-9])${escapeRegExp(needle)}(?![a-z0-9])`);
  return pattern.test(heading);
}

/** Whether every whole-word occurrence of `needle` in `heading` runs straight
 *  on into a sub-number — "schedule 2" inside "schedule 2.1 — rates". Both
 *  already lowercased. Used only to keep a sub-schedule from counting as a
 *  second heading for the parent number. */
function continuesAsSubNumber(needle: string, heading: string): boolean {
  const bounded = new RegExp(`(?<![a-z0-9])${escapeRegExp(needle)}(?![a-z0-9])`, "g");
  let any = false;
  for (const match of heading.matchAll(bounded)) {
    any = true;
    if (!/^\.\d/.test(heading.slice(match.index + match[0].length))) return false;
  }
  return any;
}

function letteredTargetExists(matchText: string, headingTextsLower: string[]): boolean {
  const needle = matchText.toLowerCase();
  return headingTextsLower.some((heading) => labelAppearsIn(needle, heading));
}

/** What every rule that asks "does this target exist" reads. Shared so the
 *  rules can never silently disagree about what counts as existing. */
interface HeadingIndex {
  /** Numbers taken from headings that LEAD with their digits ("4.01  Fees").
   *  Unchanged in meaning since AIVA-7506 — `numericTargetExists`' prefix walk
   *  is defined over this set and only this one. */
  headingNumbers: Set<string>;
  /** Numbers from headings that spell the category first ("Section 4. Fees
   *  and Payment"). Kept apart from `headingNumbers` on purpose — see
   *  `numericTargetExists` for what merging them would break. */
  sectionWordNumbers: Set<string>;
  headingTextsLower: string[];
}

function headingIndex(headings: RawHeading[]): HeadingIndex {
  const headingNumbers = new Set<string>();
  const sectionWordNumbers = new Set<string>();
  for (const heading of headings) {
    const leading = HEADING_NUMBER.exec(heading.text)?.[1];
    if (leading) {
      headingNumbers.add(leading);
      continue;
    }
    // AIVA-8544 follow-up: a document whose top-level headings are titled
    // "Section 1. Definitions and Interpretation" used to contribute NO
    // top-level numbers at all, so every single bare "Section N" reference in
    // it was reported as pointing at nothing. Confirmed live on the ticket's
    // own document: 57 sub-heading numbers indexed, zero top-level ones.
    const worded = headingOwnNumber(heading.text);
    if (worded) sectionWordNumbers.add(worded);
  }
  return { headingNumbers, sectionWordNumbers, headingTextsLower: headings.map((h) => h.text.toLowerCase()) };
}

function referenceTargetExists(targetLabel: string, matchText: string, index: HeadingIndex): boolean {
  const baseLabel = stripSubclauseLetter(targetLabel);
  if (!isExplicitPrefixCategory(matchText) && NUMERIC_LABEL.test(baseLabel)) {
    return numericTargetExists(baseLabel, index);
  }
  return letteredTargetExists(matchText, index.headingTextsLower);
}

// ---------------------------------------------------------------------------
// broken_reference — reference target exists (deterministic; the walking-skeleton rule)
// ---------------------------------------------------------------------------

export interface RawBrokenReference {
  matchText: string;
  /** The section/article/exhibit/schedule/clause label the reference names,
   * e.g. "10.01(a)" out of "Section 10.01(a)". */
  targetLabel: string;
  /** Which occurrence of this exact matchText, counted the way a live
   *  `body.search(matchText, { matchWholeWord: true })` counts it — which is
   *  NOT the same as "how many times this sweep's own regex produced it".
   *  A longer label whose extra part is separated by a non-alphanumeric
   *  character ("Section 3.01") IS one more live occurrence of the shorter
   *  one ("Section 3"), because Word's whole-word boundary is alphanumeric
   *  and "." is not. `documentOccurrenceCounter` mints this by counting the
   *  literal text in the document rather than tallying what a regex
   *  produced, which is what keeps the two in step; see its own docstring
   *  for the five separate bugs that disagreement caused.
   *
   *  Counted across EVERY
   *  paragraph in the document — heading paragraphs included, even though
   *  a heading's own occurrence is never itself pushed into this list (a
   *  heading isn't a cross-reference to itself). A live body.search() at
   *  anchor time finds a heading's own textual occurrence just as readily
   *  as a body one, so if this index were counted only over the
   *  heading-excluded references a rule actually sees (as it used to be),
   *  every reference after a same-text heading would be silently off by
   *  one — the exact shape of bug inconsistent_formatting's and shortened_hyperlink_text's own occurrenceIndex bugs
   *  turned out to be, just one level upstream of where those were fixed.
   *  Every rule reading this sweep (missing_hyperlink, topic_mismatch, inconsistent_formatting, and any future one)
   *  must use this value directly rather than recomputing its own
   *  occurrence count over its own filtered view of the list. */
  occurrenceIndex: number;
  paragraphIndex: number;
}

export interface RawHeading {
  level: number;
  text: string;
  paragraphIndex: number;
  /** "word-style" = a real Word `Heading N` paragraph, authoritative.
   * "inferred" = promoted from a plain-text multi-part number by
   * `collectHeadings`'s structural checks (see its own docstring).
   * Optional — every OTHER heading source in this file (test fixtures,
   * older callers) still omits it; only `collectHeadings` sets it. */
  source?: "word-style" | "inferred";
}

export interface BrokenReferenceSweep {
  references: RawBrokenReference[];
  headings: RawHeading[];
  /** The document's numbered outline, for numbering_order alone — a superset of
   *  the numbered entries in `headings`, since it also carries paragraphs whose
   *  number Word's list formatting renders. Optional so a hand-built sweep in a
   *  test (or an older caller) still type-checks; absent means "nothing
   *  numbered was collected", not "the document has no numbering". */
  numberedItems?: RawNumberedItem[];
  /** Every number any paragraph opens with (`leadingNumbers`), for
   *  numbering_order's gap check. Absent: only `numberedItems` count. */
  documentNumbers?: string[];
}

// No trailing `\b`: a `)` closing a "(a)" suffix is already a non-word
// character, so a word-boundary assertion right after it has nothing to
// transition against and forces the engine to backtrack out of the
// parenthetical group entirely — silently dropping "(a)" off "10.01(a)".
//
// At most one trailing "(x)" sub-clause, not a repeated `*` — matching
// _strip_subclause_letter's own single-strip expectation on the backend
// (rules.py: `re.sub(r"\([a-z0-9]+\)$", ...)`, no repeat), and avoiding the
// nested-quantifier shape (a `*`-repeated group containing its own `+`)
// that made this regex's complexity too high to read confidently.
//
// Case-insensitive throughout is safe here — the label is a digit
// sequence, and digits have no case.
const BROKEN_REFERENCE_NUMERIC_PATTERN = /\b(Section|Article|Exhibit|Schedule|Clause)\s+(\d+(?:\.\d+)*(?:\([a-z0-9]+\))?)/gi;

// Lettered references take TWO regexes, not one: this case-INSENSITIVE scan
// finds every candidate so it can be counted, and BROKEN_REFERENCE_LETTER_ACCEPT
// below decides which of those counted candidates is a real reference. They
// were a single case-sensitive `/g` pattern until AIVA-8300, which is exactly
// the bug — one regex cannot do both jobs, because the two jobs disagree
// about case.
//
// The scan has to be case-INsensitive because `occurrenceIndex` exists to
// index into `body.search(matchText, { matchCase: false })`'s results at
// anchor time, and that search is case-INsensitive too. A document's Table of
// Contents and its headings both render a lettered reference's text in CAPS
// ("EXHIBIT A"), which a case-SENSITIVE scan never matches and therefore
// never counted — while the live search finds every one of them. In the
// reported document the counter saw 3 occurrences of "Exhibit A" where the
// live search found 5, so the finding for the first real body mention carried
// occurrenceIndex 0 and anchored to `items[0]` — the TOC entry — putting
// Apply's hyperlink into the Table of Contents instead of into the reference.
// Numeric references were always immune: their own pattern is `/gi`, so it
// counts "SCHEDULE 1" in a TOC exactly as the live search does.
//
// This is also what defeated the TOC guard in `findBrokenReference` below
// (AIVA-8065/8199): that guard deliberately COUNTS a TOC's mirrored
// occurrence so the indices stay aligned, but there was nothing for it to
// count when the scan could not match CAPS in the first place.
//
// Scanning case-insensitively costs nothing in precision, because the
// trailing `\b` — not the case-sensitivity — is what rejects the prose false
// matches ACCEPT's own comment describes: "an Exhibit to this Agreement" and
// "this Clause applies" never match this scan either way, since the letter
// has to be a whole word. All the `i` flag newly admits is case variants of a
// real reference: "EXHIBIT A" (a TOC entry or heading) and "exhibit a" ("we
// will exhibit a prototype"). Neither is ACCEPTed as a reference, and both
// SHOULD be counted — `body.search("Exhibit A", { matchCase: false })` finds
// both, so the counter has to see them for occurrence N here to mean
// occurrence N there.
//
// `[a-z]`, not `[A-Za-z]`: under the `i` flag the two are the same set, and
// spelling both out is a duplicated character class (Sonar typescript:S5869).
const BROKEN_REFERENCE_LETTER_SCAN = /\b(Section|Article|Exhibit|Schedule|Clause)\s+([a-z])\b/gi;

// Deliberately case-SENSITIVE, unlike the numeric pattern above. The lettered
// branch used to share one `/gi` regex with that one, which was a real bug:
// under a shared `i` flag, `[A-Z]` stops meaning "uppercase" and matches ANY
// letter, so ordinary prose like "attached as an Exhibit to this Agreement"
// or "this Clause applies" was read as a reference to "Exhibit t" / "Clause
// a" — the trailing letter of an unrelated word, not a real lettered
// reference. A genuine lettered reference ("Exhibit A") is always written
// capitalized in practice, so requiring case (keyword AND letter) eliminates
// the false match without losing any real one.
//
// Applied as a whole-match test against a candidate the scan above already
// counted, rather than as the scan itself — counting and accepting are
// separate decisions (see the scan's own comment). Anchored and non-global:
// no `lastIndex` state to reset between `.test()` calls.
const BROKEN_REFERENCE_LETTER_ACCEPT = /^(?:Section|Article|Exhibit|Schedule|Clause)\s+[A-Z]$/;

/** Every reference-shaped match in one paragraph's text, in document order.
 *
 * The two patterns are merged back into a single position-ordered list: a
 * caller relying on `references` reflecting reading order must not see an
 * order that depends on which pattern happened to run first.
 *
 * `isReference` separates "counted" from "is actually a cross-reference" —
 * the lettered half is SCANned case-insensitively so every occurrence gets
 * counted, then narrowed back to the case-sensitive definition by ACCEPT (see
 * BROKEN_REFERENCE_LETTER_SCAN's own comment). Only the lettered half is
 * narrowed; a numeric match could never satisfy ACCEPT. Callers must count
 * every entry and push only the ones flagged `isReference`.
 */
function scanReferenceMatches(
  text: string,
): Array<{ matchText: string; targetLabel: string; isReference: boolean; offset: number }> {
  const numeric = [...text.matchAll(BROKEN_REFERENCE_NUMERIC_PATTERN)].map((m) => ({ m, isLettered: false }));
  const lettered = [...text.matchAll(BROKEN_REFERENCE_LETTER_SCAN)].map((m) => ({ m, isLettered: true }));
  return [...numeric, ...lettered]
    .sort((a, b) => a.m.index - b.m.index)
    .map(({ m, isLettered }) => ({
      matchText: m[0],
      targetLabel: m[2] ?? "",
      isReference: !isLettered || BROKEN_REFERENCE_LETTER_ACCEPT.test(m[0]),
      // Where the match starts, so `documentOccurrenceCounter` can tell this
      // occurrence from another of the same text in the same paragraph.
      offset: m.index,
    }));
}

// ---------------------------------------------------------------------------
// Heading collection — explicit Word Heading-N paragraphs (authoritative)
// UNION structurally-promoted plain-text sub-clause numbers (AIVA-7947).
//
// Legal templates commonly number sub-clauses ("1.1 Definitions", "10.01(a)
// Notice") as ordinary body text, never applying a Word Heading style below
// the top-level Article/Section — which used to make every rule keyed off
// `collectHeadings`'s output ("only checks the main heading, not
// sub-headings"). A prior attempt fixed this by treating ANY paragraph
// starting with a multi-part number as a heading, which is unsafe: ordinary
// prose like "1.2 million customers were affected" or a decimal expression
// at a paragraph's start would also qualify. This version treats a
// multi-part number as a CANDIDATE only — it is promoted to a heading solely
// on deterministic structural evidence (a sequential sibling under the same
// parent number, or an explicit heading whose own number IS that parent),
// never on the number pattern alone.
// ---------------------------------------------------------------------------

// A genuine multi-part structural number at the very start of a paragraph,
// e.g. "1.1" out of "1.1 Definitions" or "10.01(a) Notice of Termination".
// A lone "1." is excluded on purpose (requires a SECOND "."-separated part):
// that is an ordinary numbered-list item, not structural sub-numbering.
const SUB_HEADING_NUMBER = /^(\d+(?:\.\d+)+)(?=[\s().\]:]|$)/;

// Immediately following the number, one of these words means "this is a
// quantity/measurement", not a heading title — kills "1.2 million dollars
// were generated" and "3.5 percent of the vote" without needing any
// structural signal at all. Deliberately NOT relying on capitalization
// (legal documents use inconsistent casing conventions) or a generic
// sentence detector — a small, explainable denylist is easier to reason
// about and extend than either.
const QUANTITY_CONTINUATION_WORDS = new Set([
  "million",
  "billion",
  "trillion",
  "thousand",
  "percent",
  "%",
  "years",
  "year",
  "days",
  "day",
  "months",
  "month",
  "hours",
  "hour",
  "minutes",
  "minute",
  "dollars",
  "dollar",
  "times",
  "km",
  "kg",
  "miles",
  "mile",
  "meters",
  "metre",
  "metres",
]);

// A candidate paragraph is disqualified past this many words after its
// number — a real sub-heading title is short; a full sentence isn't.
const MAX_HEADING_CANDIDATE_WORDS = 12;

// A candidate that's actually a formal defined-term declaration (e.g. "2.8
// "Services" means the services described in Section 4...") is a NUMBERED
// LIST ITEM in a Definitions section, not a structural sub-heading — even
// though it can be short enough, and have enough numbered siblings sharing
// its parent stem, to otherwise pass every other signal here. Confirmed
// against a real planted test document: "2.8" and "2.10" (the two SHORTEST
// of 11 "N.M "Term" means..." definitions — the other 9 were long enough to
// already fail the word-count check below) were promoted as inferred
// sub-headings, which numbering_order's numbering check then compared against the
// document's REAL section numbers (inferred headings are appended after
// explicit ones, not in true document order) and flagged as "out of
// sequence," suggesting a nonsensical renumber to "15.1". Built from the shared
// definition phrases (AIVA-9190), so "has the meaning set forth in" counts too.
const LOOKS_LIKE_DEFINED_TERM_DECLARATION = new RegExp(
  String.raw`^"[^"]{1,60}"\s+(?:${DEFINITION_VERB}|${DEFINITION_MEANING_VERB})\b`,
  "i",
);

/**
 * A RUN-IN heading's title: the part before the body text that shares its
 * paragraph. Legal drafting very commonly writes
 *
 *     10.01  Obligation of Confidence.  Each party shall keep confidential…
 *
 * as ONE paragraph — title and body together — so the word-count check below
 * measured 22 words and rejected a real sub-heading (AIVA-8540). Every
 * `Section 10.01` reference then had no target to resolve against and
 * broken_reference reported it missing.
 *
 * The boundary is the typographic convention that makes it a run-in heading in
 * the first place: a short title ending in `.` or `:`, followed by the DOUBLE
 * space (or more) that separates it from the sentence after it. Deliberately
 * not a single space — that would split ordinary prose at its first full stop
 * and promote half a sentence. The 80-character ceiling keeps a long opening
 * sentence from qualifying however it is spaced.
 *
 * Returns null when the text is not shaped like a run-in heading, which leaves
 * every existing judgement exactly as it was.
 */
// `[^.:]{0,79}` rather than a lazy `.{1,80}?`: a negated class cannot
// backtrack across the terminator it is looking for, so this is linear where
// the lazy form was super-linear on a long paragraph with no match (Sonar
// S5852). Same matches — the lazy form also stopped at the first `.` or `:`.
const RUN_IN_HEADING_TITLE = /^([^.:]{0,79}[.:])\s{2,}\S/;

/**
 * A run-in heading's own heading text — its number and title, without the body
 * paragraph that shares its line.
 *
 * `10.01  Obligation of Confidence.  Each party shall keep confidential…`
 * is ONE paragraph, so promoting it (AIVA-8540) made `RawHeading.text` carry
 * the whole clause. That is wrong on its own terms — the body is not part of
 * the heading — and it broke the judge outright: `RawHeading.text` is bounded
 * server-side, and a 764-character "heading" fails validation, which rejects
 * the whole payload and kills every rule in the check, not just that heading.
 *
 * Deliberately NOT solved by raising that bound. The body text is not heading
 * text at any length: it would still be ~95% of the heading payload sent to
 * every judgment prompt, still make "which heading does this link name?"
 * harder to answer, and the next document with a longer clause would fail
 * again at whatever the new number was.
 *
 * Falls back to the full text when the paragraph is not run-in shaped, so an
 * ordinary `3. Charges and Invoicing` heading is unchanged.
 */
function headingTextOf(paragraphText: string, numberMatchLength: number): string {
  const rest = paragraphText.slice(numberMatchLength).trim();
  const title = RUN_IN_HEADING_TITLE.exec(rest)?.[1];
  return title ? `${paragraphText.slice(0, numberMatchLength).trim()}  ${title}` : paragraphText;
}

/** Signal D (heading-like text): short, and not obviously a quantity/prose
 * continuation or a defined-term declaration. Cheap enough to run on every
 * candidate before the pricier structural checks (Signal B/C), so a
 * paragraph like "1.2 million dollars were generated in Q3." never even
 * reaches those. */
function looksHeadingLike(text: string, numberMatchLength: number): boolean {
  const rest = text.slice(numberMatchLength).trim();
  if (!rest) return true; // a bare number with nothing after it — let structural evidence decide
  // Order matters: the defined-term and quantity checks below must see the
  // FULL text, not a run-in title, or `2.8  "Services"  means…` would be
  // measured on its title alone and slip past the declaration check.
  if (LOOKS_LIKE_DEFINED_TERM_DECLARATION.test(straightenTermQuotes(rest))) return false;
  // `.find` over `.filter(...)[0]` — stops at the first hit instead of walking
  // the whole split (Sonar S4623-family).
  const firstWordOf = (s: string) =>
    trimTrailingChars((s.split(/\s+/).find(Boolean) ?? "").toLowerCase(), (c) => ".,;:".includes(c));
  if (QUANTITY_CONTINUATION_WORDS.has(firstWordOf(rest))) return false;
  // A run-in heading is measured on its TITLE, not on the body paragraph that
  // shares its line — the whole point of the convention. Anything else is
  // measured whole, exactly as before.
  const title = RUN_IN_HEADING_TITLE.exec(rest)?.[1] ?? rest;
  return title.split(/\s+/).filter(Boolean).length <= MAX_HEADING_CANDIDATE_WORDS;
}

/** `text` without its trailing run of characters `isTrailing` accepts. A loop
 *  rather than a `[...]+$` pattern, which backtracks quadratically on a long
 *  run of the same characters that does not end the string (typescript:S5852). */
function trimTrailingChars(text: string, isTrailing: (c: string) => boolean): string {
  let end = text.length;
  while (end > 0 && isTrailing(text[end - 1]!)) end -= 1;
  return text.slice(0, end);
}

interface HeadingCandidate {
  paragraphIndex: number;
  text: string;
  /** e.g. ["1","1"] for "1.1", ["10","01"] for "10.01" — never length 1
   * (SUB_HEADING_NUMBER always requires a second part). */
  numberParts: string[];
}

/** All but the last numeric component — "1" for "1.1", "1.1" for "1.1.1".
 * This is what a candidate's OWN promotion checks against its potential
 * siblings and parent heading. */
function parentStem(numberParts: string[]): string {
  return numberParts.slice(0, -1).join(".");
}

/**
 * Promote candidates to headings using the parsed numbering hierarchy, not
 * physical proximity (a legal document's sub-clauses under one section are
 * not guaranteed to be within N paragraphs of each other, and hierarchy is
 * the more reliable signal the design explicitly prefers).
 *
 * A candidate is promoted when it has a sequential sibling — another
 * candidate sharing the same parent stem (Signal B: "1.1"/"1.2"/"1.3" all
 * share parent "1") — OR its own parent stem matches an EXPLICIT heading's
 * number (Signal C). Checking siblings among ALL candidates in one pass,
 * before either fires, also handles nested numbering for free: "1.1.1" and
 * "1.1.2" both have parent stem "1.1" and so promote each other via Signal
 * B alone, with no dependency on whether "1.1" itself is Word-styled.
 */
function promoteHeadingCandidates(
  candidates: HeadingCandidate[],
  explicitHeadingNumbers: Set<string>,
): HeadingCandidate[] {
  const countByParentStem = new Map<string, number>();
  for (const candidate of candidates) {
    const stem = parentStem(candidate.numberParts);
    countByParentStem.set(stem, (countByParentStem.get(stem) ?? 0) + 1);
  }
  return candidates.filter((candidate) => {
    const stem = parentStem(candidate.numberParts);
    const hasSequentialSibling = (countByParentStem.get(stem) ?? 0) >= 2;
    const hasMatchingParentHeading = explicitHeadingNumbers.has(stem);
    return hasSequentialSibling || hasMatchingParentHeading;
  });
}

/** One body paragraph, plus the number Word's own list formatting renders in
 *  front of it. */
interface ParagraphWithListNumber {
  index: number;
  text: string;
  style: string;
  outlineLevel: number | undefined;
  /** What the reader actually sees in front of the paragraph — "15.1", "a)",
   *  "●" — or null when the paragraph is not a list item. Word GENERATES this
   *  from the list definition and it is not part of `text`, which is the whole
   *  reason numbering_order used to be blind to an auto-numbered document
   *  (AIVA-8297). */
  listNumber: string | null;
  /** Word's own identity for the list this paragraph belongs to, or null when it
   *  is not a list item. */
  listId: string | null;
  /** The paragraph's own (0-based) level within its list, or null when it is
   *  not a list item. Needed to put a broken paragraph back on the right list
   *  AT THE RIGHT DEPTH — `attachToList` takes both. */
  listLevel: number | null;
  /** Its text with tracked changes applied, when that differs from `text` —
   *  read only for a paragraph that opens with a number (`readNumberedCurrentTexts`). */
  currentText?: string;
}

/**
 * The paragraph sweep `collectHeadings` runs, widened to carry each paragraph's
 * Word-generated list number.
 *
 * The list read is best-effort and deliberately in its own sync, mirroring
 * `countListGlyphWords` in wordDocument.ts: `ListItem` needs WordApi 1.3, and a
 * host that will not serve it must degrade to the text-only behaviour this file
 * had before, never fail the whole scan.
 */
function sweepParagraphsWithListNumbers(): Promise<ParagraphWithListNumber[]> {
  return Word.run(async (context) => {
    const body = context.document.body.paragraphs;
    body.load("items/text,items/style,items/outlineLevel");
    await context.sync();
    const base = body.items.map((p, index) => ({
      index,
      text: (p.text ?? "").trim(),
      style: p.style ?? "",
      outlineLevel: p.outlineLevel,
      listNumber: null as string | null,
      listId: null as string | null,
      listLevel: null as number | null,
    }));
    await readListNumbersInto(context, base);
    await readNumberedCurrentTexts(context, body.items, base);
    return base;
  });
}

/**
 * Until a tracked fix is accepted, `text` still holds the deleted number: a
 * heading renumbered 10.5 → 2.05 reads "2.0510.5  Change Control", which a
 * rescan took for clause 2.0510 (AIVA-8297 retest). So each paragraph that
 * opens with a number also gets its text as the document now reads. One
 * batched call; a host without WordApi 1.4 keeps the plain text.
 */
async function readNumberedCurrentTexts(
  context: Word.RequestContext,
  items: readonly Word.Paragraph[],
  base: ParagraphWithListNumber[],
) {
  const candidates = base.flatMap((p) => (headingOwnNumber(p.text) ? [{ paragraph: items[p.index]!, index: p.index }] : []));
  if (candidates.length === 0) return;
  const current = await readCurrentTexts(context, candidates);
  for (const [index, reviewed] of current) {
    // Reviewed text wraps content controls (review anchors) in "<" … ">".
    const text = reviewed.replace(/[<>\r]/g, "").trim();
    const paragraph = base[index]!;
    if (text !== paragraph.text) paragraph.currentText = text;
  }
}

/**
 * Fill in each paragraph's Word-generated list number, and the identity of the
 * list it came from.
 *
 * TWO independent reads, each on a FRESH paragraphs proxy and each in its own
 * try, rather than one combined load — deliberately, and both parts matter:
 *
 *  - Fresh proxy, because `countListGlyphWords` (wordDocument.ts) is this
 *    codebase's one pattern proven against a real host, and it loads the
 *    navigation property onto a collection it has not already synced.
 *  - Separately, because `listOrNullObject` is only ever an optimisation: it
 *    scopes a sequence to its own list so a Schedule that legitimately restarts
 *    at 1 is not flagged. `listItemOrNullObject` is what actually makes an
 *    auto-numbered document visible at all (AIVA-8297). Combined into one load
 *    string, a host that would not serve the former took the latter down with
 *    it — and since the whole read is best-effort, that failure surfaced as
 *    "no numbering issues found", which is the very bug this is fixing.
 */
async function readListNumbersInto(
  context: Word.RequestContext,
  base: ParagraphWithListNumber[],
): Promise<void> {
  try {
    const paragraphs = context.document.body.paragraphs;
    // Both properties come off the SAME ListItem, so a host serving one serves
    // the other — unlike `listOrNullObject` below, which is its own object.
    paragraphs.load("items/listItemOrNullObject/listString,items/listItemOrNullObject/level");
    await context.sync();
    paragraphs.items.forEach((p, index) => {
      const item = p.listItemOrNullObject;
      const entry = base[index];
      if (!entry || !item || item.isNullObject) return;
      entry.listNumber = (item.listString ?? "").trim() || null;
      entry.listLevel = typeof item.level === "number" ? item.level : null;
    });
  } catch {
    // No list metadata at all on this host — every number must come from text,
    // which is exactly how this file behaved before AIVA-8297.
    return;
  }

  try {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/listOrNullObject/id");
    await context.sync();
    paragraphs.items.forEach((p, index) => {
      const list = p.listOrNullObject;
      const entry = base[index];
      if (!entry || !list || list.isNullObject || list.id == null) return;
      entry.listId = String(list.id);
    });
  } catch {
    /* numbers without list identity — every list shares one sequence */
  }
}

// ---------------------------------------------------------------------------
// numbered items — the document's numbered OUTLINE, which is what
// numbering_order actually reasons about.
//
// Deliberately a separate collection from `collectHeadings`'s headings rather
// than more entries in it: `headings` is also what broken_reference,
// ambiguous_reference and missing_hyperlink resolve references against, and
// folding every numbered list paragraph into that set would change all three
// rules' behaviour. numbering_order is the only rule that needs to see a
// paragraph purely because it carries a number.
// ---------------------------------------------------------------------------

/** A paragraph carrying a number the reader can see, from either source. */
export interface RawNumberedItem {
  text: string;
  paragraphIndex: number;
  /** Word's generated list number ("15.1"), when the list renders one. Takes
   *  precedence over any number in `text`: what the list renders IS what the
   *  document shows. Absent for a plain-text-numbered paragraph. */
  listNumber?: string;
  /** Which list this item belongs to, or null/absent when its number is typed
   *  text. Sequences are compared within one list only, so a Schedule that
   *  legitimately restarts at 1 under its own list is not a finding. */
  listId?: string | null;
  /** This item's own 0-based depth within its list. Carried into the fix so it
   *  can be re-attached to the correct list AT THE SAME depth. */
  listLevel?: number | null;
  /** Kept so a `RawHeading` is usable as a numbered item unchanged — unused by
   *  the sequence check itself, which reads depth off the number. */
  level?: number;
  /** AIVA-8297: `false` when this list item restarts at 1 with nothing between
   *  it and the numbered item before it that opens a new section — Word's
   *  "Restart at 1" on an ordinary clause. Absent otherwise, including a
   *  restart after "SCHEDULE 2", which is legitimate. See
   *  `restartsWithoutSectionBreak`. */
  afterSectionBreak?: false;
  /** `text` with tracked changes applied, when it differs. */
  currentText?: string;
}

/** A paragraph that opens an attachment or a new part of the document even
 *  when it is typed in Normal style — "SCHEDULE 2", "Part B", "Annex 1". */
const SECTION_OPENING_PARAGRAPH = /^(?:schedule|exhibit|annex|appendix|attachment|part)\b/i;

/**
 * Whether a list item that starts again at 1 does so with no new section to
 * justify it (AIVA-8297).
 *
 * Word implements both a Schedule's own numbering and a manual "Restart at 1"
 * as a NEW list opening at 1, so `evaluateNumberingOrder` cannot tell them
 * apart from the items alone — and treated every one as legitimate, which is
 * how a manually broken sequence went unreported. What separates them is what
 * sits between the restart and the numbered item before it:
 *
 *  - a heading that is not itself numbered ("SCHEDULE 2", "Definitions"), or a
 *    paragraph opening with Schedule/Exhibit/Annex/Appendix/Attachment/Part:
 *    a new section, so the restart is legitimate;
 *  - a previous item that is a heading with a TYPED number ("2. Scope"),
 *    followed by list items: a list nested under that heading, legitimate;
 *  - a previous item that is a heading, followed by a BODY list: the same;
 *  - nothing of the kind: a clause renumbered by hand, which is the defect.
 */
function restartsWithoutSectionBreak(
  item: { isHeading: boolean; fromList: boolean; number: string },
  previous: { isHeading: boolean; fromList: boolean } | null,
  sectionOpenedSincePrevious: boolean,
): boolean {
  if (!item.fromList || !previous || sectionOpenedSincePrevious) return false;
  const parts = HEADING_NUMBER.exec(item.number)?.[1]?.split(".").map(Number) ?? [];
  if (parts.length === 0 || !parts.every((p) => p === 1)) return false;
  if (previous.isHeading && (!previous.fromList || !item.isHeading)) return false;
  return true;
}

/**
 * Every paragraph whose number the reader can see, in document order.
 *
 * Two sources, and the distinction matters downstream (see
 * `evaluateNumberingOrder`): a number Word's list formatting GENERATED has no
 * text to rewrite, while a typed one does.
 *
 * A list-numbered paragraph needs none of `looksHeadingLike`/
 * `promoteHeadingCandidates`' structural evidence: those exist to decide
 * whether a bare "1.2" at the start of a paragraph is a sub-clause or the
 * start of the sentence "1.2 million customers were affected". Word telling us
 * the paragraph is item "1.2" of a list settles that outright — prose never
 * carries a list number.
 */
function collectNumberedItems(
  paragraphs: ParagraphWithListNumber[],
  inferredHeadingIndices: ReadonlySet<number>,
): RawNumberedItem[] {
  const items: RawNumberedItem[] = [];
  // AIVA-8297: what the sweep has seen since the last numbered item, so a list
  // that restarts at 1 can be told apart from a Schedule's own numbering. See
  // `restartsWithoutSectionBreak`.
  let previous: { isHeading: boolean; fromList: boolean } | null = null;
  let sectionOpened = false;
  const push = (paragraph: ParagraphWithListNumber, item: RawNumberedItem, fromList: boolean) => {
    const isHeading = headingLevelFrom(paragraph.style, paragraph.outlineLevel) !== null;
    const current = { isHeading, fromList, number: item.listNumber ?? item.text };
    if (restartsWithoutSectionBreak(current, previous, sectionOpened)) item.afterSectionBreak = false;
    items.push(item);
    previous = { isHeading, fromList };
    sectionOpened = false;
  };
  for (const paragraph of paragraphs) {
    const opensSection =
      headingLevelFrom(paragraph.style, paragraph.outlineLevel) !== null || SECTION_OPENING_PARAGRAPH.test(paragraph.text);
    if (paragraph.listNumber && HEADING_NUMBER.test(paragraph.listNumber)) {
      // A bullet or a lettered "(a)" list has no numeric glyph, so
      // HEADING_NUMBER rejects it here and it never joins a sequence.
      //
      // An EMPTY list paragraph is skipped for the same reason the text branch
      // below skips one: a finding anchors by searching the document for its
      // own `matchText`, and there is nothing to search for. Confirmed live —
      // a blank line left mid-list while editing produced a finding reading
      // `Item 15.1 ("")`, which came back `unanchored` and failed on Apply
      // with "the fix handler returned an unexpected result". A blank line is
      // not a numbering defect worth reporting, and it must not consume the
      // number that the next real item is measured against either.
      if (!paragraph.text) continue;
      push(
        paragraph,
        {
          text: paragraph.text,
          paragraphIndex: paragraph.index,
          listNumber: paragraph.listNumber,
          listId: paragraph.listId,
          listLevel: paragraph.listLevel,
        },
        true,
      );
      continue;
    }
    // Text-numbered: exactly the paragraphs that were already reaching
    // numbering_order before — a real heading, or one of this sweep's promoted
    // sub-clause candidates — so a document with no list numbering behaves the
    // way it did, just walked in document order.
    const isHeading = headingLevelFrom(paragraph.style, paragraph.outlineLevel) !== null;
    const isItem =
      (isHeading || inferredHeadingIndices.has(paragraph.index)) &&
      Boolean(paragraph.text) &&
      HEADING_NUMBER.test(paragraph.text);
    if (isItem) {
      const item: RawNumberedItem = { text: paragraph.text, paragraphIndex: paragraph.index };
      if (paragraph.currentText !== undefined) item.currentText = paragraph.currentText;
      push(paragraph, item, false);
      continue;
    }
    // An unnumbered heading or "SCHEDULE 2" line opens a new section for
    // whatever list follows it.
    if (opensSection) sectionOpened = true;
  }
  return items;
}

/**
 * Every heading-shaped paragraph in the document, for the rules (broken_reference, bracket_heading_mismatch,
 * defined_term_section_mismatch, alphabetical_order, numbering_order, missing_defined_term, ambiguous_reference) that resolve or order references against headings.
 * See this section's own header comment for why plain-text sub-clause
 * numbers need a promotion step rather than being trusted outright.
 */
async function collectHeadings(): Promise<{
  headings: RawHeading[];
  numberedItems: RawNumberedItem[];
  documentNumbers: string[];
}> {
  const map = await readWordDocumentMap();
  const explicit: RawHeading[] = (map?.headings ?? []).map((h, i) => ({
    level: h.level,
    text: h.text,
    paragraphIndex: i,
    source: "word-style",
  }));
  const explicitHeadingNumbers = new Set<string>();
  for (const heading of explicit) {
    const match = HEADING_NUMBER.exec(heading.text);
    if (match?.[1]) explicitHeadingNumbers.add(match[1]);
  }

  const paragraphs = await sweepParagraphsWithListNumbers();

  const candidates: HeadingCandidate[] = [];
  for (const paragraph of paragraphs) {
    // The shared predicate, not a local regex — same reasoning as
    // findExhibitScheduleMismatch's own sweep: an HTML-pasted document
    // reports every paragraph as "Normal", so a style-only test would treat
    // its real (outline-level-only) headings as unstyled candidates too.
    if (headingLevelFrom(paragraph.style, paragraph.outlineLevel) !== null) continue; // already captured above
    const match = SUB_HEADING_NUMBER.exec(paragraph.text);
    if (!match?.[1]) continue;
    if (!looksHeadingLike(paragraph.text, match[0].length)) continue;
    // AIVA-8540 follow-up: the heading's own text, not the body paragraph it
    // shares a line with — see `headingTextOf`.
    candidates.push({
      paragraphIndex: paragraph.index,
      text: headingTextOf(paragraph.text, match[0].length),
      numberParts: match[1].split("."),
    });
  }

  const inferred: RawHeading[] = promoteHeadingCandidates(candidates, explicitHeadingNumbers).map((c) => ({
    level: c.numberParts.length,
    text: c.text,
    paragraphIndex: c.paragraphIndex,
    source: "inferred",
  }));

  return {
    headings: [...explicit, ...inferred],
    numberedItems: collectNumberedItems(paragraphs, new Set(inferred.map((h) => h.paragraphIndex))),
    documentNumbers: leadingNumbers(paragraphs),
  };
}

/** Every number a paragraph opens with, rendered or typed, as `numberKey`s —
 *  so numbering_order never calls a number missing that is merely uncollected. */
function leadingNumbers(paragraphs: readonly ParagraphWithListNumber[]): string[] {
  const keys = new Set<string>();
  for (const paragraph of paragraphs) {
    const rendered = paragraph.listNumber ? HEADING_NUMBER.exec(paragraph.listNumber)?.[1] : undefined;
    // headingOwnNumber reads "Clause 10.02 …" as well as "10.02 …".
    for (const value of [rendered, headingOwnNumber(paragraph.currentText ?? paragraph.text)]) {
      if (value) keys.add(numberKey(value.split(".").map(Number)));
    }
  }
  return [...keys];
}

/**
 * Raw sweep only — collects every reference-shaped mention and every
 * heading in the document. Does NOT decide whether a reference's target
 * exists (that is deterministic backend logic, `rules.evaluate_r03`) and
 * does NOT anchor anything (anchors land only on confirmed findings, via
 * `anchorFindings`, after evaluation).
 */
export async function findBrokenReference(): Promise<BrokenReferenceSweep> {
  const references: RawBrokenReference[] = await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/outlineLevel");
    // A Table of Contents field's own rendered entries mirror
    // every heading's text as their own separate paragraphs — but a TOC
    // entry is styled "TOC 1"/"TOC 2", never "Heading N", so
    // isHeadingParagraph's style check below never catches them. Loaded
    // unconditionally here (not gated behind supportsFields() until after
    // this sync) so it costs nothing extra on a host that DOES support
    // fields: one shared sync with the paragraph load already happening
    // anyway, rather than a second round trip just to find out whether a
    // TOC exists.
    const fields = supportsFields() ? context.document.body.fields : null;
    fields?.load("items/code");
    // AIVA-8296: loaded on this SAME sync as the paragraphs and fields above,
    // for the identical reason that load is — one shared round trip rather
    // than a second one just to discover whether the document has comments.
    const comments =
      typeof context.document.body.getComments === "function" ? context.document.body.getComments() : null;
    comments?.load("items");
    await context.sync();

    // Same TOC-detection approach as ensureBookmarkAndLinkTo's own guard
    // (AIVA-8065) and findShortenedHyperlinkText's own guard (AIVA-8199): a
    // paragraph is "inside a TOC" when it compares
    // Inside/InsideStart/InsideEnd/Equal against any TOC field's own
    // rendered range. The comparison itself needs its own extra sync
    // (compareLocationWith is an action, not a loadable property) — skipped
    // entirely when the document has no TOC field at all, or the host
    // doesn't support fields, rather than paying for it on every scan.
    const insideToc = await tocParagraphPredicate(context, paragraphs, fields);

    // AIVA-8296, same shape as the TOC guard above: skipped entirely when the
    // document carries no comments at all (the overwhelmingly common case on a
    // first scan), so an unreviewed document pays nothing for this.
    let insideComment = (_paragraphIndex: number): boolean => false;
    const commentRanges = comments?.items.map((c) => c.getRange()) ?? [];
    if (commentRanges.length > 0) {
      insideComment = await commentParagraphPredicate(context, paragraphs.items, commentRanges);
    }

    const found: RawBrokenReference[] = [];
    // Counted across EVERY paragraph, headings AND TOC entries and this
    // review's own comments included — see RawBrokenReference.occurrenceIndex
    // and `documentOccurrenceCounter`. None of those is special-cased here any
    // more: the counter reads the document's text, so whatever a live
    // body.search() will find, it has already counted.
    const counter = documentOccurrenceCounter(paragraphs.items.map((p) => p.text ?? ""));
    paragraphs.items.forEach((p, paragraphIndex) => {
      // A heading's own title is not a cross-reference to itself. A section
      // literally titled "Section 24 — Indemnification" used to register as
      // a "reference" to Section 24, which every rule reading this shared
      // sweep (missing_hyperlink, bracket_heading_mismatch, topic_mismatch, inconsistent_formatting, exhibit_schedule_mismatch, hyperlink_target_mismatch, shortened_hyperlink_text) then tried to
      // hyperlink/format/topic-match as if it were an inline mention —
      // headings aren't underlined like body cross-references, so inconsistent_formatting
      // flagged them as formatting outliers, and topic_mismatch ended up asking
      // whether a heading's own paragraph was "about the right topic" and
      // commenting on the heading itself when it wasn't. A TOC entry
      // mirroring that same heading text is the identical problem one level
      // removed — confirmed live: a document with a Table of Contents had
      // every heading's TOC entry flagged as a missing hyperlink, since a
      // TOC line is ordinary body text with no field of its own unless the
      // TOC was built with hyperlinks.
      const isHeadingParagraph = headingLevelFrom(p.style ?? "", p.outlineLevel) !== null;
      const isTocParagraph = insideToc(paragraphIndex);
      // AIVA-8296: a comment this review ITSELF wrote is body text like any
      // other, so its own wording gets swept as a fresh reference on the next
      // scan. Several fix comments quote the reference they are about
      // ("this reference matches 2 different headings", bracket_heading_mismatch's
      // corrected title), so applying one fix ADDED findings rather than
      // removing one — the operator saw the count go UP after Apply, and the
      // resolved-dedup guard could not suppress them because they sit at a
      // genuinely new location with a new occurrenceIndex.
      //
      // Identical in shape to the TOC problem (AIVA-8180) one level removed,
      // and filtered the identical way: only the PUSH is skipped, never the
      // count — see the occurrenceIndex note directly below, and
      // RawBrokenReference.occurrenceIndex's own docstring. A comment's text
      // is found by the live body.search() at anchor time exactly as a TOC
      // entry's mirror is, so skipping its count (rather than just its
      // output) would reintroduce the occurrenceIndex-drift class AIVA-8065
      // audited out of this file.
      const isCommentParagraph = insideComment(paragraphIndex);
      // A heading's own title, a TOC entry's mirror, a review comment's
      // wording and a lettered candidate the case-sensitive definition
      // rejects are all found by the live body.search() at anchor time, so
      // all of them occupy a position in its results. The counter reads the
      // document itself, so none of them can be missed here — only the PUSH
      // is filtered.
      for (const { matchText, targetLabel, isReference, offset } of scanReferenceMatches(p.text ?? "")) {
        const occurrenceIndex = counter.at(paragraphIndex, offset, matchText);
        if (occurrenceIndex === null) continue;
        if (isHeadingParagraph || isTocParagraph || isCommentParagraph || !isReference) continue;
        found.push({ matchText, targetLabel, occurrenceIndex, paragraphIndex });
      }
    });
    return found;
  });

  const { headings, numberedItems, documentNumbers } = await collectHeadings();

  return { references, headings, numberedItems, documentNumbers };
}

/** Whitespace-collapsed, trimmed text — for comparing what Word reports for a
 *  paragraph with what it reports for a range spanning several. */
function squashedText(text: string | undefined): string {
  return (text ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Which paragraphs sit inside a comment's range (AIVA-8296), asking Word only
 * where the text says it is possible (AIVA-8925).
 *
 * It used to compare every paragraph with every comment — one Word call per
 * pair, and a fresh paragraph range for each, so a reviewed document with a few
 * dozen comments cost tens of thousands of calls in one round trip. A paragraph
 * can only be inside a comment's range if that range's text contains the
 * paragraph's, and the answer is only used for a paragraph that holds a
 * reference. Those two facts come from text already loaded, so only the
 * remaining pairs go to Word. One extra round trip reads the comments' text.
 */
async function commentParagraphPredicate(
  context: Word.RequestContext,
  paragraphs: readonly Word.Paragraph[],
  commentRanges: readonly Word.Range[],
): Promise<(paragraphIndex: number) => boolean> {
  for (const range of commentRanges) range.load("text");
  await context.sync();
  const commentTexts = commentRanges.map((range) => squashedText(range.text).toLowerCase());

  const comparisons = new Map<number, OfficeExtension.ClientResult<Word.LocationRelation>[]>();
  paragraphs.forEach((paragraph, paragraphIndex) => {
    const text = squashedText(paragraph.text).toLowerCase();
    if (!text || scanReferenceMatches(paragraph.text ?? "").length === 0) return;
    const holding = commentRanges.filter((_, i) => commentTexts[i]!.includes(text));
    if (holding.length === 0) return;
    const paragraphRange = paragraph.getRange();
    comparisons.set(
      paragraphIndex,
      holding.map((commentRange) => paragraphRange.compareLocationWith(commentRange)),
    );
  });
  if (comparisons.size === 0) return () => false;
  await context.sync();
  return (paragraphIndex: number) =>
    (comparisons.get(paragraphIndex) ?? []).some((cmp) =>
      (["Inside", "InsideStart", "InsideEnd", "Equal"] as Word.LocationRelation[]).includes(cmp.value),
    );
}

/** Deterministic evaluation, ported from rules.py's `evaluate_r03` — a
 * reference whose target does not resolve to a real heading. Not a pure 1:1
 * port: `letteredTargetExists`'s word-boundary match (see `labelAppearsIn`)
 * is a fix that landed in the Python original after this port was drafted —
 * carried forward here so the two don't silently diverge on a false
 * positive like "Exhibit B" matching "Exhibit B1".
 *
 * AIVA-8742: Exhibit/Schedule references are skipped. The sweep still
 * collects them because other rules read it, but whether one points at a real
 * attachment is exhibit_schedule_mismatch's question (AIVA-8560). Asking it
 * here too reported an exhibit whose title is not a heading-styled paragraph
 * as a broken reference — the same deferral missing_hyperlink already makes. */
export function evaluateBrokenReference(sweep: BrokenReferenceSweep): FindingCreate[] {
  const index = headingIndex(sweep.headings);
  const findings: FindingCreate[] = [];
  for (const reference of sweep.references) {
    if (isExplicitPrefixCategory(reference.matchText)) continue;
    if (referenceTargetExists(reference.targetLabel, reference.matchText, index)) {
      continue;
    }
    findings.push(
      toFindingCreate({
        ruleId: "broken_reference",
        matchText: reference.matchText,
        // AIVA-8544 follow-up: this was the ONE deterministic rule that never
        // passed an occurrence number. `toFindingCreate` only writes
        // `fix_params.occurrenceIndex` when it is given one, so every
        // broken-reference finding silently anchored to occurrence ZERO — the
        // document's FIRST hit for that text, which for "Section 13" is the
        // heading "Section 13. Force Majeure". Reported live as "AiVA is
        // pointing to the heading", indistinguishable on screen from the
        // counting defect this ticket began with, and entirely separate from
        // it: no correct count survives not being passed on. Every other rule
        // in this file already passes it; `defined_not_used` is the sole
        // deliberate exception, documented at its own call site.
        occurrenceIndex: reference.occurrenceIndex,
        problem: `"${reference.matchText}" does not appear to point to an existing section in this document.`,
        comment: "section reference not found",
        fixHandler: "word.docreview.fix_broken_reference",
        fixParams: { commentText: "section reference not found" },
      }),
    );
  }
  return findings;
}

/**
 * How many headings carry each leading number — "7.02" -> 2 when a document
 * numbers two different clauses 7.02 (AIVA-8372).
 *
 * `headingIndex`'s own `headingNumbers` is a Set, so a repeated number collapses
 * into one entry and the repetition is invisible to every rule reading it. That
 * is correct for the question IT answers ("does this target exist?") and is why
 * a duplicate needs its own count rather than a change there.
 *
 * Read with `headingOwnNumber`, the same reader `headingIndex` uses, so a
 * heading that spells its category first ("Section 2. Engagement and Scope of
 * Services") counts as 2. A bare `HEADING_NUMBER` match only saw headings that
 * LEAD with digits: two "5.02 …" sub-headings were caught while two
 * "Section 2. …" headings were invisible, and a reference to Section 2 was
 * never reported as ambiguous.
 */
function headingCountByNumber(headings: RawHeading[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const heading of headings) {
    const number = headingOwnNumber(heading.text);
    if (number) counts.set(number, (counts.get(number) ?? 0) + 1);
  }
  return counts;
}

/**
 * ambiguous_reference — a reference that could plausibly point to more than one
 * heading ("ambiguous"). Ported from rules.py's `evaluate_cc`; reuses
 * broken_reference's own sweep (no sweep of its own).
 *
 * TWO shapes of ambiguity, deliberately kept apart:
 *
 * 1. A WORDED or LETTERED reference whose text appears in several heading
 *    titles — "Exhibit Note" when the document has two of them. Matched on
 *    heading TEXT.
 * 2. A NUMERIC reference to a number the document uses TWICE — "Section 7.02"
 *    where both `7.02 Foreground IP` and `7.02 Licence` exist (AIVA-8372).
 *
 * Shape 2 used to be skipped outright: numeric references were declared "never
 * ambiguous by this definition" and left to broken_reference (existence) and
 * numbering_order (ordering). Neither covers it — broken_reference asks only
 * whether the target EXISTS, and 7.02 does exist, twice; numbering_order's
 * heading check looks at headings with NO number. So a reader met "the licence
 * granted under Section 7.02" with no way to tell which clause was meant, and
 * no rule said so. It is also the most literal case the spec describes: a
 * reference that "could point to more than one place".
 *
 * Still NOT reported for a number used once — that is broken_reference's and
 * numbering_order's ground, and duplicating them here would double-report.
 */
export function evaluateAmbiguousReference(sweep: BrokenReferenceSweep): FindingCreate[] {
  const { headingTextsLower } = headingIndex(sweep.headings);
  const headingsPerNumber = headingCountByNumber(sweep.headings);
  const findings: FindingCreate[] = [];
  for (const reference of sweep.references) {
    const baseLabel = stripSubclauseLetter(reference.targetLabel);
    // Routed exactly as `referenceTargetExists` routes it. A Schedule/Exhibit
    // reference names a heading TITLED with that prefix ("Schedule 2 — Fee
    // Schedule"), never a bare-numbered one, so a numeric label is NOT a
    // section number for it. Sending "Schedule 2" down the numeric branch
    // counted headings that LEAD with "2" — none, in a document whose two
    // schedules are both titled "Schedule 2 — …" — so the real ambiguity was
    // never reported, while two unrelated "2." sections reported a false one
    // (AIVA-8547).
    const explicitPrefix = isExplicitPrefixCategory(reference.matchText);
    if (!explicitPrefix && NUMERIC_LABEL.test(baseLabel)) {
      const duplicates = headingsPerNumber.get(baseLabel) ?? 0;
      if (duplicates <= 1) continue;
      const commentText = `this reference matches ${duplicates} different headings numbered ${baseLabel} — which one is meant?`;
      findings.push(
        toFindingCreate({
          ruleId: "ambiguous_reference",
          matchText: reference.matchText,
          occurrenceIndex: reference.occurrenceIndex,
          problem: `"${reference.matchText}" matches ${duplicates} different headings numbered ${baseLabel} in this document — it isn't clear which one this reference means.`,
          comment: commentText,
          fixHandler: "word.docreview.fix_ambiguous_reference",
          fixParams: { commentText },
        }),
      );
      continue;
    }
    const needle = reference.matchText.toLowerCase();
    // A NUMBERED schedule/exhibit is not named by a heading that carries it as
    // the start of a longer number: "Schedule 2.1 — Rates" is a different
    // schedule from "Schedule 2", even though "." is a word boundary. Lettered
    // labels ("Exhibit B") keep the plain whole-word match they always had.
    const numberedAttachment = explicitPrefix && NUMERIC_LABEL.test(baseLabel);
    const candidateCount = headingTextsLower.filter(
      (heading) => labelAppearsIn(needle, heading) && !(numberedAttachment && continuesAsSubNumber(needle, heading)),
    ).length;
    if (candidateCount <= 1) continue;
    const commentText = `this reference matches ${candidateCount} different headings — which one is meant?`;
    findings.push(
      toFindingCreate({
        ruleId: "ambiguous_reference",
        matchText: reference.matchText,
        occurrenceIndex: reference.occurrenceIndex,
        problem: `"${reference.matchText}" matches ${candidateCount} different headings in this document — it isn't clear which one this reference means.`,
        comment: commentText,
        fixHandler: "word.docreview.fix_ambiguous_reference",
        fixParams: { commentText },
      }),
    );
  }
  return findings;
}

/** Shared body for every comment-only "flag" fix (broken_reference, topic_mismatch, exhibit_schedule_mismatch-ambiguous,
 * defined_term_section_mismatch, hyperlink_target_mismatch-unknown, shortened_hyperlink_text-unknown, defined_not_used, used_not_defined, ambiguous_reference, missing_defined_term): insert `commentText` at
 * the anchor as a genuine tracked change, guarded by the same
 * anchor-missing/stale checks every fix uses. Each rule keeps its own
 * exported wrapper — named, independently callable, and documented with its
 * own rule-specific rationale — so the tool registry, the skill prompt, and
 * any future "just re-run this one fix" caller never depend on a shared
 * function name that could change out from under them. */
/**
 * Resolve `anchorTag`, refuse to write if the live text has drifted from
 * `expected`, and run `edit` with change tracking forced on.
 *
 * Every fix in this file shares exactly this shape, and four of them had their
 * own copy of it. Each copy is a place the staleness guard could be forgotten
 * — and that guard is what stops a fix writing over text the user has since
 * changed, so it is not a detail worth duplicating.
 */
function withResolvedAnchor(
  anchorTag: string,
  expected: string,
  edit: (context: Word.RequestContext, cc: Word.ContentControl) => Promise<string | void>,
): Promise<FixResult> {
  return Word.run(async (context) => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return { applied: false, stale: true, reason: "anchor_missing" };
    if (isStale(cc.text, expected)) {
      return { applied: false, stale: true, reason: "text_changed" };
    }
    // `edit` optionally returns the id of a comment it inserted — see
    // FixResult.commentId. Anything else it returns is ignored.
    let commentId: string | undefined;
    const { tracked } = await withTrackingForced(context, async () => {
      const written = await edit(context, cc);
      if (typeof written === "string" && written) commentId = written;
    });
    return { applied: true, stale: false, tracked, ...(commentId ? { commentId } : {}) };
  });
}

/**
 * Insert `text` as a comment on `range` and return the id Word assigned it,
 * or `undefined` when this host will not give one up.
 *
 * The id is loaded in a second sync rather than assumed: `insertComment`
 * returns a proxy whose properties are not populated until the queued
 * commands run, and a host without the comments API (pre-WordApi 1.4) can
 * return a proxy that never resolves an id rather than throwing. Callers
 * treat a missing id as "fall back to comparing the comment's text", so
 * this never fails the fix itself — a comment that was written but whose id
 * we could not read is still a comment the operator can see.
 *
 * Idempotent (AIVA-8561): when `range` already carries a comment with exactly
 * this text, that comment is reused instead of a second, identical one being
 * inserted. Applying the same finding again — a re-run in a new chat, where
 * nothing server-side remembers the first Apply — used to stack duplicates.
 */
async function insertCommentAndGetId(
  context: Word.RequestContext,
  range: Word.Range,
  text: string,
): Promise<string | undefined> {
  const existing = await existingCommentOnRange(context, range, text);
  if (existing !== null) return existing || undefined;
  const comment = range.insertComment(text);
  await context.sync();
  try {
    comment.load("id");
    await context.sync();
    const id = comment.id;
    return typeof id === "string" && id ? id : undefined;
  } catch {
    // Comment written, id unreadable on this host — not a failure.
    return undefined;
  }
}

/**
 * The id of a comment already on `range` whose text is exactly `text` —
 * `""` when one exists but the host will not give up its id — or `null` when
 * there is none.
 *
 * Also `null` when the host cannot list a range's comments (pre-WordApi 1.4)
 * or the read throws: the caller then inserts exactly as it always did, so a
 * host that cannot answer gets today's behaviour, never a skipped comment.
 */
async function existingCommentOnRange(
  context: Word.RequestContext,
  range: Word.Range,
  text: string,
): Promise<string | null> {
  if (typeof range.getComments !== "function") return null;
  try {
    const comments = range.getComments();
    comments.load("items/id,items/content");
    await context.sync();
    const wanted = text.trim();
    const match = comments.items.find((c) => (c.content ?? "").trim() === wanted);
    return match ? String(match.id ?? "") : null;
  } catch {
    return null;
  }
}

async function flagWithComment(anchorTag: string, expected: string, commentText: string): Promise<FixResult> {
  return withResolvedAnchor(anchorTag, expected, async (context, cc) => {
    try {
      return await insertCommentAndGetId(context, cc.getRange(), commentText);
    } catch (err) {
      // AIVA-9042: desktop Word answers AccessDenied to an add-in's comment on
      // footnote text (verified live), though a person can comment there by
      // hand. The comment goes on the footnote's number in the main text
      // instead — which Word allows — and says which footnote and which words
      // it is about. Anything else that throws is a real failure, rethrown.
      const note = await footnoteHoldingAnchor(context, anchorTag);
      if (!note) throw err;
      return insertCommentAndGetId(context, note.reference, `${commentText} (footnote ${note.footnote}: ${expected.trim()})`);
    }
  });
}

/** The footnote whose text holds the anchor `tag`, with its number mark in the
 *  main text — or null when no footnote holds it. */
async function footnoteHoldingAnchor(
  context: Word.RequestContext,
  tag: string,
): Promise<{ reference: Word.Range; footnote: number } | null> {
  const notes = await loadFootnotes(context);
  if (notes.length === 0) return null;
  const found = notes.map((note) => {
    const cc = note.body.contentControls.getByTag(tag).getFirstOrNullObject();
    cc.load("isNullObject");
    return cc;
  });
  await context.sync();
  const k = found.findIndex((cc) => !cc.isNullObject);
  return k === -1 ? null : { reference: notes[k]!.reference, footnote: k + 1 };
}

/**
 * Flag a reference whose target does not exist. Comment-only — broken_reference has no
 * safe deterministic rewrite (there is no "correct" section number to
 * substitute), so the fix is making the miss visible to the operator, not
 * rewriting text.
 */
export async function fixBrokenReference(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// missing_hyperlink — a cross-reference that should be clickable but has no hyperlink
// gets one added
// ---------------------------------------------------------------------------

export interface RawMissingHyperlinkCandidate {
  matchText: string;
  /** Which occurrence of this exact text, in document order — the same
   *  reference text can appear more than once, only some of which are
   *  missing a hyperlink. Without this, a finding always anchors to
   *  occurrence 0, regardless of which one was actually flagged. */
  occurrenceIndex: number;
  targetLabel: string;
  hasHyperlink: boolean;
  /** No working link, but the text carries the Hyperlink character style — a
   *  link whose target is missing, not a missing link (AIVA-8669). Set only
   *  when true; absent means plain unlinked text, as on a sweep from before it. */
  looksLinked?: boolean;
  paragraphIndex: number;
}

export interface MissingHyperlinkSweep {
  references: RawMissingHyperlinkCandidate[];
  headings: RawHeading[];
}

/** `Document.getBookmarkRangeOrNullObject`/`Range.insertBookmark`
 * (WordApiDesktop 1.4) — desktop-only, unlike `body.fields` (WordApi 1.4,
 * universal). missing_hyperlink's fix and hyperlink_target_mismatch's find+fix both need to resolve or create a
 * bookmark, so they degrade gracefully (empty sweep / comment fallback)
 * rather than claim false certainty on Word on the web. */
export function supportsBookmarks(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApiDesktop", "1.4");
  } catch {
    return false;
  }
}

/** `{ REF bookmark … \h }` — a cross-reference Word renders as a clickable
 *  link (Insert > Cross-reference with "Insert as hyperlink", the default). A
 *  REF without `\h` is plain text that merely mirrors the target, so it does
 *  not count. */
function isClickableCrossReference(fieldCode: string): boolean {
  return /^\s*REF\s/i.test(fieldCode) && /\\h(?![a-z])/i.test(fieldCode);
}

/** Where a reference's range can sit relative to a cross-reference field's
 *  rendered result and still be that link: the same text, inside it
 *  ("Section 4" in a result reading "Section 4 (Fees)"), containing it
 *  ("Section " typed before a field rendering "4"), or overlapping it. */
const ON_FIELD_RELATIONS: ReadonlySet<string> = new Set([
  "Equal",
  "Inside",
  "InsideStart",
  "InsideEnd",
  "Contains",
  "ContainsStart",
  "ContainsEnd",
  "OverlapsBefore",
  "OverlapsAfter",
]);

/**
 * Which of `ranges` sit on a clickable cross-reference field (AIVA-8536).
 *
 * Word stores a working link three ways, and `Range.hyperlink` sees only two
 * of them: a `w:hyperlink` element and a `{ HYPERLINK \l … }` field. A
 * `{ REF x \h }` field — what Insert > Cross-reference writes, the usual way a
 * contract links its own clauses — is clickable but creates no hyperlink
 * object, so `Range.hyperlink` reads empty. Confirmed in Word's object model:
 * the REF-\h result reports 0 hyperlinks where the equivalent HYPERLINK field
 * reports 1. Every such reference used to be flagged "cross-reference has no
 * hyperlink", and Apply then tried to put a second link on top of the field.
 *
 * One fields read and one batched round of `compareLocationWith`. Fails open
 * — an older host (no WordApi 1.4 fields) or a read that throws answers
 * `false` for everything, which is exactly the behaviour before this check.
 */
async function onClickableCrossReference(
  context: Word.RequestContext,
  ranges: ReadonlyArray<Word.Range | undefined>,
  /** The text of the paragraph each range sits in, when known (AIVA-8925). */
  paragraphTexts: ReadonlyArray<string | undefined> = [],
): Promise<boolean[]> {
  const none = ranges.map(() => false);
  if (!supportsFields() || ranges.every((r) => !r)) return none;
  try {
    const fields = context.document.body.fields;
    fields.load("items/code");
    await context.sync();
    const results = fields.items.filter((f) => isClickableCrossReference(f.code ?? "")).map((f) => f.result);
    if (results.length === 0) return none;
    // AIVA-8925: a range can only sit on a field that is in its own paragraph,
    // and whatever text Word reports for that field — the whole result, or only
    // its first run (§10 of the reference doc) — is part of that paragraph's
    // text. So a field whose text the paragraph does not contain is skipped;
    // comparing every range with every field cost one call per pair
    // (references × REF fields). A field reading empty, or a range whose
    // paragraph is unknown, is always compared. Only letters and digits are
    // compared, so a stray mark in either reading — leftover anchor brackets,
    // the paragraph mark inside a multi-paragraph result — cannot hide a link.
    for (const result of results) result.load("text");
    await context.sync();
    const lettersAndDigits = (text: string | undefined) => (text ?? "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
    const fieldTexts = results.map((result) => lettersAndDigits(result.text));
    const comparisons = ranges.map((range, i) => {
      if (!range) return [];
      const paragraph = paragraphTexts[i] === undefined ? undefined : lettersAndDigits(paragraphTexts[i]);
      return results
        .filter((_, j) => paragraph === undefined || !fieldTexts[j] || paragraph.includes(fieldTexts[j]!))
        .map((result) => range.compareLocationWith(result));
    });
    if (comparisons.every((list) => list.length === 0)) return none;
    await context.sync();
    return comparisons.map((list) => list.some((c) => ON_FIELD_RELATIONS.has(c.value)));
  } catch (err) {
    logOfficeError(err, "word-docreview-crossref-fields");
    return none;
  }
}

/**
 * Raw sweep only. Reuses broken_reference's own reference regex — a cross-reference is a
 * cross-reference — and reads each occurrence's own `hyperlink` property
 * (`Range.hyperlink`, WordApi 1.3, universal — this find step works on Word
 * on the web even though missing_hyperlink's fix does not). evaluate_r02 (backend)
 * decides whether a hyperlink-less reference's target actually exists,
 * reusing the same heading data broken_reference already resolves.
 */
export async function findMissingHyperlink(
  existingReferences?: RawBrokenReference[],
  existingHeadings?: RawHeading[],
): Promise<MissingHyperlinkSweep> {
  let references = existingReferences;
  let headings = existingHeadings;
  if (!references || !headings) {
    const r03 = await findBrokenReference();
    references ??= r03.references;
    headings ??= r03.headings;
  }

  return Word.run(async (context) => {
    // Word-bounded, the same way the anchor step searches — see
    // `queueWordBoundedSearch` (AIVA-8547): matchWholeWord alone still let
    // "Section 2" match inside "Section 24" and corrupt this occurrence count.
    const searchesByText = new Map<string, WordBoundedSearch>();
    for (const ref of references) {
      if (searchesByText.has(ref.matchText)) continue;
      searchesByText.set(ref.matchText, queueWordBoundedSearch(context.document.body, ref.matchText));
    }
    const paragraphs = loadOptionalParagraphs(context);
    await context.sync();

    // Indexed by the SAME occurrenceIndex the anchor step uses, over the same
    // hit list — see `wordBoundedHits`.
    const rangesByText = wordBoundedHitLists(searchesByText, paragraphTextsOf(paragraphs));

    // ref.occurrenceIndex already counts across the WHOLE document,
    // headings included, exactly matching what the unfiltered live search
    // above finds — see RawBrokenReference.occurrenceIndex's own docstring.
    // Recomputing a local seenCount here used to count only over this
    // heading-excluded reference list, silently mismatching rangesByText's
    // indices whenever a heading's own text also matched the reference
    // pattern and sat earlier in the document.
    const rangeOf = references.map((ref) => {
      const ranges = rangesByText.get(ref.matchText) ?? [];
      return ranges[ref.occurrenceIndex] ?? ranges[ranges.length - 1];
    });
    // AIVA-8925: only the hits a reference resolves to are read — the others
    // (headings, TOC lines, unreferenced repeats) were loaded and never used.
    loadOnce(rangeOf, "hyperlink,style");
    await context.sync();
    // AIVA-8536: a cross-reference field is a working link that
    // `Range.hyperlink` cannot see — see `onClickableCrossReference`.
    // A reference that already has a hyperlink needs no field check (AIVA-8925).
    const referenceParagraphs = paragraphTextsOf(paragraphs);
    const onCrossReference = await onClickableCrossReference(
      context,
      rangeOf.map((range) => (range?.hyperlink ? undefined : range)),
      // A hit that fell back to the last one may sit in another paragraph, so
      // its paragraph is treated as unknown and every field is compared.
      references.map((ref) =>
        ref.occurrenceIndex < (rangesByText.get(ref.matchText)?.length ?? 0)
          ? referenceParagraphs[ref.paragraphIndex]
          : undefined,
      ),
    );
    const out: RawMissingHyperlinkCandidate[] = references.map((ref, i) => {
      const hasHyperlink = Boolean(rangeOf[i]?.hyperlink) || onCrossReference[i]!;
      return {
        matchText: ref.matchText,
        occurrenceIndex: ref.occurrenceIndex,
        targetLabel: ref.targetLabel,
        hasHyperlink,
        ...(!hasHyperlink && looksLikeLink(rangeOf[i]?.style) ? { looksLinked: true } : {}),
        paragraphIndex: ref.paragraphIndex,
      };
    });
    return { references: out, headings: headings ?? [] };
  });
}

/** Text carrying Word's Hyperlink character style — it looks like a link to
 *  the reader. Same localized-name caveat as `UNDERLINING_STYLES`: a
 *  non-English build reports another name and reads as plain unlinked text. */
function looksLikeLink(style: string | undefined): boolean {
  return UNDERLINING_STYLES.has((style ?? "").trim().toLowerCase());
}

/**
 * The finding text for a reference with no working link (AIVA-8669). One that
 * already looks like a link — formatted as one, but pointing nowhere — is a
 * link with its target missing, and calling it "no hyperlink" told the
 * operator something they could see was false. Apply fixes both the same way:
 * it points the text at the right heading.
 */
function unlinkedReferenceText(
  matchText: string,
  target: "section" | "attachment",
  looksLinked: boolean | undefined,
): { problem: string; commentText: string } {
  if (looksLinked) {
    return {
      problem: `Hyperlink present, target missing. "${matchText}" is formatted as a link, but the link does not point to the ${target}.`,
      commentText: "hyperlink present, target missing",
    };
  }
  return {
    problem: `"${matchText}" points to a real ${target} but has no hyperlink.`,
    commentText: "cross-reference has no hyperlink",
  };
}

/** Deterministic evaluation, ported 1:1 from rules.py's `evaluate_r02` — a
 * reference whose target exists but carries no hyperlink.
 *
 * AIVA-8366: excludes Exhibit/Schedule references — exhibit_schedule_mismatch/
 * R07 is now the only rule that flags those, once its own label check finds
 * the label correct (see evaluateExhibitScheduleMismatch's own comment).
 * Without this exclusion the same unlinked "Exhibit B" mention was flagged
 * twice, under two different rule_ids, for the same underlying problem. */
export function evaluateMissingHyperlink(sweep: MissingHyperlinkSweep): FindingCreate[] {
  const index = headingIndex(sweep.headings);
  const headingByNumber = new Map<string, string>();
  for (const heading of sweep.headings) {
    // `headingOwnNumber`, not `HEADING_NUMBER`: this map supplies the target
    // heading's own text for the comment and the fix. Reading only
    // digit-leading headings left it empty for a "Section 4. Fees and
    // Payment" document, so the finding named no target at all.
    const number = headingOwnNumber(heading.text);
    if (number && !headingByNumber.has(number)) headingByNumber.set(number, heading.text);
  }

  const findings: FindingCreate[] = [];
  for (const reference of sweep.references) {
    if (reference.hasHyperlink) continue;
    if (isExplicitPrefixCategory(reference.matchText)) continue;
    if (!referenceTargetExists(reference.targetLabel, reference.matchText, index)) {
      continue;
    }
    const baseLabel = stripSubclauseLetter(reference.targetLabel);
    const targetHeadingText = headingByNumber.get(baseLabel) ?? "";
    const { problem, commentText } = unlinkedReferenceText(reference.matchText, "section", reference.looksLinked);
    findings.push(
      toFindingCreate({
        ruleId: "missing_hyperlink",
        matchText: reference.matchText,
        occurrenceIndex: reference.occurrenceIndex,
        problem,
        comment: commentText,
        fixHandler: "word.docreview.fix_missing_hyperlink",
        fixParams: { targetHeadingText, commentText },
      }),
    );
  }
  return findings;
}

/** Word bookmark names must start with a letter and contain only
 * letters/digits/underscores — sanitise the target heading's own text into
 * one, prefixed like every anchor tag to keep this feature's writes
 * recognisable and to avoid colliding with a bookmark already in the
 * document. */
function deriveBookmarkName(headingText: string): string {
  const sanitized = headingText
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^[^A-Za-z]+/, "")
    .slice(0, 30);
  return `AIVA_${sanitized || "Heading"}`;
}

/**
 * Shared by missing_hyperlink's and hyperlink_target_mismatch's fixes: ensure a bookmark exists at the given
 * target heading (creating one, deterministically named, if none does yet)
 * and point `cc`'s own range's hyperlink at it. Desktop-only — creating or
 * resolving a bookmark needs `WordApiDesktop` — and callers must already be
 * inside `withTrackingDisabled`: a tracked hyperlink is recorded as an
 * insertion of the linked text, so rejecting it deletes that text (AIVA-8206).
 * Throws on any failure (heading not found, bookmark write rejected); the
 * caller decides the comment fallback, not this helper, since what "still
 * gets shown to the operator" differs per rule.
 */
async function ensureBookmarkAndLinkTo(
  context: Word.RequestContext,
  cc: Word.ContentControl,
  targetHeadingText: string,
): Promise<void> {
  const bookmarkName = await ensureHeadingBookmark(context, targetHeadingText);
  cc.getRange().hyperlink = `#${bookmarkName}`;
  await context.sync();
}

/** What `pickHeadingCandidate` needs to know about one search hit for a
 *  heading's text. */
export interface BookmarkTargetCandidate {
  /** Inside a TOC field's rendered result (compareLocationWith). */
  readonly inTocField: boolean;
  /** Its paragraph's style, e.g. "Heading 1" or "TOC 1". */
  readonly style: string;
  readonly outlineLevel: number | null;
  /** Its whole paragraph's text. */
  readonly paragraphText: string;
}

/**
 * Pure: which hit for a heading's text is the heading itself, or -1 when none
 * can be trusted (AIVA-8300, reopened on staging: the link opened the Table of
 * Contents).
 *
 * A Table of Contents repeats every heading's text, and its entries come FIRST
 * in the document, so a plain search always offers the TOC line before the
 * heading. Never a TOC line: one inside a TOC field, OR styled "TOC N" — a
 * contents page that is not a field (pasted, or rebuilt by hand) has only its
 * style to give it away. Of the rest, in order of trust:
 *  1. a heading (style or outline level) whose paragraph is exactly the text;
 *  2. any heading;
 *  3. a paragraph that is exactly the text — a TOC line never is, since it
 *     also carries the page number;
 *  4. any remaining hit.
 * -1 rather than a guess: a link into the TOC is worse than the comment the
 * caller falls back to.
 */
export function pickHeadingCandidate(candidates: readonly BookmarkTargetCandidate[], headingText: string): number {
  // Case-insensitive, like the heading search itself (matchCase: false).
  const exactKey = (text: string) => normalizeForCompare(text).toLowerCase();
  const wanted = exactKey(headingText);
  const eligible = candidates
    .map((candidate, index) => ({ candidate, index }))
    .filter(({ candidate }) => !candidate.inTocField && !/^TOC\s*\d/i.test(candidate.style.trim()));
  const isHeading = (c: BookmarkTargetCandidate) => headingLevelFrom(c.style, c.outlineLevel) !== null;
  const isExact = (c: BookmarkTargetCandidate) => exactKey(c.paragraphText) === wanted;
  const ranked =
    eligible.find(({ candidate }) => isHeading(candidate) && isExact(candidate)) ??
    eligible.find(({ candidate }) => isHeading(candidate)) ??
    eligible.find(({ candidate }) => isExact(candidate)) ??
    eligible[0];
  return ranked?.index ?? -1;
}

/**
 * The bookmark on `targetHeadingText`'s heading — the half of
 * `ensureBookmarkAndLinkTo` that finds the heading, shared with
 * bracket_heading_mismatch, which links a range of its own (AIVA-8745).
 * Throws when the heading cannot be found.
 *
 * AIVA-8300 (reopened on staging): the heading is found, and the bookmark
 * placed on it, EVERY time. It used to reuse an `AIVA_` bookmark of the same
 * name without looking at where it was, so one that an earlier run (or an
 * older build, before the TOC guard) had put on the TOC line sent every later
 * link there, and no fix could ever move it. `insertBookmark` deletes any
 * bookmark of the same name first, so re-placing it also corrects every link
 * already pointing at it.
 */
async function ensureHeadingBookmark(context: Word.RequestContext, targetHeadingText: string): Promise<string> {
  const bookmarkName = deriveBookmarkName(targetHeadingText);
  const headingResults = context.document.body.search(targetHeadingText, { matchCase: false });
  headingResults.load("items");
  const fields = context.document.body.fields;
  fields.load("items/code");
  await context.sync();
  if (headingResults.items.length === 0) throw new Error("target heading not found");

  // A TOC field's rendered range, to tell its entries apart structurally —
  // style alone trusts that a template never styles a TOC entry like a
  // heading. See `pickHeadingCandidate` for how the two signals combine.
  const tocResultRanges = fields.items.filter((f) => /^\s*TOC\b/i.test(f.code ?? "")).map((f) => f.result);
  const candidateParagraphs = headingResults.items.map((r) => r.paragraphs.getFirstOrNullObject());
  for (const p of candidateParagraphs) p.load("style,outlineLevel,text,isNullObject");
  const locationComparisons = headingResults.items.map((r) => tocResultRanges.map((toc) => r.compareLocationWith(toc)));
  await context.sync();

  const candidates: BookmarkTargetCandidate[] = headingResults.items.map((_, i) => {
    const paragraph = candidateParagraphs[i]!;
    return {
      inTocField: locationComparisons[i]!.some((c) => TOC_CONTAINMENT.includes(c.value)),
      style: paragraph.isNullObject ? "" : (paragraph.style ?? ""),
      outlineLevel: paragraph.isNullObject ? null : (paragraph.outlineLevel ?? null),
      paragraphText: paragraph.isNullObject ? "" : (paragraph.text ?? ""),
    };
  });
  const headingRange = headingResults.items[pickHeadingCandidate(candidates, targetHeadingText)];
  if (!headingRange) throw new Error("target heading not found outside the Table of Contents");
  headingRange.insertBookmark(bookmarkName);
  await context.sync();
  return bookmarkName;
}

const TOC_CONTAINMENT = ["Inside", "InsideStart", "InsideEnd", "Equal"] as Word.LocationRelation[];

/**
 * Shared by missing_hyperlink's and hyperlink_target_mismatch's fixes: on a desktop host, ensure a bookmark
 * exists at `targetHeadingText` and point the anchored reference's
 * hyperlink at it as an untracked edit; otherwise — or if anything about
 * locating the heading or writing the bookmark doesn't hold, matching the
 * design doc's own admission that this needs per-Word-build verification —
 * fall back to a comment, never leaving the document in a half-written
 * state.
 */
function fixByLinkingOrCommenting(
  anchorTag: string,
  expected: string,
  targetHeadingText: string,
  commentText: string,
): Promise<FixResult> {
  return Word.run(async (context) => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return { applied: false, stale: true, reason: "anchor_missing" };
    if (isStale(cc.text, expected)) {
      return { applied: false, stale: true, reason: "text_changed" };
    }

    if (supportsBookmarks() && targetHeadingText) {
      try {
        // AIVA-8206: written with Track Changes OFF, unlike every other fix.
        // Word records "add a hyperlink to existing text" as an insertion of
        // that text, so rejecting the tracked link deleted the words it sat
        // on ("See  for fees."). The link changes no wording, so leaving it
        // untracked costs no audit trail the operator relies on — and Ctrl+Z
        // or Remove Hyperlink still undo it.
        await withTrackingDisabled(context, async () => {
          await ensureBookmarkAndLinkTo(context, cc, targetHeadingText);
        });
        return { applied: true, stale: false, tracked: false };
      } catch (err) {
        // Fall through to the comment fallback below — never leave a
        // half-created bookmark or a broken hyperlink in the document.
        // Logged (not just swallowed) so a bookmark-linking failure is
        // diagnosable in production instead of silently downgrading to a
        // comment with no trace of why.
        logOfficeError(err, "word-docreview-link-or-comment-fallback");
      }
    }

    let commentId: string | undefined;
    const { tracked } = await withTrackingForced(context, async () => {
      commentId = await insertCommentAndGetId(context, cc.getRange(), commentText);
    });
    return { applied: true, stale: false, tracked, ...(commentId ? { commentId } : {}) };
  });
}

/** Add a hyperlink to a cross-reference that resolves but has none. */
export async function fixMissingHyperlink(
  anchorTag: string,
  expected: string,
  targetHeadingText: string,
  commentText: string,
): Promise<FixResult> {
  return fixByLinkingOrCommenting(anchorTag, expected, targetHeadingText, commentText);
}

// ---------------------------------------------------------------------------
// bracket_heading_mismatch — bracket (parenthetical) heading matches the real target heading
// ---------------------------------------------------------------------------

export interface RawBracketRef {
  /** The full "Section N (bracket text)" phrase — used only for the
   *  finding's displayed problem message. NOT anchored — see
   *  `bracketMatchText`'s own docstring for why. */
  matchText: string;
  /** Just the parenthetical suffix, e.g. "(Payment Terms)" — sliced
   *  directly out of `matchText`'s own tail (not reconstructed from the
   *  trimmed `bracketText`), so it's guaranteed to literally appear in the
   *  document exactly as searched.
   *
   *  This is what actually gets anchored and replaced. Anchoring the FULL
   *  "Section N (...)" phrase instead — confirmed against a real dev
   *  session's console trace — throws Word's own "GeneralException" from
   *  `insertContentControl()` whenever another rule (missing_hyperlink/broken_reference/inconsistent_formatting)
   *  independently anchors the plain "Section N" reference at the same
   *  spot: that plain reference is a strict substring of bracket_heading_mismatch's own longer
   *  match, so whichever finding anchors first wins the range, and the
   *  second `insertContentControl()` call fails because Word won't wrap a
   *  range that overlaps an already-anchored one. The bracket suffix alone
   *  never overlaps a plain numeric reference, since it always comes
   *  strictly after it. */
  bracketMatchText: string;
  /** Which occurrence of this exact bracketMatchText, in document order —
   *  without this, a document with the same wrong bracket text used more
   *  than once (e.g. two different mislabeled sections both bracketed
   *  "(Payment Terms)") always anchored to occurrence 0 regardless of
   *  which one was actually flagged, corrupting the second finding's
   *  anchor. Same reasoning as missing_hyperlink's/topic_mismatch's/inconsistent_formatting's/shortened_hyperlink_text's own occurrenceIndex.
   *  No heading-exclusion complication here unlike broken_reference's own count: this
   *  sweep never excludes headings from its paragraph walk in the first
   *  place, so a plain per-bracketMatchText count already matches what a
   *  live, unfiltered search finds. */
  occurrenceIndex: number;
  sectionNumber: string;
  bracketText: string;
  paragraphIndex: number;
}

export interface BracketHeadingMismatchSweep {
  references: RawBracketRef[];
  headings: RawHeading[];
  /** AIVA-8310: the title under a label-only heading, keyed by the heading's
   *  text — "ARTICLE 14" → "LIMITATION OF LIABILITY". Optional: absent on a
   *  sweep from an older build. */
  titlesBelow?: Record<string, string>;
}

// A longer, worded bracket ("(Payment Terms)") — distinct from broken_reference's
// lettered-subclause bracket ("(a)"), which this pattern's `[A-Za-z]` start
// plus 2+ char minimum excludes.
const BRACKET_HEADING_MISMATCH_PATTERN = /\b(Section|Article|Clause)\s+(\d+(?:\.\d+)*)\s*\(([A-Za-z][^)]{1,80})\)/g;

/** Whether a bracket after a reference names a PLACE rather than a title —
 *  "(formerly Section 9)", "(as amended by Clause 4.2)". A bracket carrying a
 *  number is a pointer, never a section heading, so neither
 *  bracket_heading_mismatch nor hyperlink_target_mismatch may read it as one.
 *  Before both shared this, bracket_heading_mismatch flagged "Section 10
 *  (formerly Section 9)" as mis-titled and offered to overwrite the
 *  renumbering note with the target's heading. */
function bracketNamesAPlace(bracketText: string): boolean {
  return /\d/.test(bracketText);
}

/** Raw sweep only — see the broken_reference sweep's docstring for why anchoring and
 * judging both happen elsewhere. */
export async function findBracketHeadingMismatch(): Promise<BracketHeadingMismatchSweep> {
  const { references, titlesBelow } = await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/outlineLevel");
    // A Table of Contents field's own rendered entries mirror every
    // heading's text as their own separate paragraphs — but a TOC entry is
    // ordinary body text, not a real cross-reference a human wrote, so a
    // custom TOC style that happens to render a heading's own bracketed
    // topic ("Section 4 (Old Title)") would otherwise be scanned as a real
    // bracket reference. Same TOC-detection approach as findBrokenReference's
    // own guard and findExhibitScheduleMismatch's own guard (AIVA-8366
    // follow-up) — this sweep was the one place left in the file that never
    // had it.
    const fields = supportsFields() ? context.document.body.fields : null;
    fields?.load("items/code");
    await context.sync();

    const insideToc = await tocParagraphPredicate(context, paragraphs, fields);

    const found: RawBracketRef[] = [];
    const counter = documentOccurrenceCounter(paragraphs.items.map((p) => p.text ?? ""));
    paragraphs.items.forEach((p, paragraphIndex) => {
      const text = p.text ?? "";
      const isTocParagraph = insideToc(paragraphIndex);
      for (const match of text.matchAll(BRACKET_HEADING_MISMATCH_PATTERN)) {
        const parenStart = match[0].lastIndexOf("(");
        const bracketMatchText = match[0].slice(parenStart);
        // The bracket, not the whole match, is what the anchor step searches
        // for — so its own offset is what identifies the occurrence.
        const occurrenceIndex = counter.at(paragraphIndex, match.index + parenStart, bracketMatchText);
        if (occurrenceIndex === null) continue;
        // The TOC mirror still counts toward the index above (Word's search finds
        // it too); only the push is skipped — AIVA-8366.
        if (isTocParagraph) continue;
        if (bracketNamesAPlace(match[3] ?? "")) continue;
        found.push({
          matchText: match[0],
          bracketMatchText,
          occurrenceIndex,
          sectionNumber: match[2] ?? "",
          bracketText: (match[3] ?? "").trim(),
          paragraphIndex,
        });
      }
    });
    return { references: found, titlesBelow: labelOnlyHeadingTitles(paragraphs.items, insideToc) };
  });

  const { headings } = await collectHeadings();

  return { references, headings, titlesBelow };
}

/** Each label-only heading's title from the line below it (AIVA-8310):
 *  "ARTICLE 14" in Heading 1, "LIMITATION OF LIABILITY" as the next paragraph. */
function labelOnlyHeadingTitles(
  paragraphs: readonly Word.Paragraph[],
  insideToc: (paragraphIndex: number) => boolean,
): Record<string, string> {
  const texts = paragraphs.map((p) => p.text ?? "");
  const titles: Record<string, string> = {};
  paragraphs.forEach((p, index) => {
    const heading = texts[index]!.trim();
    if (headingLevelFrom(p.style ?? "", p.outlineLevel) === null || insideToc(index)) return;
    if (!isLabelOnlyHeading(heading) || heading in titles) return;
    const title = titleBelowLabelHeading(texts, index, insideToc);
    if (title) titles[heading] = title;
  });
  return titles;
}

// A heading's own leading number PLUS the rest of its text — mirrors
// rules.py's `_HEADING_NUMBER_AND_TOPIC`, built from `HEADING_NUMBER`'s own
// source so the two can never drift apart, same reasoning as the backend's.
//
// `HEADING_NUMBER` only captures the digits, not whatever separates them
// from the title — so for "4. Terms and Termination", group 2 used to be
// ". Terms and Termination" (the period rode along, since `.trim()` strips
// whitespace, not punctuation), which could never equal a clean bracket
// text and made every "N. Title"-style heading a guaranteed false mismatch.
// The optional delimiter + whitespace here is consumed, not captured, so it
// never reaches the topic group at all.
const HEADING_NUMBER_AND_TOPIC = new RegExp(String.raw`${HEADING_NUMBER.source}[.:)-]?\s*(.*)$`);

/** A heading split into its own number and its title, for either shape:
 *  "4.01  Fees" and "Section 4. Fees and Payment" both yield
 *  `{ number: "4.01" | "4", topic: "Fees" | "Fees and Payment" }`.
 *
 *  The word-prefixed shape used to yield nothing, so bracket_heading_mismatch
 *  could not resolve a topic for any top-level section and silently judged
 *  none of them — a planted "Section 3 (Fees and Payment)" pointing at a
 *  section actually titled "Term and Termination" went unreported. Reusing
 *  `SECTION_WORD_PREFIX` keeps this in step with `headingOwnNumber`, so the
 *  rule that decides a target EXISTS and the rule that reads its TITLE can
 *  never disagree about which headings have numbers. */
function headingNumberAndTopic(text: string): { number: string; topic: string } | null {
  for (const candidate of [text, text.replace(SECTION_WORD_PREFIX, "")]) {
    const match = HEADING_NUMBER_AND_TOPIC.exec(candidate);
    if (match?.[1] !== undefined) return { number: match[1], topic: (match[2] ?? "").trim() };
  }
  return null;
}

/** Deterministic evaluation, ported from rules.py's `evaluate_r04` — a
 * bracketed heading that doesn't match the target section's real title.
 * Only judges brackets whose target section number actually resolves to a
 * heading — a genuinely missing target is broken_reference's concern, not bracket_heading_mismatch's. A
 * section number resolving to MORE THAN ONE heading (a genuine duplicate-
 * numbering defect — numbering_order's concern) is treated as unresolved too: with two
 * candidate topics, neither is confidently "the" correct one, so no
 * correction is proposed rather than silently keying off whichever heading
 * was seen last (a fix that landed in the Python original after this port
 * was drafted — carried forward here). */
export function evaluateBracketHeadingMismatch(sweep: BracketHeadingMismatchSweep): FindingCreate[] {
  const headingByNumber = new Map<string, { topic: string; text: string }>();
  const ambiguousNumbers = new Set<string>();
  for (const heading of sweep.headings) {
    const split = headingNumberAndTopic(heading.text);
    if (!split) continue;
    if (headingByNumber.has(split.number)) {
      // Two headings claiming one number. Unchanged behaviour: neither topic
      // is confidently "the" correct one, so the rule declines rather than
      // key off whichever was seen last.
      ambiguousNumbers.add(split.number);
      continue;
    }
    // AIVA-8310: "ARTICLE 14" carries no title of its own; it is the line below.
    const topic = split.topic || readableTitle(sweep.titlesBelow?.[heading.text.trim()] ?? "");
    headingByNumber.set(split.number, { topic, text: heading.text });
  }

  const findings: FindingCreate[] = [];
  for (const reference of sweep.references) {
    if (ambiguousNumbers.has(reference.sectionNumber)) continue;
    const heading = headingByNumber.get(reference.sectionNumber);
    // A title that cannot be read is unknown, not a mismatch — never offer "()".
    if (!heading?.topic || heading.topic.toLowerCase() === reference.bracketText.toLowerCase()) continue;
    // Anchor/replace ONLY the bracket suffix, not the full "Section N
    // (...)" phrase — see bracketMatchText's own docstring for why
    // anchoring the full phrase throws a real Word exception whenever
    // another rule anchors the plain "Section N" reference at the same
    // spot. AIVA-8745: the fix links the whole phrase all the same, found
    // from the anchor at apply time — `referenceText` is what precedes it.
    const referenceText = reference.matchText
      .slice(0, reference.matchText.length - reference.bracketMatchText.length)
      .trimEnd();
    findings.push(
      toFindingCreate({
        ruleId: "bracket_heading_mismatch",
        matchText: reference.bracketMatchText,
        occurrenceIndex: reference.occurrenceIndex,
        problem: `"${reference.matchText}" shows "${reference.bracketText}" in brackets, but the target section is actually titled "${heading.topic}".`,
        comment: "Corrected the bracketed heading to match the target section and linked the reference to it.",
        fixHandler: "word.docreview.fix_bracket_heading_mismatch",
        fixParams: { correctFullText: `(${heading.topic})`, referenceText, headingText: heading.text },
      }),
    );
  }
  return findings;
}

/** Where a bracket_heading_mismatch fix links the corrected reference to. */
export interface BracketReferenceLink {
  /** The reference in front of the bracket, as written: "Section 3". */
  referenceText: string;
  /** The heading it refers to, which the link opens. */
  headingText: string;
}

/** The reference written immediately in front of the anchored bracket — the
 *  nearest match for `referenceText` before it in the same paragraph — or null
 *  when it is already linked (a hyperlink, or a field such as a REF
 *  cross-reference: hyperlink_target_mismatch owns where an existing link
 *  points) or cannot be found. Best effort: anything that goes wrong reads as
 *  "do not link". */
async function unlinkedReferenceBefore(
  context: Word.RequestContext,
  cc: Word.ContentControl,
  referenceText: string,
): Promise<Word.Range | null> {
  try {
    const bracket = cc.getRange(Word.RangeLocation.whole);
    const hits = bracket.paragraphs.getFirst().search(referenceText, { matchCase: false });
    hits.load("items/hyperlink");
    await context.sync();
    const relations = hits.items.map((hit) => hit.compareLocationWith(bracket));
    await context.sync();
    let nearest: Word.Range | null = null;
    for (const [i, hit] of hits.items.entries()) {
      const relation = relations[i]?.value;
      if (relation === "Before" || relation === "AdjacentBefore") nearest = hit;
    }
    if (!nearest || nearest.hyperlink) return null;
    if (supportsFields()) {
      const fields = nearest.fields;
      fields.load("items");
      await context.sync();
      if (fields.items.length > 0) return null;
    }
    return nearest;
  } catch (error) {
    logOfficeError(error, "docreview.bracket_heading_mismatch.find_reference");
    return null;
  }
}

/** Link "Section 3 (Charges and Invoicing)" — the reference and its corrected
 *  bracket together — to the heading, through the same bookmark
 *  missing_hyperlink's fix uses. Written with Track Changes OFF, as every link
 *  is since AIVA-8206: the link changes no wording, and a tracked link on
 *  existing text deletes that text when rejected. The bracket's own tracked
 *  rewrite stays tracked inside it, so rejecting the rewrite restores the old
 *  bracket text within the link rather than removing anything. A host that
 *  will not link the whole phrase (it spans the bracket's anchor) gets the
 *  reference alone linked; one that will not link at all is logged and left
 *  with the corrected bracket — never a half-written link. */
async function linkReferenceToHeading(
  context: Word.RequestContext,
  reference: Word.Range,
  cc: Word.ContentControl,
  headingText: string,
): Promise<void> {
  try {
    await withTrackingDisabled(context, async () => {
      const bookmark = `#${await ensureHeadingBookmark(context, headingText)}`;
      try {
        reference.expandTo(cc.getRange(Word.RangeLocation.whole)).hyperlink = bookmark;
        await context.sync();
      } catch (error) {
        logOfficeError(error, "docreview.bracket_heading_mismatch.link_phrase");
        reference.hyperlink = bookmark;
        await context.sync();
      }
    });
  } catch (error) {
    logOfficeError(error, "docreview.bracket_heading_mismatch.link");
  }
}

/** Shared body for every genuine tracked-edit "replace the whole matched
 * text with the correct value" fix (bracket_heading_mismatch, exhibit_schedule_mismatch-known, shortened_hyperlink_text-known, numbering_order): swap
 * the anchor's own range text for `correctFullText`, guarded the same way
 * every fix is. Each rule keeps its own exported wrapper for the same
 * reason `flagWithComment` does. */
async function replaceAnchorText(anchorTag: string, expected: string, correctFullText: string): Promise<FixResult> {
  return withResolvedAnchor(anchorTag, expected, async (context, cc) => {
    cc.getRange().insertText(correctFullText, Word.InsertLocation.replace);
    await context.sync();
  });
}

/** Correct the bracket text to match the target section's real heading —
 * a genuine tracked-change rewrite, since (unlike broken_reference) the correct value
 * is known: the target heading's own text.
 *
 * AIVA-8745: with `link`, the reference is also linked to that heading —
 * "Section 3 (Charges and Invoicing)" as one link — on a host that supports
 * bookmarks (desktop Word), unless it is already linked. The reference is found
 * BEFORE the rewrite, while the text in front of the bracket is exactly as
 * scanned. A finding from an older build carries no `link` and only rewrites. */
export async function fixBracketHeadingMismatch(
  anchorTag: string,
  expected: string,
  correctFullText: string,
  link?: BracketReferenceLink,
): Promise<FixResult> {
  if (!link) return replaceAnchorText(anchorTag, expected, correctFullText);
  return Word.run(async (context) => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return { applied: false, stale: true, reason: "anchor_missing" };
    if (isStale(cc.text, expected)) return { applied: false, stale: true, reason: "text_changed" };

    const reference = supportsBookmarks() ? await unlinkedReferenceBefore(context, cc, link.referenceText) : null;
    const { tracked } = await withTrackingForced(context, async () => {
      cc.getRange().insertText(correctFullText, Word.InsertLocation.replace);
      await context.sync();
    });
    if (reference) await linkReferenceToHeading(context, reference, cc, link.headingText);
    return { applied: true, stale: false, tracked };
  });
}

// ---------------------------------------------------------------------------
// inconsistent_formatting — consistent cross-reference formatting (usually underlined)
// ---------------------------------------------------------------------------

export interface RawFormattingReference {
  matchText: string;
  /** Which occurrence of this exact text, in document order — the same
   *  reference text can appear more than once with different formatting. */
  occurrenceIndex: number;
  /** `Word.UnderlineType` as a string, e.g. "Single" | "None".
   *
   *  DIRECT character formatting only. A cross-reference inserted as a
   *  hyperlink carries its underline through the built-in Hyperlink character
   *  STYLE, not through a run property, so this reads "None" for text the
   *  operator can see is underlined — Word's own Home-tab U button is
   *  likewise inactive on it. Read `style` alongside this, never alone:
   *  `displayedUnderline` is what the rule must compare (AIVA-8540). */
  underline: string;
  /** The range's style name, e.g. "Hyperlink" | "Normal" | "" when unreadable.
   *  Carried solely so a style-supplied underline is not mistaken for none.
   *  Optional: absent on a sweep persisted before AIVA-8540, which then behaves
   *  exactly as it did then rather than failing. */
  style?: string;
  paragraphIndex: number;
  /** The reference names the very part it sits in — "this Exhibit B" in
   *  Exhibit B's own text. It is a label, not a pointer to somewhere else, so
   *  it neither sets the document's style nor is held to it. Optional: absent
   *  (treated as false) on a sweep persisted before it existed. */
  selfReference?: boolean;
}

export interface InconsistentFormattingSweep {
  references: RawFormattingReference[];
}

/**
 * Raw sweep only. Reads each occurrence's own underline — majority-vs-outlier
 * is a deterministic backend decision (`evaluate_r06`), not something this
 * sweep judges.
 *
 * `existingReferences` lets a caller that already ran the broken_reference sweep this
 * turn (the common case — `word.docreview.scan` runs every rule together)
 * pass its reference list straight in, instead of this function repeating
 * broken_reference's own paragraph sweep. Word.run round trips add up on a real document,
 * and `word.docreview.scan` already has a 20s client-side timeout to stay
 * under — omit the argument only when calling this rule standalone.
 */
/**
 * What this sweep records when a reference's live range did not come back, so
 * its underline could not be read at all (AIVA-8547).
 *
 * Deliberately NOT a `Word.UnderlineType` value: it must never be mistaken
 * for a formatting style, because `majorityUnderline` decides the document's
 * house style by counting styles, and `fixInconsistentFormatting` writes that
 * style back. A sentinel that looks like a style poisons both — which is
 * exactly how "every reference measured against a non-style" happened.
 */
const UNREADABLE_UNDERLINE = "Unknown";

/**
 * A reference whose words are underlined differently — "Exhibit" underlined,
 * "A" not. Word reports such a range as "Mixed", which this rule used to skip
 * outright (AIVA-8547): a fully underlined LINK can read "Mixed" too, when part
 * of it takes its underline from the Hyperlink style and part from the run.
 * `readMixedRangesByWord` tells the two apart by reading each word; only a
 * genuine part-underline ends up here. Like "Unknown" it never votes on the
 * house style — it is no style at all — but unlike "Unknown" it IS a visible
 * inconsistency, so it is flagged against the style the rest carry.
 */
const PARTIAL_UNDERLINE = "Partial";

/**
 * One reading for a reference read word by word: the shared value when the
 * words that read a definite value agree, `PARTIAL_UNDERLINE` only when two
 * of them read DIFFERENT definite values, and "Mixed" — still unresolved, so
 * still skipped — when no word read a definite value or one could not be read.
 * Each value is already `displayedUnderline`d: a linked word counts as
 * underlined, whatever its run says.
 *
 * A word reading "Mixed" is NO evidence either way. Real Word reports a fully
 * underlined digit as mixed (confirmed through Word itself on the 24-page MSA
 * document: in "Article 10", "Article " read Single and "10" read mixed, though
 * both runs carry the identical `<w:u w:val="single"/>`). Counting such a word
 * as a disagreement turned 23 fully underlined references into "only partly
 * underlined" findings; only a word that reads a real, different value — "B"
 * reading None after an underlined "Exhibit " — is a half underline.
 */
export function combinedUnderline(words: readonly string[]): string {
  if (words.some((w) => w !== "Mixed" && !SETTABLE_UNDERLINE_VALUES.has(w))) return "Mixed";
  const definite = new Set(words.filter((w) => SETTABLE_UNDERLINE_VALUES.has(w)));
  if (definite.size === 0) return "Mixed";
  return definite.size === 1 ? [...definite][0]! : PARTIAL_UNDERLINE;
}

/** Queue `properties` once on each distinct range, skipping gaps. Returns
 *  whether anything was queued, so a caller can skip an empty sync. */
function loadOnce(ranges: ReadonlyArray<Word.Range | undefined>, properties: string): boolean {
  let queued = false;
  for (const range of new Set(ranges)) {
    if (!range) continue;
    range.load(properties);
    queued = true;
  }
  return queued;
}

/**
 * Re-read every range Word reports as "Mixed", one word at a time, and return
 * each one's `combinedUnderline`. One extra round trip, and only when some
 * reference actually read "Mixed". A host without `getTextRanges` (pre-WordApi
 * 1.3), or a read that fails, leaves the range out of the map — it stays
 * "Mixed" and abstains, exactly as before.
 */
async function readMixedRangesByWord(
  context: Word.RequestContext,
  ranges: ReadonlyArray<Word.Range | undefined>,
): Promise<Map<Word.Range, string>> {
  const pending: Array<[Word.Range, Word.RangeCollection]> = [];
  for (const range of new Set(ranges)) {
    if (!range) continue;
    if ((range.font.underline as unknown as string) !== "Mixed" || typeof range.getTextRanges !== "function") continue;
    const words = range.getTextRanges([" "], true);
    words.load("items/font/underline,items/style");
    pending.push([range, words]);
  }
  const resolved = new Map<Word.Range, string>();
  if (pending.length === 0) return resolved;
  try {
    await context.sync();
  } catch (err) {
    logOfficeError(err, "word-docreview-mixed-underline");
    return resolved;
  }
  for (const [range, words] of pending) {
    resolved.set(
      range,
      combinedUnderline(words.items.map((w) => displayedUnderline(w.font.underline as unknown as string, w.style))),
    );
  }
  return resolved;
}

/**
 * Character styles that render their text underlined without setting any run
 * underline. Word applies `Hyperlink` to a cross-reference inserted with
 * "insert as hyperlink" — the overwhelmingly common way these are authored —
 * and `FollowedHyperlink` after it has been clicked.
 *
 * Matched case-insensitively against the style NAME. Word returns the
 * localized name here, so a non-English build reports e.g. "Lien hypertexte"
 * and falls through to the direct value — the pre-AIVA-8540 behaviour, no
 * worse than before, never a wrong claim about a style this cannot identify.
 */
const UNDERLINING_STYLES: ReadonlySet<string> = new Set(["hyperlink", "followedhyperlink"]);

/**
 * What the operator actually SEES, from the two things that produce it.
 *
 * `font.underline` is direct run formatting only. A cross-reference inserted
 * as a hyperlink gets its underline from the Hyperlink character style, so the
 * run says "None" while the text is plainly underlined on screen — Word's own
 * Home-tab U button is inactive on exactly these, which is how this was
 * finally pinned down (AIVA-8540). Comparing the raw run value made every such
 * reference an outlier against an underlined majority, and Apply then wrote a
 * direct underline the reference did not need.
 *
 * Direct formatting wins when it is itself an underline: a run that sets
 * "Double" over the Hyperlink style really is Double, and the style adds
 * nothing. The style is consulted only to rescue a "None" that is not the
 * whole story — it can never turn a real direct underline into something else,
 * and it never touches `UNREADABLE_UNDERLINE`, which must stay unreadable so
 * `majorityUnderline` keeps excluding it.
 */
export function displayedUnderline(underline: string, style: string | undefined): string {
  if (underline !== "None") return underline;
  // `style` is optional at the type level for older persisted sweeps and is
  // absent outright when Office.js does not return one; an unknown style can
  // only mean "no style-supplied underline", never a claim either way.
  return UNDERLINING_STYLES.has((style ?? "").trim().toLowerCase()) ? "Single" : "None";
}

export async function findInconsistentFormatting(
  existingReferences?: RawBrokenReference[],
): Promise<InconsistentFormattingSweep> {
  const raw = existingReferences ?? (await findBrokenReference()).references;

  return Word.run(async (context) => {
    // Rides the searches below — one extra property on an existing round trip,
    // not a new one — so the hit counts Word returns can be checked against
    // what the document's own text says. See `occurrenceCountDisagrees`.
    const paragraphs = loadOptionalParagraphs(context);
    // Two-phase, and deliberately not "load + read .items" in the same
    // pass: a RangeCollection's `.items` is not populated until the load it
    // queued has actually round-tripped through context.sync() — reading it
    // any earlier throws "The property 'items' is not available...".
    const searchesByText = new Map<string, WordBoundedSearch>();
    for (const ref of raw) {
      if (searchesByText.has(ref.matchText)) continue;
      // Word-bounded — see `queueWordBoundedSearch`. Without it, "Section 2"
      // matches inside "Section 24" too, which split one real formatting
      // outlier into two findings — one correctly labelled "Section 24", one a
      // phantom "Section 2" highlighting the other's text — and read one
      // reference's underline as another's. matchWholeWord alone was the
      // earlier attempt; Word ignores it for a phrase (AIVA-8547).
      searchesByText.set(ref.matchText, queueWordBoundedSearch(context.document.body, ref.matchText));
    }
    await context.sync();

    // Reads formatting BY occurrenceIndex, so it must index the same hit list
    // the anchor step does — see `wordBoundedHits`. A disagreement makes it read
    // one reference's underline and attribute it to another.
    const rangesByText = wordBoundedHitLists(searchesByText, paragraphTextsOf(paragraphs));

    // `style` rides alongside `font/underline` in the SAME load — a
    // cross-reference inserted as a hyperlink draws its underline from the
    // Hyperlink character style, where `font.underline` is "None" (AIVA-8540).
    // One extra property on a load already being made costs no round trip.
    //
    // AIVA-8925: read only the hits a reference resolves to — every other hit
    // (headings, TOC lines, unreferenced repeats) was loaded, and "Mixed" ones
    // even re-read word by word, and never used. A distrusted text is never
    // read at all (see below), and that check needs only the hit counts.
    const paragraphTexts = paragraphTextsOf(paragraphs);
    const distrusted = distrustedMatchTexts(paragraphTexts, rangesByText, "inconsistent_formatting");
    const used = raw
      .filter((ref) => !distrusted.has(ref.matchText))
      .map((ref) => rangesByText.get(ref.matchText)?.[ref.occurrenceIndex]);
    if (loadOnce(used, "font/underline,style")) await context.sync();
    // "Mixed" says only that the underline varies across the range — a
    // half-underlined "Exhibit A" and a link drawn partly from the Hyperlink
    // style both read that way. Resolved word by word; see `PARTIAL_UNDERLINE`.
    const byWord = await readMixedRangesByWord(context, used);

    // ref.occurrenceIndex already counts across the whole document,
    // headings included, exactly matching what the unfiltered live search
    // above finds — see RawBrokenReference.occurrenceIndex's own docstring.
    // (A heading-only filter used to be needed here, before that count was
    // fixed at its own source; keeping one on top of the corrected index
    // would double-correct and land on the WRONG occurrence again. That is
    // still true, and is why the guard below is a count CHECK rather than a
    // filter: it refuses an untrustworthy index, it never shifts one.)
    const out: RawFormattingReference[] = raw.map((ref) => {
      // Set only when true, so every other payload is exactly what it was (same
      // convention as AIVA-8669's `looksLinked`).
      const selfTag = namesItself(paragraphTexts[ref.paragraphIndex] ?? "", ref.matchText) ? { selfReference: true } : {};
      // Word counts this text differently than its own document text does, so
      // NO index into these ranges is safe — including one that happens to
      // land on a heading, which is what AIVA-8544 was reported as. Reported
      // unreadable, exactly like a range that could not be read at all
      // (AIVA-8547): out of the majority vote, and no finding.
      if (distrusted.has(ref.matchText)) {
        return {
          matchText: ref.matchText,
          occurrenceIndex: ref.occurrenceIndex,
          underline: UNREADABLE_UNDERLINE,
          paragraphIndex: ref.paragraphIndex,
          ...selfTag,
        };
      }
      const ranges = rangesByText.get(ref.matchText) ?? [];
      // AIVA-8547: NO positional fallback. This used to be
      // `?? ranges[ranges.length - 1]`, which silently read the LAST
      // occurrence's underline whenever occurrenceIndex overshot what the
      // live search found — attributing one reference's formatting to a
      // different one. That is worse than not knowing: a borrowed value looks
      // like a real vote, so it skews the very majority this rule measures
      // everything against, and the outlier it should have caught then reads
      // as conforming. Reporting it as unreadable instead keeps it out of the
      // vote entirely (see `majorityUnderline`).
      const range = ranges[ref.occurrenceIndex];
      const wordByWord = range ? byWord.get(range) : undefined;
      const underline = wordByWord ?? (range?.font.underline as unknown as string) ?? UNREADABLE_UNDERLINE;
      // A word-by-word reading has already applied each word's own style
      // (`displayedUnderline`), so the whole range's style must not be applied
      // again on top of it — "None" from a plain word would turn "Single".
      const style = wordByWord === undefined ? ((range?.style as unknown as string) ?? "") : "";
      // AIVA-8540: one line per reference, at the exact point the values are
      // read, so a "why was this flagged" question is answerable from the
      // console without instrumenting the build. Both inputs to
      // `displayedUnderline` are shown, never just the one that won — the
      // run value alone is what made this rule's behaviour unexplainable.
      console.debug(
        `[docReview] inconsistent_formatting: ${JSON.stringify(ref.matchText)} occurrence=${ref.occurrenceIndex} ` +
          `font.underline=${JSON.stringify(underline)} style=${JSON.stringify(style)} ` +
          `displayed=${JSON.stringify(displayedUnderline(underline, style))}`,
      );
      return {
        matchText: ref.matchText,
        occurrenceIndex: ref.occurrenceIndex,
        underline,
        style,
        paragraphIndex: ref.paragraphIndex,
        ...selfTag,
      };
    });
    return { references: out };
  });
}

/** Whether `paragraph` names `matchText` as itself — "this Exhibit B", "This
 *  Article 4" — the way an attachment or article refers to its own text. Such
 *  a mention is a label, not a cross-reference to somewhere else, so it takes
 *  no part in the document's formatting style (see `RawFormattingReference`).
 *  Read from the paragraph's own text because the sweep's references carry
 *  no surrounding words; a paragraph that ALSO points at the same label
 *  elsewhere is rare enough that the whole paragraph is judged together. */
function namesItself(paragraph: string, matchText: string): boolean {
  return new RegExp(String.raw`\bthis\s+${escapeRegExp(matchText)}\b`, "i").test(paragraph);
}

/** The underline value shared by a strict majority of `refs`, or `null` when
 * no value clears 50% — extracted from `evaluateInconsistentFormatting`
 * purely to keep that function's cognitive complexity down (a function call
 * doesn't add nesting the way an inlined loop-inside-loop does); the logic
 * itself is unchanged from the original single-function version. */
/**
 * Underline values this rule may PROPOSE as a fix (AIVA-8546).
 *
 * `font.underline` is read-write, but not every value it can REPORT is a value
 * it will ACCEPT:
 *
 * - `"Unknown"` is not a `Word.UnderlineType` member at all — it is this
 *   file's own fallback for a reference whose range could not be read (see
 *   `findInconsistentFormatting`'s `?? "Unknown"`).
 * - `"Mixed"` is a real member, but a read-only STATE meaning "the underline
 *   varies across this range". Writing it back is meaningless.
 *
 * Either one reaching `cc.getRange().font.underline = …` makes Word reject the
 * whole `context.sync()` with `InvalidArgument`, which surfaced as a red
 * "InvalidArgument" banner the moment the operator pressed Apply — the finding
 * was created and anchored perfectly, and only the write failed.
 *
 * Deliberately an allow-list of the values Word documents as settable, not a
 * deny-list of the two known-bad ones: a future Word build adding another
 * read-only sentinel would slip straight through a deny-list and reintroduce
 * exactly this bug.
 */
const SETTABLE_UNDERLINE_VALUES: ReadonlySet<string> = new Set([
  "None",
  "Single",
  "Word",
  "Double",
  "Thick",
  "Dotted",
  "DottedHeavy",
  "Dashed",
  "DashedHeavy",
  "DashLine",
  "DashLineHeavy",
  "DashLineLong",
  "DashLineLongHeavy",
  "DotDashLine",
  "DotDashLineHeavy",
  "TwoDotDashLine",
  "TwoDotDashLineHeavy",
  "Wave",
  "WaveHeavy",
  "WaveDouble",
]);

function majorityUnderline(refs: RawFormattingReference[]): string | null {
  // AIVA-8547: vote among the references whose underline could actually be
  // READ. `"Unknown"` is not a formatting style — it is `findInconsistentFormatting`'s
  // fallback for a reference whose live range did not come back (see its own
  // `?? UNREADABLE_UNDERLINE`), and `"Mixed"` is Word's read-only "varies
  // across this range" state. Neither describes a house style, so neither
  // gets a say in what the house style IS.
  //
  // Counting them used to decide the rule outright: enough unreadable
  // references made `"Unknown"` the majority, and every genuinely
  // inconsistent reference in that category was then measured against a
  // non-style. Before AIVA-8546 that surfaced as `InvalidArgument` on Apply;
  // after it, as the rule silently finding nothing at all. Both are the same
  // root cause — an unreadable reference was allowed to define the standard.
  //
  // The denominator narrows with the numerator: a majority is a majority of
  // the references that actually voted, not of every reference swept. A
  // category where nothing is readable votes on nothing and returns null.
  const readable = refs.filter((ref) => SETTABLE_UNDERLINE_VALUES.has(ref.underline));
  if (readable.length === 0) return null;

  const counts = new Map<string, number>();
  for (const ref of readable) counts.set(ref.underline, (counts.get(ref.underline) ?? 0) + 1);
  let majorityValue = "";
  let majorityCount = 0;
  for (const [value, count] of counts) {
    if (count > majorityCount) {
      majorityValue = value;
      majorityCount = count;
    }
  }
  return majorityCount > readable.length / 2 ? majorityValue : null;
}

/** Deterministic evaluation, ported from rules.py's `evaluate_r06` — the
 * document's own majority underline formatting is treated as the house
 * style; anything else without a clear majority is left alone rather than
 * guessing which way is "correct". The majority is computed PER reference
 * category (Section vs. Article vs. Exhibit vs. Schedule vs. clause), not
 * across the whole sweep as one population — a document can legitimately
 * format cross-references to different things differently, and comparing
 * them as one pool would flag a consistent-within-its-own-category
 * reference just because a differently-categorized majority outnumbers it
 * (a fix that landed in the Python original after this port was drafted —
 * carried forward here). */
export function evaluateInconsistentFormatting(sweep: InconsistentFormattingSweep): FindingCreate[] {
  // Split into per-category and per-finding helpers purely to keep this under
  // Sonar's cognitive-complexity cap (S3776); `flatMap` keeps the findings in
  // exactly the order the nested loops produced — category, then reference.
  const byCategory = referencesByCategory(sweep.references.filter((ref) => !ref.selfReference));
  const documentStyle = establishedStyle([...byCategory.values()].flat());
  return [...byCategory.entries()].flatMap(([category, refs]) =>
    categoryOutlierFindings(refs, categoryTargetStyle(refs, documentStyle, byCategory, category)),
  );
}

/** The style a set of references establishes: a strict majority among at
 *  least two readable ones, else null. One reference never decides how a
 *  document is formatted. */
function establishedStyle(refs: readonly RawFormattingReference[]): string | null {
  const readable = refs.filter((ref) => SETTABLE_UNDERLINE_VALUES.has(ref.underline));
  return readable.length >= 2 ? majorityUnderline(readable) : null;
}

/**
 * The underline a category's references should carry.
 *
 * - **A category of one** has no style of its own: it is held to the other
 *   categories' style. The one "Exhibit A" (or "Article 4") in a document
 *   whose Section and Schedule references are all linked used to be skipped
 *   outright, since a category of one cannot outvote itself.
 * - **A category whose own style differs from the document's** is held to the
 *   document's. Reported on the MSA test document: every Section and Schedule
 *   reference was linked and underlined, every Exhibit reference plain, and
 *   none was flagged — the Exhibits agreed with each other, so each was judged
 *   "consistent" against a style only Exhibits had. The document's style is
 *   ONE majority over every reference, so two categories can never flag each
 *   other in opposite directions; the smaller one follows the larger.
 * - **Otherwise** a category keeps its own majority, as before.
 *
 * References that name themselves ("this Exhibit B") are already left out —
 * see `RawFormattingReference.selfReference`.
 */
function categoryTargetStyle(
  refs: readonly RawFormattingReference[],
  documentStyle: string | null,
  byCategory: ReadonlyMap<string, RawFormattingReference[]>,
  category: string,
): string | null {
  if (refs.length < 2) {
    return establishedStyle([...byCategory.entries()].filter(([c]) => c !== category).flatMap(([, r]) => r));
  }
  // `establishedStyle`, not a bare majority: a part-underlined reference does
  // not vote, so one plain reference beside two partial ones would otherwise
  // "be" the category's style and strip their underlines.
  const own = establishedStyle(refs);
  return own !== null && documentStyle !== null && own !== documentStyle ? documentStyle : own;
}

/** References grouped by `referenceCategory`, in first-seen order. Each one's
 *  underline is resolved ONCE, here, so the majority, the outlier test and the
 *  written targetUnderline all read the same value — what the operator sees,
 *  not the run property alone (AIVA-8540). Resolving it at each use is how the
 *  three drift apart. */
function referencesByCategory(references: readonly RawFormattingReference[]): Map<string, RawFormattingReference[]> {
  const byCategory = new Map<string, RawFormattingReference[]>();
  for (const raw of references) {
    const reference = { ...raw, underline: displayedUnderline(raw.underline, raw.style) };
    const category = referenceCategory(reference.matchText);
    const bucket = byCategory.get(category) ?? [];
    bucket.push(reference);
    byCategory.set(category, bucket);
  }
  return byCategory;
}

/** One category's outliers against the style it should carry — see
 *  `categoryTargetStyle`. */
function categoryOutlierFindings(refs: RawFormattingReference[], majorityValue: string | null): FindingCreate[] {
  if (majorityValue === null) return [];
  // AIVA-8546: never propose a fix Word will refuse to write. The majority
  // is computed from what Word REPORTED when reading, and two of those
  // values can never be written back — see SETTABLE_UNDERLINE_VALUES. A
  // finding built on one of them anchors fine and then fails at Apply with
  // "InvalidArgument", which reads to the operator as a broken card rather
  // than as "there is no correct formatting to apply here".
  //
  // Skipping the whole category is the honest outcome: if the house style
  // cannot be expressed as a settable value, this rule has nothing to say
  // about the outliers either. Same spirit as `majorityUnderline` returning
  // null when no clear majority exists — say nothing rather than guess.
  if (!SETTABLE_UNDERLINE_VALUES.has(majorityValue)) return [];

  return refs
    .filter((reference) => differsFromMajority(reference.underline, majorityValue))
    .map((reference) => inconsistentFormattingFinding(reference, majorityValue));
}

/** Whether a reference's underline is a real, different value from the
 *  majority. AIVA-8547: only a reference whose underline was actually READ
 *  can be "formatted differently". `"Unknown"` (the range could not be read,
 *  or its occurrence index was distrusted) and `"Mixed"` (Word's "varies
 *  across this range") are not values that differ from the house style — they
 *  are the absence of one. Flagging them reported references the operator
 *  could plainly see were underlined like every other, and the sweep already
 *  promises such a reference "no finding". They abstain here exactly as they
 *  abstain from the vote in `majorityUnderline`. */
function differsFromMajority(underline: string, majorityValue: string): boolean {
  // A part-underlined reference has no single value, but it plainly does not
  // match a house style that underlines references whole (or not at all).
  return underline !== majorityValue && (SETTABLE_UNDERLINE_VALUES.has(underline) || underline === PARTIAL_UNDERLINE);
}

function inconsistentFormattingFinding(reference: RawFormattingReference, majorityValue: string): FindingCreate {
  return toFindingCreate({
    ruleId: "inconsistent_formatting",
    matchText: reference.matchText,
    problem:
      reference.underline === PARTIAL_UNDERLINE
        ? `"${reference.matchText}" is only partly underlined, unlike the document's other cross-references.`
        : `"${reference.matchText}" is formatted differently from the document's other similar cross-references.`,
    comment: "Applied the document's standard cross-reference formatting.",
    fixHandler: "word.docreview.fix_inconsistent_formatting",
    fixParams: { targetUnderline: majorityValue },
    occurrenceIndex: reference.occurrenceIndex,
  });
}

/** Apply the document's own majority underline style to an outlier
 * cross-reference — deterministic, since "what most references already
 * look like" is computed from the sweep, not guessed.
 *
 * Writes to the anchor's CONTENT range, never its default (Whole) range
 * (AIVA-8180). Whole includes the content control's own start/end tags, and
 * a tracked format change there is recorded on the control's own mark
 * formatting (`w:sdtPr/w:rPr`) as well as on the text — Word then shows
 * THREE "Formatted: Underline" balloons (an empty one on each tag, plus the
 * text) for one reference. Confirmed against real document XML: the Content
 * range produces exactly one revision, even when the reference spans
 * several runs.
 *
 * A previous attempt merged the range into one run first (an untracked
 * reinsert of its own text) on the theory that the balloons were per-run.
 * Reverted in 88ba95a26: the reinsert invalidated the anchor, and the
 * balloons were never per-run to begin with.
 */
export async function fixInconsistentFormatting(
  anchorTag: string,
  expected: string,
  targetUnderline: string,
): Promise<FixResult> {
  // AIVA-8546, second line of defence. evaluateInconsistentFormatting no
  // longer CREATES a finding carrying an unwritable value, but a finding
  // persisted by an earlier build is still sitting in Cosmos with one, and
  // Apply reads fix_params straight from storage. Without this, those older
  // cards keep throwing "InvalidArgument" until their thread ages out.
  //
  // Reported as a refusal, not an exception: `applied: false` with a reason
  // leaves the finding actionable-looking-but-honest, the same shape every
  // other "we could not write this" path in this file returns.
  if (!SETTABLE_UNDERLINE_VALUES.has(targetUnderline)) {
    console.warn(
      `[docReview] fix_inconsistent_formatting: refusing to write unsettable underline ${JSON.stringify(targetUnderline)}`,
    );
    return { applied: false, reason: "unsettable_underline" };
  }
  return withResolvedAnchor(anchorTag, expected, async (context, cc) => {
    cc.getRange(Word.RangeLocation.content).font.underline = targetUnderline as Word.UnderlineType;
    await context.sync();
  });
}

// ---------------------------------------------------------------------------
// topic_mismatch — a cross-reference's own sentence should be about the same topic as
// the section it points to (LLM judgment — this sweep only gathers the two
// snippets being compared, never decides the match itself)
// ---------------------------------------------------------------------------

export interface RawTopicMatchCandidate {
  matchText: string;
  /** Which occurrence of this exact matchText, in document order — without
   *  this, a document where the same reference text (e.g. "Section 4")
   *  appears many times always anchors this finding to occurrence 0,
   *  regardless of which real occurrence the LLM actually judged as a
   *  topic mismatch. Same reasoning as missing_hyperlink's/inconsistent_formatting's own occurrenceIndex. */
  occurrenceIndex: number;
  /** The sentence/paragraph the reference itself sits in. */
  referenceContext: string;
  /** "" when the target section number doesn't resolve to any heading —
   *  broken_reference's concern, not topic_mismatch's, so evaluate_r05 skips these without asking
   *  the LLM anything. */
  targetHeading: string;
  targetFirstText: string;
  paragraphIndex: number;
}

export interface TopicMismatchSweep {
  candidates: RawTopicMatchCandidate[];
}

/** How much of the target section's own text to send — a snippet, never the
 * whole section, matching the design doc's "the LLM never receives the
 * whole contract" rule. */
const TOPIC_MISMATCH_TARGET_SNIPPET_LENGTH = 300;

/**
 * Pure: the paragraphs of the Definitions section whose entry only points at
 * where its term is defined — `"Deliverables" has the meaning given in Section
 * 2.04` (AIVA-8543).
 *
 * A pointer's subject is its TERM, not a topic of its own, so judging its
 * reference for topic compared "Deliverables" with "2.04 Acceptance" and
 * flagged a correct reference. Whether the term really is defined there is
 * defined_term_section_mismatch's question, which it already asks. So
 * topic_mismatch leaves these references to it.
 *
 * Found the way alphabetical_order finds the section (AIVA-8606's
 * `selectDefinitionsSection` and `parseDefinitionEntry`), so the two rules
 * agree on what the Definitions section is. Only a real one counts
 * (`isNamedDefinitionsSection`). An entry that DEFINES its term — `"Fees" means
 * the amounts payable under Section 4` — is not a pointer, and its reference
 * is still judged. List levels and tracked deletions are not read: they decide
 * an entry's rank, not whether it is in the section, and reading them costs
 * Word round trips a scan near its time limit cannot spare.
 */
export function definitionPointerParagraphs(
  texts: readonly string[],
  styles: readonly string[],
  outlineLevels: ReadonlyArray<number | null>,
  colonEntries: ReadonlySet<number> = new Set(),
): Set<number> {
  const inventory = collectDeclaredTerms(texts, colonEntries);
  const entries = definitionEntriesByParagraph(inventory, texts.length);
  const section = selectDefinitionsSection(
    texts.map((text, index) => ({
      text,
      style: styles[index] ?? "",
      outlineLevel: outlineLevels[index] ?? null,
      entry: entries[index] ?? null,
      listLevel: null,
      deleted: false,
    })),
  );
  const pointers = new Set<number>();
  if (!isNamedDefinitionsSection(section)) return pointers;
  for (const { shape, phrasing, paragraphIndex } of inventory) {
    const inSection = paragraphIndex > section.startParagraph && paragraphIndex <= section.endParagraph;
    if (shape === "entry" && phrasing !== "defines" && inSection) pointers.add(paragraphIndex);
  }
  return pointers;
}

/** Raw sweep only. Reuses broken_reference's own reference regex (a cross-reference is a
 * cross-reference) and defined_term_section_mismatch's heading-resolution approach (tocFromParagraphs)
 * to find each reference's target section — but, unlike defined_term_section_mismatch, gathers the
 * target's OWN text for a human/LLM topic judgment, not a term-presence
 * check. */
export async function findTopicMismatchData(
  existingReferences?: RawBrokenReference[],
): Promise<TopicMismatchSweep> {
  const references = existingReferences ?? (await findBrokenReference()).references;
  if (references.length === 0) return { candidates: [] };

  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    // AIVA-8925: one targeted load, not "every property of every paragraph" and
    // then a second round trip for the three this reads.
    paragraphs.load("items/text,items/style,items/outlineLevel");
    await context.sync();

    const texts = paragraphs.items.map((p) => p.text ?? "");
    const styles = paragraphs.items.map((p) => p.style ?? "");
    // Without outline levels this resolves no sections at all on a document
    // pasted in from HTML, which carries no paragraph styles.
    const outlineLevels = paragraphs.items.map((p) => p.outlineLevel ?? null);
    const toc = tocFromParagraphs(texts, styles, outlineLevels);
    // Recovers where in its paragraph each reference sits, so the sentence
    // holding it can be quoted (AIVA-8543). The same counter that minted
    // `occurrenceIndex` maps a match's offset back to that index, so this
    // picks exactly the occurrence the finding will anchor on — even when the
    // same reference text appears twice in one paragraph.
    const counter = documentOccurrenceCounter(texts);
    const offsetOf = (ref: RawBrokenReference): number | undefined =>
      boundedOccurrenceOffsets(texts[ref.paragraphIndex] ?? "", ref.matchText).find(
        (offset) => counter.at(ref.paragraphIndex, offset, ref.matchText) === ref.occurrenceIndex,
      );
    // AIVA-8543: "in accordance with 4.02", where the bare number is a Word
    // cross-reference or a link to a bookmark. Read here, not in the shared
    // reference sweep, so no other rule starts treating bare numbers as
    // references.
    const linked = await findLinkedBareNumbers(context, texts, styles, outlineLevels);

    // AIVA-8543: two kinds of reference are another rule's to judge, not this
    // one's. A Definitions entry that only points at where its term is
    // defined is defined_term_section_mismatch's. An Exhibit/Schedule
    // reference is exhibit_schedule_mismatch's, which compares it with the
    // document's real attachments and their titles
    // (`findExhibitScheduleMeaningCandidates`) — here it was compared with a
    // numbered section, and could get a second card for an issue that rule
    // already reports.
    const pointers = definitionPointerParagraphs(texts, styles, outlineLevels, await readColonEntries(context, paragraphs));
    const judgedHere = (ref: RawBrokenReference) => !pointers.has(ref.paragraphIndex) && !isExplicitPrefixCategory(ref.matchText);

    // ref.occurrenceIndex already counts across the whole document,
    // headings included, matching what a live body.search() at anchor time
    // finds — see RawBrokenReference.occurrenceIndex's own docstring.
    const candidates: RawTopicMatchCandidate[] = [...references, ...linked].filter(judgedHere).map((ref) => {
      const baseLabel = ref.targetLabel.replace(/\([a-z0-9]+\)$/i, "");
      // `headingOwnNumber`, not a leading-digit regex (AIVA-8543): a heading
      // written "Section 4. Fees and Payment" carries its number AFTER the
      // word, and the bare regex resolved no target for it — so every such
      // reference was dropped before the LLM ever judged it. The same fix
      // AIVA-8544 already made for broken_reference/missing_hyperlink.
      const entry = toc.find((e) => headingOwnNumber(e.title) === baseLabel);
      const targetHeading = entry?.title ?? "";
      const targetFirstText = entry
        ? texts.slice(entry.startParagraph, entry.endParagraph + 1).join(" ").slice(0, TOPIC_MISMATCH_TARGET_SNIPPET_LENGTH)
        : "";
      return {
        matchText: ref.matchText,
        occurrenceIndex: ref.occurrenceIndex,
        referenceContext: referenceSentence(texts[ref.paragraphIndex] ?? "", offsetOf(ref), ref.matchText.length),
        targetHeading,
        targetFirstText,
        paragraphIndex: ref.paragraphIndex,
      };
    });
    return { candidates };
  });
}

/** A section number that is the whole of a link's text — "4.02", "12". */
const BARE_SECTION_NUMBER = /^\d+(?:\.\d+)*$/;

/**
 * Bare section numbers that are links — a Word cross-reference
 * (`REF … \h`, Insert > Cross-reference) or a link to a bookmark in the
 * document — as references for topic_mismatch (AIVA-8543: "apply any proceeds
 * in accordance with 4.02"). A bare number with no link is never read: in
 * plain text "4.02" cannot be told apart from an amount or a count.
 *
 * Skipped: a number the text before it already names ("Section " + a field
 * showing "4.02") — the reference sweep has that one whole — and anything in a
 * heading or TOC line. `occurrenceIndex` is asked of Word, as for every other
 * link (`occurrenceIndicesOf`); a link Word cannot place is dropped rather
 * than given a guessed index.
 *
 * Never fails the topic check: on a host without the APIs, or any error,
 * the result is empty and the text references are judged as before.
 */
async function findLinkedBareNumbers(
  context: Word.RequestContext,
  texts: readonly string[],
  styles: readonly string[],
  outlineLevels: ReadonlyArray<number | null>,
): Promise<RawBrokenReference[]> {
  try {
    const body = context.document.body;
    const fields = supportsFields() ? body.fields : null;
    fields?.load("items/code");
    const whole = typeof body.getRange === "function" ? body.getRange(Word.RangeLocation.whole) : null;
    const linkRanges = typeof whole?.getHyperlinkRanges === "function" ? whole.getHyperlinkRanges() : null;
    linkRanges?.load("items/text,items/hyperlink");
    await context.sync();

    const crossReferences = (fields?.items ?? []).filter((f) => {
      const code = f.code ?? "";
      return isClickableCrossReference(code) || (/^\s*HYPERLINK\b/i.test(code) && parseHyperlinkBookmark(code) !== null);
    });
    for (const f of crossReferences) f.result.load("text");
    if (crossReferences.length > 0) await context.sync();

    const links: Array<{ text: string; range: Word.Range }> = [];
    for (const f of crossReferences) {
      const text = (f.result.text ?? "").trim();
      if (BARE_SECTION_NUMBER.test(text)) links.push({ text, range: f.result });
    }
    for (const range of linkRanges?.items ?? []) {
      const text = (range.text ?? "").trim();
      if (BARE_SECTION_NUMBER.test(text) && (range.hyperlink ?? "").startsWith("#")) links.push({ text, range });
    }
    if (links.length === 0) return [];

    const leadsIn = await referenceWordLeadsInto(
      context,
      links.map((l) => l.range),
    );
    // AIVA-8925: a link the text before it already names is dropped below, so
    // it is not placed either — in a REF-heavy document ("Section " + a field
    // showing "4.02") that is most of them. A link's position never depends on
    // the other links passed with it.
    const kept = links.filter((_, i) => !leadsIn[i]);
    const placed = await occurrenceIndicesOf(context, kept);
    const seen = new Set<string>();
    const found: RawBrokenReference[] = [];
    kept.forEach((link, i) => {
      const occurrenceIndex = placed[i] ?? -1;
      // A field link is listed by both body.fields and getHyperlinkRanges.
      const key = `${occurrenceCountKey(link.text)}\u001f${occurrenceIndex}`;
      if (occurrenceIndex === -1 || seen.has(key)) return;
      seen.add(key);
      const at = paragraphOccurrence(texts, link.text, occurrenceIndex);
      if (!at) return;
      const style = styles[at.paragraphIndex] ?? "";
      if (headingLevelFrom(style, outlineLevels[at.paragraphIndex] ?? null) !== null || /^toc\b/i.test(style)) return;
      found.push({ matchText: link.text, targetLabel: link.text, occurrenceIndex, paragraphIndex: at.paragraphIndex });
    });
    return found;
  } catch (err) {
    console.warn("[docReview] topic_mismatch: could not read linked section numbers; judging text references only", err);
    return [];
  }
}

/** The longest reference sentence sent to the LLM — a runaway "sentence" (a
 *  clause-long paragraph with no full stop) is trimmed to a window around the
 *  reference instead. */
const REFERENCE_SENTENCE_MAX_LENGTH = 400;

/** A sentence end: ".", "!" or "?" followed by whitespace and then a capital
 *  or an opening quote/bracket. The dot in "4.02" or "Section 10.01(a)" is
 *  followed by a digit, so it never counts. */
const SENTENCE_END = /[.!?](?=\s+["“‘(]?[A-Z])/g;

/**
 * The sentence of `paragraph` that holds the reference starting at `offset`
 * (AIVA-8543). topic_mismatch used to send the WHOLE paragraph: the LLM then
 * judged a correct reference against whatever else the paragraph said — a
 * planted mismatch in the next sentence got this reference flagged for it —
 * and the finding card quoted the paragraph's opening words, often not the
 * sentence with the reference at all.
 *
 * Falls back to the whole paragraph when the offset is unknown.
 */
function referenceSentence(paragraph: string, offset: number | undefined, length: number): string {
  if (offset === undefined || offset < 0 || offset > paragraph.length) return paragraph;
  let start = 0;
  let end = paragraph.length;
  for (const match of paragraph.matchAll(SENTENCE_END)) {
    const cut = match.index + 1;
    if (cut <= offset) start = cut;
    else if (cut >= offset + length) {
      end = cut;
      break;
    }
  }
  if (end - start > REFERENCE_SENTENCE_MAX_LENGTH) {
    start = Math.max(start, offset - REFERENCE_SENTENCE_MAX_LENGTH / 2);
    end = Math.min(end, start + REFERENCE_SENTENCE_MAX_LENGTH);
  }
  return paragraph.slice(start, end).trim();
}

/**
 * Narrows the raw sweep down to what `word_docreview_judge` actually needs
 * to judge — mirrors the skip `evaluate_r05` (rules.py, now deleted) used to
 * do server-side before calling the LLM: a candidate whose target never
 * resolved to anything (broken_reference's concern, not topic_mismatch's) is dropped here instead,
 * so the backend's own evaluate_r05 is now an unconditional judge-every-
 * candidate loop.
 */
export function filterTopicMismatchJudgmentCandidates(sweep: TopicMismatchSweep): TopicMismatchSweep {
  return { candidates: sweep.candidates.filter((c) => c.targetHeading || c.targetFirstText) };
}

/** Flag a cross-reference whose topic the backend's LLM judgment decided
 * doesn't match its target section. Comment-only — same reasoning as every
 * other judgment-flavoured rule: there's no safe deterministic rewrite for
 * "this doesn't seem to be about the right thing." */
export async function fixTopicMismatch(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// exhibit_schedule_mismatch — the names and numbers of Exhibits and Schedules must match
// everywhere they are mentioned (deterministic when exactly one heading
// exists for a prefix; LLM judgment when more than one does — this sweep
// only gathers headings + mentions, never decides which case applies)
// ---------------------------------------------------------------------------

export interface RawExhibitLabel {
  matchText: string;
  /** Which occurrence of this exact matchText, in document order — counted
   *  over EVERY mention, headings included (even though a heading mention
   *  is never itself flagged), matching what a live body.search() at
   *  anchor time will find. Without this, the deterministic evaluate below
   *  always anchored to occurrence 0 for a mismatched label used more than
   *  once (e.g. several sentences all wrongly saying "Exhibit C"). Same
   *  reasoning as missing_hyperlink's/bracket_heading_mismatch's/topic_mismatch's/inconsistent_formatting's/defined_term_section_mismatch's/hyperlink_target_mismatch's/shortened_hyperlink_text's/alphabetical_order's/missing_defined_term's own
   *  occurrenceIndex. */
  occurrenceIndex: number;
  prefix: string;
  label: string;
  isHeading: boolean;
  /** AIVA-8366: whether this mention already carries a real Word hyperlink —
   *  missing_hyperlink/R02 deliberately excludes Exhibit/Schedule (see
   *  isExplicitPrefixCategory's own comment), so this is what
   *  evaluateExhibitScheduleMismatch now uses to flag an unlinked-but-
   *  correctly-labelled mention itself, the same way missing_hyperlink does
   *  for every other reference category. Always `false` on a heading
   *  mention — a heading is never flagged, so it's never computed for one. */
  hasHyperlink: boolean;
  /** Same as `RawMissingHyperlinkCandidate.looksLinked` (AIVA-8669). */
  looksLinked?: boolean;
  context: string;
  paragraphIndex: number;
  /** AIVA-8308: on a heading that is only its label ("EXHIBIT A"), the
   *  title on the paragraph below it ("STATEMENT OF WORK"). */
  subtitle?: string;
}

export interface ExhibitScheduleMismatchSweep {
  mentions: RawExhibitLabel[];
}

// The keyword itself is matched in three explicit case variants — Title
// Case, ALL CAPS ("EXHIBIT C", a common attachment-heading style), and
// lowercase — rather than a blanket `/i` flag. A shared `i` flag would also
// case-fold the LABEL class below, and `[A-Z0-9]+` under `/i` matches any
// run of letters/digits — turning "attached as an Exhibit to this
// Agreement" into a false match on label "to". Real Exhibit/Schedule
// labels are letters/digits only, never fully lowercase, so keeping that
// class genuinely uppercase-only (no `/i` on this pattern at all) closes
// the false-positive without losing the all-caps-heading case it was
// added for.
const EXHIBIT_SCHEDULE_PATTERN = /\b(Exhibit|EXHIBIT|exhibit|Schedule|SCHEDULE|schedule)\s+([A-Z0-9]+(?:\.\d+)?)\b/g;

/** "B", "AA", "A1", "3", "2A", "IV", optionally dotted ("1.2"). In ALL-CAPS
 *  text the pattern above also matches words — "FEE SCHEDULE AND RATE CARD"
 *  is not a citation of Schedule AND (AIVA-8308). */
const EXHIBIT_LABEL_SHAPE = /^(?:\d+[A-Z]?|([A-Z])\1*\d*|[IVXLC]+)(?:\.\d+)?$/;

/** Longest paragraph read as a heading's title when it sits on its own line —
 *  the same cap as a bracketed title (`bracketTitleAt`). */
const MAX_SEPARATE_TITLE_LENGTH = 80;

/** The title on the line under a label-only heading: `ARTICLE 14` /
 *  `LIMITATION OF LIABILITY`. The next non-empty paragraph, unless it is long
 *  body text, a TOC entry or itself a label. One reader for every rule that
 *  needs it (exhibit_schedule_mismatch, bracket_heading_mismatch — AIVA-8310). */
function titleBelowLabelHeading(
  paragraphTexts: readonly string[],
  headingIndex: number,
  insideToc: (paragraphIndex: number) => boolean,
): string | undefined {
  let index = headingIndex + 1;
  while (index < paragraphTexts.length && paragraphTexts[index]!.trim() === "") index += 1;
  const text = trimTitleSeparators(paragraphTexts[index] ?? "");
  if (!text || text.length > MAX_SEPARATE_TITLE_LENGTH || insideToc(index)) return undefined;
  const lead = new RegExp(EXHIBIT_SCHEDULE_PATTERN.source).exec(text);
  if (lead?.index === 0 && EXHIBIT_LABEL_SHAPE.test(lead[2]!)) return undefined;
  if (isLabelOnlyHeading(text)) return undefined;
  return text;
}

/** "EXHIBIT A" alone on its heading line: the title is the next paragraph
 *  ("STATEMENT OF WORK"). `undefined` when the heading has its own. */
function labelOnlyHeadingSubtitle(
  heading: RawExhibitLabel,
  paragraphTexts: readonly string[],
  insideToc: (paragraphIndex: number) => boolean,
): string | undefined {
  if (trimTitleSeparators(heading.context.trim().slice(heading.matchText.length)) !== "") return undefined;
  return titleBelowLabelHeading(paragraphTexts, heading.paragraphIndex, insideToc);
}

/**
 * Raw sweep only. A heading-styled paragraph starting with "Exhibit X"/
 * "Schedule Y" is the attachment's own real label (`isHeading: true`);
 * every other occurrence is an ordinary body mention referring to one.
 * evaluate_r07/evaluate_r07_ambiguous (backend) decide whether each mention
 * matches — this never judges, just reports what it found.
 */
export function findExhibitScheduleMismatch(): Promise<ExhibitScheduleMismatchSweep> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    // AIVA-8925: one targeted load (see findTopicMismatchData).
    paragraphs.load("items/text,items/style,items/outlineLevel");
    // A Table of Contents field's own rendered entries mirror every
    // heading's text as their own separate paragraphs — but a TOC entry is
    // styled "TOC 1"/"TOC 2", never "Heading N", so isHeadingParagraph below
    // never catches it. Loaded in the same round trip as the paragraph list
    // itself, same reasoning as findBrokenReference's own TOC guard.
    const fields = supportsFields() ? context.document.body.fields : null;
    fields?.load("items/code");
    await context.sync();

    // Same TOC-detection approach as findBrokenReference's own guard and
    // findShortenedHyperlinkText's own guard (AIVA-8199, extended to
    // collectShortenedHyperlinkRanges' element-form path this same session):
    // a paragraph is "inside a TOC" when it compares
    // Inside/InsideStart/InsideEnd/Equal against any TOC field's own
    // rendered range. Without this, an Exhibit/Schedule heading that's also
    // listed in a Table of Contents has its text mirrored as an ordinary,
    // non-heading-styled TOC paragraph — which this sweep would otherwise
    // read as a real body mention, and (via the AIVA-8366 hyperlink check
    // below) flag as "no hyperlink" whenever the TOC itself isn't
    // hyperlinked. This function was the one place in the file that added a
    // new hyperlink-reading path this session without this guard.
    const insideToc = await tocParagraphPredicate(context, paragraphs, fields);

    const mentions: RawExhibitLabel[] = [];
    const paragraphTexts = paragraphs.items.map((p) => p.text ?? "");
    const counter = documentOccurrenceCounter(paragraphTexts);
    paragraphs.items.forEach((p, paragraphIndex) => {
      const text = p.text ?? "";
      const style = p.style ?? "";
      // The shared predicate, not a local regex: an HTML-pasted document reports
      // every paragraph as "Normal", so a style-only test finds no headings and
      // every Exhibit label reads as a body mention.
      const isHeadingParagraph = headingLevelFrom(style, p.outlineLevel) !== null;
      const isTocParagraph = insideToc(paragraphIndex);
      for (const match of text.matchAll(EXHIBIT_SCHEDULE_PATTERN)) {
        if (!EXHIBIT_LABEL_SHAPE.test(match[2]!)) continue;
        const matchText = match[0]!;
        const isHeading = isHeadingParagraph && text.trim().toLowerCase().startsWith(matchText.toLowerCase());
        const occurrenceIndex = counter.at(paragraphIndex, match.index, matchText);
        if (occurrenceIndex === null) continue;
        // The TOC mirror still counts toward the index above (Word's search finds
        // it too); only the push is skipped — AIVA-8366.
        if (isTocParagraph) continue;
        mentions.push({
          matchText,
          occurrenceIndex,
          prefix: match[1]!,
          label: match[2]!,
          isHeading,
          hasHyperlink: false,
          context: text,
          paragraphIndex,
        });
      }
    });
    for (const mention of mentions) {
      const subtitle = mention.isHeading ? labelOnlyHeadingSubtitle(mention, paragraphTexts, insideToc) : undefined;
      if (subtitle) mention.subtitle = subtitle;
    }

    // AIVA-8366: live-search each distinct mention text once, same
    // matchWholeWord/hyperlink-load pattern findMissingHyperlink uses, so a
    // body mention's own hasHyperlink is known here too. occurrenceIndex
    // already counts across EVERY mention in the sweep, headings included
    // (see its own docstring), matching what this unfiltered live search
    // finds — same alignment findMissingHyperlink relies on.
    // Word-bounded, like every other occurrence-indexed search here — see
    // `queueWordBoundedSearch` (AIVA-8547): "Exhibit A" must not hit inside
    // "Exhibit AB".
    const searchesByText = new Map<string, WordBoundedSearch>();
    for (const mention of mentions) {
      if (searchesByText.has(mention.matchText)) continue;
      searchesByText.set(mention.matchText, queueWordBoundedSearch(context.document.body, mention.matchText));
    }
    const mentionParagraphs = loadOptionalParagraphs(context);
    await context.sync();

    const rangesByText = wordBoundedHitLists(searchesByText, paragraphTextsOf(mentionParagraphs));

    const bodyMentions = mentions.filter((m) => !m.isHeading);
    const rangeOf = bodyMentions.map((mention) => {
      const ranges = rangesByText.get(mention.matchText) ?? [];
      return ranges[mention.occurrenceIndex] ?? ranges[ranges.length - 1];
    });
    // AIVA-8925: only the hits a body mention resolves to are read.
    loadOnce(rangeOf, "hyperlink,style");
    await context.sync();
    // AIVA-8536: same blind spot as findMissingHyperlink — an "Exhibit B"
    // inserted via Insert > Cross-reference is clickable but carries no
    // hyperlink object. See `onClickableCrossReference`.
    // A mention that already has a hyperlink needs no field check (AIVA-8925).
    const onCrossReference = await onClickableCrossReference(
      context,
      rangeOf.map((range) => (range?.hyperlink ? undefined : range)),
      // A mention's `context` is its paragraph's own text.
      // ...unless its hit fell back to the last one, which may sit elsewhere.
      bodyMentions.map((mention) =>
        mention.occurrenceIndex < (rangesByText.get(mention.matchText)?.length ?? 0) ? mention.context : undefined,
      ),
    );
    bodyMentions.forEach((mention, i) => {
      mention.hasHyperlink = Boolean(rangeOf[i]?.hyperlink) || onCrossReference[i]!;
      if (!mention.hasHyperlink && looksLikeLink(rangeOf[i]?.style)) mention.looksLinked = true;
    });

    return { mentions };
  });
}

/** {"exhibit": {"B", "C"}, "schedule": {"1", "2"}} — every attachment's own
 * labelled heading, grouped by prefix (case-insensitive). Mirrors rules.py's
 * `group_exhibit_headings`, shared by `evaluateExhibitScheduleMismatch`
 * (the single-heading, deterministic case) and `findExhibitScheduleAmbiguousMentions`
 * (the zero/multi-heading case, which needs the LLM), so both see the same
 * set of "real" labels. */
function groupExhibitHeadings(sweep: ExhibitScheduleMismatchSweep): Map<string, Set<string>> {
  const headings = new Map<string, Set<string>>();
  for (const mention of sweep.mentions) {
    if (!mention.isHeading) continue;
    const key = mention.prefix.toLowerCase();
    if (!headings.has(key)) headings.set(key, new Set());
    headings.get(key)!.add(mention.label);
  }
  return headings;
}

/** The real attachment heading's own paragraph text for `prefix`/`label`
 * (e.g. the "EXHIBIT B — DATA PROCESSING OVERVIEW" paragraph itself) — the
 * exhibit_schedule_mismatch equivalent of `findMissingHyperlink`'s own
 * `sweep.headings.find(...).text` lookup, which this sweep has no
 * `RawHeading[]` list to reuse (see `RawExhibitLabel.context`'s own
 * docstring: a heading mention's `context` IS its full paragraph text). */
function headingContextFor(sweep: ExhibitScheduleMismatchSweep, prefix: string, label: string): string {
  const key = prefix.toLowerCase();
  return sweep.mentions.find((m) => m.isHeading && m.prefix.toLowerCase() === key && m.label === label)?.context ?? "";
}

/** Deterministic evaluation, ported from rules.py's `evaluate_r07`. With
 * exactly one heading for a prefix, the "correct" label is known with
 * certainty, so a mismatched label is rewritten to it. With 2+ headings
 * (AIVA-8560), a label that is plainly not attached — a different numbering
 * style, or a letter/number no attachment has — is flagged as a comment
 * (`unattachedLabelFinding`); only a dotted sub-label whose base is attached
 * is left to `findExhibitScheduleAmbiguousMentions`. Zero headings: nothing
 * is known about the attachments, so nothing is flagged. Every attachment the
 * body never refers to is also flagged (`unreferencedAttachmentFindings`).
 *
 * AIVA-8366: also flags a correctly-labelled mention that carries no real
 * hyperlink. missing_hyperlink/R02 deliberately excludes Exhibit/Schedule
 * (see isExplicitPrefixCategory's own comment on why the two rules would
 * otherwise collide resolving the same bare-digit target), so this rule is
 * now the only one that catches an unlinked exhibit/schedule reference —
 * kept inside the same "exactly one real heading" boundary this rule
 * already only operates within, so a reference whose label can't be
 * resolved with certainty (zero or 2+ headings of that prefix) is left
 * alone rather than guessing which attachment it should link to. */
export function evaluateExhibitScheduleMismatch(sweep: ExhibitScheduleMismatchSweep): FindingCreate[] {
  const headings = groupExhibitHeadings(sweep);
  const attachments = attachmentsOf(sweep);
  const bracketTitleOf = bracketTitles(sweep);
  const findings: FindingCreate[] = [];
  for (const mention of sweep.mentions) {
    if (mention.isHeading) continue;
    const labels = headings.get(mention.prefix.toLowerCase());
    if (!labels) continue;
    // AIVA-8560: which attachment the reference's own "(Fee Schedule)" names,
    // when that is a DIFFERENT attachment than the label points at.
    const named = attachmentNamedBy(bracketTitleOf.get(mention), mention, attachments);
    if (labels.has(mention.label) && named) {
      findings.push(wrongAttachmentFinding(mention, named, attachments));
      continue;
    }
    if (labels.size > 1) {
      const unattached = unattachedLabelFinding(mention, labels, named);
      if (unattached) findings.push(unattached);
      continue;
    }
    const single = singleAttachmentFinding(sweep, mention, [...labels][0]!);
    if (single) findings.push(single);
  }
  findings.push(...unreferencedAttachmentFindings(sweep, headings));
  return findings;
}

/** The exactly-one-attachment case of `evaluateExhibitScheduleMismatch`: the
 *  correct label is known, so a mismatched one is rewritten to it, and a
 *  correct one with no hyperlink is flagged (AIVA-8366). */
function singleAttachmentFinding(
  sweep: ExhibitScheduleMismatchSweep,
  mention: RawExhibitLabel,
  correctLabel: string,
): FindingCreate | null {
  if (mention.label !== correctLabel) {
    return toFindingCreate({
      ruleId: "exhibit_schedule_mismatch",
      matchText: mention.matchText,
      occurrenceIndex: mention.occurrenceIndex,
      problem: `"${mention.matchText}" does not match the document's own ${mention.prefix} ${correctLabel}.`,
      comment: `Corrected the label to match the document's ${mention.prefix} ${correctLabel}.`,
      fixHandler: "word.docreview.fix_exhibit_schedule_mismatch",
      fixParams: { correctFullText: `${mention.prefix} ${correctLabel}` },
    });
  }
  if (mention.hasHyperlink) return null;
  const { problem, commentText } = unlinkedReferenceText(mention.matchText, "attachment", mention.looksLinked);
  return toFindingCreate({
    ruleId: "exhibit_schedule_mismatch",
    matchText: mention.matchText,
    occurrenceIndex: mention.occurrenceIndex,
    problem,
    comment: commentText,
    fixHandler: "word.docreview.fix_missing_hyperlink",
    fixParams: { targetHeadingText: headingContextFor(sweep, mention.prefix, correctLabel), commentText },
  });
}

/** "Schedule 1.2" → "1" — the attachment a dotted sub-label points into. */
function baseExhibitLabel(label: string): string {
  return label.split(".")[0]!;
}

/** Whether a label is a letter ("B") or a number ("1", "1.2") — the two
 *  numbering styles an attachment set can use. */
function exhibitLabelKind(label: string): "letter" | "number" {
  return /^\d/.test(label) ? "number" : "letter";
}

/** "exhibit" → "Exhibit": a readable prefix for the finding text, whatever
 *  case the document itself used ("EXHIBIT", "exhibit"). */
function displayPrefix(prefix: string): string {
  return prefix.charAt(0).toUpperCase() + prefix.slice(1).toLowerCase();
}

/** "Exhibit A, Exhibit B" — the attachments that DO exist, in natural order. */
function listAttachments(prefix: string, labels: Set<string>): string {
  return [...labels]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .map((label) => `${displayPrefix(prefix)} ${label}`)
    .join(", ");
}

/** A comment-only exhibit_schedule_mismatch finding — the same flag handler
 *  the LLM-judged path uses, so resolved-comment suppression and AIVA-8561's
 *  "comment already in the document" check both apply to it. */
function exhibitFlagFinding(matchText: string, occurrenceIndex: number, problem: string, commentText: string): FindingCreate {
  return toFindingCreate({
    ruleId: "exhibit_schedule_mismatch",
    matchText,
    occurrenceIndex,
    problem,
    comment: commentText,
    fixHandler: "word.docreview.fix_exhibit_schedule_mismatch_flag",
    fixParams: { commentText },
  });
}

/**
 * AIVA-8560: a mention whose label is not attached, where the document has
 * 2+ attachments of that prefix. Deterministic — the set of attachments is
 * known, so "there is no Exhibit E" needs no judgment, and this used to go to
 * the LLM, which often let it through.
 *
 * Two flavours, most specific first:
 *  - numbering style: "Exhibit 1" when every exhibit is lettered (or the
 *    reverse) — the reference uses a scheme the document never uses at all;
 *  - not attached: "Exhibit E" when only Exhibit A–D exist.
 *
 * A dotted sub-label whose base IS attached ("Schedule 1.2" with Schedule 1
 * present) is left alone here — it most likely points INTO that attachment,
 * which is a judgment call `findExhibitScheduleAmbiguousMentions` still sends
 * to the LLM.
 */
function unattachedLabelFinding(
  mention: RawExhibitLabel,
  labels: Set<string>,
  named?: Attachment,
): FindingCreate | null {
  if (labels.has(mention.label) || labels.has(baseExhibitLabel(mention.label))) return null;
  const prefix = displayPrefix(mention.prefix);
  const attached = listAttachments(mention.prefix, labels);
  // "Schedule 3 (Approved Subcontractors)": the bracket says which attachment
  // was meant, so the comment can name the right label outright.
  const pointer = named ? ` ${named.title} is ${named.name}.` : "";
  const headingKinds = new Set([...labels].map(exhibitLabelKind));
  if (!headingKinds.has(exhibitLabelKind(mention.label))) {
    const style = headingKinds.has("letter") ? "lettered" : "numbered";
    return exhibitFlagFinding(
      mention.matchText,
      mention.occurrenceIndex,
      `"${mention.matchText}" doesn't match how this document labels its ${prefix.toLowerCase()}s (${attached}).${pointer}`,
      `This document's ${prefix.toLowerCase()}s are ${style} (${attached}) — there is no ${prefix} ${mention.label}.${pointer}`,
    );
  }
  return exhibitFlagFinding(
    mention.matchText,
    mention.occurrenceIndex,
    `"${mention.matchText}" is referred to, but the document has no ${prefix} ${mention.label} attached.${pointer}`,
    `No ${prefix} ${mention.label} is attached — this document's ${prefix.toLowerCase()}s are ${attached}.${pointer}`,
  );
}

/** An attached Exhibit/Schedule, as its own heading describes it. */
interface Attachment {
  /** "Schedule 1" — display-cased, whatever case the heading used. */
  name: string;
  prefixKey: string;
  label: string;
  /** "Fee Schedule" — the heading text after its label, else the paragraph
   *  below a label-only heading (`subtitle`); "" when neither has one. */
  title: string;
  /** `normaliseTitle(title)` — what a bracketed title is compared against. */
  titleKey: string;
}

/** "fee schedules" / "The “Fee Schedule”" → "fee schedule": case, quotes, a
 *  leading "the", punctuation and a plural "s" never make two titles differ. */
function normaliseTitle(title: string): string {
  return straightenQuotes(title)
    .toLowerCase()
    .replace(/^\s*the\s+/, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w.length > 3 ? w.replace(/s$/, "") : w))
    .join(" ");
}

const TITLE_SMALL_WORDS = new Set([
  "a", "an", "and", "as", "at", "but", "by", "for", "from", "in", "into", "nor", "of", "on", "or", "the", "to", "upon", "with",
]);

/** "STATEMENT OF WORK" → "Statement of Work", so an all-caps heading reads
 *  normally inside a sentence; any other casing is kept as the drafter wrote it. */
function readableTitle(title: string): string {
  if (title !== title.toUpperCase()) return title;
  return title
    .toLowerCase()
    .replace(/[a-z0-9']+/g, (w, offset: number) =>
      offset > 0 && TITLE_SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1),
    );
}

/** Characters between an attachment's label and its title ("SCHEDULE 1 — FEE
 *  SCHEDULE"), besides whitespace. */
const TITLE_LEADING_SEPARATORS = new Set([".", ":", "-", "–", "—"]);

/** "  — Fee Schedule." → "Fee Schedule". A character loop rather than a
 *  trailing-anchored regex: `/[\s.]+$/` backtracks over every run of spaces
 *  and dots it cannot reach the end from (Sonar typescript:S5852). */
function trimTitleSeparators(text: string): string {
  const isSpace = (c: string) => c.trim() === "";
  let start = 0;
  while (start < text.length && (isSpace(text[start]!) || TITLE_LEADING_SEPARATORS.has(text[start]!))) start += 1;
  let end = text.length;
  while (end > start && (isSpace(text[end - 1]!) || text[end - 1] === ".")) end -= 1;
  return text.slice(start, end);
}

/** Every attachment heading, first one per prefix+label. */
function attachmentsOf(sweep: ExhibitScheduleMismatchSweep): Attachment[] {
  const out: Attachment[] = [];
  const seen = new Set<string>();
  for (const heading of sweep.mentions) {
    if (!heading.isHeading) continue;
    const prefixKey = heading.prefix.toLowerCase();
    if (seen.has(`${prefixKey}\u0000${heading.label}`)) continue;
    seen.add(`${prefixKey}\u0000${heading.label}`);
    const text = heading.context.trim();
    const startsWithLabel = text.toLowerCase().startsWith(heading.matchText.toLowerCase());
    const own = startsWithLabel ? trimTitleSeparators(text.slice(heading.matchText.length)) : "";
    const title = readableTitle(own || heading.subtitle || "");
    out.push({
      name: `${displayPrefix(heading.prefix)} ${heading.label}`,
      prefixKey,
      label: heading.label,
      title,
      titleKey: normaliseTitle(title),
    });
  }
  return out;
}

const BRACKET_TITLE_QUOTES = new Set(['"', "'", "“", "”", "‘", "’"]);

/**
 * `(Fee Schedule)` / `(the “Statement of Work”)` at the start of `text` →
 * "Fee Schedule" / "Statement of Work". `undefined` when `text` does not open
 * with a bracket, the bracket nests or never closes, or the title is under 2
 * or over 80 characters. Parsed by hand rather than with one regex: a lazy
 * title between optional quotes and optional whitespace backtracks
 * super-linearly (Sonar typescript:S5852).
 */
function bracketTitleAt(text: string): string | undefined {
  const opened = text.trimStart();
  if (!opened.startsWith("(")) return undefined;
  const close = opened.indexOf(")");
  if (close === -1) return undefined;
  let title = opened.slice(1, close);
  if (title.includes("(")) return undefined;
  title = title.trim();
  if (title.toLowerCase().startsWith("the ")) title = title.slice(4).trimStart();
  if (BRACKET_TITLE_QUOTES.has(title.charAt(0))) title = title.slice(1);
  if (BRACKET_TITLE_QUOTES.has(title.charAt(title.length - 1))) title = title.slice(0, -1);
  title = title.trim();
  if (title.length < 2 || title.length > 80 || /["“”]/.test(title)) return undefined;
  return title;
}

/**
 * The bracketed title straight after each body mention, if it has one. A
 * mention carries no offset, so its place in `context` is found by counting:
 * the Nth "Exhibit B" swept from a paragraph is the Nth match in its text.
 */
function bracketTitles(sweep: ExhibitScheduleMismatchSweep): Map<RawExhibitLabel, string> {
  const out = new Map<RawExhibitLabel, string>();
  const seen = new Map<string, number>();
  for (const mention of sweep.mentions) {
    const key = `${mention.paragraphIndex}\u0000${mention.matchText}`;
    const nth = seen.get(key) ?? 0;
    seen.set(key, nth + 1);
    if (mention.isHeading) continue;
    const hit = [...mention.context.matchAll(EXHIBIT_SCHEDULE_PATTERN)].filter((m) => m[0] === mention.matchText)[nth];
    if (hit?.index === undefined) continue;
    const bracket = bracketTitleAt(mention.context.slice(hit.index + hit[0].length));
    if (bracket) out.set(mention, bracket);
  }
  return out;
}

/**
 * The attachment a reference's bracketed title names, when that is NOT the
 * one its label points at: `Exhibit B (Fee Schedule)` where the Fee Schedule
 * is Schedule 1. `undefined` when there is no bracket, when it matches the
 * label's own attachment, or when it names no attachment at all — a bracket
 * that is merely descriptive ("Exhibit B (as amended)") is not evidence of
 * anything.
 */
function attachmentNamedBy(
  bracket: string | undefined,
  mention: RawExhibitLabel,
  attachments: Attachment[],
): Attachment | undefined {
  if (!bracket) return undefined;
  const key = normaliseTitle(bracket);
  if (!key) return undefined;
  const own = attachments.find((a) => a.prefixKey === mention.prefix.toLowerCase() && a.label === mention.label);
  if (own?.titleKey === key) return undefined;
  return attachments.find((a) => a !== own && a.titleKey === key);
}

/**
 * AIVA-8560: a reference whose label IS attached but whose bracketed title
 * names a different attachment — `Exhibit B (Fee Schedule)` when Exhibit B is
 * the Service Level Agreement and the Fee Schedule is Schedule 1. Comment-only:
 * either the label or the title is wrong, and only the drafter knows which.
 */
function wrongAttachmentFinding(mention: RawExhibitLabel, named: Attachment, attachments: Attachment[]): FindingCreate {
  const cited = `${displayPrefix(mention.prefix)} ${mention.label}`;
  const own = attachments.find((a) => a.prefixKey === mention.prefix.toLowerCase() && a.label === mention.label);
  const ownPart = own?.title ? `${cited} is the ${own.title}; ` : "";
  const detail = `${ownPart}the ${named.title} is ${named.name}.`;
  return exhibitFlagFinding(
    mention.matchText,
    mention.occurrenceIndex,
    `"${mention.matchText}" cites the wrong attachment — ${detail}`,
    `Wrong attachment cited — ${detail}`,
  );
}

/**
 * AIVA-8560: an attached Exhibit/Schedule that the body never refers to —
 * flagged on the attachment's own heading.
 *
 * A body mention counts as referring to an attachment when its label, or the
 * base of a dotted sub-label ("Schedule 1.2"), matches. With a SINGLE heading
 * for the prefix, any body mention of that prefix counts: a mislabelled one is
 * corrected to that heading by the single-heading path above, so flagging the
 * heading as unreferenced as well would only be noise. Table of Contents
 * entries never count — the sweep already drops them.
 */
function unreferencedAttachmentFindings(
  sweep: ExhibitScheduleMismatchSweep,
  headings: Map<string, Set<string>>,
): FindingCreate[] {
  const findings: FindingCreate[] = [];
  const flagged = new Set<string>();
  for (const heading of sweep.mentions) {
    if (!heading.isHeading) continue;
    const key = heading.prefix.toLowerCase();
    if (flagged.has(`${key}\u0000${heading.label}`)) continue;
    const single = headings.get(key)?.size === 1;
    const referenced = sweep.mentions.some(
      (m) =>
        !m.isHeading &&
        m.prefix.toLowerCase() === key &&
        (single || m.label === heading.label || baseExhibitLabel(m.label) === heading.label),
    );
    if (referenced) continue;
    flagged.add(`${key}\u0000${heading.label}`);
    const name = `${displayPrefix(heading.prefix)} ${heading.label}`;
    findings.push(
      exhibitFlagFinding(
        heading.matchText,
        heading.occurrenceIndex,
        `${name} is attached but never referred to in the body of the document.`,
        `${name} is attached but never referred to in the body.`,
      ),
    );
  }
  return findings;
}

/** One Exhibit/Schedule mention that needs an LLM judgment call — its label
 * matches none of the document's own attachments of that prefix, and more
 * than one such attachment exists (so there's no single certain
 * correction). `candidateLabels` is precomputed and sorted here, mirroring
 * what the backend's own `group_exhibit_headings` used to resolve
 * server-side — Python no longer computes this, only judges with it. Shaped
 * for `word_docreview_judge`'s `r07_ambiguous` argument. */
export interface ExhibitScheduleAmbiguousMention {
  matchText: string;
  /** Which occurrence of this exact matchText, in document order — without
   *  this, a repeated ambiguous mention (or the same text used elsewhere)
   *  always anchors to occurrence 0. Same reasoning as topic_mismatch's own
   *  occurrenceIndex. Counted over EVERY mention in the sweep, headings
   *  included — a heading occurrence still counts as a real hit a live
   *  body.search() would find, even though headings themselves are never
   *  flagged. */
  occurrenceIndex: number;
  prefix: string;
  context: string;
  candidateLabels: string[];
}

/** Ported 1:1 from rules.py's (now-deleted) `evaluate_r07_ambiguous` bucket
 * split — the LLM-judgment call itself now happens in
 * `word_docreview_judge`, this only decides WHICH mentions qualify. */
export function findExhibitScheduleAmbiguousMentions(sweep: ExhibitScheduleMismatchSweep): ExhibitScheduleAmbiguousMention[] {
  const headings = groupExhibitHeadings(sweep);
  // mention.occurrenceIndex already counts across EVERY mention in the
  // sweep, headings included — computed once in findExhibitScheduleMismatch
  // itself, matching what a live body.search() at anchor time will find.
  const mentions: ExhibitScheduleAmbiguousMention[] = [];
  for (const mention of sweep.mentions) {
    if (mention.isHeading) continue;
    const labels = headings.get(mention.prefix.toLowerCase());
    if (!labels || labels.size <= 1 || labels.has(mention.label)) continue;
    // AIVA-8560: a label that is plainly not attached is now flagged
    // deterministically (`unattachedLabelFinding`). Only a dotted sub-label
    // whose base IS attached ("Schedule 1.2" with Schedule 1 present) still
    // needs judgment — sending the rest too would flag them twice.
    if (!labels.has(baseExhibitLabel(mention.label))) continue;
    mentions.push({
      matchText: mention.matchText,
      occurrenceIndex: mention.occurrenceIndex,
      prefix: mention.prefix,
      context: mention.context,
      candidateLabels: [...labels].sort((a, b) => a.localeCompare(b)),
    });
  }
  return mentions;
}

/** One attachment as the meaning judge sees it: "Schedule 1", "Fee Schedule". */
export interface AttachmentSummary {
  name: string;
  title: string;
}

/**
 * AIVA-8543: an Exhibit/Schedule mention whose label IS attached, for the
 * judge to check that its sentence is about what that attachment actually is —
 * "the key personnel listed in Schedule 1" when Schedule 1 is the Fee
 * Schedule. Exhibit/Schedule references used to reach topic_mismatch for this,
 * which compared them with numbered SECTIONS; this rule knows the attachments.
 */
export interface ExhibitScheduleMeaningCandidate {
  matchText: string;
  occurrenceIndex: number;
  /** The mention's own sentence, never the whole paragraph (see referenceSentence). */
  sentence: string;
  /** "Schedule 1". */
  attachmentName: string;
  /** "Fee Schedule". */
  attachmentTitle: string;
  /** Every attachment with a title, so the judge can name the one meant. */
  attachments: AttachmentSummary[];
}

/** Where each body mention sits in its own paragraph — the Nth "Schedule 1"
 *  swept from a paragraph is the Nth match in its text (same counting as
 *  `bracketTitles`). */
function mentionOffsets(sweep: ExhibitScheduleMismatchSweep): Map<RawExhibitLabel, number> {
  const out = new Map<RawExhibitLabel, number>();
  const seen = new Map<string, number>();
  for (const mention of sweep.mentions) {
    const key = `${mention.paragraphIndex}\u0000${mention.matchText}`;
    const nth = seen.get(key) ?? 0;
    seen.set(key, nth + 1);
    if (mention.isHeading) continue;
    const hit = [...mention.context.matchAll(EXHIBIT_SCHEDULE_PATTERN)].filter((m) => m[0] === mention.matchText)[nth];
    if (hit?.index !== undefined) out.set(mention, hit.index);
  }
  return out;
}

/**
 * Pure: which mentions the meaning judge should see (AIVA-8543). Only one whose
 * label is attached and that code has not already flagged: a label that is not
 * attached ("Exhibit E") and a bracketed title naming a different attachment
 * ("Exhibit B (Fee Schedule)") are decided deterministically, so sending them
 * too would give one problem two cards. A bracket that agrees with the
 * attachment's own title ("Schedule 2 (Approved Subcontractors)") already says
 * what the sentence means, so it is not sent either. Nor is a mention of an
 * attachment with no title — there would be nothing to compare against.
 */
export function findExhibitScheduleMeaningCandidates(sweep: ExhibitScheduleMismatchSweep): ExhibitScheduleMeaningCandidate[] {
  const headings = groupExhibitHeadings(sweep);
  const attachments = attachmentsOf(sweep);
  const bracketTitleOf = bracketTitles(sweep);
  const offsetOf = mentionOffsets(sweep);
  const summaries = attachments.filter((a) => a.title).map((a) => ({ name: a.name, title: a.title }));
  const candidates: ExhibitScheduleMeaningCandidate[] = [];
  for (const mention of sweep.mentions) {
    if (mention.isHeading || !headings.get(mention.prefix.toLowerCase())?.has(mention.label)) continue;
    const own = attachments.find((a) => a.prefixKey === mention.prefix.toLowerCase() && a.label === mention.label);
    if (!own?.title) continue;
    const bracket = bracketTitleOf.get(mention);
    if (bracket !== undefined && (normaliseTitle(bracket) === own.titleKey || attachmentNamedBy(bracket, mention, attachments))) {
      continue;
    }
    candidates.push({
      matchText: mention.matchText,
      occurrenceIndex: mention.occurrenceIndex,
      sentence: referenceSentence(mention.context, offsetOf.get(mention), mention.matchText.length),
      attachmentName: own.name,
      attachmentTitle: own.title,
      attachments: summaries,
    });
  }
  return candidates;
}

/** Correct an Exhibit/Schedule label to match the document's own single
 * real attachment of that prefix — a genuine tracked-change rewrite, since
 * (unlike the ambiguous multi-attachment case) the correct value is known
 * with certainty. */
export async function fixExhibitScheduleMismatch(
  anchorTag: string,
  expected: string,
  correctFullText: string,
): Promise<FixResult> {
  return replaceAnchorText(anchorTag, expected, correctFullText);
}

/** Flag an Exhibit/Schedule mention the LLM judged doesn't match any of the
 * document's own attachments of that prefix, where more than one such
 * attachment exists. Comment-only — with more than one candidate, there's
 * no single safe label to substitute. */
export async function fixExhibitScheduleMismatchFlag(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// defined_term_section_mismatch — a sentence that says a term is defined in a
// section, when the document defines it somewhere else (or nowhere)
// ---------------------------------------------------------------------------

/**
 * One sentence the LLM is asked to read (AIVA-8297 follow-up): does it claim
 * that `term` is defined in `matchText`'s section?
 *
 * This used to be a regex for one exact wording — `"X" (as defined in
 * Section N)` — so every other way a contract says it ("has the meaning given
 * in Clause 4", "as set out in Section 4", "see Section 4") went unchecked, and
 * a clean result meant nothing. Now the code gathers the FACTS (where each term
 * is really defined, and what the referenced section contains) and only the
 * question of what the sentence MEANS goes to the model, the same split every
 * other judged rule uses.
 */
export interface RawTermSectionCandidate {
  /** The reference itself ("Section 4") — what the finding anchors to. */
  matchText: string;
  occurrenceIndex: number;
  term: string;
  /** The referenced section number, sub-clause letter dropped ("4.02"). */
  referenceLabel: string;
  /** The sentence holding both the term and the reference — all the LLM sees. */
  sentence: string;
  /** The referenced section's own heading, for the comment and the prompt. */
  referencedSectionHeading: string;
  /** Section numbers whose text actually DEFINES the term (`"X" means…`,
   *  `(the "X")`). Empty when no definition was found anywhere. */
  definedInSections: string[];
  paragraphIndex: number;
}

export interface DefinedTermSectionMismatchSweep {
  candidates: RawTermSectionCandidate[];
  /** Pointer entries (`"X" has the meaning set forth in Section N`) whose
   *  section could not be found, so they were never checked (AIVA-9191),
   *  each named "X (Section N)". */
  unresolvedPointers?: string[];
  /** Candidates past MAX_TERM_SECTION_CANDIDATES, never sent to the judge. */
  overLimit?: number;
}

/** A bound on judgment calls per scan — each candidate is one LLM call. In
 *  practice the code-side filters below leave far fewer. */
const MAX_TERM_SECTION_CANDIDATES = 60;

/** A quoted Title-Case phrase inside a sentence — a term being named. Linear:
 *  one bounded run of non-quote characters. */
const QUOTED_TERM_IN_SENTENCE = /"([A-Z][^"]{0,60})"/g;

/** Any quoted span, curly or straight — blanked before bare terms are matched. */
const QUOTED_SPAN = /["“][^"“”]{0,200}["”]/g;

/** How this rule keys a term: lower-case with straight quotes, so the inventory's
 *  `Seller’s Software` and a sentence's straightened `Seller's Software` meet. */
function termKey(term: string): string {
  return straightenQuotes(term).toLowerCase();
}

/** Whether definition section `defined` is, or sits inside, section `claimed`
 *  — "4.02" and "4" both satisfy a claim of "4". */
function sectionWithin(defined: string, claimed: string): boolean {
  return defined === claimed || defined.startsWith(`${claimed}.`);
}

/** The number of the deepest numbered section each paragraph belongs to, from
 *  the heading outline, typed or auto-numbered (see `headingSectionNumber`). */
function sectionNumberByParagraph(
  toc: readonly TocEntry[],
  listNumbers: ReadonlyMap<number, string>,
  paragraphCount: number,
): Array<string | null> {
  const numbered = toc
    .map((entry) => ({ entry, number: headingSectionNumber(entry, listNumbers) }))
    .filter((e): e is { entry: TocEntry; number: string } => e.number !== null)
    .sort((a, b) => a.entry.startParagraph - b.entry.startParagraph);
  const out: Array<string | null> = Array.from({ length: paragraphCount }, () => null);
  // Later-starting sections are deeper (a sub-heading starts after its
  // parent), so assigning in start order leaves each paragraph with its
  // deepest enclosing section.
  for (const { entry, number } of numbered) {
    for (let i = entry.startParagraph; i <= entry.endParagraph && i < paragraphCount; i++) out[i] = number;
  }
  return out;
}

/** Where each term is actually DEFINED, keyed lower-case, plus every
 *  definition's span so a term's own definition sentence is not mistaken for
 *  a claim about somewhere else. A by-reference entry ("has the meaning given
 *  in Section 2.05") is a claim, not a definition, so it is left out. */
function collectTermDefinitions(
  definitions: readonly DeclaredTerm[],
  sectionOf: ReadonlyArray<string | null>,
): { definedIn: Map<string, Set<string>>; spans: Array<{ paragraphIndex: number; key: string; start: number }> } {
  const definedIn = new Map<string, Set<string>>();
  const spans: Array<{ paragraphIndex: number; key: string; start: number }> = [];
  for (const { term, paragraphIndex, offset } of definitions) {
    const key = termKey(term);
    spans.push({ paragraphIndex, key, start: offset });
    const section = sectionOf[paragraphIndex];
    if (!section) continue;
    if (!definedIn.has(key)) definedIn.set(key, new Set());
    definedIn.get(key)!.add(section);
  }
  return { definedIn, spans };
}

function groupByParagraph(declared: readonly DeclaredTerm[]): Map<number, DeclaredTerm[]> {
  const byParagraph = new Map<number, DeclaredTerm[]>();
  for (const d of declared) byParagraph.set(d.paragraphIndex, [...(byParagraph.get(d.paragraphIndex) ?? []), d]);
  return byParagraph;
}

/** "Waived Benefits (Section 7.4)": the pointer entry a reference belongs to —
 *  the last one starting before it in the paragraph, else its first. `pointers`
 *  is never empty: it comes from `groupByParagraph`. */
function pointerLabel(pointers: readonly DeclaredTerm[], ref: { matchText: string; offset: number }): string {
  const owner = pointers.reduce((last, p) => (p.offset <= ref.offset ? p : last));
  return `${owner.term} (${ref.matchText.replace(/\s+/g, " ")})`;
}

/** Whether `text` mentions `term` as a whole word, ignoring case — the old
 *  rule's own test, kept for a term no definition was found for. A plural
 *  counts: a section about "Arrangements" mentions "Arrangement". */
function mentionsTerm(text: string, term: string): boolean {
  return new RegExp(String.raw`\b${escapeRegExp(termKey(term))}(?:e?s)?\b`).test(termKey(text));
}

/** Every term named in `sentence`: quoted phrases, and known defined terms used
 *  bare (case-sensitive and plural-tolerant, as `countTermOccurrences` counts).
 *  A bare match inside a quoted name is part of that name, not a use: "Liability"
 *  in `"Due From Liabilities" has the meaning…` is not a claim about Liability. */
function termsInSentence(sentence: string, knownTerms: ReadonlyMap<string, string>): Map<string, string> {
  const found = new Map<string, string>();
  for (const match of straightenTermQuotes(sentence).matchAll(QUOTED_TERM_IN_SENTENCE)) {
    const term = (match[1] ?? "").trim();
    if (term) found.set(termKey(term), term);
  }
  const unquoted = doubleQuoteSingleQuotedTerms(sentence).replace(QUOTED_SPAN, (span) => " ".repeat(span.length));
  for (const [key, term] of knownTerms) {
    if (!found.has(key) && countTermOccurrences(term, unquoted) > 0) found.set(key, term);
  }
  return found;
}

/**
 * Raw sweep only. Gathers every (term, section reference) pair that shares a
 * sentence and COULD be a wrong claim, with the facts the judgment needs.
 *
 * Code rules out everything a fact already settles, so the model is asked only
 * where the answer depends on meaning:
 *  - the referenced section does not exist → broken_reference's concern;
 *  - the term is defined inside the referenced section → the claim is right
 *    whatever the sentence says;
 *  - no definition was found anywhere, but the referenced section mentions
 *    the term → given the benefit of the doubt, as the old rule did;
 *  - the sentence is the term's own definition → a definition that mentions
 *    a section is not a claim that the term is defined there.
 * What is left is sent to `word_docreview_judge`, which asks the one question
 * code cannot answer: does this sentence claim the term is defined there?
 */
export function findDefinedTermSectionMismatch(): Promise<DefinedTermSectionMismatchSweep> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    // AIVA-8925: one targeted load, not "every property of every paragraph" and
    // then a second round trip for the three this reads.
    paragraphs.load("items/text,items/style,items/outlineLevel");
    await context.sync();

    const texts = paragraphs.items.map((p) => p.text ?? "");
    const styles = paragraphs.items.map((p) => p.style ?? "");
    const outlineLevels = paragraphs.items.map((p) => p.outlineLevel ?? null);
    const toc = tocFromParagraphs(texts, styles, outlineLevels);
    const headingParagraphs = new Set(toc.map((e) => e.startParagraph));
    // AIVA-9191: real agreements number their sections automatically, so the
    // number is not in the heading text and every reference went unresolved.
    const listNumbers = await readHeadingListNumbers(context, paragraphs, toc);
    const declared = await readDeclaredTerms(context, paragraphs, texts);
    // Where a term is defined counts `(collectively, the "X")` too: most body
    // definitions are written that way, and without them a misnumbered pointer
    // read as "defined nowhere" (AIVA-9191). Known terms stay in-place only.
    const definitions = declared.filter((d) => d.phrasing !== "points");
    const pointersByParagraph = groupByParagraph(declared.filter((d) => d.phrasing === "points"));
    const facts: TermSectionFacts = {
      texts,
      // Capitalised only, like QUOTED_TERM_IN_SENTENCE: an interpretation clause's
      // `the word "to" means…` would otherwise match nearly every sentence.
      knownTerms: new Map(
        definitions.filter((d) => d.shape !== "passing" && /^[A-Z]/.test(d.term)).map((d) => [termKey(d.term), d.term]),
      ),
      ...collectTermDefinitions(definitions, sectionNumberByParagraph(toc, listNumbers, texts.length)),
    };
    const sectionByNumber = sectionsByNumber(toc, listNumbers);
    const counter = documentOccurrenceCounter(texts);

    const candidates: RawTermSectionCandidate[] = [];
    const seen = new Set<string>();
    const unresolvedPointers: string[] = [];
    texts.forEach((text, paragraphIndex) => {
      if (headingParagraphs.has(paragraphIndex) || /^TOC\s*\d/i.test(styles[paragraphIndex] ?? "")) return;
      const pointers = pointersByParagraph.get(paragraphIndex);
      for (const ref of scanReferenceMatches(text)) {
        const claim = resolveTermSectionClaim(ref, text, paragraphIndex, sectionByNumber, counter);
        if (claim) pushTermSectionCandidates(facts, claim, seen, candidates);
        else if (pointers && namesMissingSection(ref, text, sectionByNumber)) unresolvedPointers.push(pointerLabel(pointers, ref));
      }
    });
    // Every candidate is gathered so the user can be told how many went unchecked.
    return {
      candidates: candidates.slice(0, MAX_TERM_SECTION_CANDIDATES),
      unresolvedPointers,
      overLimit: Math.max(0, candidates.length - MAX_TERM_SECTION_CANDIDATES),
    };
  });
}

interface NumberedSection {
  heading: string;
  start: number;
  end: number;
}

/** Each section number → the first heading carrying it, with its paragraph span.
 *  An auto-numbered heading gets its number in front ("3.2 Fees"), and a run-in
 *  clause is cut to its title: the backend caps the heading at 500 characters
 *  and one longer heading fails the whole judge call (as AIVA-8310 found). */
function sectionsByNumber(toc: readonly TocEntry[], listNumbers: ReadonlyMap<number, string>): Map<string, NumberedSection> {
  const byNumber = new Map<string, NumberedSection>();
  for (const entry of toc) {
    const number = headingSectionNumber(entry, listNumbers);
    if (number && !byNumber.has(number)) {
      const listNumber = listNumbers.get(entry.startParagraph);
      const numbered = listNumber && !headingOwnNumber(entry.title) ? `${listNumber} ${entry.title}` : entry.title;
      byNumber.set(number, {
        heading: hyperlinkTargetHeadingText(numbered),
        start: entry.startParagraph,
        end: entry.endParagraph,
      });
    }
  }
  return byNumber;
}

/** What the document says about its terms, shared by every claim in one sweep. */
interface TermSectionFacts {
  texts: readonly string[];
  knownTerms: ReadonlyMap<string, string>;
  definedIn: ReadonlyMap<string, ReadonlySet<string>>;
  spans: ReadonlyArray<{ paragraphIndex: number; key: string; start: number }>;
}

/** One numeric section reference whose section exists, with the sentence it sits in. */
interface TermSectionClaim {
  matchText: string;
  occurrenceIndex: number;
  label: string;
  section: NumberedSection;
  sentence: string;
  sentenceStart: number;
  paragraphIndex: number;
}

/** Exhibits and Schedules are attachments with their own numbering, so
 *  "Schedule 4.25" is never Section 4.25 of the body. */
const ATTACHMENT_REFERENCE = /^(exhibit|schedule)\b/i;

/** "Section 7701 of the Code", "Section 2.1 of the Merger Agreement": a section
 *  of another document. "of the Agreement" and "of Article 5" stay in this one. */
const OF_ANOTHER_DOCUMENT = /^\s+of\s+(?:the\s+)?(?!(?:Agreement|Article|Section|Clause)\b)[A-Z]/;

type SectionReference = { matchText: string; targetLabel: string; isReference: boolean; offset: number };

/** A numbered reference to a section of this document's body: not lettered, not
 *  an Exhibit or Schedule, and not a section of another document. */
function isBodySectionReference(ref: SectionReference, text: string): boolean {
  if (!ref.isReference || !/^\d/.test(ref.targetLabel) || ATTACHMENT_REFERENCE.test(ref.matchText)) return false;
  return !OF_ANOTHER_DOCUMENT.test(text.slice(ref.offset + ref.matchText.length));
}

/** A numbered body-section reference whose section the document does not have. */
function namesMissingSection(
  ref: SectionReference,
  text: string,
  sectionByNumber: ReadonlyMap<string, NumberedSection>,
): boolean {
  return isBodySectionReference(ref, text) && !sectionByNumber.has(stripSubclauseLetter(ref.targetLabel));
}

/** How many unchecked pointers a note names before "and N more". */
const MAX_NAMED_POINTERS = 5;

/** What the user should know this rule could not check, in plain words, or
 *  nothing. Shown under the review so "no issues" is never read as "all checked". */
export function definedTermSectionNotes(sweep: DefinedTermSectionMismatchSweep): string[] {
  const notes: string[] = [];
  const unresolved = sweep.unresolvedPointers ?? [];
  if (unresolved.length > 0) {
    const subject =
      unresolved.length === 1
        ? "1 definition points to a section this check couldn't find, so it wasn't checked"
        : `${unresolved.length} definitions point to sections this check couldn't find, so they weren't checked`;
    const more = unresolved.length > MAX_NAMED_POINTERS ? ` and ${unresolved.length - MAX_NAMED_POINTERS} more` : "";
    notes.push(`${subject}: ${unresolved.slice(0, MAX_NAMED_POINTERS).join(", ")}${more}.`);
  }
  const overLimit = sweep.overLimit ?? 0;
  if (overLimit > 0) {
    const total = MAX_TERM_SECTION_CANDIDATES + overLimit;
    notes.push(`Defined term sections: checked ${MAX_TERM_SECTION_CANDIDATES} of ${total} references, the rest weren't checked.`);
  }
  return notes;
}

/** The reference as a claim worth checking, or null: a lettered or unaccepted
 *  match, an Exhibit/Schedule, or a section that does not exist (broken_reference's concern). */
function resolveTermSectionClaim(
  ref: { matchText: string; targetLabel: string; isReference: boolean; offset: number },
  text: string,
  paragraphIndex: number,
  sectionByNumber: ReadonlyMap<string, NumberedSection>,
  counter: ReturnType<typeof documentOccurrenceCounter>,
): TermSectionClaim | null {
  if (!isBodySectionReference(ref, text)) return null;
  const label = stripSubclauseLetter(ref.targetLabel);
  const section = sectionByNumber.get(label);
  if (!section) return null;
  const occurrenceIndex = counter.at(paragraphIndex, ref.offset, ref.matchText);
  if (occurrenceIndex === null) return null;
  const sentence = referenceSentence(text, ref.offset, ref.matchText.length);
  return {
    matchText: ref.matchText,
    occurrenceIndex,
    label,
    section,
    sentence,
    sentenceStart: Math.max(0, text.indexOf(sentence)),
    paragraphIndex,
  };
}

/** Adds one candidate per term the claim's sentence names, unless code already
 *  settles it (see `findDefinedTermSectionMismatch`'s own list) or the same
 *  term/reference pair was already sent. */
function pushTermSectionCandidates(
  facts: TermSectionFacts,
  claim: TermSectionClaim,
  seen: Set<string>,
  candidates: RawTermSectionCandidate[],
): void {
  for (const [key, term] of termsInSentence(claim.sentence, facts.knownTerms)) {
    const definedInSections = [...(facts.definedIn.get(key) ?? [])];
    if (settledInCode(facts, claim, key, term, definedInSections)) continue;
    const pairKey = `${key}\u0000${claim.matchText.toLowerCase()}\u0000${claim.occurrenceIndex}`;
    if (seen.has(pairKey)) continue;
    seen.add(pairKey);
    candidates.push({
      matchText: claim.matchText,
      occurrenceIndex: claim.occurrenceIndex,
      term,
      referenceLabel: claim.label,
      sentence: claim.sentence,
      referencedSectionHeading: claim.section.heading,
      definedInSections,
      paragraphIndex: claim.paragraphIndex,
    });
  }
}

/** True when a fact already answers the claim, so the model is never asked. */
function settledInCode(
  facts: TermSectionFacts,
  claim: TermSectionClaim,
  key: string,
  term: string,
  definedInSections: readonly string[],
): boolean {
  const { paragraphIndex, sentenceStart, sentence } = claim;
  const ownDefinition = facts.spans.some(
    (s) =>
      s.paragraphIndex === paragraphIndex &&
      s.key === key &&
      s.start >= sentenceStart &&
      s.start < sentenceStart + sentence.length,
  );
  if (ownDefinition) return true;
  if (definedInSections.some((s) => sectionWithin(s, claim.label))) return true;
  return definedInSections.length === 0 && mentionsTerm(sectionTextExcept(facts.texts, claim.section, paragraphIndex), term);
}

/** The section's text WITHOUT the paragraph making the claim: a claim that sits
 *  inside the section it names would otherwise count as the section mentioning
 *  the term, and excuse itself. */
function sectionTextExcept(texts: readonly string[], section: NumberedSection, paragraphIndex: number): string {
  return texts
    .slice(section.start, section.end + 1)
    .filter((_, i) => section.start + i !== paragraphIndex)
    .join(" ");
}

/** Flag a term claimed to be defined in a section that doesn't define it.
 *  Comment-only — only the drafter knows whether the reference or the
 *  definition is the one that moved. */
export async function fixDefinedTermSectionMismatch(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// defined_not_used — a defined term (declared as "the \"X\"", or formally as
// `"X" means ...`) that never appears again in the document
// ---------------------------------------------------------------------------

export interface RawDeclaredTerm {
  matchText: string;
  term: string;
  /** How many times `term` appears as a whole word anywhere in the
   *  document, INCLUDING its own declaration — so 1 means "only here".
   *  Counted inflection-tolerantly (see `countTermOccurrences`): a term
   *  declared in the singular but only ever USED in the plural is genuinely
   *  used, and must not be reported as unused. */
  occurrenceCount: number;
  paragraphIndex: number;
  /** AIVA-9042: the 1-based footnote the declaration sits in, when it is in
   *  one rather than in the main text. */
  footnote?: number;
}

export interface DefinedNotUsedSweep {
  terms: RawDeclaredTerm[];
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

// The gap between two words of a multi-word defined term, as a regex fragment —
// "Statement of Work" must still match across a line break or a double space.
const TERM_WORD_GAP = String.raw`\s+`;

/** One word of a defined term as a regex fragment that also matches its
 * ordinary English plural — "Affiliate" matching "Affiliates", "Party"
 * matching "Parties", "Statement of Work" matching "Statements of Work".
 *
 * AIVA-8313: without this, a term declared in the singular and used only in
 * the plural — the normal way a contract reads: `"Affiliate" means ...`
 * declared once, then "its Affiliates" everywhere after — counts as ONE
 * occurrence and is reported as never used. That false positive is worse
 * than a miss here: it tells a reviewer to delete a definition the document
 * genuinely relies on. */
function inflectedWordPattern(word: string): string {
  if (/[^aeiou]y$/i.test(word)) return `${anyHyphen(escapeRegExp(word.slice(0, -1)))}(?:y|ies)`;
  if (/(?:[sxz]|ch|sh)$/i.test(word)) return `${anyHyphen(escapeRegExp(word))}(?:es)?`;
  return `${anyHyphen(escapeRegExp(word))}s?`;
}

/** Word's non-breaking hyphen (U+001E in paragraph text, U+2011 elsewhere) and
 *  a plain hyphen are one character to a reader: "Pre\u2011Closing" is "Pre-Closing". */
const HYPHEN_CLASS = String.raw`[-\u001e\u2011]`;
const HYPHEN_CHARS = new Set(["-", "\u001e", "\u2011"]);
function anyHyphen(escaped: string): string {
  return [...escaped].map((c) => (HYPHEN_CHARS.has(c) ? HYPHEN_CLASS : c)).join("");
}

/** How many times `term` appears as a whole word (or as its plural) in
 * `fullText`, INCLUDING its own declaration.
 *
 * Deliberately case-SENSITIVE: a defined term is capitalised wherever it is
 * really used, and the lower-case common noun is not a use of it. The
 * distinction matters because a formal definition routinely echoes its own
 * term in lower case (`"Services" means the services described in Section
 * 1`), which a case-insensitive count would read as a second occurrence —
 * silently making this rule unable to ever flag such a definition. */
function countTermOccurrences(term: string, fullText: string): number {
  const pattern = termOccurrencePattern(term);
  if (!pattern) return 0;
  // matchAll, not match: this pattern is always global, and `String.match` on a
  // pattern whose flags aren't statically visible reads as the non-global
  // single-match case (typescript:S6594). matchAll is also what every other
  // sweep in this file uses.
  return [...fullText.matchAll(pattern)].length;
}

/** The global, case-SENSITIVE, inflection-tolerant whole-word pattern for one
 * defined term — see `countTermOccurrences` for why each of those holds.
 * Shared with used_before_defined, which must agree with defined_not_used
 * about what counts as a use of a term. `null` for an empty term. */
function termOccurrencePattern(term: string): RegExp | null {
  const body = termPatternBody(term);
  return body ? new RegExp(String.raw`\b${body}\b`, "g") : null;
}

/** `term` as an inflection-tolerant regex fragment, or null for an empty term. */
function termPatternBody(term: string): string | null {
  const words = term.trim().split(/\s+/).filter(Boolean).map(inflectedWordPattern);
  // Joined through a named separator rather than inline, so the pattern stays a
  // single-level template literal (typescript:S4624).
  return words.length > 0 ? words.join(TERM_WORD_GAP) : null;
}

/** Whether `phrase` is one of `terms` or its plural ("Disputes" for "Dispute"). */
function namesAnyTerm(phrase: string, terms: readonly RegExp[]): boolean {
  return terms.some((pattern) => pattern.test(phrase));
}

/** Each term as a whole-phrase, case-insensitive matcher for `namesAnyTerm`. */
function wholeTermMatchers(terms: Iterable<string>): RegExp[] {
  return [...new Set(terms)].flatMap((term) => {
    const body = termPatternBody(term);
    return body ? [new RegExp(`^${body}$`, "i")] : [];
  });
}

/** Raw sweep only: each declared term (AIVA-9190 inventory) at its first
 *  declaration, with how often it appears. One finding per term, not per shape.
 *
 *  AIVA-9042: footnotes are read after the main text, as one more "paragraph"
 *  each. A use in a footnote is a use — the operator manual tells the
 *  proofreader to repeat every search in the footnotes — and a term declared in
 *  a footnote is declared there. */
export function findDefinedNotUsed(): Promise<DefinedNotUsedSweep> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/outlineLevel");
    await context.sync();
    const bodyTexts = paragraphs.items.map((p) => p.text ?? "");
    const footnoteTexts = await loadFootnoteTexts(context);

    const texts = [...bodyTexts, ...footnoteTexts];
    const fullText = texts.join(" ");
    // An "or" entry's other name is a use of the same term; its own mention
    // in the entry is not, hence the -1.
    const terms = firstDeclarations(await readDeclaredTerms(context, paragraphs, texts)).map(
      ({ matchText, term, paragraphIndex, alternativeTerm }): RawDeclaredTerm => ({
        matchText,
        term,
        occurrenceCount:
          countTermOccurrences(term, fullText) + (alternativeTerm ? countTermOccurrences(alternativeTerm, fullText) - 1 : 0),
        paragraphIndex,
        ...(paragraphIndex >= bodyTexts.length ? { footnote: paragraphIndex - bodyTexts.length + 1 } : {}),
      }),
    );
    return { terms };
  });
}

/** Deterministic evaluation, ported 1:1 from rules.py's `evaluate_r11` — a
 * defined term never used again anywhere in the document.
 * `occurrenceCount` counts the declaration itself, so a count of 1 means
 * "nowhere else". */
export function evaluateDefinedNotUsed(sweep: DefinedNotUsedSweep): FindingCreate[] {
  const findings: FindingCreate[] = [];
  for (const term of sweep.terms) {
    if (term.occurrenceCount > 1) continue;
    findings.push(
      toFindingCreate({
        ruleId: "defined_not_used",
        matchText: term.matchText,
        // No occurrenceIndex, and that is CORRECT here rather than the
        // omission it was in broken_reference. This rule fires only when
        // `occurrenceCount === 1` — the term appears exactly once in the whole
        // document, so its declaration is the only place `matchText` occurs
        // and occurrence zero is the only occurrence there is. Adding an index
        // would mean counting in a sweep that has no reason to count.
        // Checked when broken_reference's missing index was found; left alone
        // deliberately, not overlooked.
        //
        // AIVA-9042: still true for a declaration in a footnote. Occurrence
        // zero of a text that occurs once is that one, wherever it is; the
        // anchor step finds no hit in the main text and looks in the
        // footnotes (see footnotes.ts).
        problem:
          term.footnote === undefined
            ? `"${term.term}" is defined but never used again in this document.`
            : `"${term.term}" is defined but never used again in this document ${footnoteLabel(term.footnote)}.`,
        comment: "defined term is never used again",
        fixHandler: "word.docreview.fix_defined_not_used",
        fixParams: {
          commentText: "defined term is never used again",
          ...(term.footnote === undefined ? {} : footnoteSource(term.footnote)),
        },
      }),
    );
  }
  return findings;
}

/** Flag a term defined but never used again. Comment-only, same reasoning
 * as broken_reference/defined_term_section_mismatch — nothing here is safe to rewrite unattended. */
export async function fixDefinedNotUsed(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// ambiguous_reference — a lettered/worded reference matches more than one heading (needs no
// sweep of its own: it evaluates purely from broken_reference's own sweep)
// ---------------------------------------------------------------------------

/** Flag an ambiguous reference. Comment-only — with more than one
 * candidate heading, there is no single safe correction, only a flag for
 * the operator to resolve. */
export async function fixAmbiguousReference(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// numbering_order — heading numbers out of sequence (needs no sweep of its own: it
// evaluates purely from the headings broken_reference/bracket_heading_mismatch already collect)
// ---------------------------------------------------------------------------

/** Where a paragraph over Word's search limit (`WORD_SEARCH_MAX_LENGTH`) is cut
 *  for a numbering finding's anchor — with room to spare below the limit.
 *  Anything up to the limit anchors whole (`numberingAnchorText`). */
const NUMBERING_ANCHOR_MAX_LENGTH = 200;

/**
 * The text a numbering_order finding anchors on: the paragraph's own text, or —
 * for a heading run into its clause ("12.02  Provider Warranties.  Provider
 * represents…", often 500+ characters) — its opening, cut at a word boundary
 * (AIVA-9179). The whole paragraph is over Word's search limit, so it could
 * never be anchored, and it pushed every finding batched with it into a slow
 * one-at-a-time fallback. The opening still starts at the paragraph's start
 * and still holds the number, so the counting and the typed-number fix below
 * work exactly as before.
 */
export function numberingAnchorText(text: string): string {
  // Only a text Word could not search is shortened, so every heading that
  // anchored before keeps exactly the match text it had.
  if (text.length <= WORD_SEARCH_MAX_LENGTH) return text;
  const head = text.slice(0, NUMBERING_ANCHOR_MAX_LENGTH);
  // Drop the last (possibly cut) word and the spaces before it. Walked by hand
  // rather than `/\s+\S*$/`, which backtracks on long runs of spaces.
  const isSpace = (at: number) => /\s/.test(head[at - 1] ?? "");
  let cut = head.length;
  while (cut > 0 && !isSpace(cut)) cut -= 1;
  while (cut > 0 && isSpace(cut)) cut -= 1;
  return (cut > 0 ? head.slice(0, cut) : head).trimEnd();
}

/** Which occurrence of each heading's own exact text, in TRUE document
 *  order — matching what a live body.search() would find. Needed because
 *  `collectHeadings()`'s own combined list is explicit headings (in their
 *  own document order) followed by inferred ones (in their own document
 *  order), NOT reliably one single document-order sequence — so a plain
 *  walk-in-array-order seenCount, correct for every OTHER rule's paragraph
 *  walk, would be wrong here. Sorting by each heading's own paragraphIndex
 *  first recovers the true order before counting. Shared by numbering_order's two
 *  independent evaluators, both of which anchor a heading's own text (or its opening, `numberingAnchorText`) —
 *  without this, two identically-worded headings (a real, if rare, drafting
 *  duplicate) would both anchor to whichever occurrence came first. */
function headingOccurrenceIndexByRef<T extends { text: string; paragraphIndex: number }>(
  headings: readonly T[],
): Map<T, number> {
  const byDocumentOrder = [...headings].sort((a, b) => a.paragraphIndex - b.paragraphIndex);
  // The same literal counting every sweep uses, over the only text this
  // function is given. Counting the heading texts as DOCUMENT text (rather
  // than ticking a per-heading tally) is what makes a heading whose wording
  // is contained in a later one — "Definitions" inside "Definitions and
  // Interpretation" — occupy the position a live search really gives it.
  //
  // KNOWN LIMIT, pre-dating AIVA-8544 and deliberately not widened here:
  // these are the heading paragraphs only, while the anchor step searches the
  // whole BODY. A heading whose exact wording also appears in ordinary prose
  // earlier in the document will still be counted one short. Closing it means
  // minting this index in the sweep, where the paragraphs are, and carrying it
  // on RawHeading — a change to the sweep/evaluate boundary that both
  // evaluators and the backend see, which is not this bugfix's to make.
  const counter = documentOccurrenceCounter(byDocumentOrder.map((h) => h.text));
  const result = new Map<T, number>();
  byDocumentOrder.forEach((heading, index) => {
    // Offset 0: the anchor text is the paragraph's own text or its opening
    // (`numberingAnchorText`), so its match starts where the paragraph does.
    const occurrenceIndex = counter.at(index, 0, numberingAnchorText(heading.text));
    if (occurrenceIndex !== null) result.set(heading, occurrenceIndex);
  });
  return result;
}

/** Lexicographic comparison of two numeric-part tuples, e.g. [1,2] vs [1,3]
 * — mirrors Python tuple comparison, which rules.py's `evaluate_r14`
 * originally relied on (a shorter tuple that's a prefix of a longer one
 * compares as "less"). */
function compareNumberTuples(a: number[], b: number[]): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i] !== b[i]) return a[i]! - b[i]!;
  }
  return a.length - b.length;
}

/** The smallest number of `depth` components, zero-padded like
 * `highestParts`, that is strictly greater than `highestParts` — mirrors
 * rules.py's `_next_number_at_depth` exactly (see its own docstring, now
 * deleted, for the full reasoning: truncate-and-increment for a shallower
 * depth, pad-with-zeros for a deeper one). */
function nextNumberAtDepth(highestParts: string[], depth: number): string {
  const baseParts =
    highestParts.length >= depth
      ? highestParts.slice(0, depth)
      : [...highestParts, ...new Array<string>(depth - highestParts.length).fill("0")];
  const last = baseParts[baseParts.length - 1]!;
  const width = last.length;
  baseParts[baseParts.length - 1] = String(Number(last) + 1).padStart(width, "0");
  return baseParts.join(".");
}

/** The number the reader actually sees in front of an item, and whether Word
 * generated it. A list number wins over any digits in the text: when the list
 * renders "15.1", that is what the document shows, whatever the text says. */
interface ItemNumber {
  value: string;
  /** Word generated this number from list formatting — there is no text to
   *  rewrite. See `numberingOrderFinding`. */
  fromList: boolean;
  /** How much of `item.text` the number occupies, so a text-sourced fix can
   *  replace exactly the number and leave the title alone. `[0, 0]` for a
   *  list-sourced number, which occupies none of the text. */
  textSpan: [start: number, end: number];
  /** Its number is in an unaccepted tracked change: measured as it will read,
   *  never itself reported — the fix would rewrite text that is mid-change. */
  pending?: boolean;
}

function itemNumber(item: RawNumberedItem): ItemNumber | null {
  const fromList = item.listNumber ? HEADING_NUMBER.exec(item.listNumber) : null;
  if (fromList?.[1]) return { value: fromList[1], fromList: true, textSpan: [0, 0] };
  const fromText = HEADING_NUMBER.exec(item.text);
  if (item.currentText !== undefined) {
    const shown = HEADING_NUMBER.exec(item.currentText)?.[1];
    if (!shown) return null;
    if (shown !== fromText?.[1]) return { value: shown, fromList: false, textSpan: [0, 0], pending: true };
  }
  if (!fromText?.[1]) return null;
  // HEADING_NUMBER is `^\s*(...)`, always matching from index 0, so group 1
  // starts at (match length - group length) and runs to the full match's own
  // length — mirrors Python's match.start(1)/end(1).
  return {
    value: fromText[1],
    fromList: false,
    textSpan: [fromText[0].length - fromText[1].length, fromText[0].length],
  };
}

/**
 * Whether a number is where a list NATURALLY starts — 1, 1.1, 1.1.1 — as
 * opposed to an arbitrary value someone set by hand.
 *
 * This is the whole basis for telling a legitimate restart from the defect,
 * and it exists because Word gives us no other signal: confirmed against a
 * real document, "Set Numbering Value" puts the renumbered paragraph in a NEW
 * LIST, exactly as a Schedule that restarts its own numbering does. Scoping
 * each list to its own sequence therefore cannot work — it gives the broken
 * item a private sequence with nothing before it to be out of order with,
 * which is the very thing this rule has to catch (AIVA-8297).
 *
 * So one sequence runs through the document, as it always has for typed
 * numbers, and a new list interrupts it only when it opens at its natural
 * start. "SCHEDULE 2" beginning again at 1 does; a clause silently renumbered
 * to 10.2 does not, and is still measured against the 15.5 before it.
 */
function isNaturalListStart(number: number[]): boolean {
  return number.every((part) => part === 1);
}

/**
 * Deterministic evaluation, ported from rules.py's `evaluate_r14` — an item
 * numbered lower than (or equal to) the highest number seen before it in its
 * own sequence, plus the jumps and in-section gaps described at the end.
 *
 * One sequence runs through the whole document, interrupted only by a list
 * that opens at its natural start (`isNaturalListStart`) — NOT one sequence
 * per Word list, which would hide the defect this rule exists to find.
 *
 * Walks in DOCUMENT order, not the order items arrive in: `collectHeadings`
 * returns explicit headings followed by inferred ones, so an array-order walk
 * compared a section's sub-clauses against the LAST section in the document and
 * flagged nearly every one of them (the same trap `headingOccurrenceIndexByRef`
 * above already documents for occurrence counting).
 *
 * The fix depends on where the number came from. A typed number is text, so
 * only the leading number substring is replaced and the title is untouched — a
 * genuine tracked-edit fix. A number Word GENERATED from list formatting has no
 * text to replace (AIVA-8297): rewriting the paragraph text would insert a
 * second, literal number in front of the one Word already renders, so those are
 * flagged with a comment for the operator instead, the same way every other
 * judgment-flavoured rule here does.
 *
 * AIVA-8297 retest: the sections first. The longest run of items whose
 * sections never go back keeps its sections (`keptSections`); any other
 * sub-clause belongs in the section of the kept item before it (10.5 among 2.x,
 * 21.00 between 10.x and 11.x), so a stray never becomes the highest and never
 * drags correct clauses with it. Within a section a clause's
 * number is its position — the previous clause's + 1 — so a gap (10.01, 10.03),
 * a swap, a repeat and every clause after a correction are renumbered together,
 * and a sub-clause follows its parent's corrected number. Top-level numbers
 * keep the highest-seen check, and gaps between them stay allowed: an unstyled
 * section heading is never collected, so 1, 9, 10 can be a complete document.
 */
export function evaluateNumberingOrder(
  items: RawNumberedItem[],
  documentNumbers?: readonly string[],
): FindingCreate[] {
  const occurrenceIndexByItem = headingOccurrenceIndexByRef(items);
  const entries = numberedEntries(items);
  const collected = new Set(entries.map((e) => numberKey(e.number)));
  const walk: NumberingWalk = {
    entries,
    kept: keptSections(entries),
    highest: null,
    lastKept: null,
    parents: [],
    bySection: new Map(),
    elsewhere: new Set((documentNumbers ?? []).filter((n) => !collected.has(n))),
    inDocument: new Set([...collected, ...(documentNumbers ?? [])]),
  };
  const findings: FindingCreate[] = [];
  entries.forEach((_, index) => {
    const finding = placeEntry(index, walk, occurrenceIndexByItem);
    if (finding) findings.push(finding);
  });
  return findings;
}

/** What the walk knows so far. */
interface NumberingWalk {
  entries: readonly NumberedEntry[];
  /** Its section is in the longest run that never goes back (`keptSections`). */
  kept: readonly boolean[];
  highest: { number: number[]; parts: string[]; listId: string | null | undefined } | null;
  /** Where the last kept item ended up. */
  lastKept: string[] | null;
  /** The last item placed at each depth (index 0 = top level). */
  parents: PlacedParent[];
  /** The last clause placed in each section ("10" → its 10.0x). */
  bySection: Map<string, PlacedClause>;
  /** Numbers some paragraph opens with that no collected item carries (`numberKey`s). */
  elsewhere: ReadonlySet<string>;
  /** Every number in the document (`numberKey`s). */
  inDocument: ReadonlySet<string>;
}

interface PlacedParent {
  original: number[];
  corrected: string[];
  moved: boolean;
}

interface PlacedClause {
  /** Its number's last part once placed. */
  n: number;
  /** Zero-padding of that part ("03" → 2). */
  width: number;
  /** Its number as the document shows it. */
  original: string;
  /** Its own last part. */
  own: number;
  renumbered: boolean;
}

/**
 * Marks the items whose sections form the longest run that never goes back,
 * per stretch between Word list restarts. A section's own number ("2") comes
 * before its clauses and counts double, being the surer anchor. Of equally
 * long runs, the one with the smallest section jumps wins (2, 2, 2, 3 over
 * 2, 2, 10, 10), then the one starting earliest — as the highest-seen check
 * did: in 10., 8.01 it is 8.01 that is out of place.
 */
function keptSections(entries: readonly NumberedEntry[]): boolean[] {
  const kept = entries.map(() => false);
  let start = 0;
  for (let i = 1; i <= entries.length; i++) {
    if (i < entries.length && !entries[i]!.restartsSequence) continue;
    markSectionRun(entries.slice(start, i), start, kept);
    start = i;
  }
  return kept;
}

function markSectionRun(entries: readonly NumberedEntry[], offset: number, kept: boolean[]) {
  const sections = entries.map((e) => e.number[0]!);
  const isHeading = entries.map((e) => e.number.length === 1);
  const weight = (i: number) => (isHeading[i] ? 2 : 1);
  const follows = (i: number, j: number) =>
    sections[j]! > sections[i]! || (sections[j] === sections[i] && (isHeading[i] || !isHeading[j]));
  // From the right: the best run starting at each item, and the item after it.
  const length: number[] = [];
  const jumps: number[] = [];
  const next: number[] = [];
  let first = -1;
  for (let i = sections.length - 1; i >= 0; i--) {
    length[i] = weight(i);
    jumps[i] = 0;
    next[i] = -1;
    for (let j = i + 1; j < sections.length; j++) {
      if (!follows(i, j)) continue;
      const total = length[j]! + weight(i);
      const jump = sections[j]! - sections[i]! + jumps[j]!;
      if (total > length[i]! || (total === length[i]! && jump < jumps[i]!)) {
        length[i] = total;
        jumps[i] = jump;
        next[i] = j;
      }
    }
    if (first < 0 || length[i]! > length[first]! || (length[i] === length[first] && jumps[i]! <= jumps[first]!)) first = i;
  }
  for (let i = first; i >= 0; i = next[i]!) kept[offset + i] = true;
}

/** Places one item and returns its finding, if it is misnumbered. */
function placeEntry(
  index: number,
  walk: NumberingWalk,
  occurrenceIndexByItem: Map<RawNumberedItem, number>,
): FindingCreate | null {
  const entry = walk.entries[index]!;
  if (entry.restartsSequence) {
    walk.bySection = new Map();
    walk.parents = [];
  }
  if (entry.restartsSequence || entry.current.pending) {
    // A number mid-change counts as the document now shows it.
    place(walk, index, entry.parts, false);
    return null;
  }
  const section = entry.number.length > 1 ? sectionFor(index, walk) : null;
  if (!section) {
    // The original evaluate_r14 check, against the highest, which a flagged
    // item never replaces. The list the highest is on is the one it broke from.
    const { highest } = walk;
    if (highest && compareNumberTuples(entry.number, highest.number) <= 0) {
      const corrected = nextNumberAtDepth(highest.parts, entry.number.length);
      return numberingOrderFinding(entry.item, entry.current, corrected, occurrenceIndexByItem, highest.listId);
    }
    place(walk, index, entry.parts, false);
    return null;
  }
  const target = positionInSection(index, section, walk);
  const listId = walk.highest?.listId;
  place(walk, index, target.parts, target.moved);
  if (!target.moved) return null;
  const corrected = target.parts.join(".");
  return numberingOrderFinding(entry.item, entry.current, corrected, occurrenceIndexByItem, listId, target.reason);
}

/**
 * The section a sub-clause belongs in, as placed numbers: its own, unless its
 * section is not kept and differs from the kept item before it (or, at the
 * start, after it). Null when there is no section of
 * the right depth to put it in — the item then gets the highest-seen check.
 */
function sectionFor(index: number, walk: NumberingWalk): { parts: string[]; parent?: PlacedParent } | null {
  const entry = walk.entries[index]!;
  const depth = entry.number.length;
  const own = entry.parts.slice(0, -1);
  if (!walk.kept[index]) {
    const ref = walk.lastKept ?? nextKept(index, walk)?.parts ?? null;
    if (ref && Number(ref[0]) !== entry.number[0]) {
      if (ref.length >= depth) return { parts: ref.slice(0, depth - 1) };
      return ref.length === depth - 1 ? { parts: ref } : null;
    }
  }
  // A sub-clause follows its parent's corrected number, and one whose stated
  // parent is behind the current one (1.01.01 after 1.02) belongs under it.
  const parent = walk.parents[depth - 2];
  const ownParent = entry.number.slice(0, -1);
  if (parent && compareNumberTuples(parent.original, ownParent) === 0) return { parts: parent.corrected, parent };
  if (parent && compareNumberTuples(ownParent, parent.corrected.map(Number)) < 0) return { parts: parent.corrected };
  return { parts: own };
}

function nextKept(index: number, walk: NumberingWalk): NumberedEntry | null {
  for (let i = index + 1; i < walk.entries.length && !walk.entries[i]!.restartsSequence; i++) {
    if (walk.kept[i]) return walk.entries[i]!;
  }
  return null;
}

/**
 * The number a sub-clause should carry in `section`: one past the last clause
 * placed there, skipping numbers another paragraph already holds. The first
 * clause in a section keeps its number unless the next one there is not above
 * it (4.07, 4.02 → 4.01), and a clause moved in from another section goes just
 * before the next one there.
 */
function positionInSection(
  index: number,
  section: { parts: string[]; parent?: PlacedParent },
  walk: NumberingWalk,
): { parts: string[]; moved: boolean; reason?: string } {
  const entry = walk.entries[index]!;
  const sectionNumber = section.parts.map(Number);
  const key = numberKey(sectionNumber);
  const last = walk.bySection.get(key);
  const ownPart = entry.parts[entry.parts.length - 1]!;
  const own = Number(ownPart);
  const width = last?.width ?? ownPart.length;
  const label = (n: number) => [...section.parts, String(n).padStart(width, "0")].join(".");
  const sectionMoved = compareNumberTuples(sectionNumber, entry.number.slice(0, -1)) !== 0;
  let expected = firstPlaceInSection(index, key, sectionMoved ? null : own, walk);
  if (last) {
    expected = last.n + 1;
    while (walk.elsewhere.has(numberKey([...sectionNumber, expected]))) expected++;
  }
  if (!sectionMoved && expected === own) return { parts: entry.parts, moved: false };
  return { parts: label(expected).split("."), moved: true, reason: moveReason(entry, section, last, own, expected, label, walk) };
}

/** The first number in a section: `own`, unless the next clause there is not above it. */
function firstPlaceInSection(index: number, key: string, own: number | null, walk: NumberingWalk): number {
  const depth = key.split(".").length + 1;
  for (let i = index + 1; i < walk.entries.length && !walk.entries[i]!.restartsSequence; i++) {
    const e = walk.entries[i]!;
    if (e.number.length < depth) break;
    if (e.number.length > depth || !walk.kept[i]) continue;
    if (numberKey(e.number.slice(0, -1)) !== key) break;
    const nextOwn = e.number[depth - 1]!;
    return own !== null && own < nextOwn ? own : Math.max(1, nextOwn - 1);
  }
  return own ?? 1;
}

/** Why the clause moves, or undefined for the plain "out of sequence". */
function moveReason(
  entry: NumberedEntry,
  section: { parts: string[]; parent?: PlacedParent },
  last: PlacedClause | undefined,
  own: number,
  expected: number,
  label: (n: number) => string,
  walk: NumberingWalk,
): string | undefined {
  if (section.parent?.moved && expected === own) {
    return `sits under ${section.parent.original.join(".")}, which is renumbered to ${section.parent.corrected.join(".")}`;
  }
  if (!last || own < expected || numberKey(entry.number.slice(0, -1)) !== numberKey(section.parts.map(Number))) {
    return undefined;
  }
  if (last.renumbered && own === last.own + 1) return `follows ${last.original}, which is renumbered to ${label(last.n)}`;
  const sectionNumber = section.parts.map(Number);
  if (walk.inDocument.has(numberKey([...sectionNumber, expected]))) return undefined;
  const before = last.renumbered ? `${last.original} (renumbered to ${label(last.n)})` : last.original;
  const prefix = `${numberKey(sectionNumber)}.`;
  const rangeHeld = [...walk.inDocument].some((k) => {
    const rest = k.startsWith(prefix) ? k.slice(prefix.length) : "";
    return rest !== "" && !rest.includes(".") && Number(rest) > expected && Number(rest) < own;
  });
  const missing = own - expected > 1 && !rangeHeld ? `${label(expected)} to ${label(own - 1)}` : label(expected);
  return `follows ${before}, so ${missing} is missing`;
}

function place(walk: NumberingWalk, index: number, parts: string[], moved: boolean) {
  const entry = walk.entries[index]!;
  const number = parts.map(Number);
  const depth = number.length;
  if (depth > 1) {
    walk.bySection.set(numberKey(number.slice(0, -1)), {
      n: number[depth - 1]!,
      width: parts[depth - 1]!.length,
      original: entry.current.value,
      own: entry.number[entry.number.length - 1]!,
      renumbered: moved,
    });
  }
  // A clause holds its ancestors' places: 1.2.1 means 1.2 is taken in section 1.
  for (let k = 1; k < depth - 1; k++) {
    const key = numberKey(number.slice(0, k));
    if ((walk.bySection.get(key)?.n ?? -Infinity) >= number[k]!) continue;
    const ancestor = parts.slice(0, k + 1);
    walk.bySection.set(key, {
      n: number[k]!,
      width: parts[k]!.length,
      original: ancestor.join("."),
      own: number[k]!,
      renumbered: false,
    });
  }
  walk.parents.length = depth - 1;
  walk.parents[depth - 1] = { original: entry.number, corrected: parts, moved };
  // A renumbered item is put back on the list the run before it was on.
  const listId = moved ? (walk.highest?.listId ?? entry.item.listId) : entry.item.listId;
  walk.highest = { number, parts, listId };
  if (walk.kept[index]) walk.lastKept = parts;
}

/** One numbered item, read once, in document order. */
interface NumberedEntry {
  item: RawNumberedItem;
  current: ItemNumber;
  parts: string[];
  number: number[];
  /** A new Word list opening at its natural start after a new section — the
   *  sequence starts over here (see `isNaturalListStart`). */
  restartsSequence: boolean;
}

function numberedEntries(items: RawNumberedItem[]): NumberedEntry[] {
  const byDocumentOrder = [...items].sort((a, b) => a.paragraphIndex - b.paragraphIndex);
  const entries: NumberedEntry[] = [];
  let currentListId: string | null | undefined;
  for (const item of byDocumentOrder) {
    const current = itemNumber(item);
    if (!current) continue;
    const parts = current.value.split(".");
    const number = parts.map(Number);
    const opensNewList = current.fromList && item.listId != null && item.listId !== currentListId;
    if (current.fromList) currentListId = item.listId;
    // A restart with no new section before it is Word's "Restart at 1" on an
    // ordinary clause, measured against the sequence it broke (AIVA-8297).
    const restartsSequence = opensNewList && isNaturalListStart(number) && item.afterSectionBreak !== false;
    entries.push({ item, current, parts, number, restartsSequence });
  }
  return entries;
}

/** "10.02" and "10.2" are the same number. */
function numberKey(number: readonly number[]): string {
  return number.join(".");
}

/** How a finding names its item: the quoted text, led by the number Word
 *  renders when the number is generated rather than typed. */
function numberingItemLabel(item: RawNumberedItem, current: ItemNumber, anchorText: string): string {
  const shown = anchorText.length < item.text.length ? `${anchorText}…` : anchorText;
  return current.fromList ? `Item ${current.value} ("${shown}")` : `"${shown}"`;
}

/** The finding for one out-of-sequence item, in whichever of the two shapes its
 * number source allows — see `evaluateNumberingOrder`'s own docstring. */
function numberingOrderFinding(
  item: RawNumberedItem,
  current: ItemNumber,
  correctedNumberText: string,
  occurrenceIndexByItem: Map<RawNumberedItem, number>,
  targetListId?: string | null,
  reason = "is numbered out of sequence with the items before it",
): FindingCreate {
  const occurrenceIndex = occurrenceIndexByItem.get(item) ?? 0;
  const anchorText = numberingAnchorText(item.text);
  const itemLabel = numberingItemLabel(item, current, anchorText);
  const problem = `${itemLabel} ${reason}.`;
  const commentText = `${problem} It should be ${correctedNumberText}.`;
  if (current.fromList) {
    // A generated number has no text to replace, so the correction is to put
    // the paragraph back on the list it broke away from and let Word renumber
    // it. `targetListId` is null when the run before it was typed rather than
    // generated, or when this host would not serve list identity — the fix
    // then degrades to the comment alone (see `fixNumberingOrderRelist`).
    return toFindingCreate({
      ruleId: "numbering_order",
      matchText: anchorText,
      occurrenceIndex,
      problem,
      comment: commentText,
      fixHandler: "word.docreview.fix_numbering_order_relist",
      fixParams: {
        // problem + correctedNumber, not a ready-made comment: the fix composes
        // the wording itself once it knows whether the re-attach worked.
        problem,
        correctedNumber: correctedNumberText,
        targetListId: targetListId ?? null,
        listLevel: item.listLevel ?? null,
      },
    });
  }
  const [numberStart, numberEnd] = current.textSpan;
  // The fix replaces the anchored text, so the correction is made inside the
  // same opening the finding anchors on. The number leads the paragraph, so
  // it is always inside it.
  const correctedText = anchorText.slice(0, numberStart) + correctedNumberText + anchorText.slice(numberEnd);
  return toFindingCreate({
    ruleId: "numbering_order",
    matchText: anchorText,
    occurrenceIndex,
    problem,
    comment: commentText,
    fixHandler: "word.docreview.fix_numbering_order",
    fixParams: {
      correctFullText: correctedText,
      commentText: `${problem} Corrected the number to ${correctedNumberText}.`,
    },
  });
}

/**
 * The one number that differs between a heading's text and its correction —
 * "12.02" → "12.04" — or null when they differ by anything else. Widened to
 * the whole number on each side, so a search for it inside the heading finds
 * the leading number, not a lone digit somewhere in its title.
 */
export function changedNumber(before: string, after: string): { from: string; to: string } | null {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start += 1;
  let endBefore = before.length;
  let endAfter = after.length;
  while (endBefore > start && endAfter > start && before[endBefore - 1] === after[endAfter - 1]) {
    endBefore -= 1;
    endAfter -= 1;
  }
  const isNumberChar = (c: string | undefined) => c !== undefined && /[\d.]/.test(c);
  while (start > 0 && isNumberChar(before[start - 1])) start -= 1;
  while (endBefore < before.length && isNumberChar(before[endBefore])) {
    endBefore += 1;
    endAfter += 1;
  }
  const from = before.slice(start, endBefore);
  const to = after.slice(start, endAfter);
  if (!/^\d+(?:\.\d+)*\.?$/.test(from) || !/^\d+(?:\.\d+)*\.?$/.test(to)) return null;
  return { from, to };
}

/** Correct a heading's number to the value evaluate_r14 (backend) derived
 * from the highest number seen before it — a genuine tracked-change
 * rewrite, since (unlike alphabetical_order's reorder) the correct value is a single
 * self-contained text replacement with no risk of disturbing any other
 * paragraph. Only the number changes; the heading's own title text is
 * untouched (evaluate_r14 already preserves it in `correctFullText`). */
export async function fixNumberingOrder(
  anchorTag: string,
  expected: string,
  correctFullText: string,
  commentText: string,
): Promise<FixResult> {
  return withResolvedAnchor(anchorTag, expected, async (context, cc) => {
    // AIVA-9179: replace only the number. The anchor can be a run-in clause's
    // whole opening ("12.02  Provider Warranties.  Provider represents…"), and
    // rewriting all of it would flatten a bold title into the clause text and
    // show the reviewer a 200-character tracked change for a two-digit fix.
    const change = changedNumber(expected, correctFullText);
    let replaced = false;
    const anchored = cc.getRange();
    // A host (or test double) without Range.search keeps the whole-text replace.
    if (change && typeof anchored.search === "function") {
      const hits = anchored.search(change.from, { matchCase: true });
      hits.load("items");
      await context.sync();
      const first = hits.items[0];
      if (first) {
        first.insertText(change.to, Word.InsertLocation.replace);
        replaced = true;
      }
    }
    if (!replaced) cc.getRange().insertText(correctFullText, Word.InsertLocation.replace);
    await context.sync();
    // Both, not either/or: the tracked edit is the correction itself and the
    // comment is what tells the reviewer WHY it changed. Applying one without
    // the other leaves either an unexplained revision or an uncorrected
    // document.
    return insertCommentAndGetId(context, cc.getRange(), commentText);
  });
}

/**
 * Correct a clause whose number Word GENERATED, by putting the paragraph back
 * on the list it broke away from — Word then renumbers it to the value the
 * evaluation derived, with no text edit at all.
 *
 * This is the auto-numbered counterpart to `fixNumberingOrder` above, and it
 * exists because there is no number in the text to replace: confirmed against a
 * real document, Word implements a manual renumber ("Set Numbering Value") by
 * moving the paragraph to a NEW list, so moving it back is the correction.
 * Runs under `withTrackingForced` exactly like every other fix here, so the
 * numbering change is recorded as a revision rather than applied silently.
 *
 * Falls back to the comment alone — never failing outright — when the
 * re-attach is impossible or throws: a host that will not serve list identity
 * gives a null `targetListId`, and a document can always be corrected by hand
 * from the comment. Mirrors `fixByLinkingOrCommenting`'s own fallback
 * discipline, including logging rather than silently swallowing the reason.
 */
export async function fixNumberingOrderRelist(
  anchorTag: string,
  expected: string,
  targetListId: number | null,
  listLevel: number | null,
  problem: string,
  correctedNumber: string,
): Promise<FixResult> {
  return withResolvedAnchor(anchorTag, expected, async (context, cc) => {
    const renumbered = await reattachToList(context, cc, targetListId, listLevel, correctedNumber);
    // The comment says what actually happened, which is why it is composed
    // HERE and not at evaluation time. A comment reading "Corrected the
    // number to 15.6" beside a clause still numbered 10.2 is worse than no
    // comment at all: the reviewer trusts it and ships the broken document.
    return insertCommentAndGetId(
      context,
      cc.getRange(),
      renumbered
        ? `${problem} Corrected the number to ${correctedNumber}.`
        : `${problem} It should be ${correctedNumber}. This number is generated by Word's own list ` +
            `formatting rather than typed, and it could not be corrected automatically — set it on the ` +
            `list itself (Home > Multilevel List > Set Numbering Value).`,
    );
  });
}

/**
 * Put a paragraph back on `targetListId` at `listLevel`, reporting whether it
 * actually happened. Returns false — never throws — when there is no list to
 * return to or the host refuses the move, so the caller can tell the reviewer
 * the truth either way.
 *
 * DETACH FIRST, then attach, with a sync between: `attachToList` attaches a
 * paragraph that is not currently a list item, and throws `GeneralException`
 * on one that already belongs to a list — which every paragraph this fix
 * touches does, by definition. Verified against a real document: calling
 * `attachToList` alone threw, and detach-then-attach renumbered 10.2 to 15.5.
 *
 * If the attach fails AFTER the detach succeeded, the paragraph is left with no
 * numbering at all — a worse document than the out-of-sequence one we started
 * from — so that case puts it back on its original list before giving up.
 *
 * The result is then VERIFIED by reading the rendered number back, because a
 * call that does not throw is not proof that it worked. Anything other than
 * `expectedNumber` is rolled back and reported as a failure, so the document
 * and the comment can never disagree.
 */
async function reattachToList(
  context: Word.RequestContext,
  cc: Word.ContentControl,
  targetListId: number | null,
  listLevel: number | null,
  expectedNumber: string,
): Promise<boolean> {
  if (targetListId === null) return false;
  const paragraph = cc.getRange().paragraphs.getFirst();
  paragraph.load("listOrNullObject/id,listItemOrNullObject/level");
  await context.sync();
  const originalList = paragraph.listOrNullObject;
  const originalListId = originalList && !originalList.isNullObject && originalList.id != null ? originalList.id : null;
  const originalItem = paragraph.listItemOrNullObject;
  const originalLevel = originalItem && !originalItem.isNullObject ? (originalItem.level ?? 0) : 0;

  try {
    paragraph.detachFromList();
    await context.sync();
  } catch (err) {
    // Nothing has changed yet, so there is nothing to undo.
    logOfficeError(err, "word-docreview-numbering-detach-failed");
    return false;
  }

  try {
    paragraph.attachToList(targetListId, listLevel ?? 0);
    await context.sync();
  } catch (err) {
    logOfficeError(err, "word-docreview-numbering-relist-fallback");
    await restoreOriginalList(context, paragraph, originalListId, originalLevel);
    return false;
  }

  // Read the number back rather than trusting that a call which did not throw
  // did what it was asked. Confirmed against a real document: Word accepted
  // `attachToList` for a paragraph separated from its target list by an
  // unnumbered one, renumbered nothing, and raised no error — so the comment
  // claimed a correction the reader could see had not happened. A fix that
  // cannot prove it worked has to report that it did not.
  if ((await renderedNumber(context, paragraph)) === expectedNumber) return true;
  await restoreOriginalList(context, paragraph, originalListId, originalLevel);
  return false;
}

/** The number Word currently renders for a paragraph, digits only ("15.5"), or
 *  null when it is not a list item. */
async function renderedNumber(context: Word.RequestContext, paragraph: Word.Paragraph): Promise<string | null> {
  try {
    paragraph.load("listItemOrNullObject/listString");
    await context.sync();
    const item = paragraph.listItemOrNullObject;
    if (!item || item.isNullObject) return null;
    return HEADING_NUMBER.exec((item.listString ?? "").trim())?.[1] ?? null;
  } catch (err) {
    logOfficeError(err, "word-docreview-numbering-verify-failed");
    return null;
  }
}

/**
 * Put a paragraph back where it came from, after a re-attach that failed or
 * that Word accepted without renumbering.
 *
 * Detaches first, because by this point the paragraph may already be ON the
 * target list (the "accepted but renumbered nothing" case), and `attachToList`
 * throws on a paragraph that is still a list item — the same trap
 * `reattachToList` itself documents. Skipping that detach made this rollback
 * silently do nothing, leaving the clause on the wrong list.
 *
 * Best-effort by necessity — if this fails there is nothing further to try —
 * but logged rather than swallowed, because the document is then left with a
 * mis-numbered or unnumbered clause and that needs to be diagnosable.
 */
async function restoreOriginalList(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  originalListId: number | null,
  originalLevel: number,
): Promise<void> {
  if (originalListId === null) return;
  try {
    paragraph.detachFromList();
    await context.sync();
  } catch {
    // Already detached — the attach never landed. Nothing to undo first.
  }
  try {
    paragraph.attachToList(originalListId, originalLevel);
    await context.sync();
  } catch (err) {
    logOfficeError(err, "word-docreview-numbering-restore-failed");
  }
}

/** The nearest heading index in `headings` on the given side (`-1` for
 * before, `1` for after) whose text matches `HEADING_NUMBER` — used only by
 * `evaluateNumberingOrderHeadingAnomalies` below to find a candidate's numbered
 * neighbors. Returns `null` when no such heading exists on that side. */
function nearestNumberedNeighbor(
  headings: RawHeading[],
  hasNumber: boolean[],
  index: number,
  step: -1 | 1,
): number | null {
  for (let i = index + step; i >= 0 && i < headings.length; i += step) {
    if (hasNumber[i]) return i;
  }
  return null;
}

/**
 * Heuristic sibling to `evaluateNumberingOrder` above: that check only
 * ever looks at headings that already HAVE a leading number, so a heading
 * that's missing its number entirely — or was given the wrong Heading
 * style — is invisible to it, not flagged. Confirmed against a real planted
 * test document: a heading literally titled "Confidentiality (6.1 – 6.2)"
 * was styled Heading 2 while every sibling section ("5. Payment", "7.
 * Intellectual Property", ...) was Heading 1, and its own title carried no
 * "6." at all — silently skipped by the check above with zero findings.
 *
 * Flagging "any unnumbered heading" outright would be too broad: this same
 * document also has entirely legitimate unnumbered Heading 1 sections
 * ("TABLE OF CONTENTS", "SCHEDULE 1", "EXHIBIT A", etc.). The
 * distinguishing signal is heading LEVEL, not just the absence of a number:
 * those legitimate cases sit at the SAME level as their numbered neighbors,
 * while the planted defect sits at a DIFFERENT level than both the nearest
 * numbered heading before it and the nearest numbered heading after it (and
 * those two agree with each other, so there's a real "expected level" to
 * compare against — a heading with no numbered neighbor on one side, like a
 * trailing Schedule/Exhibit section, is left alone).
 *
 * A heuristic, not a certainty — a document that genuinely nests an
 * unnumbered sub-heading at a different level between two numbered
 * top-level sections would also trip this. Comment-only fix, like every
 * other judgment-flavoured rule here: there's no safe rewrite for "this
 * might be wrong," only a flag for the operator to check.
 */
export function evaluateNumberingOrderHeadingAnomalies(headings: RawHeading[]): FindingCreate[] {
  const occurrenceIndexByHeading = headingOccurrenceIndexByRef(headings);
  // headingOwnNumber, not HEADING_NUMBER: "Section 2. Engagement" is numbered
  // (AIVA-8297 retest — every typed "Section N." heading was flagged).
  const hasNumber = headings.map((h) => headingOwnNumber(h.text) !== null);
  const findings: FindingCreate[] = [];
  headings.forEach((heading, index) => {
    if (hasNumber[index]) return;
    const prev = nearestNumberedNeighbor(headings, hasNumber, index, -1);
    const next = nearestNumberedNeighbor(headings, hasNumber, index, 1);
    if (prev === null || next === null) return;
    const prevLevel = headings[prev]!.level;
    const nextLevel = headings[next]!.level;
    if (prevLevel !== nextLevel || heading.level === prevLevel) return;
    const commentText = `This heading has no section number and is styled at a different heading level (Heading ${heading.level}) than the numbered Heading ${prevLevel} sections around it — check whether it is missing its number or using the wrong heading style.`;
    findings.push(
      toFindingCreate({
        ruleId: "numbering_order",
        matchText: numberingAnchorText(heading.text),
        occurrenceIndex: occurrenceIndexByHeading.get(heading) ?? 0,
        problem: commentText,
        comment: commentText,
        fixHandler: "word.docreview.fix_numbering_order_flag",
        fixParams: { commentText },
      }),
    );
  });
  return findings;
}

/** Comment-only fix for `evaluateNumberingOrderHeadingAnomalies` — mirrors
 * `fixUsedNotDefined`'s own reasoning: there's no safe deterministic
 * rewrite for "this heading might be wrong," only a flag for the operator. */
export async function fixNumberingOrderHeadingAnomalyFlag(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// shortened_hyperlink_text — a cross-reference/hyperlink field's shown text was wrongly
// shortened (e.g. "Section 10.01(a)" collapsed down to just "(a)")
// ---------------------------------------------------------------------------

export interface RawHyperlinkField {
  matchText: string;
  fieldCode: string;
  occurrenceIndex: number;
  /** "" when no bookmark resolved (unsupported host, or the field carries
   *  none) — evaluate_r10 (backend) falls back to a comment in that case. */
  targetHeading: string;
}

export interface ShortenedHyperlinkTextSweep {
  fields: RawHyperlinkField[];
}

/** `body.fields` / `Field.code` / `Field.result` (WordApi 1.4) — universal,
 * unlike bookmark resolution (`getBookmarkRangeOrNullObject`,
 * WordApiDesktop 1.4 only). shortened_hyperlink_text's find step only needs field text and code,
 * so it works on Word on the web; a fix that needed the field's bookmark
 * target would not. */
export function supportsFields(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.4");
  } catch {
    return false;
  }
}

/**
 * Whether paragraph `i` of `paragraphs` sits inside any Table of Contents
 * field's own rendered range — the TOC-detection shape findBrokenReference,
 * findBracketHeadingMismatch and findExhibitScheduleMismatch all share (and
 * ensureBookmarkAndLinkTo's AIVA-8065 / findShortenedHyperlinkText's AIVA-8199
 * guards follow). "Inside" means Inside/InsideStart/InsideEnd/Equal.
 *
 * Both `paragraphs` and `fields` (`items/code`) must already be loaded. The
 * comparison costs one extra sync — `compareLocationWith` is an action, not a
 * loadable property — so it is skipped entirely when the document has no TOC
 * field, or the host does not support fields (`fields` is `null`), and every
 * paragraph then reads as outside.
 */
async function tocParagraphPredicate(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  fields: Word.FieldCollection | null,
): Promise<(paragraphIndex: number) => boolean> {
  const tocResultRanges = fields?.items.filter((f) => /^\s*TOC\b/i.test(f.code ?? "")).map((f) => f.result) ?? [];
  if (tocResultRanges.length === 0) return () => false;
  // AIVA-8925: only a paragraph whose text IS one of the TOC's own lines can be
  // inside it, so only those go to Word. This compared every paragraph with
  // every TOC field, one call per pair, in each of three sweeps. The TOC's
  // lines are read as PARAGRAPHS — paragraph text is what every sweep and the
  // occurrence counter already rely on — never as `Field.result.text`, which
  // Word can return as a field's first run only (§10 of the reference doc). A
  // blank paragraph is never asked about: no caller needs its answer.
  const tocLines = tocResultRanges.map((toc) => {
    const lines = toc.paragraphs;
    lines.load("items/text");
    return lines;
  });
  await context.sync();
  const tocLineTexts = tocLines.map(
    (lines) => new Set(lines.items.map((line) => squashedText(line.text).toLowerCase()).filter(Boolean)),
  );
  const comparisons = new Map<number, OfficeExtension.ClientResult<Word.LocationRelation>[]>();
  paragraphs.items.forEach((p, paragraphIndex) => {
    const text = squashedText(p.text).toLowerCase();
    if (!text) return;
    const holding = tocResultRanges.filter((_, i) => tocLineTexts[i]!.has(text));
    if (holding.length === 0) return;
    const paragraphRange = p.getRange();
    comparisons.set(
      paragraphIndex,
      holding.map((toc) => paragraphRange.compareLocationWith(toc)),
    );
  });
  if (comparisons.size === 0) return () => false;
  await context.sync();
  return (paragraphIndex: number) =>
    (comparisons.get(paragraphIndex) ?? []).some((cmp) =>
      (["Inside", "InsideStart", "InsideEnd", "Equal"] as Word.LocationRelation[]).includes(cmp.value),
    );
}

/** Whether a range sits inside (or is) any TOC field's result, given its
 *  `compareLocationWith` result against each one. */
function isInsideAnyToc(comparisons: ReadonlyArray<{ value: Word.LocationRelation }>): boolean {
  return comparisons.some((cmp) =>
    (["Inside", "InsideStart", "InsideEnd", "Equal"] as Word.LocationRelation[]).includes(cmp.value),
  );
}

/** Parse the bookmark name out of a REF field's own code, e.g.
 * `REF _Ref186613471 \h` → `_Ref186613471`. `null` if the code doesn't
 * start with REF. */
function parseRefBookmark(fieldCode: string): string | null {
  const match = /^REF\s+(\S+)/i.exec(fieldCode.trim());
  return match?.[1] ?? null;
}

/** Raw sweep only. Reads every REF/HYPERLINK field's displayed text and
 * field code — evaluate_r10 (backend) decides whether the text looks
 * wrongly shortened. Returns an empty sweep, not a thrown error, on a host
 * that doesn't support WordApi 1.4 fields.
 *
 * Also resolves each field's bookmark target (WordApiDesktop-only) when
 * available, so a genuine tracked-edit fix knows what the full reference
 * text should be — `targetHeading` stays "" on a host without desktop
 * bookmark resolution, or when the field carries none.
 */
/**
 * For each distinct text in *matchTexts*, the body-search positions that are
 * backed by a field — in field order (AIVA-8315).
 *
 * `shortened_hyperlink_text` anchors by searching the whole body for a bare
 * fragment like `"(a)"` and taking the Nth hit. Ordinary prose contains those
 * freely, so the Nth FIELD showing `"(a)"` is rarely the Nth `"(a)"` in the
 * document. This resolves the one into the other: it runs the same
 * `body.search` the anchor step will run, asks Word which of those ranges lie
 * inside a field's own result range, and returns those positions.
 *
 * Returns a map of counting-key → ascending body indices. An entry missing (or
 * shorter than the field count) means Word could not place a field against any
 * search hit; the caller falls back to the field ordinal, which is the
 * pre-AIVA-8315 behaviour — no worse than before, and never a crash.
 */
async function countBodyOccurrences(
  context: Word.RequestContext,
  matchTexts: readonly string[],
  /** Every body field, with `code` and `result.text` already loaded by the
   *  caller — read again here, they cost a sync and a full field reload. */
  fieldItems: readonly Word.Field[],
): Promise<Map<string, number[]>> {
  const result = new Map<string, number[]>();
  const distinct = [...new Set(matchTexts.map((t) => occurrenceCountKey(t)).filter((t) => t.trim()))];
  if (distinct.length === 0) return result;
  // A host (or a test double) that cannot search the body gets the
  // pre-AIVA-8315 behaviour rather than a thrown sweep: an empty map makes
  // every caller fall back to its field ordinal. Losing the corrected index
  // is a worse anchor; losing the whole sweep is no findings at all.
  if (typeof context.document.body.search !== "function") return result;

  // One search per distinct text, all queued into a single sync — the same
  // matchWholeWord/matchCase settings anchorChunkBatched uses, so the hit
  // list here IS the hit list the anchor step will see.
  const searchesByKey = new Map<string, WordBoundedSearch>();
  for (const key of distinct) {
    const original = matchTexts.find((t) => occurrenceCountKey(t) === key) ?? key;
    searchesByKey.set(key, queueWordBoundedSearch(context.document.body, original));
  }
  const paragraphs = loadOptionalParagraphs(context);
  await profiledStep(
    "    count: body searches",
    () => context.sync(),
    () =>
      [...searchesByKey.entries()]
        .map(([key, search]) => `${key}: ${search.strict.items.length} strict / ${search.loose.items.length} plain`)
        .slice(0, 12)
        .join(", "),
  );

  // The positions this returns ARE occurrence indices, so they are counted
  // over the same word-bounded hit list the anchor step anchors against — see
  // `wordBoundedHits` (AIVA-8547).
  const hitsByKey = wordBoundedHitLists(searchesByKey, paragraphTextsOf(paragraphs));
  const searches = distinct.map((key) => ({ key, items: hitsByKey.get(key) ?? [] }));

  // Which search hits sit inside a field's own result range? compareLocationWith
  // is the same test the TOC guard above uses.
  //
  // Only a field SHOWING this text can back the hit. Any other field that
  // merely contains it — a link reading "Section 4.02", or the whole TOC —
  // is not the link being placed, yet "Inside" it is just as true. Counting
  // those gave the REF field showing "4.02" the position of an earlier,
  // complete "Section 4.02" link, so the finding anchored onto a reference
  // that was never shortened.
  //
  // AIVA-8925: every key's comparisons go out in ONE sync — no key depends on
  // another's answer. This used to sync once per distinct text, in sequence.
  const comparisonsByKey = searches.map(({ key, items }) => {
    const showingKey = fieldItems.filter((f) => occurrenceCountKey(f.result.text ?? "") === key);
    return { key, comparisons: items.map((hit) => showingKey.map((f) => hit.compareLocationWith(f.result))) };
  });
  await profiledStep("    count: field compares", () => context.sync());
  for (const { key, comparisons } of comparisonsByKey) {
    const positions: number[] = [];
    comparisons.forEach((row, index) => {
      if (isInsideAnyToc(row)) positions.push(index);
    });
    result.set(key, positions);
  }
  return result;
}

/**
 * Append every `w:hyperlink`-element link whose text looks shortened, which
 * `body.fields` cannot report (AIVA-8315).
 *
 * Word stores a link two ways. `{ REF x \h }` / `{ HYPERLINK \l "x" }` are
 * FIELDS and appear in `body.fields`. `<w:hyperlink w:anchor="x">` — what
 * Insert > Links > Link > Place in This Document writes — is an ELEMENT with
 * no field code, absent from that collection entirely. Only the field form was
 * ever swept, so a hand-authored link reading just "(a)" was never a finding.
 *
 * Detection reuses `Range.hyperlink` (WordApi 1.3, universal), the same probe
 * `findMissingHyperlink` uses, so both rules agree on what counts as a link.
 *
 * *out* is appended in place, skipping any (text, occurrence) pair a field
 * already claimed — a `{ HYPERLINK }` FIELD also sets `Range.hyperlink`, so
 * without that check the same link would be reported twice.
 *
 * AIVA-8199 follow-up: `tocResultRanges` gives this path the same
 * inside-a-TOC awareness the field-based path already has — this path had
 * none at all when AIVA-8315 added it, silently reintroducing the exact bug
 * AIVA-8199 fixed for the more common link representation (per this
 * function's own docstring, a real MSA had 73 element-form links against
 * only 20 fields, so most TOC entries were flagged through here regardless
 * of the field path's own filter).
 */
async function collectShortenedHyperlinkRanges(
  context: Word.RequestContext,
  out: RawHyperlinkField[],
  tocResultRanges: Word.Range[],
): Promise<void> {
  // Links read whole, straight from Word, where the host can list them —
  // see `collectShortenedLinksFromHyperlinkRanges`. The word-by-word search
  // below is the fallback for a host that cannot.
  if (await collectShortenedLinksFromHyperlinkRanges(context, out, tocResultRanges)) return;
  await collectShortenedHyperlinkRangesBySearch(context, out, tocResultRanges);
}

/**
 * The fast path: `Range.getHyperlinkRanges()` (WordApi 1.3) lists the
 * document's LINKS, each one whole — the same read hyperlink_target_mismatch
 * uses (`collectHyperlinkTargetElementLinks`, AIVA-8310).
 *
 * AIVA-8315: the search fallback matches `\(?[A-Za-z0-9]@\)?`, i.e. EVERY word
 * in the document, then asks each one whether it is a link and whether it sits
 * in the contents page — about 2,200 ranges on a 10-page MSA, all to keep the
 * handful that are links. That pushed the whole review scan past its 60s limit
 * ("the document review scan timed out"). Listing the links instead costs one
 * range per link.
 *
 * Each link is numbered by `occurrenceIndicesOf` — Word says which of its
 * search hits IS the link — which is also how the field path numbers a field,
 * so a field-backed link both reads still dedupes on (text, occurrence).
 *
 * Returns false (nothing appended) when the host cannot list links, so the
 * caller falls back; true once it has handled the document either way.
 */
async function collectShortenedLinksFromHyperlinkRanges(
  context: Word.RequestContext,
  out: RawHyperlinkField[],
  tocResultRanges: Word.Range[],
): Promise<boolean> {
  const body = context.document.body;
  if (typeof body.getRange !== "function" || typeof body.search !== "function") return false;
  let linkRanges: Word.RangeCollection;
  try {
    linkRanges = body.getRange(Word.RangeLocation.whole).getHyperlinkRanges();
    linkRanges.load("items/text,items/hyperlink");
    const listed = linkRanges;
    await profiledStep(
      "    links: list every link",
      () => context.sync(),
      () => `${listed.items.length} links`,
    );
  } catch (err) {
    console.warn("[docReview] shortened_hyperlink_text: getHyperlinkRanges unavailable; falling back to search", err);
    return false;
  }

  // Only links that already LOOK shortened go any further. "^" starts a Word
  // search code (^p, ^t), so such text cannot be searched for literally.
  const links = linkRanges.items.flatMap((range) => {
    const text = (range.text ?? "").trim();
    if (!text || !range.hyperlink || !looksShortenedText(text) || text.includes("^")) return [];
    return [{ range, text }];
  });
  if (links.length === 0) return true;

  // Same TOC test as the field path: inside a TOC field's own result range.
  const tocComparisons = links.map((l) => tocResultRanges.map((toc) => l.range.compareLocationWith(toc)));
  if (tocResultRanges.length > 0) {
    await profiledStep(
      "    links: TOC check",
      () => context.sync(),
      () => `${links.length} short-looking links`,
    );
  }
  const insideToc = (i: number) =>
    (tocComparisons[i] ?? []).some((cmp) =>
      (["Inside", "InsideStart", "InsideEnd", "Equal"] as Word.LocationRelation[]).includes(cmp.value),
    );

  const placed = await profiledStep("    links: place each link", () => occurrenceIndicesOf(context, links));
  const claimed = new Set(out.map((f) => `${occurrenceCountKey(f.matchText)}\u001f${f.occurrenceIndex}`));
  const shortened = links.flatMap((link, i) => {
    const occurrenceIndex = placed[i] ?? -1;
    // A link Word cannot place among its own hits has no index the anchor
    // step could use; skipped rather than given a guessed one.
    if (occurrenceIndex === -1 || insideToc(i)) return [];
    if (claimed.has(`${occurrenceCountKey(link.text)}\u001f${occurrenceIndex}`)) return [];
    return [{ ...link, occurrenceIndex }];
  });

  // AIVA-8547: a link the text before it already names ("Section " + "10.3").
  const leadsIn = await referenceWordLeadsInto(
    context,
    shortened.map((s) => s.range),
  );
  shortened.forEach(({ range, text, occurrenceIndex }, i) => {
    if (leadsIn[i]) return;
    out.push({
      matchText: text,
      // No field code exists for an element-form link; the hyperlink address
      // is the closest equivalent and is what a reader needs to see.
      fieldCode: `HYPERLINK ${range.hyperlink}`,
      occurrenceIndex,
      targetHeading: "",
    });
  });
  return true;
}

/** The pre-`getHyperlinkRanges` path, kept for a host that cannot list links:
 *  searches every word and keeps the ones that are links. */
async function collectShortenedHyperlinkRangesBySearch(
  context: Word.RequestContext,
  out: RawHyperlinkField[],
  tocResultRanges: Word.Range[],
): Promise<void> {
  const body = context.document.body;
  // An older host (or a test double) without search simply contributes
  // nothing here; the field-based findings above still stand.
  if (typeof body.search !== "function") return;

  // A shortened reference is a bare bracketed letter or number. Searching for
  // the shape directly is far cheaper than walking every paragraph, and the
  // wildcard syntax is Word's own.
  const collection = body.search(String.raw`\(?[A-Za-z0-9]@\)?`, { matchWildcards: true });
  collection.load("items/text");
  await context.sync();

  const ranges = collection.items;
  for (const r of ranges) r.load("hyperlink");
  await context.sync();

  // Same compareLocationWith approach as the field path above: a range is
  // "inside a TOC" when it compares Inside/InsideStart/InsideEnd/Equal
  // against any TOC field's own result range.
  const locationComparisons = ranges.map((r) => tocResultRanges.map((toc) => r.compareLocationWith(toc)));
  await context.sync();
  const insideToc = (i: number) => isInsideAnyToc(locationComparisons[i] ?? []);

  // Position among ALL body matches of the same text, exactly as
  // countBodyOccurrences computes it for the field path — the anchor step
  // searches the whole body either way. Counted for EVERY match, TOC
  // entries included, same reasoning as the field path's own seenCount —
  // only the push below is filtered.
  const seen = new Map<string, number>();
  const claimed = new Set<string>();
  for (const f of out) claimed.add(`${occurrenceCountKey(f.matchText)}\u001f${f.occurrenceIndex}`);

  // Every match is counted; only the links that pass every other filter are
  // collected, so the leading text below is read for those few only.
  const shortened: Array<{ range: Word.Range; text: string; occurrenceIndex: number }> = [];
  ranges.forEach((range, i) => {
    const text = (range.text ?? "").trim();
    if (!text) return;
    const key = occurrenceCountKey(text);
    const occurrenceIndex = seen.get(key) ?? 0;
    seen.set(key, occurrenceIndex + 1);

    if (!range.hyperlink) return;
    if (!looksShortenedText(text)) return;
    if (claimed.has(`${key}\u001f${occurrenceIndex}`)) return;
    if (insideToc(i)) return;
    shortened.push({ range, text, occurrenceIndex });
  });

  // AIVA-8547: a link the text before it already names ("Section " + "10.3")
  // is a full reference — same check the field path makes, so a field-form
  // link it skipped is not reported through this path instead.
  const leadsIn = await referenceWordLeadsInto(
    context,
    shortened.map((s) => s.range),
  );
  shortened.forEach(({ range, text, occurrenceIndex }, i) => {
    if (leadsIn[i]) return;
    out.push({
      matchText: text,
      // No field code exists for an element-form link; the hyperlink address
      // is the closest equivalent and is what a reader needs to see.
      fieldCode: `HYPERLINK ${range.hyperlink}`,
      occurrenceIndex,
      targetHeading: "",
    });
  });
}

export function findShortenedHyperlinkText(
  /** AIVA-8925: the scan keeps only fields whose text looks shortened — the
   *  only ones that can become findings. The standalone tool
   *  (`word.docreview.find_shortened_hyperlink_text`) passes false and keeps
   *  its documented contract of every REF/HYPERLINK field. */
  { shortenedOnly = true }: { shortenedOnly?: boolean } = {},
): Promise<ShortenedHyperlinkTextSweep> {
  if (!supportsFields()) return Promise.resolve({ fields: [] });
  return Word.run(async (context) => {
    const fields = context.document.body.fields;
    fields.load("items/code,items/result/text");
    await profiledStep(
      "shortened: read fields",
      () => context.sync(),
      () => `${fields.items.length} fields`,
    );

    // A Table of Contents field's own rendered entries are themselves nested
    // HYPERLINK fields (Word's native "use hyperlinks" TOC generates one per
    // entry, pointing at the heading's own bookmark) — their display text is
    // the TOC's own condensed style, not a cross-reference a human wrote, so
    // the "was this shortened" heuristic below has no business flagging them
    // (AIVA-8199). Same TOC-detection approach as ensureBookmarkAndLinkTo's
    // own guard (AIVA-8065): a field is "inside a TOC" when its own result
    // range compares Inside/InsideStart/InsideEnd/Equal against any TOC
    // field's own result range.
    const tocResultRanges = fields.items.filter((f) => /^\s*TOC\b/i.test(f.code ?? "")).map((f) => f.result);

    const candidates: Array<{ matchText: string; fieldCode: string; bookmark: string | null; field: Word.Field }> =
      [];
    fields.items.forEach((f) => {
      const code = (f.code ?? "").trim();
      const isRef = /^REF\b/i.test(code);
      const isHyperlink = /^HYPERLINK\b/i.test(code);
      if (!isRef && !isHyperlink) return;
      const bookmark = isRef ? parseRefBookmark(code) : parseHyperlinkBookmark(code);
      // A HYPERLINK field with no bookmark is an ordinary EXTERNAL link
      // ("click here", a URL) — not a cross-reference at all, so shortened_hyperlink_text's "was
      // this shortened" heuristic (a short bare word/number) has no
      // business examining it; a link literally reading "here" would
      // otherwise false-match as a shortened reference. REF fields are
      // always internal, so they never need this check.
      if (isHyperlink && !bookmark) return;
      // AIVA-8925: only a field whose text LOOKS shortened can become a finding
      // (`evaluateShortenedHyperlinkText` drops every other), so nothing else is
      // worth a search, a TOC check or a bookmark lookup. On a document with a
      // hyperlinked TOC or many "Section 4.02" REF fields that was nearly every
      // field. Occurrence counting is per text, and a text that looks shortened
      // never shares a key with one that does not, so no kept index moves.
      const matchText = f.result.text ?? "";
      if (shortenedOnly && !looksShortenedText(matchText.trim())) return;
      candidates.push({ matchText, fieldCode: code, bookmark, field: f });
    });

    const locationComparisons = candidates.map((c) => tocResultRanges.map((toc) => c.field.result.compareLocationWith(toc)));
    await profiledStep(
      "shortened: TOC check",
      () => context.sync(),
      () => `${candidates.length} candidate fields`,
    );
    const insideToc = (i: number) => isInsideAnyToc(locationComparisons[i] ?? []);

    // occurrenceIndex must count "the Nth time THIS exact matchText appears",
    // matching what a live body.search(matchText) at anchor time will find —
    // the same reasoning as missing_hyperlink's/topic_mismatch's/inconsistent_formatting's/exhibit_schedule_mismatch's own seenCount. That live
    // search (anchorChunkBatched) is a plain text search over the whole body
    // and so is TOC-INCLUSIVE — a TOC entry's own occurrence must still
    // increment this count even though it's filtered out of the findings
    // below, or every same-text candidate AFTER a TOC entry would silently
    // point anchoring at the wrong live match (the exact class of bug
    // AIVA-8065's occurrenceIndex audit fixed elsewhere in this file).
    //
    // AIVA-8315 — and it is not enough to count only the FIELDS. This rule's
    // matchText is a bare fragment like "(a)", which ordinary prose contains
    // freely: a sub-clause list reading "information that: (a) ... (b) ..."
    // holds several, none of them a field. Counting field candidates alone
    // numbered the one real shortened link as occurrence 0 while the live
    // body search saw it as the third "(a)" in the document — so the anchor
    // landed on a plain-text "(a)" in an unrelated clause. Confirmed against
    // a real MSA: three "(a)" in the body, only the last a hyperlink, and the
    // finding anchored onto the first.
    //
    // So the count is taken over the BODY TEXT, exactly as the anchor search
    // will read it, and each field claims the next unclaimed position of its
    // own text. findBrokenReference does the same thing for the same reason:
    // every candidate increments, only the push is filtered.
    const bodyOccurrenceCounts = await profiledStep(
      "shortened: count body occurrences",
      () =>
        countBodyOccurrences(
          context,
          candidates.map((c) => c.matchText),
          fields.items,
        ),
      (counts) => `${counts.size} distinct texts: ${[...counts.keys()].slice(0, 12).join(" | ")}`,
    );

    // AIVA-8547: "Section " typed + a field showing "10.3" is a full reference.
    // Read only for fields that have the shortened shape and are not TOC entries.
    const leadsIn = await profiledStep("shortened: lead-in reads", () =>
      referenceWordLeadsInto(
        context,
        candidates.map((c, i) => (!insideToc(i) && looksShortenedText(c.matchText.trim()) ? c.field.result : null)),
      ),
    );

    const seenCount = new Map<string, number>();
    const out: RawHyperlinkField[] = [];
    const kept: Array<{ bookmark: string | null }> = [];
    candidates.forEach((c, i) => {
      const key = occurrenceCountKey(c.matchText);
      // Where this field sits among the live body matches of its own text.
      // Fields are enumerated in document order and body.search returns hits
      // in document order too, so the Nth field bearing this text is the Nth
      // *field-backed* body match — the plain-text hits before it are what
      // shift the index, and bodyOccurrenceCounts is what accounts for them.
      const fieldOrdinal = seenCount.get(key) ?? 0;
      seenCount.set(key, fieldOrdinal + 1);
      const occurrenceIndex = bodyOccurrenceCounts.get(key)?.[fieldOrdinal] ?? fieldOrdinal;
      // Counted above either way; only the push is skipped — same as a TOC entry.
      if (insideToc(i) || leadsIn[i]) return;
      out.push({ matchText: c.matchText, fieldCode: c.fieldCode, occurrenceIndex, targetHeading: "" });
      kept.push({ bookmark: c.bookmark });
    });

    // AIVA-8315 — everything above reads `body.fields`, which returns ONLY
    // field-shaped links: `{ REF x \h }` from Insert > Cross-reference, and
    // the older `{ HYPERLINK \l "x" }`. Word's OTHER representation, a
    // `w:hyperlink` element, carries no field code at all, so `body.fields`
    // never reports it and this rule could not see it however short its text
    // was. That is what Insert > Links > Link > Place in This Document
    // produces, and it is the common way to author one by hand — confirmed
    // against a real MSA: 73 hyperlink elements, 20 fields, and not one
    // cross-reference field among them.
    //
    // `Range.hyperlink` (WordApi 1.3, universal) is how missing_hyperlink
    // already detects a link, and it reads the RANGE rather than the field,
    // so it sees both representations. Reusing it here keeps the two rules
    // answering "is this a link?" the same way.
    await profiledStep("shortened: element links", () => collectShortenedHyperlinkRanges(context, out, tocResultRanges));

    if (supportsBookmarks()) {
      // Sequential, like hyperlink_target_mismatch's own resolution loop: each bookmark's range
      // and paragraph aren't known until it resolves, so this can't be
      // batched into a single sync.
      // Bounded by `kept`, not by `out`: collectShortenedHyperlinkRanges
      // appends element-form links beyond the field-derived entries, and
      // those carry no parsed bookmark to resolve (AIVA-8315). They keep
      // targetHeading: "" — the same fallback a field with no bookmark
      // already gets, which the backend renders as a comment.
      // AIVA-8925: all lookups queued, one sync — an OrNullObject chain needs no
      // sync between its steps (same as `queueLinkTarget`). This used to sync
      // once per field, in sequence.
      const lookups = kept.map(({ bookmark }) => {
        if (!bookmark) return null;
        const range = context.document.getBookmarkRangeOrNullObject(bookmark);
        range.load("isNullObject");
        const para = range.paragraphs.getFirstOrNullObject();
        para.load("isNullObject,text");
        return { range, para };
      });
      if (lookups.some(Boolean)) {
        await profiledStep(
          "shortened: bookmark lookups",
          () => context.sync(),
          () => `${lookups.filter(Boolean).length} bookmarks`,
        );
      }
      lookups.forEach((lookup, i) => {
        if (!lookup || lookup.range.isNullObject || lookup.para.isNullObject) return;
        out[i]!.targetHeading = lookup.para.text;
      });
    }

    return { fields: out };
  });
}

/**
 * The text just BEFORE a link already supplies the reference word, so the
 * reader sees a full reference and the link is not shortened (AIVA-8547).
 *
 * Insert > Cross-reference's usual result is "Section " typed as plain text
 * and a `{ REF _Ref… \r \h }` field showing only "10.3" — and this rule, which
 * read the FIELD alone, flagged that "10.3" as a shortened reference. Worse,
 * its fix replaced the field text with "Section 10.3", leaving the document
 * reading "Section Section 10.3".
 *
 * Three shapes lead in, all ending exactly where the link starts:
 * - the word itself: "Section ", "Articles ";
 * - the word and a label the link continues: "Section 10.3" + "(a)";
 * - a plural list continuing: "Sections 10.2, " / "Sections 10.2 and " (a "." inside
 *   a number does not end the list; a full stop does).
 * Same word list as `HAS_REFERENCE_WORD`.
 */
const REFERENCE_WORD_LEADS_IN = new RegExp(
  [
    String.raw`\b(?:section|article|clause|exhibit|schedule)s?[\s\u00a0]*$`,
    String.raw`\b(?:section|article|clause|exhibit|schedule)s?[\s\u00a0]+[a-z0-9]+(?:\.[a-z0-9]+)*$`,
    String.raw`\b(?:section|article|clause|exhibit|schedule)s[\s\u00a0](?:[^.;:]|\.(?=[0-9a-z])){0,80}?(?:,|\band|\bor|\bto|\bthrough)[\s\u00a0]*$`,
  ].join("|"),
  "i",
);

/** Queue a read of the paragraph text that runs up to where `range` starts.
 *  `null` when the host (or a test double) cannot express that range. */
function queueTextLeadingInto(range: Word.Range): Word.Range | null {
  const paragraphs = (range as { paragraphs?: Word.ParagraphCollection }).paragraphs;
  if (typeof paragraphs?.getFirst !== "function" || typeof range.getRange !== "function") return null;
  const before = paragraphs.getFirst().getRange(Word.RangeLocation.start).expandTo(range.getRange(Word.RangeLocation.start));
  before.load("text");
  return before;
}

/** For each range, whether the text leading into it already carries the
 *  reference word — see `REFERENCE_WORD_LEADS_IN`. `false` wherever that
 *  cannot be read, so a host without the API keeps the previous behaviour
 *  exactly; one extra sync, and only when something was queued. */
async function referenceWordLeadsInto(context: Word.RequestContext, ranges: ReadonlyArray<Word.Range | null>): Promise<boolean[]> {
  try {
    const queued = ranges.map((r) => (r ? queueTextLeadingInto(r) : null));
    if (queued.every((q) => q === null)) return ranges.map(() => false);
    await context.sync();
    return queued.map((q) => q !== null && REFERENCE_WORD_LEADS_IN.test(q.text ?? ""));
  } catch (err) {
    console.warn("[docReview] shortened_hyperlink_text: could not read the text before a link; checking the link alone", err);
    return ranges.map(() => false);
  }
}

/** Whether a link's own text has the shortened SHAPE — the only links worth
 *  reading the surrounding text for. */
function looksShortenedText(text: string): boolean {
  return LOOKS_SHORTENED.test(text) && !HAS_REFERENCE_WORD.test(text);
}

// A cross-reference/hyperlink field's shown text that looks like it was
// wrongly shortened down to a bare sub-clause letter or number, dropping the
// "Section"/"Article"/... prefix that made it a real reference. Mirrors
// rules.py's `_LOOKS_SHORTENED` (`HAS_REFERENCE_WORD` is shared more widely —
// see its own declaration near the top of the file, with `NUMERIC_LABEL`).
const LOOKS_SHORTENED = /^\(?[a-z0-9]+(?:\.[a-z0-9]+)*(?:\([a-z0-9]+\))?\)?$/i;


/** Deterministic evaluation, ported 1:1 from rules.py's `evaluate_r10` — a
 * genuine tracked-edit fix when the field's bookmark target resolved
 * (desktop-only), falling back to a comment otherwise. */
export function evaluateShortenedHyperlinkText(sweep: ShortenedHyperlinkTextSweep): FindingCreate[] {
  const findings: FindingCreate[] = [];
  for (const field of sweep.fields) {
    const text = field.matchText.trim();
    if (!text || !LOOKS_SHORTENED.test(text) || HAS_REFERENCE_WORD.test(text)) continue;
    const headingMatch = field.targetHeading ? HEADING_NUMBER.exec(field.targetHeading) : null;
    if (headingMatch?.[1]) {
      const corrected = `Section ${headingMatch[1]}`;
      findings.push(
        toFindingCreate({
          ruleId: "shortened_hyperlink_text",
          matchText: field.matchText,
          problem: `"${field.matchText}" looks like a cross-reference whose visible text was shortened.`,
          comment: `Restored the full reference text (${corrected}).`,
          fixHandler: "word.docreview.fix_shortened_hyperlink_text",
          fixParams: { correctFullText: corrected },
          occurrenceIndex: field.occurrenceIndex,
        }),
      );
    } else {
      findings.push(
        toFindingCreate({
          ruleId: "shortened_hyperlink_text",
          matchText: field.matchText,
          problem: `"${field.matchText}" looks like a cross-reference whose visible text was shortened.`,
          comment: "cross-reference text may have been wrongly shortened",
          fixHandler: "word.docreview.fix_shortened_hyperlink_text_flag",
          fixParams: { commentText: "cross-reference text may have been wrongly shortened" },
          occurrenceIndex: field.occurrenceIndex,
        }),
      );
    }
  }
  return findings;
}

/** Restore a cross-reference field's full text — "Section " plus the
 * resolved target heading's own leading number, computed by evaluate_r10
 * (backend). A genuine tracked-edit replace, the same primitive bracket_heading_mismatch/numbering_order
 * already use. */
export async function fixShortenedHyperlinkText(
  anchorTag: string,
  expected: string,
  correctFullText: string,
): Promise<FixResult> {
  return replaceAnchorText(anchorTag, expected, correctFullText);
}

/** Flag a cross-reference field whose shown text looks wrongly shortened,
 * with no resolved bookmark target to restore the full text from (see
 * `fixShortenedHyperlinkText` for when there is one).
 * Comment-only. */
export async function fixShortenedHyperlinkTextFlag(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// hyperlink_target_mismatch — a clickable cross-reference must jump to the correct section
// (LLM judgment — this sweep only resolves what a hyperlink actually
// points to, never decides whether that's the right place)
// ---------------------------------------------------------------------------

export interface RawHyperlinkTarget {
  matchText: string;
  /** "" when the bookmark never resolved. Read together with
   *  `targetMissing`: empty + targetMissing means the link is DEAD (its
   *  bookmark is gone), empty + !targetMissing means the target resolved to
   *  genuinely empty text. */
  targetHeading: string;
  targetText: string;
  occurrenceIndex: number;
  /** AIVA-8310 — the link names a bookmark that does not exist in this
   *  document, so clicking it goes nowhere. Previously indistinguishable
   *  from "resolved to nothing" and dropped before the backend ever saw it;
   *  it is now a deterministic finding, with no LLM judgment involved (the
   *  link is objectively broken — there is nothing to weigh up). */
  targetMissing: boolean;
  /** AIVA-8310 reopened — does the label the link names ("Section 10",
   *  "Exhibit B") agree with the heading it opens? Decided here, with the
   *  same reference and heading readers every other rule uses, because it is
   *  a fact: the backend's judge compares TOPICS and a bare label has none.
   *  See `checkLinkLabel`. */
  labelCheck: LinkLabelCheck;
  /** The ONE heading the link's own label names, for the re-point fix; ""
   *  when zero or several headings carry it. */
  correctHeading: string;
  /** "Service Credits" out of "Section 14 (Service Credits)"; "" when the
   *  link names no topic. All that is left for the judge once the label
   *  matches. */
  linkTopic: string;
  /** AIVA-8310: the label is wrong but the link is right — the title in
   *  brackets written with it names the heading it opens: "Exhibit B (the
   *  "Statement of Work")" opening Exhibit A, the Statement of Work. */
  bracketNamesTarget?: boolean;
}

export type LinkLabelCheck = "match" | "mismatch" | "unknown";

export interface HyperlinkTargetMismatchSweep {
  targets: RawHyperlinkTarget[];
}

const HYPERLINK_TARGET_SNIPPET_LENGTH = 300;

/**
 * The heading a link lands on, as sent to the judge — the paragraph's own
 * heading text, never the whole clause it shares a line with.
 *
 * AIVA-8310 (reopened): this was the raw `para.text`. In a document whose
 * clauses are written run-in (`7.03  Client Materials.  Client shall…`) that
 * is the entire clause — 559 characters on QA's 24-page MSA. The backend
 * bounds `targetHeading` at 500, so one such link failed validation for the
 * whole sweep and no link in the document was judged. The same failure
 * AIVA-8540's follow-up fixed for `RawHeading.text`, and fixed the same way:
 * `headingTextOf` keeps `7.03  Client Materials.`.
 *
 * A link can also land on a plain body paragraph that is not heading-shaped
 * at all, which `headingTextOf` rightly leaves whole — so the result is still
 * capped. The judge only needs the opening words to know what a section is
 * about; `targetText` already carries the same cap for the same reason.
 */
function hyperlinkTargetHeadingText(paragraphText: string): string {
  const number = HEADING_NUMBER.exec(paragraphText);
  const heading = number ? headingTextOf(paragraphText, number[0].length) : paragraphText;
  return heading.slice(0, HYPERLINK_TARGET_SNIPPET_LENGTH);
}

/** What may follow a heading's category word when it is the whole heading: `14`, `A`, `4.02`. */
const BARE_HEADING_LABEL = /^\s+[A-Z0-9]{1,6}(?:\.\d+)*$/i;

/** A heading that is a label and nothing else — `ARTICLE 14`, `EXHIBIT A`,
 *  `14.` — whose title is the NEXT paragraph. Read with the same category
 *  reader (`headingKind`) the label check uses, so the two agree on which
 *  words name a heading's kind. */
function isLabelOnlyHeading(heading: string): boolean {
  const withoutStop = heading.replace(/\.$/, "");
  if (NUMERIC_LABEL.test(withoutStop)) return true;
  const kind = headingKind(withoutStop);
  return kind !== "" && BARE_HEADING_LABEL.test(withoutStop.slice(kind.length));
}

/**
 * What a link lands on, as the judge needs to see it (AIVA-8310 reopened).
 *
 * Two heading conventions hid the very thing the judge compares:
 *
 * - **The title on its own line.** QA's 24-page MSA writes `ARTICLE 14` and,
 *   as the next paragraph, `LIMITATION OF LIABILITY`. Reading only the
 *   bookmark's paragraph sent the judge `ARTICLE 14` with no topic, so
 *   `Section 14 (Service Credits)` was waved through. The same blind spot the
 *   complete reference lists for bracket_heading_mismatch.
 * - **A number Word adds itself.** An auto-numbered heading's number is list
 *   formatting, not paragraph text (the AIVA-8297 lesson), so the backend
 *   could not read which section a link had opened. `listString` restores it.
 *
 * `text` gathers the opening of what follows, because a bookmark often covers
 * the heading line alone and the judge would otherwise see the label twice.
 */
function describeLinkTarget(
  rangeText: string,
  paragraphText: string,
  listString: string,
  followingTexts: readonly string[],
): { heading: string; text: string } {
  const own = paragraphText.trim();
  const numbered = listString && !own.startsWith(listString) ? `${listString} ${own}` : own;
  let heading = hyperlinkTargetHeadingText(numbered).trim();
  // The same title reader exhibit_schedule_mismatch and bracket_heading_mismatch use.
  const title = isLabelOnlyHeading(heading) ? titleBelowLabelHeading([heading, ...followingTexts], 0, () => false) : undefined;
  if (title) heading = `${heading} — ${title}`;
  const opening = [rangeText.trim() || numbered, ...followingTexts.map((t) => t.trim())].filter(Boolean).join(" ");
  return {
    heading: heading.slice(0, HYPERLINK_TARGET_SNIPPET_LENGTH),
    text: opening.slice(0, HYPERLINK_TARGET_SNIPPET_LENGTH),
  };
}

/** The paragraph after `paragraph`, or null on a host (or test double)
 *  without `getNextOrNullObject`. */
function nextParagraphOf(paragraph: Word.Paragraph | null): Word.Paragraph | null {
  return paragraph && typeof paragraph.getNextOrNullObject === "function" ? paragraph.getNextOrNullObject() : null;
}

interface LinkLabel {
  /** "section" / "article" / "clause" / "exhibit" / "schedule", lowercased. */
  category: string;
  /** "10", "4.02" (sub-clause letter stripped) or a letter, "B". */
  label: string;
  /** The reference as written, "Exhibit B". */
  text: string;
}

/**
 * The label a link's own text names — its FIRST one, so
 * `Section 10 (formerly Section 9)` names Section 10.
 *
 * Read with broken_reference's own reference patterns rather than a pattern of
 * this rule's own, so this rule recognises exactly the references every other
 * rule does and the vocabulary has one definition, not two. A link whose whole
 * text is a number (`14.02`, which QA's 24-page MSA has) reads as a section.
 *
 * @returns the label, or `null` when the text names none — "the Services", "here".
 */
function linkOwnLabel(linkText: string): LinkLabel | null {
  const numeric = new RegExp(BROKEN_REFERENCE_NUMERIC_PATTERN.source, "i").exec(linkText);
  const letter = new RegExp(BROKEN_REFERENCE_LETTER_SCAN.source, "i").exec(linkText);
  // An upper-case letter only, as broken_reference accepts: "Schedule a" is prose.
  const lettered = letter && /^[A-Z]$/.test(letter[2] ?? "") ? letter : null;
  const first = [numeric, lettered]
    .filter((m): m is RegExpExecArray => m !== null)
    .sort((a, b) => a.index - b.index)[0];
  if (first) return labelOf(first[1]!.toLowerCase(), stripSubclauseLetter(first[2]!), first[0]);
  const bare = linkText.trim();
  if (!NUMERIC_LABEL.test(bare)) return null;
  return labelOf("section", bare, bare);
}

function labelOf(category: string, label: string, text: string): LinkLabel {
  return { category, label, text };
}

/** "4.02" and "4.2" are the same number, so parts compare as integers. */
function numberParts(label: string): number[] {
  return label.split(".").map(Number);
}

/** One number lies within the other: equal, or one is the other's parent. */
function numbersOverlap(a: string, b: string): boolean {
  const [x, y] = [numberParts(a), numberParts(b)];
  return x.slice(0, Math.min(x.length, y.length)).every((part, i) => part === y[i]);
}

function sameNumber(a: string, b: string): boolean {
  return numberParts(a).length === numberParts(b).length && numbersOverlap(a, b);
}

/**
 * Does a link's label agree with the heading it opens? AIVA-8310 reopened.
 *
 * A fact, not a judgment, so it is decided here and never by the backend's
 * judge — which compares TOPICS, and "Section 10" opening "Section 7.
 * Intellectual Property" has none, so the same link was flagged on one run
 * and passed on the next. Built only from readers the other rules already
 * share: `headingOwnNumber` (both "4.01  Fees" and "Section 4. Fees"),
 * `referenceCategory`, and the explicit-prefix rule for Exhibits and
 * Schedules, matched on heading TEXT as `letteredTargetExists` does.
 *
 * A link may land on the section it names or anywhere inside it, and a
 * sub-clause link on its parent: "Section 4" opening "4.02" and "Section
 * 4.02" opening "Section 4" both land in the right section. "unknown" — the
 * judge decides, as before — whenever either side cannot be read with
 * confidence, so an unanticipated shape costs an LLM call, never a false
 * finding.
 */
/** The kind of thing a HEADING names, "" for none. `referenceCategory` alone
 *  matches a prefix, which is right for a reference's own text but would read
 *  a heading titled "Scheduled Maintenance" as a Schedule — so the word must
 *  also end where the heading's label begins. */
function headingKind(heading: string): string {
  const category = referenceCategory(heading);
  return category && /^\s/.test(heading.slice(category.length)) ? category : "";
}

function checkLinkLabel(link: LinkLabel | null, headingText: string): LinkLabelCheck {
  if (!link) return "unknown";
  const heading = headingText.trim();
  const headingCategory = headingKind(heading);
  if (isExplicitPrefixCategory(link.text)) {
    // "Schedule 2" opening "2. Key Personnel" may be that schedule's own
    // numbered clause; only a heading that names its kind can be compared.
    if (!headingCategory) return "unknown";
    if (headingCategory !== link.category) return "mismatch";
    return labelAppearsIn(link.text.toLowerCase(), heading.toLowerCase()) ? "match" : "mismatch";
  }
  const headingNumber = headingOwnNumber(heading);
  if (headingNumber && NUMERIC_LABEL.test(link.label)) {
    return numbersOverlap(link.label, headingNumber) ? "match" : "mismatch";
  }
  // A section link landing on an Exhibit or Schedule is wrong whatever the number.
  return headingCategory === "exhibit" || headingCategory === "schedule" ? "mismatch" : "unknown";
}

/** The ONE heading a link's label names exactly, for the re-point fix — ""
 *  when zero or several carry it. Exact, unlike `checkLinkLabel`: re-pointing
 *  "Section 4" at 4.02 because it lies inside Section 4 would be a guess. */
function headingNamedBy(link: LinkLabel | null, headings: readonly RawHeading[]): string {
  if (!link) return "";
  const explicit = isExplicitPrefixCategory(link.text);
  const needle = link.text.toLowerCase();
  const named = headings.filter((h) => {
    const text = (h.text ?? "").trim();
    if (explicit) return headingKind(text) === link.category && labelAppearsIn(needle, text.toLowerCase());
    const own = headingOwnNumber(text);
    return own !== null && NUMERIC_LABEL.test(link.label) && sameNumber(own, link.label);
  });
  return named.length === 1 ? named[0]!.text : "";
}

/** "Service Credits" out of "Section 14 (Service Credits)" — read with
 *  bracket_heading_mismatch's own pattern. A bracket carrying a number names
 *  a place, not a topic: "(formerly Section 9)". */
function bracketTopicOf(linkText: string): string {
  const topic = new RegExp(BRACKET_HEADING_MISMATCH_PATTERN.source).exec(linkText)?.[3]?.trim() ?? "";
  return topic && !bracketNamesAPlace(topic) ? topic : "";
}

/** Parse the bookmark name out of a HYPERLINK field's own code, e.g.
 * `HYPERLINK \l "_Ref186613471"` → `_Ref186613471`. `null` for an external
 * URL (no `\l` switch) — nothing for this rule to resolve. */
function parseHyperlinkBookmark(fieldCode: string): string | null {
  const match = /\\l\s+"([^"]+)"/.exec(fieldCode);
  return match?.[1] ?? null;
}

/** Parse the bookmark name out of a `Range.hyperlink` address — the element
 * form's equivalent of a field code. An internal link reads `#bookmark`
 * (what missing_hyperlink's own fix writes) or `file.docx#bookmark`; an
 * external one carries a scheme and is not this rule's concern, since
 * "does it land in the right SECTION" is meaningless for a web address.
 * `null` means "not an internal link", not "broken link". */
function parseHyperlinkAddressBookmark(address: string): string | null {
  const trimmed = address.trim();
  if (!trimmed || /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;
  const hash = trimmed.lastIndexOf("#");
  if (hash === -1) return null;
  const bookmark = trimmed.slice(hash + 1).trim();
  return bookmark || null;
}

/** Where a search hit sits relative to a link range when it IS that link. */
const HIT_IS_THE_LINK = ["Equal", "Inside", "InsideStart", "InsideEnd"] as Word.LocationRelation[];

/**
 * Each link's position in the word-bounded hit list for its text — the list
 * the anchor step indexes (`wordBoundedHits`, AIVA-8300/AIVA-8547) —
 * found by asking Word which hit lies on the link's OWN range, never by
 * counting. -1 when Word places it on none, or the host cannot search.
 *
 * Shared by both of hyperlink_target_mismatch's passes so they number a link
 * identically, which is what lets the second pass recognise a link the first
 * already reported (AIVA-8310 reopened — see collectHyperlinkTargetFieldLinks).
 */
async function occurrenceIndicesOf(
  context: Word.RequestContext,
  links: ReadonlyArray<{ text: string; range: Word.Range }>,
): Promise<number[]> {
  const body = context.document.body;
  if (links.length === 0 || typeof body.search !== "function") return links.map(() => -1);

  // One search per distinct text, word-bounded exactly as the anchor step
  // searches — see `queueWordBoundedSearch` (AIVA-8547).
  const searchesByKey = new Map<string, WordBoundedSearch>();
  for (const { text } of links) {
    const key = occurrenceCountKey(text);
    if (searchesByKey.has(key)) continue;
    searchesByKey.set(key, queueWordBoundedSearch(body, text));
  }
  const paragraphs = loadOptionalParagraphs(context);
  await context.sync();

  const hitsByKey = wordBoundedHitLists(searchesByKey, paragraphTextsOf(paragraphs));
  const placements = links.map((link) =>
    (hitsByKey.get(occurrenceCountKey(link.text)) ?? []).map((hit) => hit.compareLocationWith(link.range)),
  );
  await context.sync();
  return placements.map((row) => row.findIndex((cmp) => HIT_IS_THE_LINK.includes(cmp.value)));
}

/**
 * Append every `w:hyperlink`-element link, which `body.fields` cannot report
 * (AIVA-8310) — read as WHOLE links.
 *
 * AIVA-8310 reopened: this used to search the body for each reference text
 * broken_reference had found and ask every hit whether it sat in a link. A
 * whole-word search breaks at ".", so searching "Section 4" also hit the
 * first half of the link "Section 4.02", and that half was reported as a
 * link of its own — half the link highlighted, a duplicate "Section 12"
 * beside "Section 12.05", and a false "Section 9 opens Section 10" cut out of
 * the correct link "Section 10 (formerly Section 9)". The question was being
 * asked of a fragment, so no filter on the fragments could fix it.
 *
 * `Range.getHyperlinkRanges` (WordApi 1.3) returns each link as Word itself
 * stores it, full display text included. It reads ranges rather than fields,
 * so it sees BOTH of Word's representations — which is why the (text,
 * occurrence) pairs the field pass already claimed are skipped, or a
 * `{ HYPERLINK }` field would be reported twice.
 *
 * `occurrenceIndex` is still the link's position in the word-bounded hit
 * list for its text — the list the anchor step indexes (`wordBoundedHits`,
 * AIVA-8300/AIVA-8547). Which hit is the link is asked of Word (`compareLocationWith`),
 * never inferred from counting; a link Word cannot place is skipped rather
 * than given a guessed index, since a finding anchored on the wrong text reads
 * as real (the AIVA-8547 reasoning).
 *
 * `out` is appended in place; the returned array is each appended target's
 * bookmark and link range, positionally aligned with those new entries so the
 * caller's single resolution loop can cover both passes.
 */
async function collectHyperlinkTargetElementLinks(
  context: Word.RequestContext,
  out: RawHyperlinkTarget[],
): Promise<LinkSource[]> {
  const body = context.document.body;
  // An older host (or a test double) without these contributes nothing;
  // the field-based targets still stand.
  if (typeof body.getRange !== "function" || typeof body.search !== "function") return [];

  const linkRanges = body.getRange("Whole").getHyperlinkRanges();
  linkRanges.load("items/text,items/hyperlink");
  await context.sync();

  const links = linkRanges.items.flatMap((range) => {
    const text = (range.text ?? "").trim();
    const bookmark = parseHyperlinkAddressBookmark(range.hyperlink ?? "");
    // A Table of Contents is built from links onto `_Toc*` bookmarks. They
    // are correct by construction — Word generates both ends — so judging
    // them only spends an LLM call to conclude "matches". "^" starts a Word
    // search code (^p, ^t), so such text cannot be searched for literally.
    if (!bookmark || /^_Toc/i.test(bookmark) || !text || text.length > WORD_SEARCH_MAX_LENGTH || text.includes("^")) {
      return [];
    }
    return [{ range, text, bookmark }];
  });
  if (links.length === 0) return [];

  const placed = await occurrenceIndicesOf(context, links);

  const claimed = new Set(out.map((t) => `${occurrenceCountKey(t.matchText)}\u001f${t.occurrenceIndex}`));
  const bookmarks: LinkSource[] = [];
  links.forEach((link, i) => {
    const occurrenceIndex = placed[i] ?? -1;
    if (occurrenceIndex === -1) {
      console.warn("[docreview] hyperlink_target_mismatch: Word could not place a link among its own search hits", {
        matchText: link.text,
      });
      return;
    }
    const key = `${occurrenceCountKey(link.text)}\u001f${occurrenceIndex}`;
    if (claimed.has(key)) return;
    claimed.add(key);
    out.push({
      matchText: link.text,
      targetHeading: "",
      targetText: "",
      occurrenceIndex,
      targetMissing: false,
      labelCheck: "unknown",
      correctHeading: "",
      linkTopic: "",
    });
    bookmarks.push({ bookmark: link.bookmark, range: link.range });
  });
  return bookmarks;
}

/**
 * Raw sweep only. Reads every internal HYPERLINK field (`body.fields`,
 * WordApi 1.4) and resolves its bookmark target (`getBookmarkRangeOrNullObject`,
 * WordApiDesktop-only) to that target's own heading and opening text.
 * evaluate_r09 (backend) judges whether the two plausibly match — this
 * never judges, just reports what it found. Returns an empty sweep, not a
 * thrown error, on a host without desktop bookmark resolution (Word on the
 * web) — end to end, not just the fix, since there is nothing meaningful to
 * report without knowing where a link actually lands.
 */
export async function findHyperlinkTargetMismatchData(
  existingHeadings?: RawHeading[],
): Promise<HyperlinkTargetMismatchSweep> {
  // supportsFields() gates only the FIELD half of the sweep now — the
  // element half needs `Range.getHyperlinkRanges` (WordApi 1.3), which every
  // host supporting bookmarks already has. Bookmark resolution is still the
  // hard requirement: without it there is no way to say where a link lands.
  if (!supportsBookmarks()) return { targets: [] };

  // The heading list names the ONE heading a wrong link should be re-pointed
  // to. broken_reference already built it in a full scan; collected here
  // only for the standalone `word.docreview.find_hyperlink_target_mismatch`
  // handler — the same shape findMissingHyperlink uses.
  const headings = existingHeadings ?? (await collectHeadings()).headings;

  return Word.run(async (context) => {
    const targets: RawHyperlinkTarget[] = [];
    // Two passes, one per representation Word uses for a link — see
    // collectHyperlinkTargetElementLinks for why the second exists at all.
    // Each returns its targets' bookmarks and link ranges, aligned with the
    // entries it appended, so one resolution loop can cover both.
    const bookmarks = [
      ...(await collectHyperlinkTargetFieldLinks(context, targets)),
      ...(await collectHyperlinkTargetElementLinks(context, targets)),
    ];

    // Every link's destination is queued first and read back in ONE sync.
    // The OrNullObject chain needs no round trip between its steps — a gone
    // bookmark just yields null objects down the chain — so nothing here
    // depends on an earlier link's result, and a document with 30 links costs
    // one round trip rather than 30.
    const queued = bookmarks.map((source) => queueLinkTarget(context, source));
    await context.sync();
    queued.forEach((proxies, i) => applyLinkTarget(targets[i]!, proxies, headings));

    return { targets };
  });
}

/** One swept link: the bookmark it opens and its own range in the text. */
interface LinkSource {
  bookmark: string;
  range: Word.Range;
}

interface LinkTargetProxies {
  range: Word.Range;
  para: Word.Paragraph;
  listItem: Word.ListItem | undefined;
  next: Word.Paragraph | null;
  afterNext: Word.Paragraph | null;
  /** The paragraph the link itself sits in, for a title in brackets after it. */
  linkPara: Word.Paragraph | null;
}

/** Queue everything `applyLinkTarget` reads about where one link lands. */
function queueLinkTarget(context: Word.RequestContext, { bookmark, range: link }: LinkSource): LinkTargetProxies {
  const range = context.document.getBookmarkRangeOrNullObject(bookmark);
  range.load("isNullObject,text");
  const para = range.paragraphs.getFirstOrNullObject();
  para.load("isNullObject,text");
  const listItem = para.listItemOrNullObject;
  listItem?.load("isNullObject,listString");
  const next = nextParagraphOf(para);
  next?.load("isNullObject,text");
  const afterNext = nextParagraphOf(next);
  afterNext?.load("isNullObject,text");
  // Optional: a host (or test double) without it just reads no bracket.
  const linkPara = typeof link.paragraphs?.getFirstOrNullObject === "function" ? link.paragraphs.getFirstOrNullObject() : null;
  linkPara?.load("isNullObject,text");
  return { range, para, listItem, next, afterNext, linkPara };
}

/** Fill in where one link lands and what its label says about it, from
 *  proxies `queueLinkTarget` queued and the caller has synced. */
function applyLinkTarget(
  target: RawHyperlinkTarget,
  { range, para, listItem, next, afterNext, linkPara }: LinkTargetProxies,
  headings: readonly RawHeading[],
): void {
  const link = linkOwnLabel(target.matchText);
  target.correctHeading = headingNamedBy(link, headings);
  target.linkTopic = bracketTopicOf(target.matchText);
  if (range.isNullObject) {
    // The link names a bookmark this document does not have. Clicking it
    // does nothing — report it rather than drop it (AIVA-8310).
    target.targetMissing = true;
    return;
  }
  if (para.isNullObject) {
    target.targetText = (range.text ?? "").slice(0, HYPERLINK_TARGET_SNIPPET_LENGTH);
    return;
  }
  const following = [next, afterNext].filter((p) => p && !p.isNullObject).map((p) => p!.text ?? "");
  const listString = listItem && !listItem.isNullObject ? (listItem.listString ?? "").trim() : "";
  const described = describeLinkTarget(range.text ?? "", para.text ?? "", listString, following);
  target.targetHeading = described.heading;
  target.targetText = described.text;
  target.labelCheck = checkLinkLabel(link, described.heading);
  const linkParagraph = linkPara && !linkPara.isNullObject ? (linkPara.text ?? "") : "";
  if (target.labelCheck === "mismatch" && bracketNamesHeading(linkBracketTitle(link, target.matchText, linkParagraph), described.heading)) {
    // The bracket agrees with where the link goes, so the label is the mistake —
    // re-pointing would move a correct link (AIVA-8310).
    target.bracketNamesTarget = true;
    target.correctHeading = "";
  }
}

/** The title in brackets written with a link — inside it ("Section 14 (Service
 *  Credits)") or straight after it ("Exhibit B (the "Statement of Work")").
 *  "" when there is none, it names a place ("(formerly Section 9)"), or the
 *  link's text appears twice in its paragraph and the bracket cannot be placed. */
function linkBracketTitle(link: LinkLabel | null, linkText: string, paragraphText: string): string {
  const own = link ? linkText.indexOf(link.text) : -1;
  let title = own === -1 ? undefined : bracketTitleAt(linkText.slice(own + link!.text.length));
  if (!title) {
    const at = paragraphText.indexOf(linkText);
    const once = at !== -1 && !paragraphText.includes(linkText, at + 1);
    title = once ? bracketTitleAt(paragraphText.slice(at + linkText.length)) : undefined;
  }
  return title && !bracketNamesAPlace(title) ? title : "";
}

/** Whether a bracketed title names a heading — its words appear in it, compared
 *  as exhibit_schedule_mismatch compares titles (`normaliseTitle`). */
function bracketNamesHeading(bracket: string, heading: string): boolean {
  const key = normaliseTitle(bracket);
  return key !== "" && ` ${normaliseTitle(heading)} `.includes(` ${key} `);
}

/**
 * Append every `{ HYPERLINK \l "x" }` FIELD link — Word's older of two
 * representations, and the only one this rule swept before AIVA-8310.
 *
 * `out` is appended in place; the returned array is each appended target's
 * bookmark and link range, positionally aligned with those new entries.
 */
async function collectHyperlinkTargetFieldLinks(
  context: Word.RequestContext,
  out: RawHyperlinkTarget[],
): Promise<LinkSource[]> {
  // A host without `body.fields` (pre-WordApi 1.4) contributes nothing here;
  // the element pass still runs, since `Range.hyperlink` is WordApi 1.3.
  const fields = supportsFields() ? context.document.body.fields : null;
  if (!fields) return [];
  // AIVA-8925: one targeted load, not every field property and then a second
  // round trip for the two this reads.
  fields.load("items/code,items/result/text");
  await context.sync();

  // parseHyperlinkBookmark only ever matches a HYPERLINK field's own
  // "\l" switch — a REF field's code (`REF _Ref1 \h`, no `\l`) or an
  // external HYPERLINK's (no bookmark at all) both return null, so a
  // non-null bookmark alone is enough to identify a genuine internal link.
  // An empty cached display text (a field Word hasn't recalculated since
  // insertion) is excluded too — that empty string would otherwise reach
  // anchorFindings as a search term, and an empty search string throws.
  const links = fields.items
    .map((f) => ({ text: f.result.text ?? "", bookmark: parseHyperlinkBookmark(f.code ?? ""), range: f.result }))
    .filter((f): f is { text: string; bookmark: string; range: Word.Range } => f.bookmark !== null && f.text.trim().length > 0);

  // occurrenceIndex must be THIS link's position in a live
  // body.search(matchText) — the list the anchor step indexes.
  //
  // AIVA-8315 — counting over `links` alone is NOT that count: any
  // plain-text occurrence of the same string shifts it, and a link reading
  // "Section 14" almost always has a "Section 14. Governing Law" HEADING
  // above it, which the live search finds first.
  //
  // AIVA-8310 reopened — nor is "the Nth hit that lies inside ANY field",
  // which is what this used to take (`countBodyOccurrences`). A REF
  // cross-reference is a field too: on QA's 10-page MSA the survival
  // clause's `{ REF S10 }` "Section 10" came first, so the warranty link
  // "Section 10" was numbered 0 — the survival clause. The whole-link pass
  // numbered the same link 2, the two disagreed, the duplicate check missed
  // it, and one link produced two cards, one anchored on the wrong clause.
  // Asking Word which hit lies on THIS field's own result range is exact,
  // and is the same question that pass asks, so the two cannot disagree.
  const placed = await occurrenceIndicesOf(context, links);
  const canSearch = typeof context.document.body.search === "function";

  const sources: LinkSource[] = [];
  const seenCount = new Map<string, number>();
  links.forEach((link, i) => {
    const key = occurrenceCountKey(link.text);
    const linkOrdinal = seenCount.get(key) ?? 0;
    seenCount.set(key, linkOrdinal + 1);
    const position = placed[i] ?? -1;
    // AIVA-8310: a field's text carries "<" ">" around an AIVA_ anchor still in
    // the document ("<Section 5.02>"), which no search finds. Skipped, like the
    // element pass does — that pass reports the same link with its real text.
    if (position === -1 && canSearch) {
      console.warn("[docreview] hyperlink_target_mismatch: Word could not place a field link among its own search hits", {
        matchText: link.text,
      });
      return;
    }
    out.push({
      matchText: link.text,
      targetHeading: "",
      targetText: "",
      // A host that cannot search at all keeps the link ordinal: a worse anchor beats no sweep.
      occurrenceIndex: position >= 0 ? position : linkOrdinal,
      targetMissing: false,
      labelCheck: "unknown",
      correctHeading: "",
      linkTopic: "",
    });
    sources.push({ bookmark: link.bookmark, range: link.range });
  });
  return sources;
}

/**
 * Narrows the raw sweep down to what `word_docreview_judge` actually needs
 * to judge — mirrors the skip `evaluate_r09` (rules.py, now deleted) used to
 * do server-side: a target that never resolved (empty heading/text) is
 * dropped here instead, so the backend's own evaluate_r09 is now an
 * unconditional judge-every-target loop.
 *
 * AIVA-8310 — a DEAD link (`targetMissing`) is deliberately kept. It used to
 * fall into the same "never resolved, drop it" bucket as an external link,
 * which meant the single most broken thing a cross-reference can be — a link
 * that goes nowhere at all when clicked — was the one case this rule could
 * never report. The backend decides it deterministically; it is passed
 * through here only so it reaches something that can.
 */
export function filterHyperlinkTargetMismatchJudgmentTargets(sweep: HyperlinkTargetMismatchSweep): HyperlinkTargetMismatchSweep {
  return { targets: sweep.targets.filter((t) => t.targetHeading || t.targetText || t.targetMissing) };
}

/**
 * Re-point a hyperlink the backend confidently resolved a correct target
 * for — its own display text named a section number that genuinely exists
 * elsewhere in the document (evaluate_r09's job, not this function's).
 * Shares missing_hyperlink's exact bookmark-ensure-and-link primitive; falls back to a
 * comment under the same conditions missing_hyperlink does.
 */
export async function fixHyperlinkTargetMismatch(
  anchorTag: string,
  expected: string,
  targetHeadingText: string,
  commentText: string,
): Promise<FixResult> {
  return fixByLinkingOrCommenting(anchorTag, expected, targetHeadingText, commentText);
}

/** Flag a hyperlink the backend's LLM judgment decided doesn't land where
 * its own text says it should, with no confidently-known correct target to
 * re-point it to (see `fixHyperlinkTargetMismatch` for when there is
 * one). Comment-only. */
export async function fixHyperlinkTargetMismatchFlag(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// used_not_defined — a term is used like a defined term (quoted at least
// once and recurring 2+ times — AIVA-8314 — or, since AIVA-9194, a
// capitalised phrase used mid-sentence in body prose) but is never actually
// defined anywhere in the document (LLM judgment — this sweep only gathers
// candidates, never decides)
// ---------------------------------------------------------------------------

export interface RawUsedNotDefinedCandidate {
  term: string;
  occurrenceCount: number;
  evidence: string[];
  paragraphIndex: number;
  /** AIVA-9042: the 1-based footnote its first quoted use sits in, when that
   *  is in a footnote rather than the main text. The judgment labels the
   *  finding with it. */
  footnote?: number;
  /** The first quoted use as written, curly quotes kept, so the card can be
   *  anchored — the backend used to rebuild it with straight quotes. */
  matchText?: string;
  occurrenceIndex?: number;
}

export interface UsedNotDefinedSweep {
  candidates: RawUsedNotDefinedCandidate[];
  /** AIVA-9194: candidates past MAX_USED_NOT_DEFINED_CANDIDATES, never sent
   *  for judgment — told to the user by usedNotDefinedNotes. */
  overLimit?: number;
}

/** AIVA-9194: the most candidates one scan sends for judgment — each is one
 *  model call. Quoted candidates go first, then prose ones by how often they
 *  are used, so a cut drops the least-used prose phrases. */
const MAX_USED_NOT_DEFINED_CANDIDATES = 40;
/** Mid-sentence uses an unquoted phrase needs before it is a candidate. A lone
 *  capitalised word needs one more: it is far likelier to be a name or emphasis. */
const MIN_PROSE_USES = 2;
const MIN_PROSE_USES_SINGLE_WORD = 3;
/** Example sentences sent with a prose candidate — context, not the paragraph. */
const MAX_PROSE_EXAMPLES = 3;
/** Title-Case words in one prose phrase. A longer run is a title or a name
 *  written out in full, not a term. */
const MAX_PROSE_PHRASE_WORDS = 4;

/** Capitalised for a reason other than being a term: structure and calendar
 *  words, and languages/nationalities ("English law"). Section and Exhibit are
 *  AIVA-9193's own category and are not repeated here. */
const PROSE_NOT_A_TERM = new Set([
  "clause", "clauses", "schedule", "schedules", "annex", "annexes", "appendix", "appendices", "article", "articles",
  "paragraph", "paragraphs", "part", "parts", "recital", "recitals", "table", "figure", "page", "chapter",
  "january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "english", "welsh", "scottish", "irish", "british", "european", "american", "french", "german",
  "title", "subtitle", "series", "class", "inc", "ltd", "corp", "co", "plc", "llc", "llp",
]);

/** Whole phrases capitalised as names of things that are never terms. */
const PROSE_NOT_A_TERM_PHRASES = new Set([
  "eastern time", "central time", "mountain time", "pacific time", "greenwich mean time", "british summer time",
  "central european time",
]);

/** Ordinary words that open a sentence capitalised. Dropped from the front of
 *  a sentence-initial run ("The Service Credits" → "Service Credits"); a run
 *  opening with any other word is skipped, since its capital proves nothing. */
const SENTENCE_OPENERS = new Set([
  "the", "a", "an", "this", "that", "these", "those", "each", "any", "all", "no", "such", "if", "in", "where",
  "when", "upon", "subject", "for", "without", "notwithstanding", "neither", "either", "on", "at", "by", "to",
  "unless", "save", "except", "following", "during", "after", "before", "nothing", "both", "its", "their",
  "our", "your", "every", "other", "as", "with", "from", "under", "within",
]);

/** A paragraph's lead that is only a clause or list number: "5.2", "(a)", "iv)". */
const LIST_MARKER_ONLY = /^[([]?[A-Za-z0-9]{1,4}(?:\.[A-Za-z0-9]{1,4})*[.)\]]?$/;
const WORD_TOKEN = /[A-Za-z][A-Za-z'’-]*/g;
const TITLE_CASE_WORD = /^[A-Z][a-z]/;
/** "A" in "Series A Preferred Stock" — part of the name, never its start. */
const DESIGNATOR = /^[A-Z]$/;
const POSSESSIVE = /['’]s?$/;

interface ProseRun {
  phrase: string;
  offset: number;
  end: number;
}

/** Whether the word at `offset` follows the run's last word with only space,
 *  or " of ", between them. */
function continuesRun(unquoted: string, words: ReadonlyArray<{ end: number }>, offset: number): boolean {
  const last = words[words.length - 1];
  return last !== undefined && /^\s+(?:of\s+)?$/.test(unquoted.slice(last.end, offset));
}

/** Every Title-Case run in `text`, "of" allowed between two words ("Statement
 *  of Work"). Quoted spans are blanked first — the quoted sweep owns them, and
 *  a quoted name is a definition, not a use. A possessive ends its run. */
function capitalisedRuns(text: string): ProseRun[] {
  const unquoted = straightenQuotes(text).replace(/"[^"]*"/g, (quoted) => " ".repeat(quoted.length));
  const runs: ProseRun[] = [];
  let words: Array<{ word: string; offset: number; end: number }> = [];
  const flush = () => {
    while (words.length > 0 && DESIGNATOR.test(words[words.length - 1]!.word)) words.pop();
    if (words.length > 0 && words.length <= MAX_PROSE_PHRASE_WORDS) {
      const first = words[0]!;
      const last = words[words.length - 1]!;
      runs.push({ phrase: text.slice(first.offset, last.end), offset: first.offset, end: last.end });
    }
    words = [];
  };
  for (const match of unquoted.matchAll(WORD_TOKEN)) {
    const offset = match.index ?? 0;
    const token = match[0];
    if (!continuesRun(unquoted, words, offset)) flush();
    // "of" joins only when a Title-Case word follows; the gap test above checks that.
    if (token === "of" && words.length > 0) continue;
    if (DESIGNATOR.test(token) && words.length > 0) {
      words.push({ word: token, offset, end: offset + 1 });
      continue;
    }
    if (!TITLE_CASE_WORD.test(token)) {
      flush();
      continue;
    }
    const word = token.replace(POSSESSIVE, "");
    words.push({ word, offset, end: offset + word.length });
    if (word !== token) flush();
  }
  flush();
  return runs;
}

/** The run as a mid-sentence use, or null. A run opening a sentence (or a
 *  numbered clause) is capitalised by position, not meaning: an ordinary
 *  opener is dropped from its front, and any other is skipped. */
function midSentenceRun(text: string, run: ProseRun): ProseRun | null {
  const before = text.slice(0, run.offset).trimEnd();
  const opensSentence = before === "" || /[.!?:;•–—]$/.test(before) || LIST_MARKER_ONLY.test(before);
  if (!opensSentence) return run;
  const space = run.phrase.search(/\s/);
  if (space === -1 || !SENTENCE_OPENERS.has(run.phrase.slice(0, space).toLowerCase())) return null;
  const rest = run.phrase.slice(space).trimStart();
  if (rest.startsWith("of ")) return null;
  const offset = run.end - rest.length;
  return { phrase: rest, offset, end: run.end };
}

function isProseTerm(phrase: string): boolean {
  const first = phrase.split(/\s+/)[0]?.toLowerCase() ?? "";
  return !PROSE_NOT_A_TERM.has(first) && !PROSE_NOT_A_TERM_PHRASES.has(phrase.replace(/\s+/g, " ").toLowerCase());
}

/** A line made only of capitalised words — an exhibit list entry ("Exhibit C -
 *  Form of Escrow Agreement"), a signature block label, a table header. Not a
 *  sentence, so its capitals prove nothing. */
function isTitleLikeLine(text: string): boolean {
  const words = text.match(/[A-Za-z][A-Za-z'’-]*/g) ?? [];
  return words.length > 0 && words.every((w) => /^[A-Z]/.test(w) || TITLE_MINOR_WORDS.has(w));
}

/** A one-word label before a colon — "Attn:", "Telephone:" in a notices block. */
function isFieldLabel(text: string, run: ProseRun): boolean {
  return !/\s/.test(run.phrase) && /^\s*:/.test(text.slice(run.end));
}

/** The bracketed caption after a reference — "Schedule 2 (Approved
 *  Subcontractors)", "Section 3 (Fees and Payment)". It names the part being
 *  pointed at, not a term; whether it names the right part is the reference
 *  rules' question. Only a run filling the whole bracket counts. */
const REFERENCE_KEYWORDS = new Set([
  "section", "sections", "clause", "clauses", "article", "articles", "schedule", "schedules",
  "exhibit", "exhibits", "annex", "annexes", "appendix", "appendices", "part", "parts", "paragraph", "paragraphs",
]);
/** "<word> 3.1(a) (" at the end of the text before the run; the word is checked against REFERENCE_KEYWORDS. */
const REFERENCE_BEFORE_CAPTION = /\b([a-z]+)\s+[a-z0-9]+(?:\.[a-z0-9]+)*(?:\([a-z0-9]+\))*\s*\(\s*$/i;
function isReferenceCaption(text: string, run: ProseRun): boolean {
  const keyword = REFERENCE_BEFORE_CAPTION.exec(text.slice(0, run.offset))?.[1];
  return keyword !== undefined && REFERENCE_KEYWORDS.has(keyword.toLowerCase()) && /^\s*\)/.test(text.slice(run.end));
}

/** A run built from terms the document already defines is a use of them, not
 *  a new term: "Knowledge of Seller" and "Affiliate of Purchaser" (one side of
 *  "of" defined), "Process Personal Data" (defined "Process" + defined
 *  "Personal Data"). Cutting such a run down instead read "Certificate of
 *  Merger" as a use of "Certificate". */
function buildsOnKnownTerms(run: ProseRun, isKnown: (term: string) => boolean): boolean {
  const of = run.phrase.search(/\sof\s/);
  if (of !== -1) return isKnown(run.phrase.slice(0, of)) || isKnown(run.phrase.slice(of).replace(/^\s+of\s+/, ""));
  const words = run.phrase.split(/\s+/);
  for (let split = 1; split < words.length; split++) {
    if (isKnown(words.slice(0, split).join(" ")) && isKnown(words.slice(split).join(" "))) return true;
  }
  return false;
}

interface ProseUses {
  term: string;
  uses: Array<{ paragraphIndex: number; offset: number; matchText: string }>;
  contexts: string[];
  examples: string[];
}

/** Every capitalised phrase used mid-sentence in body prose, by lower-case
 *  key. Headings, contents entries, titles and a clause's run-in title are not
 *  prose; footnotes are. */
function collectProseUses(
  texts: readonly string[],
  bodyParagraph: readonly boolean[],
  isKnown: (term: string) => boolean,
): Map<string, ProseUses> {
  const byKey = new Map<string, ProseUses>();
  texts.forEach((text, paragraphIndex) => {
    if (!bodyParagraph[paragraphIndex] || isTitleLikeLine(text)) return;
    const title = runInClauseTitle(text);
    for (const found of capitalisedRuns(text)) {
      const run = midSentenceRun(text, found);
      if (!run || !isProseTerm(run.phrase) || isFieldLabel(text, run) || isReferenceCaption(text, run)) continue;
      if (buildsOnKnownTerms(run, isKnown)) continue;
      if (title && run.offset < title.end) continue;
      const key = run.phrase.replace(/\s+/g, " ").toLowerCase();
      const entry = byKey.get(key) ?? { term: run.phrase.replace(/\s+/g, " "), uses: [], contexts: [], examples: [] };
      entry.uses.push({ paragraphIndex, offset: run.offset, matchText: run.phrase });
      if (entry.contexts.length < MAX_PROSE_EXAMPLES && !entry.contexts.includes(text)) {
        entry.contexts.push(text);
        entry.examples.push(excerptAround(text, run.offset));
      }
      byKey.set(key, entry);
    }
  });
  return byKey;
}

interface UsedNotDefinedDoc {
  texts: readonly string[];
  bodyCount: number;
  fullText: string;
  known: RegExp[];
  /** Every declared term, for `isKnownProseTerm`'s reverse check. */
  declaredKeys: readonly string[];
  reconsidered: ReadonlySet<ExceptionCategory>;
  counter: ReturnType<typeof documentOccurrenceCounter>;
  setAside: Map<ExceptionCategory, string[]>;
}

/** Whether `term` is set aside as an exception category (AIVA-9193) — and noted if so. */
function setAsideAsException(doc: UsedNotDefinedDoc, term: string, contexts: readonly string[]): boolean {
  const exception = suppressingCategory(term, contexts, doc.reconsidered);
  if (!exception) return false;
  doc.setAside.set(exception, [...(doc.setAside.get(exception) ?? []), term]);
  return true;
}

/** Whether the document defines `term` in any inflection. `namesAnyTerm`
 *  reads a plural use of a singular definition; this also reads the reverse —
 *  "Acquired Asset" when the document defines "Acquired Assets". */
function isKnownProseTerm(doc: UsedNotDefinedDoc, term: string): boolean {
  if (namesAnyTerm(term, doc.known)) return true;
  const asPattern = wholeTermMatchers([term]);
  return doc.declaredKeys.some((key) => namesAnyTerm(key, asPattern));
}

function footnoteOf(doc: UsedNotDefinedDoc, paragraphIndex: number): { footnote?: number } {
  return paragraphIndex >= doc.bodyCount ? { footnote: paragraphIndex - doc.bodyCount + 1 } : {};
}

/** AIVA-9194: candidates from unquoted, capitalised prose — each used
 *  mid-sentence often enough, unknown to the inventory, not already a quoted
 *  candidate, in no exception category, and anchorable at one of its uses.
 *  Most-used first. */
function proseCandidates(
  doc: UsedNotDefinedDoc,
  bodyParagraph: readonly boolean[],
  quotedKeys: ReadonlySet<string>,
): RawUsedNotDefinedCandidate[] {
  const candidates: Array<RawUsedNotDefinedCandidate & { uses: number }> = [];
  const isKnown = (term: string) => isKnownProseTerm(doc, term);
  for (const [key, entry] of collectProseUses(doc.texts, bodyParagraph, isKnown)) {
    const singleWord = !/\s/.test(entry.term);
    if (entry.uses.length < (singleWord ? MIN_PROSE_USES_SINGLE_WORD : MIN_PROSE_USES)) continue;
    if (quotedKeys.has(key) || isKnown(entry.term)) continue;
    if (setAsideAsException(doc, entry.term, entry.contexts)) continue;
    const anchor = firstAnchorableUse(doc, entry.uses);
    if (!anchor) continue;
    candidates.push({
      term: entry.term,
      occurrenceCount: countTermOccurrences(entry.term, doc.fullText),
      evidence: entry.examples,
      paragraphIndex: anchor.paragraphIndex,
      matchText: anchor.matchText,
      occurrenceIndex: anchor.occurrenceIndex,
      ...footnoteOf(doc, anchor.paragraphIndex),
      uses: entry.uses.length,
    });
  }
  // Stable: equal counts keep document order, multi-word phrases first.
  candidates.sort((a, b) => b.uses - a.uses || Number(!/\s/.test(a.term)) - Number(!/\s/.test(b.term)));
  return candidates.map(({ uses: _uses, ...candidate }) => candidate);
}

/** The first use Word's own search will find at the same place — an unquoted
 *  candidate with no anchor would be a card pointing nowhere. */
function firstAnchorableUse(
  doc: UsedNotDefinedDoc,
  uses: ProseUses["uses"],
): { paragraphIndex: number; matchText: string; occurrenceIndex: number } | null {
  for (const use of uses) {
    const occurrenceIndex = doc.counter.at(use.paragraphIndex, use.offset, use.matchText);
    if (occurrenceIndex !== null) return { paragraphIndex: use.paragraphIndex, matchText: use.matchText, occurrenceIndex };
  }
  return null;
}

/** What the user should know used_not_defined could not check, or nothing. */
export function usedNotDefinedNotes(sweep: UsedNotDefinedSweep): string[] {
  const overLimit = sweep.overLimit ?? 0;
  if (overLimit === 0) return [];
  const total = MAX_USED_NOT_DEFINED_CANDIDATES + overLimit;
  return [
    `Used but not defined: checked ${MAX_USED_NOT_DEFINED_CANDIDATES} of ${total} capitalised phrases; the ${overLimit} used least often weren't checked.`,
  ];
}

// Any quoted, Title-Case phrase — deliberately broader than defined_not_used's
// "(the \"X\")" declaration shape and alphabetical_order's "'X' means" formal-definition
// shape, since used_not_defined's whole point is to catch a term that DOESN'T fit either
// of those (it never got a real definition at all).
const QUOTED_PHRASE_PATTERN = /"([A-Z][A-Za-z]*(?:\s+[A-Z][a-zA-Z]*){0,3})"/g;

/** AIVA-9193: one console line naming what the exception categories set aside. */
function logSetAside(setAside: ReadonlyMap<ExceptionCategory, string[]>): void {
  if (setAside.size === 0) return;
  const summary = [...setAside].map(([category, terms]) => `${category}: ${terms.join(", ")}`).join("; ");
  console.info(`[docReview] used_not_defined: set aside before judgment as exception categories — ${summary}`);
}

/** The quoted source (AIVA-8314): quoted Title-Case phrases used at least
 *  twice, quoted or not, that the declared-terms inventory does not know as
 *  defined in any shape — a pointer such as "has the meaning set forth in"
 *  included. */
function quotedCandidates(doc: UsedNotDefinedDoc): RawUsedNotDefinedCandidate[] {
  const firstQuoteByTerm = new Map<string, { paragraphIndex: number; offset: number; matchText: string }>();
  const evidenceByTerm = new Map<string, string[]>();
  const displayTermByKey = new Map<string, string>();

  doc.texts.forEach((text, paragraphIndex) => {
    for (const match of straightenTermQuotes(text).matchAll(QUOTED_PHRASE_PATTERN)) {
      const term = (match[1] ?? "").trim();
      if (!term) continue;
      const key = term.toLowerCase();
      if (!displayTermByKey.has(key)) displayTermByKey.set(key, term);
      if (!firstQuoteByTerm.has(key)) {
        const offset = match.index ?? 0;
        firstQuoteByTerm.set(key, { paragraphIndex, offset, matchText: text.slice(offset, offset + match[0].length) });
      }
      const evidence = evidenceByTerm.get(key) ?? [];
      if (evidence.length < 2) evidence.push(text);
      evidenceByTerm.set(key, evidence);
    }
  });

  const candidates: RawUsedNotDefinedCandidate[] = [];
  for (const [key, term] of displayTermByKey) {
    if (namesAnyTerm(term, doc.known)) continue;
    // Every occurrence in the document, quoted or not — same counting
    // semantics as defined_not_used's own occurrenceCount, so a term
    // quoted once and referenced bare afterward still qualifies.
    const occurrenceCount = countTermOccurrences(term, doc.fullText);
    if (occurrenceCount < 2) continue;
    if (setAsideAsException(doc, term, evidenceByTerm.get(key) ?? [])) continue;
    const first = firstQuoteByTerm.get(key);
    const occurrenceIndex = first ? doc.counter.at(first.paragraphIndex, first.offset, first.matchText) : null;
    candidates.push({
      term,
      occurrenceCount,
      evidence: evidenceByTerm.get(key) ?? [],
      paragraphIndex: first?.paragraphIndex ?? 0,
      ...(first && occurrenceIndex !== null ? { matchText: first.matchText, occurrenceIndex } : {}),
      ...(first ? footnoteOf(doc, first.paragraphIndex) : {}),
    });
  }
  return candidates;
}

/** Raw sweep only: candidates for "used like a defined term, never defined",
 *  from two sources — quoted phrases (AIVA-8314) and, since AIVA-9194,
 *  capitalised phrases used mid-sentence in body prose, quotes or not.
 *
 *  Both drop what the declared-terms inventory knows (AIVA-9190) and what falls
 *  in one of the eight exception categories (AIVA-9193) unless the document
 *  re-opened that category. The prose source also needs a minimum number of
 *  mid-sentence uses and skips headings, contents entries and run-in titles.
 *  At most MAX_USED_NOT_DEFINED_CANDIDATES go on to the model; the rest are
 *  counted in `overLimit`. */
export function findUsedNotDefinedCandidates(): Promise<UsedNotDefinedSweep> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/outlineLevel");
    await context.sync();
    const bodyTexts = paragraphs.items.map((p) => p.text ?? "");

    // AIVA-9042: footnotes after the main text, one "paragraph" each — a term
    // quoted in a footnote is a candidate, a definition in one counts, and so
    // does every use. See footnotes.ts.
    const footnoteTexts = await loadFootnoteTexts(context);
    const texts = [...bodyTexts, ...footnoteTexts];
    // AIVA-9190 follow-up (main): bold-colon entries need Word's formatting.
    const inventory = await readDeclaredTerms(context, paragraphs, texts);
    const doc: UsedNotDefinedDoc = {
      texts,
      bodyCount: bodyTexts.length,
      fullText: straightenQuotes(texts.join(" ")),
      known: wholeTermMatchers(inventory.map((declared) => declared.key)),
      declaredKeys: inventory.map((declared) => declared.key),
      reconsidered: reconsideredCategories(
        inventory.map((declared) => ({ term: declared.term, context: texts[declared.paragraphIndex] ?? "" })),
      ),
      counter: documentOccurrenceCounter(texts),
      setAside: new Map(),
    };
    const bodyParagraph = [
      ...paragraphs.items.map((p) => isBodyParagraph(p.style ?? "", p.outlineLevel ?? null)),
      ...footnoteTexts.map(() => true),
    ];

    const quoted = quotedCandidates(doc);
    const prose = proseCandidates(doc, bodyParagraph, new Set(quoted.map((c) => c.term.toLowerCase())));
    logSetAside(doc.setAside);
    const all = [...quoted, ...prose];
    if (all.length > MAX_USED_NOT_DEFINED_CANDIDATES) {
      console.info(
        `[docReview] used_not_defined: ${all.length} candidates (${quoted.length} quoted, ${prose.length} prose), judging ${MAX_USED_NOT_DEFINED_CANDIDATES}`,
      );
    }
    return {
      candidates: all.slice(0, MAX_USED_NOT_DEFINED_CANDIDATES),
      overLimit: Math.max(0, all.length - MAX_USED_NOT_DEFINED_CANDIDATES),
    };
  });
}

/** Flag a term used as if it were a defined term but never actually
 * defined. Comment-only — same reasoning as every other judgment-flavoured
 * rule: there's no safe deterministic text to insert for "please define
 * this," only a flag for the operator. */
export async function fixUsedNotDefined(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// alphabetical_order — an entry in the document's Definitions section listed
// before the entry ahead of it
// ---------------------------------------------------------------------------

export interface RawDefinition {
  matchText: string;
  /** Which occurrence of this exact matchText, in document order — without
   *  this, the same defined-term declaration sentence appearing more than
   *  once (a duplicate/redundant definition) always anchored to occurrence
   *  0 regardless of which one was actually out of order. Same reasoning
   *  as missing_hyperlink's/bracket_heading_mismatch's/topic_mismatch's/inconsistent_formatting's/defined_term_section_mismatch's/hyperlink_target_mismatch's/shortened_hyperlink_text's own occurrenceIndex. This
   *  sweep never excludes headings from its paragraph walk, so a plain
   *  per-matchText count already matches what a live, unfiltered search
   *  finds. */
  occurrenceIndex: number;
  term: string;
  paragraphIndex: number;
  /** See DefinitionEntryMatch.fullForm. */
  fullForm?: string;
}

export interface AlphabeticalOrderSweep {
  definitions: RawDefinition[];
}

/** Where a Definitions entry's own text starts: past leading whitespace and
 *  an optional typed clause number ("1.4 ", "2. "). A digit run not followed
 *  by whitespace is not a clause number, so the entry starts before it. */
function definitionEntryStart(text: string): number {
  const isSpace = (c: string | undefined) => c?.trim() === "";
  let i = 0;
  while (isSpace(text[i])) i += 1;
  let j = i;
  while (j < text.length && /[\d.]/.test(text[j]!)) j += 1;
  if (j === i || !/\d/.test(text[i]!) || !isSpace(text[j])) return i;
  while (isSpace(text[j])) j += 1;
  return j;
}

// AIVA-8606: alphabetical_order checks ONE list — the document's Definitions
// section — and nothing else. It used to rank every `"X" means` sentence in the
// whole document as one sequence, so a schedule's own definitions or an
// acronyms list read as out of order against the main list, and the fix moved
// terms between them.

/** A Definitions-section entry: a quoted term at the very START of its
 *  paragraph, then a defining verb. Anchored to the start because the same
 *  words mid-sentence ("As used herein, "X" has the meaning given to it…")
 *  point at a definition elsewhere rather than being one. "has the meaning" is
 *  matched whatever follows it ("set forth in", "given in", …): two real
 *  agreements defined 40–55% of their list that way. The term may hold digits, parentheses and lower case
 *  ("401(k) Plan", "material adverse effect"); a leading "[" admits an entry
 *  still bracketed for confirmation. */
const DEFINITION_ENTRY_TERM = /^\[?"([^"]{1,80})"\s+/;

// Matched in three small steps — term, verb, source — to stay under Sonar's
// regex complexity limit (S5843).
const DEFINITION_ENTRY_VERB = new RegExp(String.raw`^(?:${DEFINITION_VERB})\b`, "i");
const DEFINITION_ENTRY_MEANING_VERB = new RegExp(String.raw`^${DEFINITION_MEANING_VERB}\b`, "i");
/** Kept in `matchText` so the anchor covers "has the meaning set forth in" as written. */
const DEFINITION_VERB_EXTENSION = new RegExp(String.raw`^${DEFINITION_MEANING_SOURCE}\b`, "i");
/** What an entry may put between its term and verb: another name (`"X" or "Y"
 *  means`), whom it applies to (`"X" of any Person means`), or an aside
 *  (`"X" (including "Y") means`). One small pattern per shape (S5843). */
const DEFINITION_ENTRY_QUALIFIERS = [
  /^(?:,\s*)?(?:or|and|and\/or)\s+"[^"]{1,80}"\s+/i,
  /^(?:of|with\s+respect\s+to|in\s+respect\s+of)\s+(?:any|a|an|the|each|such)\s+[a-z]+\s+/i,
  /^\([^()]{1,200}\)\s+/,
];

function entryQualifier(afterTerm: string): string {
  for (const pattern of DEFINITION_ENTRY_QUALIFIERS) {
    const match = pattern.exec(afterTerm);
    if (match) return match[0];
  }
  return "";
}

/** A paragraph this long is clause text, not a heading, whatever its style
 *  says: legal templates give numbered BODY clauses an outline level, and in
 *  both real sample agreements those "Heading 3"/"WP2L3" clauses run to
 *  hundreds of characters while the true headings stay under 100. */
const MAX_SECTION_HEADING_LENGTH = 120;

/** "Definitions", "Certain Definitions", "Defined Terms", "Definitions and
 *  Interpretation". */
const DEFINITIONS_HEADING_PATTERN = /defin/i;

export interface DefinitionEntryMatch {
  term: string;
  /** From the opening quote through the defining verb, as written. */
  matchText: string;
  /** Where `matchText` starts in the paragraph's text. */
  offset: number;
  /** An acronym's full form, as the entry itself spells it out — absent for a
   *  term that is not an acronym or whose entry does not spell it out. */
  fullForm?: string;
  /** The entry's other name for the same term: "Processing" in `"Process" or "Processing" means`. */
  alternativeTerm?: string;
}

/** The other name an "or" entry gives its term; "and" names a second, separate term. */
const ALTERNATIVE_TERM = /^(?:,\s*)?or\s+"([^"]{1,80})"/i;

/** How the operator wants acronyms ordered: by their letters ("GAAP" under
 *  G-A) or by the words they stand for ("generally accepted…" under G-E). */
export type DefinitionSortKey = "abbreviation" | "full_form";

/** A value from outside the type system (tool args, stored fix params) as a
 *  sort key; anything unrecognised is the default, abbreviation. */
export function parseDefinitionSortKey(value: unknown): DefinitionSortKey {
  return value === "full_form" ? "full_form" : "abbreviation";
}

/** A term sorted by its full form: one all-capitals word ("ERISA", "R&W").
 *  "IT Assets", "ERISA Affiliate" and "401(k) Plan" sort as written. */
const ACRONYM_TERM = /^[A-Z][A-Z&]*[A-Z]$/;
const FULL_FORM_WORD = /[A-Za-z][A-Za-z'.]*/g;
/** Words a full form may run through without using up a letter ("Department
 *  of Labor"); one still supplies a letter when the acronym includes it (DOL). */
const FULL_FORM_CONNECTIVES = new Set([
  "a",
  "an",
  "and",
  "at",
  "by",
  "for",
  "from",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "under",
  "upon",
  "with",
]);
/** How far into an entry a full form is looked for: it sits at the start of
 *  the meaning, and a long definition's later words would only invite a
 *  coincidental match. */
const FULL_FORM_SEARCH_WORDS = 40;

/** The words, from `words[w]` on, whose initials spell `letters[l]` onward —
 *  or null. A connective may be skipped, except before the first letter. */
function spellInitials(letters: readonly string[], words: readonly string[], w: number, l: number): string[] | null {
  if (l === letters.length) return [];
  const word = words[w];
  if (word === undefined) return null;
  if (word[0]?.toUpperCase() === letters[l]) {
    const rest = spellInitials(letters, words, w + 1, l + 1);
    if (rest) return [word, ...rest];
  }
  if (l === 0 || !FULL_FORM_CONNECTIVES.has(word.toLowerCase())) return null;
  const rest = spellInitials(letters, words, w + 1, l);
  return rest ? [word, ...rest] : null;
}

/** The words of `meaning` an acronym `term` stands for: the first run of words
 *  whose initials spell it ("GAAP" in "United States generally accepted
 *  accounting principles" → "generally accepted accounting principles"). Null
 *  when `term` is not an acronym or the entry never spells it out — an entry
 *  that only points elsewhere ("has the meaning set forth in Section 4.10"),
 *  lists or computes something, or names it in another order. Only the
 *  document's own words count; nothing is supplied from outside it. */
export function acronymFullForm(term: string, meaning: string): string | null {
  const acronym = term.trim();
  if (!ACRONYM_TERM.test(acronym)) return null;
  const letters = [...acronym].filter((c) => c !== "&");
  const words = (straightenQuotes(meaning).match(FULL_FORM_WORD) ?? []).slice(0, FULL_FORM_SEARCH_WORDS);
  for (let start = 0; start < words.length; start += 1) {
    const spelled = spellInitials(letters, words, start, 0);
    // A word keeps its dots so "U.S." stays one word; the sentence's own full
    // stop is not part of the name.
    if (spelled) {
      let fullForm = spelled.join(" ");
      while (fullForm.endsWith(".")) fullForm = fullForm.slice(0, -1);
      return fullForm;
    }
  }
  return null;
}

/** The entry `text` opens with, or null when it is not a definition entry. */
export function parseDefinitionEntry(text: string): DefinitionEntryMatch | null {
  // Matched against a quote-straightened copy so Word's curly quotes are
  // still recognised; straightenQuotes keeps every index, so matchText and
  // term are sliced from the ORIGINAL text and still find the live document.
  const straight = straightenTermQuotes(text);
  const start = definitionEntryStart(straight);
  const rest = straight.slice(start);
  const termMatch = DEFINITION_ENTRY_TERM.exec(rest);
  const quotedTerm = termMatch?.[1];
  if (!termMatch || !quotedTerm?.trim()) return null;
  const qualifier = entryQualifier(rest.slice(termMatch[0].length));
  const afterTerm = rest.slice(termMatch[0].length + qualifier.length);
  const verb = DEFINITION_ENTRY_VERB.exec(afterTerm) ?? DEFINITION_ENTRY_MEANING_VERB.exec(afterTerm);
  if (!verb) return null;
  const verbEnd = termMatch[0].length + qualifier.length + verb[0].length;
  const extension = DEFINITION_VERB_EXTENSION.exec(rest.slice(verbEnd));
  const bracket = rest.startsWith("[") ? 1 : 0;
  const offset = start + bracket;
  const termStart = offset + 1;
  const matchEnd = start + verbEnd + (extension?.[0].length ?? 0);
  const term = text.slice(termStart, termStart + quotedTerm.length).trim();
  const fullForm = acronymFullForm(term, text.slice(matchEnd));
  const alternativeTerm = ALTERNATIVE_TERM.exec(qualifier)?.[1]?.trim();
  return {
    term,
    matchText: text.slice(offset, matchEnd),
    offset,
    ...(fullForm ? { fullForm } : {}),
    ...(alternativeTerm ? { alternativeTerm } : {}),
  };
}

// ---------------------------------------------------------------------------
// Declared-terms inventory (AIVA-9190) — every term the document defines, read
// once by one parser and filtered by each defined-terms rule
// ---------------------------------------------------------------------------

/** Where a term is declared: a Definitions-style entry opening its paragraph,
 *  `(the "X")`, an in-passing `(a "X")` / `constitute a "X"`, or `"X" means`
 *  mid-sentence. */
export type DeclarationShape = "entry" | "parenthetical" | "passing" | "inline";

/** `defines` gives the meaning here; `adopts` borrows another word's
 *  ("has the meaning given to …"); `points` sends the reader elsewhere. */
export type DefinitionPhrasing = "defines" | "adopts" | "points";

export interface DeclaredTerm {
  term: string;
  /** `term` lower-cased — how rules match one declaration to another. */
  key: string;
  shape: DeclarationShape;
  phrasing: DefinitionPhrasing;
  paragraphIndex: number;
  /** Where `matchText` starts in the paragraph. */
  offset: number;
  /** Where the term's opening quote sits — the point of declaration. */
  termOffset: number;
  /** The anchor text, as written (curly quotes kept). */
  matchText: string;
  fullForm?: string;
  alternativeTerm?: string;
}

// `Atlas Holdings Ltd. (the "Acquirer")` — the `the`/`this` lead-in is optional.
const DECLARATION_PATTERN = /\((?:(?:the|this)\s+)?"([A-Za-z][A-Za-z .,'&-]{1,40})"\)/g;
// `(a "Service Credit")`, `(collectively, the "Client Materials")`; capitalised
// terms only, so `(a "reasonable" period)` is not a definition.
const PASSING_DECLARATION_PATTERN = /\((?:[a-z]+,?\s+){1,3}"([A-Z][A-Za-z '&-]{0,39}[A-Za-z])"(?:[),]|\s+(?:and|or)\b)/g;
// `such failure shall constitute a "Chronic Failure"`.
const CONSTITUTES_DECLARATION_PATTERN = /\bconstitut(?:e|es|ed|ing)\s+(?:a|an|the)\s+"([A-Z][A-Za-z '&-]{0,39}[A-Za-z])"/g;
// The second name in one bracket: `(each a "Dispute" and collectively, "Disputes")`.
const SECOND_DECLARATION_PATTERN = /"[^"]{1,60}",?\s+(?:and|or)\s+(?:[a-z]+,?\s+){0,3}"([A-Z][A-Za-z '&-]{0,39}[A-Za-z])"\)/g;
/** A quoted capitalised term, allowing the sentence's full stop inside the quotes ("Parties."). */
const QUOTED_TERM_CAPTURE = `"([A-Z][A-Za-z '&-]{0,39}[A-Za-z])[.,]?"`;
// `referred to herein individually as a "Party"`, `hereinafter referred to as the "Buyer"`.
const REFERRED_TO_PATTERN = new RegExp(
  String.raw`\breferred\s+to\s+(?:[a-z]+\s+){0,3}as\s+(?:an?\s+|the\s+)?${QUOTED_TERM_CAPTURE}`,
  "g",
);
// Its second name: `… as a "Party" and collectively as the "Parties".`
const COLLECTIVELY_AS_PATTERN = new RegExp(
  String.raw`"[^"]{1,60}"\s+and\s+(?:[a-z]+,?\s+){0,2}as\s+(?:the\s+)?${QUOTED_TERM_CAPTURE}`,
  "g",
);
/** `"X" means …` anywhere in a sentence. */
const INLINE_DEFINITION_PATTERN = new RegExp(
  String.raw`"([A-Z][A-Z .,'&-]{1,60})"\s+(?:${DEFINITION_VERB}|${DEFINITION_MEANING_VERB}(?:${DEFINITION_MEANING_SOURCE})?)\b`,
  "gi",
);

/** How a matched phrase ties its term to a meaning. Reads only the verb — not
 *  the term ("Defined Benefit Plan") nor an aside before the verb. */
function definitionPhrasing(phrase: string, after: string): DefinitionPhrasing {
  const afterTerm = phrase.slice(phrase.indexOf('"', 1) + 1).trimStart();
  return verbPhrasing(afterTerm.slice(entryQualifier(afterTerm).length), after);
}

function verbPhrasing(verb: string, after: string): DefinitionPhrasing {
  if (!/\b(?:meanings?|defined)\b/i.test(verb)) return "defines";
  return /\bgiven\s+to$/i.test(verb) && !/^\s+it\b/i.test(after) ? "adopts" : "points";
}

/** A UK-style entry, `Business Day: a day other than…`: a capitalised term,
 *  then a colon. Read only in a Definitions section, and only when Word
 *  reports the term bold (`readColonEntries`). */
const COLON_ENTRY_TERM = /^([A-Z][\w’'&()/ -]{0,78}[\w)])\s*:\s+\S/;

/** The bold-colon entry `text` opens with, or null. `Business Day: has the
 *  meaning given in clause 3` points elsewhere, as the quoted form would. */
function colonEntryDeclaration(text: string, paragraphIndex: number): DeclaredTerm | null {
  const start = definitionEntryStart(text);
  const match = COLON_ENTRY_TERM.exec(text.slice(start));
  const term = match?.[1];
  if (!match || !term) return null;
  const after = text.slice(start + match[0].length - 1);
  const verb = DEFINITION_ENTRY_VERB.exec(after) ?? DEFINITION_ENTRY_MEANING_VERB.exec(after);
  const extension = verb ? (DEFINITION_VERB_EXTENSION.exec(after.slice(verb[0].length))?.[0] ?? "") : "";
  const verbText = (verb?.[0] ?? "") + extension;
  return {
    term,
    key: term.toLowerCase(),
    shape: "entry",
    phrasing: verbPhrasing(verbText, after.slice(verbText.length)),
    paragraphIndex,
    offset: start,
    termOffset: start,
    matchText: text.slice(start, start + match[0].indexOf(":") + 1),
  };
}

function regexDeclaration(
  text: string,
  straight: string,
  paragraphIndex: number,
  match: RegExpMatchArray,
  shape: DeclarationShape,
): DeclaredTerm {
  // Every pattern's term starts with a letter, so `term` is never empty.
  const { matchText, term } = originalMatchAndTerm(text, match);
  const offset = match.index ?? 0;
  const phrasing = shape === "inline" ? definitionPhrasing(match[0], straight.slice(offset + match[0].length)) : "defines";
  return { term, key: term.toLowerCase(), shape, phrasing, paragraphIndex, offset, termOffset: offset + match[0].lastIndexOf(`"${match[1] ?? ""}`), matchText };
}

function entryDeclaration(entry: DefinitionEntryMatch, straight: string, paragraphIndex: number): DeclaredTerm {
  const end = entry.offset + entry.matchText.length;
  return {
    term: entry.term,
    key: entry.term.toLowerCase(),
    shape: "entry",
    phrasing: definitionPhrasing(straight.slice(entry.offset, end), straight.slice(end)),
    paragraphIndex,
    offset: entry.offset,
    termOffset: entry.offset,
    matchText: entry.matchText,
    ...(entry.fullForm ? { fullForm: entry.fullForm } : {}),
    ...(entry.alternativeTerm ? { alternativeTerm: entry.alternativeTerm } : {}),
  };
}

/** The paragraph's entry: a quoted one, else a bold-colon one when `colonEntry` says Word read it so. */
function paragraphEntry(text: string, straight: string, paragraphIndex: number, colonEntry: boolean): DeclaredTerm | null {
  const entry = parseDefinitionEntry(text);
  if (entry) return entryDeclaration(entry, straight, paragraphIndex);
  return colonEntry ? colonEntryDeclaration(text, paragraphIndex) : null;
}

/** One paragraph's declarations in reading order; one per quoted term, the
 *  first shape listed winning where two shapes match the same quote. */
function paragraphDeclarations(text: string, paragraphIndex: number, colonEntry: boolean): DeclaredTerm[] {
  const straight = straightenTermQuotes(text);
  const entry = paragraphEntry(text, straight, paragraphIndex, colonEntry);
  const found = entry ? [entry] : [];
  const shapes: Array<[RegExp, DeclarationShape]> = [
    [DECLARATION_PATTERN, "parenthetical"],
    [INLINE_DEFINITION_PATTERN, "inline"],
    [PASSING_DECLARATION_PATTERN, "passing"],
    [CONSTITUTES_DECLARATION_PATTERN, "passing"],
    [SECOND_DECLARATION_PATTERN, "passing"],
    [REFERRED_TO_PATTERN, "passing"],
    [COLLECTIVELY_AS_PATTERN, "passing"],
  ];
  for (const [pattern, shape] of shapes) {
    for (const match of straight.matchAll(pattern)) {
      const declared = regexDeclaration(text, straight, paragraphIndex, match, shape);
      if (!found.some((d) => d.termOffset === declared.termOffset)) found.push(declared);
    }
  }
  return found.sort((a, b) => a.termOffset - b.termOffset);
}

/** Pure: every declaration in the document, in document order — including
 *  mid-sentence pointers, which tell the reader a term is defined elsewhere.
 *  `colonEntries`: the paragraphs `readColonEntries` found to be bold-colon entries. */
export function collectDeclaredTerms(texts: readonly string[], colonEntries: ReadonlySet<number> = new Set()): DeclaredTerm[] {
  return texts.flatMap((text, paragraphIndex) => paragraphDeclarations(text, paragraphIndex, colonEntries.has(paragraphIndex)));
}

/** Whether `declared` is where its term is declared. A mid-sentence pointer
 *  ("As used herein, "X" has the meaning set forth in …") is not. */
export function isDeclarationSite(declared: DeclaredTerm): boolean {
  return declared.shape !== "inline" || declared.phrasing !== "points";
}

/** Each term at its first declaration site, in document order. */
export function firstDeclarations(inventory: readonly DeclaredTerm[]): DeclaredTerm[] {
  const seen = new Set<string>();
  return inventory.filter((declared) => {
    if (!isDeclarationSite(declared) || seen.has(declared.key)) return false;
    seen.add(declared.key);
    return true;
  });
}

/** Each paragraph's Definitions-style entry, or null — what the section finder reads. */
function definitionEntriesByParagraph(inventory: readonly DeclaredTerm[], paragraphCount: number): Array<DefinitionEntryMatch | null> {
  const entries: Array<DefinitionEntryMatch | null> = Array.from({ length: paragraphCount }, () => null);
  for (const declared of inventory) {
    if (declared.shape !== "entry") continue;
    const { term, matchText, offset, fullForm } = declared;
    entries[declared.paragraphIndex] = { term, matchText, offset, ...(fullForm ? { fullForm } : {}) };
  }
  return entries;
}

/** The acronyms a full-form sort could only order by their letters, because
 *  their entry never spells them out — what the operator is told about. */
export function acronymsWithoutFullForm(definitions: ReadonlyArray<{ term: string; fullForm?: string }>): string[] {
  return definitions.filter((d) => ACRONYM_TERM.test(d.term.trim()) && !d.fullForm).map((d) => d.term);
}

/** How the acronyms on one alphabetical-order card were ordered, so the card
 *  itself explains an order that would otherwise look like a bug ("Notice"
 *  before "NTL" by abbreviation; "NTL" by its letters when its entry never
 *  spells it out). Written here rather than left to the chat reply, which the
 *  model was told to open with this and mostly did not. Empty when neither
 *  term is an acronym: the order is then plain alphabetical either way. */
export function sortOrderNote(
  entries: ReadonlyArray<{ term: string; fullForm?: string }>,
  sortKey: DefinitionSortKey,
): string {
  const acronyms = entries.filter((e) => ACRONYM_TERM.test(e.term.trim()));
  if (acronyms.length === 0) return "";
  if (sortKey === "abbreviation") return "Acronyms are sorted by abbreviation.";
  return acronyms
    .map(({ term, fullForm }) =>
      fullForm
        ? `"${term}" is sorted by its full form, "${fullForm}".`
        : `"${term}" has no full form in the Definitions section, so it is sorted by abbreviation.`,
    )
    .join(" ");
}

/** What an entry is ordered by: its full form when the operator asked for
 *  that and the entry spells one out; otherwise the term as written. */
export function entrySortKey(entry: { term: string; fullForm?: string }, sortKey: DefinitionSortKey): string {
  return definitionOrderingKey(sortKey === "full_form" && entry.fullForm ? entry.fullForm : entry.term);
}

/** How two entries compare: the way Litera Check compares them, because
 *  operators check AiVA's findings against Litera's. Case is ignored, hyphens
 *  are dropped ("Non-Recourse Party" sorts as "Nonrecourse Party"), and digits
 *  sort before letters by plain code order ("401(k) Plan" first). This matched
 *  Litera's out-of-order comments exactly on two real agreements. The check
 *  and the fix both compare through this, so they can never disagree about
 *  whether a term is misplaced. */
export function definitionOrderingKey(term: string): string {
  return straightenQuotes(term).toLowerCase().replace(/-/g, "").replace(/\s+/g, " ").trim();
}

/** What the section finder needs to know about one body paragraph. */
export interface DefinitionParagraphFacts {
  text: string;
  style: string;
  outlineLevel: number | null;
  entry: DefinitionEntryMatch | null;
  /** List depth, or null for a paragraph that is not a list item. */
  listLevel: number | null;
  /** Struck through by an unaccepted tracked deletion: still in Word's
   *  paragraph collection, but not an entry anyone reading the document sees. */
  deleted: boolean;
}

export interface DefinitionEntry extends DefinitionEntryMatch {
  paragraphIndex: number;
}

export interface DefinitionsSection {
  /** The section's live, top-level entries, in document order. */
  entries: DefinitionEntry[];
  /** Paragraphs an entry's extent stops before: every top-level entry of the
   *  section, struck-through ones included, so a move never swallows the
   *  struck copy an earlier fix left behind. */
  stops: number[];
  /** The section's heading paragraph; -1 when the document has no headings
   *  and the whole body stood in for the section. */
  startParagraph: number;
  /** The section's last paragraph. */
  endParagraph: number;
  /** The heading's text. Not always a "Definitions" heading: with none in the
   *  document, the finder falls back to the section holding the most entries
   *  — see `isNamedDefinitionsSection`. */
  title: string;
}

interface SectionSpan {
  title: string;
  level: number;
  start: number;
  end: number;
}

interface ScoredSpan {
  span: SectionSpan;
  entryCount: number;
}

/** Each short heading's span: up to the next heading at the same or a higher
 *  level, the way Word's own outline nests. A deeper "heading" — a numbered
 *  sub-clause with an outline level — stays inside its parent's span, so an
 *  entry's (a), (b) sub-paragraphs never cut the Definitions section short. */
function headingSpans(facts: readonly DefinitionParagraphFacts[]): SectionSpan[] {
  const headings = facts.flatMap((fact, index) => {
    const title = fact.text.trim();
    if (fact.entry || !title || title.length > MAX_SECTION_HEADING_LENGTH) return [];
    const level = headingLevelFrom(fact.style, fact.outlineLevel);
    return level === null ? [] : [{ title, level, index }];
  });
  return headings.map((heading, i) => {
    const next = headings.slice(i + 1).find((candidate) => candidate.level <= heading.level);
    return { title: heading.title, level: heading.level, start: heading.index, end: (next ? next.index : facts.length) - 1 };
  });
}

function liveEntryCount(facts: readonly DefinitionParagraphFacts[], span: SectionSpan): number {
  let count = 0;
  for (let i = span.start + 1; i <= span.end; i += 1) {
    const fact = facts[i];
    if (fact?.entry && !fact.deleted) count += 1;
  }
  return count;
}

/** More entries wins; on a tie the deeper heading, which is the narrower span
 *  ("Certain Definitions" over the article "CERTAIN DEFINITIONS" holding it).
 *  A remaining tie keeps the earlier span. */
function isBetterSpan(candidate: ScoredSpan, best: ScoredSpan): boolean {
  if (candidate.entryCount !== best.entryCount) return candidate.entryCount > best.entryCount;
  return candidate.span.level > best.span.level;
}

function containsSpan(outer: SectionSpan, inner: SectionSpan): boolean {
  return inner !== outer && inner.start > outer.start && inner.end <= outer.end;
}

/** The Definitions section: the heading naming definitions that holds the most
 *  entries. Where one such heading sits inside another — the article
 *  "CERTAIN DEFINITIONS" holding the section "Certain Definitions" — only the
 *  inner one counts: the outer always holds at least as many entries, plus the
 *  sections after it ("Construction; Interpretation"). A document with no such
 *  heading falls back to the heading section with the most entries, and one
 *  with no headings at all to the whole body. */
function chooseDefinitionsSpan(facts: readonly DefinitionParagraphFacts[]): SectionSpan {
  const scored = headingSpans(facts)
    .map((span) => ({ span, entryCount: liveEntryCount(facts, span) }))
    .filter((candidate) => candidate.entryCount > 0);
  const naming = scored.filter((candidate) => DEFINITIONS_HEADING_PATTERN.test(candidate.span.title));
  const named = naming.filter((outer) => !naming.some((inner) => containsSpan(outer.span, inner.span)));
  const pool = named.length > 0 ? named : scored;
  let best: ScoredSpan | undefined;
  for (const candidate of pool) {
    if (!best || isBetterSpan(candidate, best)) best = candidate;
  }
  return best?.span ?? { title: "", level: 0, start: -1, end: facts.length - 1 };
}

/** The list depth most entries sit at. A deeper entry is a sub-definition
 *  numbered INSIDE another entry (Litera does not rank those either); a
 *  non-list paragraph counts as depth 0. Ties go to the shallower depth. */
function mainListLevel(levels: readonly number[]): number {
  const counts = new Map<number, number>();
  for (const level of levels) counts.set(level, (counts.get(level) ?? 0) + 1);
  let main = 0;
  let mainCount = -1;
  for (const [level, count] of counts) {
    if (count > mainCount || (count === mainCount && level < main)) {
      main = level;
      mainCount = count;
    }
  }
  return main;
}

/** Pure: picks the Definitions section out of a paragraph sweep. */
export function selectDefinitionsSection(facts: readonly DefinitionParagraphFacts[]): DefinitionsSection {
  const span = chooseDefinitionsSpan(facts);
  const candidates: Array<{ index: number; fact: DefinitionParagraphFacts; entry: DefinitionEntryMatch }> = [];
  for (let index = span.start + 1; index <= span.end; index += 1) {
    const fact = facts[index];
    if (fact?.entry) candidates.push({ index, fact, entry: fact.entry });
  }
  const main = mainListLevel(candidates.filter((c) => !c.fact.deleted).map((c) => c.fact.listLevel ?? 0));
  const topLevel = candidates.filter((c) => (c.fact.listLevel ?? 0) <= main);
  return {
    entries: topLevel.filter((c) => !c.fact.deleted).map((c) => ({ ...c.entry, paragraphIndex: c.index })),
    stops: topLevel.map((c) => c.index),
    startParagraph: span.start,
    endParagraph: span.end,
    title: span.title,
  };
}

/** Whether `section` is a real Definitions section — a heading naming
 *  definitions, with at least one entry — rather than the finder's fallback
 *  guess. Only a real one is ever written into. */
export function isNamedDefinitionsSection(section: DefinitionsSection): boolean {
  return section.startParagraph >= 0 && section.entries.length > 0 && DEFINITIONS_HEADING_PATTERN.test(section.title);
}

/** Whether a heading's span holds any body text, not just deeper headings. */
function hasBodyText(facts: readonly DefinitionParagraphFacts[], span: SectionSpan): boolean {
  return facts
    .slice(span.start + 1, span.end + 1)
    .some((fact) => fact.text.trim() !== "" && headingLevelFrom(fact.style, fact.outlineLevel) === null);
}

/** Pure: the paragraphs whose text stands for the Definitions section, or null.
 *  The real section when `section` is one; otherwise, when no entry can be read,
 *  the first heading naming definitions with text under it — so a contents line
 *  styled as a heading is skipped (AIVA-9198). */
export function definitionsTextRange(
  facts: readonly DefinitionParagraphFacts[],
  section: DefinitionsSection,
): { start: number; end: number } | null {
  // A section chosen by its entries is the answer.
  if (isNamedDefinitionsSection(section)) return { start: section.startParagraph, end: section.endParagraph };
  // No readable entries: the first Definitions heading with text under it, never an empty contents line.
  const span = headingSpans(facts).find(
    (candidate) => DEFINITIONS_HEADING_PATTERN.test(candidate.title) && hasBodyText(facts, candidate),
  );
  return span ? { start: span.start, end: span.end } : null;
}

type ParagraphLayout = Pick<DefinitionParagraphFacts, "text" | "style" | "outlineLevel">;

/** Pure: the paragraphs that may be bold-colon entries, with their terms —
 *  under a heading naming definitions, not themselves a heading or a quoted
 *  entry. Signature blocks (`SELLER:`) sit outside such a section. Whether the
 *  term is bold only Word can say (`readColonEntries`). */
export function colonEntryCandidates(paragraphs: readonly ParagraphLayout[]): Map<number, string> {
  const facts = paragraphs.map(
    (p): DefinitionParagraphFacts => ({ ...p, entry: parseDefinitionEntry(p.text), listLevel: null, deleted: false }),
  );
  const isHeading = (fact: DefinitionParagraphFacts) =>
    headingLevelFrom(fact.style, fact.outlineLevel) !== null && fact.text.trim().length <= MAX_SECTION_HEADING_LENGTH;
  const candidates = new Map<number, string>();
  for (const span of headingSpans(facts).filter((s) => DEFINITIONS_HEADING_PATTERN.test(s.title))) {
    for (let index = span.start + 1; index <= span.end; index += 1) {
      const fact = facts[index];
      if (!fact || fact.entry || isHeading(fact)) continue;
      const term = colonEntryDeclaration(fact.text, index)?.term;
      if (term) candidates.set(index, term);
    }
  }
  return candidates;
}

/** The candidates whose term Word reports bold throughout (a mixed run reads
 *  null). One round trip; a host that cannot search reads none. Needs the
 *  paragraphs' text, style and outlineLevel loaded. */
async function readColonEntries(context: Word.RequestContext, paragraphs: Word.ParagraphCollection): Promise<Set<number>> {
  const candidates = colonEntryCandidates(
    paragraphs.items.map((p) => ({ text: p.text ?? "", style: p.style ?? "", outlineLevel: p.outlineLevel ?? null })),
  );
  if (candidates.size === 0) return new Set();
  try {
    const hits = [...candidates].flatMap(([index, term]) => {
      const hit = paragraphs.items[index]?.search(term, { matchCase: true }).getFirstOrNullObject();
      hit?.load("isNullObject,font/bold");
      return hit ? [{ index, hit }] : [];
    });
    await context.sync();
    return new Set(hits.filter(({ hit }) => !hit.isNullObject && hit.font.bold === true).map(({ index }) => index));
  } catch (error) {
    logOfficeError(error, "docreview.colon_entries.bold");
    return new Set();
  }
}

/** `collectDeclaredTerms` over the live document, bold-colon entries included.
 *  `texts` may run past the body (footnotes); only body paragraphs hold entries. */
async function readDeclaredTerms(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  texts: readonly string[],
): Promise<DeclaredTerm[]> {
  return collectDeclaredTerms(texts, await readColonEntries(context, paragraphs));
}

type IndexedParagraph = { paragraph: Word.Paragraph; index: number };

/** WordApi 1.3. A host without it reads every entry as top level: nested
 *  sub-definitions are then ranked too, which over-reports rather than hiding
 *  an entry. */
async function readListLevels(
  context: Word.RequestContext,
  candidates: readonly IndexedParagraph[],
): Promise<Map<number, number | null>> {
  const levels = new Map<number, number | null>();
  try {
    const items = candidates.map(({ paragraph, index }) => {
      const listItem = paragraph.listItemOrNullObject;
      listItem.load("level");
      return { listItem, index };
    });
    await context.sync();
    for (const { listItem, index } of items) levels.set(index, listItem.isNullObject ? null : (listItem.level ?? null));
  } catch (error) {
    logOfficeError(error, "docreview.alphabetical_order.list_levels");
  }
  return levels;
}

/** Each candidate's text with tracked changes applied. A paragraph an earlier
 *  fix moved away stays in body.paragraphs, struck through, until the deletion
 *  is accepted. Asking Word for the reviewed text tells it apart from the live
 *  copy; the previous approach matched deleted-change TEXT against paragraph
 *  text, which also hid the live copy (same text) and placed the next fix as if
 *  the moved term did not exist. WordApi 1.4; a host without it treats nothing
 *  as deleted. */
async function readCurrentTexts(
  context: Word.RequestContext,
  candidates: readonly IndexedParagraph[],
): Promise<Map<number, string>> {
  const current = new Map<number, string>();
  try {
    const results = candidates.map(({ paragraph, index }) => ({
      index,
      reviewed: paragraph.getReviewedText(Word.ChangeTrackingVersion.current),
    }));
    await context.sync();
    for (const { index, reviewed } of results) current.set(index, reviewed.value);
  } catch (error) {
    logOfficeError(error, "docreview.alphabetical_order.reviewed_text");
  }
  return current;
}

/** Whether nothing of a paragraph survives its tracked changes. Word's reviewed
 *  text is not plain text: it wraps every content control in "<" … ">" — a
 *  review anchor included — and keeps the paragraph mark, so
 *  `"<“Affiliate” means> any entity…\r"` comes back for a live, anchored
 *  entry. Parsing it for the term failed on every anchored entry, which made
 *  the fix lose the very entry it was asked to move. So it answers only "is
 *  anything left?"; the term is always read from the plain text. */
function isStruckThrough(reviewed: string | undefined): boolean {
  return reviewed !== undefined && !/[\p{L}\p{N}]/u.test(reviewed);
}

async function readDefinitionsSection(context: Word.RequestContext): Promise<{
  paragraphs: Word.ParagraphCollection;
  texts: string[];
  inventory: DeclaredTerm[];
  facts: DefinitionParagraphFacts[];
  section: DefinitionsSection;
}> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items/text,items/style,items/outlineLevel");
  await context.sync();

  const texts = paragraphs.items.map((p) => p.text ?? "");
  const inventory = await readDeclaredTerms(context, paragraphs, texts);
  const entries = definitionEntriesByParagraph(inventory, texts.length);
  const candidates = paragraphs.items.flatMap((paragraph, index) => (entries[index] ? [{ paragraph, index }] : []));
  const listLevels = await readListLevels(context, candidates);
  const currentTexts = await readCurrentTexts(context, candidates);

  const facts = paragraphs.items.map(
    (paragraph, index): DefinitionParagraphFacts => ({
      text: texts[index] ?? "",
      style: paragraph.style ?? "",
      outlineLevel: paragraph.outlineLevel ?? null,
      entry: entries[index] ?? null,
      listLevel: listLevels.get(index) ?? null,
      deleted: isStruckThrough(currentTexts.get(index)),
    }),
  );
  return { paragraphs, texts, inventory, facts, section: selectDefinitionsSection(facts) };
}

/** Raw sweep only: the Definitions section's entries, in document order. */
export function findAlphabeticalOrder(): Promise<AlphabeticalOrderSweep> {
  return Word.run(async (context) => {
    const { texts, section } = await readDefinitionsSection(context);
    const counter = documentOccurrenceCounter(texts);
    const definitions: RawDefinition[] = [];
    for (const entry of section.entries) {
      const occurrenceIndex = counter.at(entry.paragraphIndex, entry.offset, entry.matchText);
      if (occurrenceIndex === null) continue;
      definitions.push({
        matchText: entry.matchText,
        occurrenceIndex,
        term: entry.term,
        paragraphIndex: entry.paragraphIndex,
        ...(entry.fullForm ? { fullForm: entry.fullForm } : {}),
      });
    }
    return { definitions };
  });
}

/** A term listed before the term ahead of it. Each entry is compared with the
 *  one directly before it — not with the highest term seen so far, which
 *  flagged every entry after a single misplaced one (Apple, Mango, Banana,
 *  Cherry flagged both Banana and Cherry; ~37 findings on a real agreement
 *  where Litera reports 3). */
export function evaluateAlphabeticalOrder(
  sweep: AlphabeticalOrderSweep,
  sortKey: DefinitionSortKey = "abbreviation",
): FindingCreate[] {
  return sweep.definitions.flatMap((defn, i) => {
    const previous = sweep.definitions[i - 1];
    if (!previous || entrySortKey(defn, sortKey) >= entrySortKey(previous, sortKey)) return [];
    const note = sortOrderNote([previous, defn], sortKey);
    return [
      toFindingCreate({
        ruleId: "alphabetical_order",
        matchText: defn.matchText,
        occurrenceIndex: defn.occurrenceIndex,
        problem: `"${defn.term}" is out of alphabetical order: it comes after "${previous.term}".`,
        comment: [`Moved "${defn.term}" to its correct alphabetical position.`, note].filter(Boolean).join(" "),
        fixHandler: "word.docreview.fix_alphabetical_order",
        // The fix must order the list the way the check did. Absent means
        // abbreviation, so findings stored before full-form sorting still apply.
        fixParams: sortKey === "full_form" ? { term: defn.term, sortKey } : { term: defn.term },
      }),
    ];
  });
}

export interface DefinitionMovePlan {
  /** First and last paragraph of the entry being moved — every paragraph of
   *  it, so its (a), (b) sub-paragraphs travel with it. */
  fromStart: number;
  fromEnd: number;
  targetIndex: number;
  location: "Before" | "After";
}

/** The last paragraph of the entry starting at `start`: up to the next
 *  top-level entry (struck copies included) or the section's end, less any
 *  trailing blank paragraphs that only separate it from what follows. */
function entryExtentEnd(section: DefinitionsSection, texts: readonly string[], start: number): number {
  const nextStop = section.stops.find((stop) => stop > start);
  let end = (nextStop ?? section.endParagraph + 1) - 1;
  while (end > start && !(texts[end] ?? "").trim()) end -= 1;
  return end;
}

/** Pure: where the entry at `sourcePosition` goes — directly before the first
 *  other entry, in document order, that sorts after it; after the last entry
 *  when none does. Null when it is already there. A flagged entry always has a
 *  larger term somewhere before it, so its move can never land it back where
 *  it started — the no-op "Applied" moves the old neighbour-based placement
 *  made whenever the rest of the list was itself out of order. */
export function planDefinitionMove(
  section: DefinitionsSection,
  texts: readonly string[],
  sourcePosition: number,
  sortKey: DefinitionSortKey = "abbreviation",
): DefinitionMovePlan | null {
  const { entries } = section;
  const source = entries[sourcePosition];
  if (!source) return null;
  const key = entrySortKey(source, sortKey);
  const fromStart = source.paragraphIndex;
  const fromEnd = entryExtentEnd(section, texts, fromStart);
  const target = entries.find((entry, i) => i !== sourcePosition && entrySortKey(entry, sortKey) > key);
  if (target) {
    if (target === entries[sourcePosition + 1]) return null;
    return { fromStart, fromEnd, targetIndex: target.paragraphIndex, location: "Before" };
  }
  const last = entries[entries.length - 1];
  if (!last || last === source) return null;
  return { fromStart, fromEnd, targetIndex: entryExtentEnd(section, texts, last.paragraphIndex), location: "After" };
}

/** Moves the whole entry with its formatting — as its OOXML, not its plain
 *  text, which dropped the bold term and every paragraph after the first. */
async function moveDefinitionBlock(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  plan: DefinitionMovePlan,
): Promise<void> {
  const first = paragraphs.items[plan.fromStart];
  const last = paragraphs.items[plan.fromEnd];
  const anchor = paragraphs.items[plan.targetIndex];
  if (!first || !last || !anchor) throw new Error("alphabetical_order: entry moved out of range");

  const block = first.getRange(Word.RangeLocation.whole).expandTo(last.getRange(Word.RangeLocation.whole));
  const ooxml = block.getOoxml();
  await context.sync();

  // The package lands in a fresh paragraph, styled like the entry's own last
  // paragraph so a host that merges the package's last paragraph into the mark
  // it lands on (see writeOffsetPageNumber) keeps the entry's formatting.
  const location = plan.location === "Before" ? Word.InsertLocation.before : Word.InsertLocation.after;
  const landing = anchor.insertParagraph("", location);
  landing.style = last.style;
  const moved = landing.insertOoxml(ooxml.value, Word.InsertLocation.end);
  block.delete();
  const controls = moved.contentControls;
  controls.load("items/tag");
  landing.load("text");
  const movedParagraphs = moved.paragraphs;
  movedParagraphs.load("items/text");
  await context.sync();

  await removeLeftoverBlank(context, landing, movedParagraphs);
  // The copy carries the review anchors that sat inside the original, this
  // finding's among them. Two controls with one tag would let a later fix
  // resolve the copy instead of the original.
  for (const control of controls.items) {
    if ((control.tag ?? "").startsWith(ANCHOR_TAG_PREFIX)) control.delete(true);
  }
  await context.sync();
}

/** Removes the blank paragraph inserting the package leaves behind. Verified
 *  live on desktop Word: the entry merges into the landing paragraph and Word
 *  adds an EMPTY paragraph after it, outside the range insertOoxml returns — a
 *  blank line inside the list. Another host may instead leave the landing
 *  itself empty. Queued only — the caller's next sync applies it. */
async function removeLeftoverBlank(
  context: Word.RequestContext,
  landing: Word.Paragraph,
  movedParagraphs: Word.ParagraphCollection,
): Promise<void> {
  if (!(landing.text ?? "").trim()) {
    landing.delete();
    return;
  }
  const lastMoved = movedParagraphs.items[movedParagraphs.items.length - 1] ?? landing;
  const next = lastMoved.getNextOrNullObject();
  next.load("text");
  await context.sync();
  if (next.isNullObject || (next.text ?? "").trim()) return;
  // Only a paragraph this move CREATED goes: its pre-change ("Original") text
  // is empty, because it did not exist — a blank line already in the document
  // reads "\r" there and is left alone. A host that cannot answer leaves the
  // blank in place rather than risk deleting the author's own.
  try {
    const original = next.getReviewedText(Word.ChangeTrackingVersion.original);
    await context.sync();
    if (original.value === "") next.delete();
  } catch (error) {
    logOfficeError(error, "docreview.alphabetical_order.leftover_blank");
  }
}

/** Move a definition to its correct alphabetical position as a genuine tracked
 *  move. The position is computed from a LIVE re-read of the section, not the
 *  scan-time sweep: an earlier fix may already have moved entries. Only this
 *  ONE entry moves, so an operator never sees a move they did not apply. */
export function fixAlphabeticalOrder(
  anchorTag: string,
  expected: string,
  term: string,
  sortKey: DefinitionSortKey = "abbreviation",
): Promise<FixResult> {
  return Word.run(async (context) => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return { applied: false, stale: true, reason: "anchor_missing" };
    if (isStale(cc.text, expected)) {
      return { applied: false, stale: true, reason: "text_changed" };
    }

    // A paragraph has no stable id to match against a separately fetched
    // proxy, so the entry is found the way its anchor's fingerprint identifies
    // it: by content. Struck-through copies are not entries, so an earlier
    // move's leftover can never be picked instead.
    const { paragraphs, texts, section } = await readDefinitionsSection(context);
    const wanted = normalizeForCompare(expected);
    const sourcePosition = section.entries.findIndex((entry) =>
      normalizeForCompare(texts[entry.paragraphIndex] ?? "").includes(wanted),
    );
    const source = section.entries[sourcePosition];
    if (!source || definitionOrderingKey(source.term) !== definitionOrderingKey(term)) {
      return { applied: false, stale: true, reason: "text_changed" };
    }

    const plan = planDefinitionMove(section, texts, sourcePosition, sortKey);
    if (!plan) return { applied: false, stale: true, reason: "already_sorted" };

    const { tracked } = await withTrackingForced(context, () => moveDefinitionBlock(context, paragraphs, plan));
    return { applied: true, stale: false, tracked };
  });
}

// ---------------------------------------------------------------------------
// missing_defined_term — a term defined inline ("(the \"X\")") that is missing from the
// document's own Definitions section
// ---------------------------------------------------------------------------

export interface RawInlineTerm {
  matchText: string;
  /** Which occurrence of this exact matchText, in document order — without
   *  this, the same inline declaration wording appearing more than once
   *  always anchored to occurrence 0 regardless of which one was flagged.
   *  Same reasoning as missing_hyperlink's/bracket_heading_mismatch's/topic_mismatch's/inconsistent_formatting's/defined_term_section_mismatch's/hyperlink_target_mismatch's/shortened_hyperlink_text's/alphabetical_order's own
   *  occurrenceIndex. This sweep never excludes headings from its
   *  paragraph walk, so a plain per-matchText count already matches what a
   *  live, unfiltered search finds. */
  occurrenceIndex: number;
  term: string;
  paragraphIndex: number;
  /** AIVA-8748: whether the declaration sits inside the Definitions section
   *  itself ("Agreement" means … (the "Contract")) — already listed there, in
   *  effect. Absent when the document has no real Definitions section. */
  inDefinitionsSection?: boolean;
  /** AIVA-8748: the section the term is defined in, as the new Definitions
   *  entry names it. Null when the declaration sits under no heading at all;
   *  absent when the document has no real Definitions section. */
  definedIn?: DefinedInSection | null;
}

/** Where an inline-defined term is defined, as its Definitions entry says it. */
export interface DefinedInSection {
  /** "Section 14" — or the heading's own title when no heading above the
   *  declaration carries a number. */
  label: string;
  /** The heading's text, so the fix can find it again live and link to it. */
  headingText: string;
}

export interface MissingDefinedTermSweep {
  terms: RawInlineTerm[];
  /** The Definitions section's own text, or "" if the document has no
   *  heading matching /definitions?/i — nothing to be "missing from". One
   *  copy for the whole sweep, not per term — a document with ~50 inline
   *  terms and a multi-page Definitions section used to embed that same
   *  text 50 times over, which round-trips through the chat LLM's context
   *  as tool-call arguments (scan result -> model -> evaluate args). */
  definitionsSectionText: string;
  /** AIVA-8748: the ordering key (`definitionOrderingKey`) of every entry in
   *  the Definitions section. Present only when the document has a real one
   *  (`isNamedDefinitionsSection`) — the only kind a new entry can be added
   *  to. Absent, the rule falls back to `definitionsSectionText` and a
   *  comment, as it did before: a section whose entries the parser cannot
   *  read ("Affiliate: any entity…") still gets its missing terms flagged. */
  listedTerms?: string[];
}

const MISSING_DEFINED_TERM_COMMENT = "defined term missing from the Definitions section";

/** The word in front of a section number — "Article 5", "Clause 3.2". */
const SECTION_WORD = /^\s*(section|article|clause)\b/i;

function sectionWordOf(...texts: string[]): string {
  for (const text of texts) {
    const word = SECTION_WORD.exec(text)?.[1];
    if (word) return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }
  return "Section";
}

/** Pure: the section a declaration at `paragraphIndex` is defined in. The
 *  deepest heading above it that carries a number wins ("Section 14.2"),
 *  whether the number is typed into the heading or Word generates it
 *  (`listNumbers`, keyed by the heading's paragraph). With no number anywhere
 *  above it, the innermost heading's own title stands in — clause text a
 *  template gave an outline level is too long to be a title, so it never
 *  does. Null under no heading at all: there is nothing true to point to. */
export function definedInSectionFor(
  toc: readonly TocEntry[],
  listNumbers: ReadonlyMap<number, string>,
  paragraphIndex: number,
): DefinedInSection | null {
  // Later-starting sections are deeper (see sectionNumberByParagraph).
  const enclosing = toc
    .filter((entry) => entry.startParagraph <= paragraphIndex && paragraphIndex <= entry.endParagraph)
    .sort((a, b) => b.startParagraph - a.startParagraph);
  for (const entry of enclosing) {
    const listNumber = listNumbers.get(entry.startParagraph) ?? "";
    const number = headingSectionNumber(entry, listNumbers);
    if (number) return { label: `${sectionWordOf(listNumber, entry.title)} ${number}`, headingText: entry.title.trim() };
  }
  const innermost = enclosing.find((entry) => {
    const title = entry.title.trim();
    return title !== "" && title.length <= MAX_SECTION_HEADING_LENGTH;
  });
  if (!innermost) return null;
  const title = innermost.title.trim();
  return { label: trimTrailingChars(title, (c) => /[\s.:;,]/.test(c)), headingText: title };
}

/** A heading's section number: typed into its title ("3.2 Fees"), else the one
 *  Word draws for it. Null for a lettered level ("(c)"), so a paragraph under
 *  it keeps its parent's number. */
function headingSectionNumber(entry: TocEntry, listNumbers: ReadonlyMap<number, string>): string | null {
  return headingOwnNumber(entry.title) ?? headingOwnNumber(listNumbers.get(entry.startParagraph) ?? "");
}

/** Word's own number for each heading (`listString`, WordApi 1.3), keyed by
 *  paragraph — an auto-numbered heading has none in its text. Best effort: a
 *  host that will not serve it leaves only the typed numbers. */
async function readHeadingListNumbers(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  toc: readonly TocEntry[],
): Promise<Map<number, string>> {
  return readListStrings(
    context,
    paragraphs,
    toc.map((entry) => entry.startParagraph),
    "docreview.heading_list_numbers",
  );
}

/** Word's own list number (`listString`) for each of `indices`, keyed by
 *  paragraph, in one round trip. Best effort: a host that will not serve it
 *  leaves the map empty, and a paragraph that is no list item is absent. */
async function readListStrings(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  indices: readonly number[],
  logContext: string,
): Promise<Map<number, string>> {
  const numbers = new Map<number, string>();
  try {
    const items = indices.flatMap((index) => {
      const paragraph = paragraphs.items[index];
      if (!paragraph) return [];
      const listItem = paragraph.listItemOrNullObject;
      listItem.load("listString");
      return [{ index, listItem }];
    });
    await context.sync();
    for (const { index, listItem } of items) {
      const listString = listItem.isNullObject ? "" : (listItem.listString ?? "").trim();
      if (listString) numbers.set(index, listString);
    }
  } catch (error) {
    logOfficeError(error, logContext);
  }
  return numbers;
}

/** Raw sweep only. Reads the Definitions section the way alphabetical_order
 * does (AIVA-8606's `readDefinitionsSection`), so this rule and the fix that
 * writes into that section agree on which section it is and what it lists. */
export function findMissingDefinedTerm(): Promise<MissingDefinedTermSweep> {
  return Word.run(async (context) => {
    const { paragraphs, texts, inventory, facts, section } = await readDefinitionsSection(context);
    const styles = paragraphs.items.map((p) => p.style ?? "");
    // Without outline levels this resolves no sections at all on a document
    // pasted in from HTML, which carries no paragraph styles.
    const outlineLevels = paragraphs.items.map((p) => p.outlineLevel ?? null);
    const toc = tocFromParagraphs(texts, styles, outlineLevels);
    const textRange = definitionsTextRange(facts, section);
    const definitionsSectionText = textRange ? texts.slice(textRange.start, textRange.end + 1).join(" ") : "";
    const listed = isNamedDefinitionsSection(section) ? section : null;
    const listNumbers = listed ? await readHeadingListNumbers(context, paragraphs, toc) : new Map<number, string>();

    const terms: RawInlineTerm[] = [];
    const counter = documentOccurrenceCounter(texts);
    for (const { shape, matchText, term, paragraphIndex, offset } of inventory) {
      if (shape !== "parenthetical") continue;
      const occurrenceIndex = counter.at(paragraphIndex, offset, matchText);
      if (occurrenceIndex === null) continue;
      const placement = listed
        ? {
            inDefinitionsSection: paragraphIndex > listed.startParagraph && paragraphIndex <= listed.endParagraph,
            definedIn: definedInSectionFor(toc, listNumbers, paragraphIndex),
          }
        : {};
      terms.push({ matchText, occurrenceIndex, term, paragraphIndex, ...placement });
    }
    return {
      terms,
      definitionsSectionText,
      ...(listed ? { listedTerms: listed.entries.map((entry) => definitionOrderingKey(entry.term)) } : {}),
    };
  });
}

/** Deterministic evaluation, ported from rules.py's `evaluate_dt` — a term
 * declared inline that never appears in the document's own Definitions
 * section. Skipped entirely when the document has no such section — there's
 * nothing to be missing from.
 *
 * AIVA-8748: with a real Definitions section (`listedTerms`), "appears" means
 * an ENTRY for the term. The text search it used before counted "Late Fee"
 * as listed whenever another entry merely mentioned it, so the term was never
 * added. Each term is flagged once — at its first inline declaration — so it
 * can only ever be added once; one declared inside the Definitions section
 * counts as listed. */
export function evaluateMissingDefinedTerm(sweep: MissingDefinedTermSweep): FindingCreate[] {
  if (sweep.listedTerms) return evaluateAgainstListedTerms(sweep.terms, sweep.listedTerms);
  const findings: FindingCreate[] = [];
  if (!sweep.definitionsSectionText) return findings;
  const definitionsSectionTextLower = sweep.definitionsSectionText.toLowerCase();
  for (const term of sweep.terms) {
    if (definitionsSectionTextLower.includes(term.term.toLowerCase())) continue;
    findings.push(missingDefinedTermFinding(term, null));
  }
  return findings;
}

function evaluateAgainstListedTerms(terms: readonly RawInlineTerm[], listedTerms: readonly string[]): FindingCreate[] {
  const present = new Set(listedTerms);
  for (const term of terms) {
    if (term.inDefinitionsSection) present.add(definitionOrderingKey(term.term));
  }
  const findings: FindingCreate[] = [];
  for (const term of terms) {
    const key = definitionOrderingKey(term.term);
    if (present.has(key)) continue;
    present.add(key);
    findings.push(missingDefinedTermFinding(term, term.definedIn ?? null));
  }
  return findings;
}

/** With `definedIn`, the fix adds the entry as well as the comment; without
 *  it, the fix only comments, as it always did. */
function missingDefinedTermFinding(term: RawInlineTerm, definedIn: DefinedInSection | null): FindingCreate {
  return toFindingCreate({
    ruleId: "missing_defined_term",
    matchText: term.matchText,
    occurrenceIndex: term.occurrenceIndex,
    problem: definedIn
      ? `"${term.term}" is defined inline in ${definedIn.label} but does not appear in the Definitions section.`
      : `"${term.term}" is defined inline but does not appear in the Definitions section.`,
    comment: MISSING_DEFINED_TERM_COMMENT,
    fixHandler: "word.docreview.fix_missing_defined_term",
    fixParams: definedIn
      ? {
          commentText: MISSING_DEFINED_TERM_COMMENT,
          term: term.term,
          sectionLabel: definedIn.label,
          headingText: definedIn.headingText,
        }
      : { commentText: MISSING_DEFINED_TERM_COMMENT },
  });
}

/** What the fix adds to the Definitions section for one term. */
export interface DefinitionReference {
  term: string;
  /** "Section 14", or a heading's title — see `DefinedInSection.label`. */
  sectionLabel: string;
  /** The heading to link `sectionLabel` to; "" links nothing. */
  headingText: string;
}

export interface DefinitionInsertPlan {
  /** The paragraph the new entry is inserted next to. */
  targetIndex: number;
  location: "Before" | "After";
  /** The entry the new one takes its formatting from: the one it lands in
   *  front of, or the last entry when it goes at the end — never a (a), (b)
   *  sub-paragraph, whose indent and numbering are not an entry's. */
  neighbourIndex: number;
}

/** Pure: where a new entry for `term` goes — directly before the first entry
 *  that sorts after it, by the same key alphabetical_order checks with, or
 *  after the last entry and everything belonging to it. Null when the section
 *  already lists the term, or lists nothing to place it against. */
export function planDefinitionInsert(
  section: DefinitionsSection,
  texts: readonly string[],
  term: string,
): DefinitionInsertPlan | null {
  const key = definitionOrderingKey(term);
  const { entries } = section;
  if (entries.some((entry) => definitionOrderingKey(entry.term) === key)) return null;
  const next = entries.find((entry) => definitionOrderingKey(entry.term) > key);
  if (next) return { targetIndex: next.paragraphIndex, location: "Before", neighbourIndex: next.paragraphIndex };
  const last = entries[entries.length - 1];
  if (!last) return null;
  return {
    targetIndex: entryExtentEnd(section, texts, last.paragraphIndex),
    location: "After",
    neighbourIndex: last.paragraphIndex,
  };
}

/** Pure: the new entry's text, in pieces so each can be formatted —
 *  `"Late Fee" is defined in Section 14.` The "is defined in" wording is the
 *  ops Defined Terms SOP's, and the one `parseDefinitionEntry` reads as an
 *  entry: a later scan sees the term as listed, alphabetical_order ranks it
 *  like any other entry, and defined_term_section_mismatch checks the section
 *  it names. Quotes follow the neighbouring entry's: double or single, curly or
 *  straight. Beside a bold-colon entry it is written that way instead —
 *  `Late Fee: as defined in Section 14.` — and takes the neighbour's bold. */
export function definitionReferenceParts(
  term: string,
  sectionLabel: string,
  neighbourText: string,
): { quotedTerm: string; verb: string; label: string; end: string } {
  const entryText = neighbourText.slice(definitionEntryStart(neighbourText)).replace(/^\[/, "");
  const quotes = ENTRY_QUOTES[entryText.charAt(0)];
  if (!quotes && COLON_ENTRY_TERM.test(entryText)) {
    return { quotedTerm: term, verb: ": as defined in ", label: sectionLabel, end: "." };
  }
  const [open, close] = quotes ?? ['"', '"'];
  return { quotedTerm: `${open}${term}${close}`, verb: " is defined in ", label: sectionLabel, end: "." };
}

/** An entry's opening quote → the pair it writes. */
const ENTRY_QUOTES: Readonly<Record<string, readonly [string, string]>> = {
  "“": ["“", "”"],
  '"': ['"', '"'],
  "‘": ["‘", "’"],
  "'": ["'", "'"],
};

type TermFormat = Partial<Pick<Word.Font, "bold" | "italic" | "underline">>;

/** How the neighbouring entry formats its own term — bold, italic,
 *  underlined — so the new entry's term reads like every other one. Best
 *  effort: a host that cannot search the paragraph leaves the term plain. */
async function readTermFormat(
  context: Word.RequestContext,
  neighbour: Word.Paragraph,
  neighbourTerm: string,
): Promise<TermFormat> {
  try {
    const hit = neighbour.search(neighbourTerm, { matchCase: true }).getFirstOrNullObject();
    hit.load("isNullObject,font/bold,font/italic,font/underline");
    await context.sync();
    if (hit.isNullObject) return {};
    const format: TermFormat = {};
    // Word reports a mixed run as null; only a uniform value is copied.
    if (typeof hit.font.bold === "boolean") format.bold = hit.font.bold;
    if (typeof hit.font.italic === "boolean") format.italic = hit.font.italic;
    if (typeof hit.font.underline === "string" && hit.font.underline !== "Mixed") format.underline = hit.font.underline;
    return format;
  } catch (error) {
    logOfficeError(error, "docreview.missing_defined_term.term_format");
    return {};
  }
}

/** The text after the term would inherit its formatting; this undoes exactly
 *  what was set on the term and nothing else. */
function resetTermFormat(range: Word.Range, format: TermFormat): void {
  if (format.bold) range.font.bold = false;
  if (format.italic) range.font.italic = false;
  if (format.underline && format.underline !== "None") range.font.underline = "None";
}

/** A new paragraph takes the list depth of the one it was inserted next to —
 *  a (a) sub-paragraph's, when the entry goes after the last entry. Brought
 *  back to the neighbouring entry's own depth (WordApi 1.3); best effort. */
async function matchListLevel(context: Word.RequestContext, line: Word.Paragraph, neighbour: Word.Paragraph): Promise<void> {
  try {
    const wanted = neighbour.listItemOrNullObject;
    const actual = line.listItemOrNullObject;
    wanted.load("level");
    actual.load("level");
    await context.sync();
    if (wanted.isNullObject || actual.isNullObject || wanted.level === actual.level) return;
    actual.level = wanted.level;
    await context.sync();
  } catch (error) {
    logOfficeError(error, "docreview.missing_defined_term.list_level");
  }
}

/** The live heading paragraph `headingText` names, or null when no heading
 *  reads that way any more. */
function findHeadingParagraph(
  paragraphs: Word.ParagraphCollection,
  texts: readonly string[],
  headingText: string,
): Word.Paragraph | null {
  if (!headingText) return null;
  const styles = paragraphs.items.map((p) => p.style ?? "");
  const outlineLevels = paragraphs.items.map((p) => p.outlineLevel ?? null);
  const wanted = normalizeForCompare(headingText);
  const heading = tocFromParagraphs(texts, styles, outlineLevels).find(
    (entry) => normalizeForCompare(entry.title) === wanted,
  );
  return heading ? (paragraphs.items[heading.startParagraph] ?? null) : null;
}

/** Point `label` at `heading` through a bookmark, the way missing_hyperlink's
 *  fix does (`deriveBookmarkName`, so both name a heading's bookmark alike).
 *  The link sits on text this same tracked change inserts, so rejecting the
 *  change takes the link with it — unlike linking EXISTING text (AIVA-8206),
 *  there is nothing for it to delete. Desktop only (bookmarks need
 *  WordApiDesktop 1.4); anything that goes wrong leaves the entry unlinked
 *  rather than failing it. */
async function linkToHeading(
  context: Word.RequestContext,
  label: Word.Range,
  heading: Word.Paragraph,
  headingText: string,
): Promise<void> {
  if (!supportsBookmarks()) return;
  try {
    const bookmarkName = deriveBookmarkName(headingText);
    heading.getRange(Word.RangeLocation.content).insertBookmark(bookmarkName);
    label.hyperlink = `#${bookmarkName}`;
    await context.sync();
  } catch (error) {
    logOfficeError(error, "docreview.missing_defined_term.link");
  }
}

/** Pure: the comment left on the inline definition once its entry has been
 *  added — the finding's own comment, then the entry exactly as it now reads
 *  in the Definitions section, so the operator need not go looking for it. */
export function addedEntryComment(commentText: string, entryText: string): string {
  return `${commentText}; added it there: ${entryText}`;
}

/** Adds the entry and returns its text as written. */
async function insertDefinitionReference(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  texts: readonly string[],
  section: DefinitionsSection,
  plan: DefinitionInsertPlan,
  reference: DefinitionReference,
): Promise<string> {
  const anchor = paragraphs.items[plan.targetIndex];
  const neighbour = paragraphs.items[plan.neighbourIndex];
  const neighbourEntry = section.entries.find((entry) => entry.paragraphIndex === plan.neighbourIndex);
  if (!anchor || !neighbour || !neighbourEntry) throw new Error("missing_defined_term: entry out of range");
  // Found before anything is inserted: indices are only good until then.
  const heading = findHeadingParagraph(paragraphs, texts, reference.headingText);
  const format = await readTermFormat(context, neighbour, neighbourEntry.term);

  const parts = definitionReferenceParts(reference.term, reference.sectionLabel, texts[plan.neighbourIndex] ?? "");
  const location = plan.location === "Before" ? Word.InsertLocation.before : Word.InsertLocation.after;
  const line = anchor.insertParagraph("", location);
  line.style = neighbour.style;
  const quotedTerm = line.insertText(parts.quotedTerm, Word.InsertLocation.end);
  if (format.bold !== undefined) quotedTerm.font.bold = format.bold;
  if (format.italic !== undefined) quotedTerm.font.italic = format.italic;
  if (format.underline !== undefined) quotedTerm.font.underline = format.underline;
  const rest = [parts.verb, parts.label, parts.end].map((text) => line.insertText(text, Word.InsertLocation.end));
  for (const range of rest) resetTermFormat(range, format);
  await context.sync();

  await matchListLevel(context, line, neighbour);
  const label = rest[1];
  if (heading && label) await linkToHeading(context, label, heading, reference.headingText);
  return `${parts.quotedTerm}${parts.verb}${parts.label}${parts.end}`;
}

/** Flag a term missing from the Definitions section and — AIVA-8748 — add it
 * there: `"Late Fee" is defined in Section 14.`, in its alphabetical place,
 * formatted like its neighbours, the section linked to its heading, as a
 * tracked change alongside the comment — which then says the entry was added
 * and quotes it (`addedEntryComment`).
 *
 * The section is re-read LIVE, as alphabetical_order's fix does: an earlier
 * fix may have moved or added entries since the scan. Comment only, as before,
 * when the finding carries no `reference` (a finding from an older build, or a
 * term under no heading) or the document no longer has a Definitions section
 * to add to. A term someone has listed since the scan writes nothing.
 *
 * No `commentId` is reported when the entry was added: the rule's own
 * condition is then gone, so a rescan re-flagging the term is what proves the
 * operator rejected the entry (see `RUNTIME_CHOICE_FIX_HANDLERS`). */
export async function fixMissingDefinedTerm(
  anchorTag: string,
  expected: string,
  commentText: string,
  reference?: DefinitionReference,
): Promise<FixResult> {
  if (!reference) return flagWithComment(anchorTag, expected, commentText);
  const added = await Word.run(async (context): Promise<FixResult | null> => {
    const cc = await resolveAnchor(context, anchorTag);
    if (!cc) return { applied: false, stale: true, reason: "anchor_missing" };
    if (isStale(cc.text, expected)) return { applied: false, stale: true, reason: "text_changed" };

    const { paragraphs, texts, section } = await readDefinitionsSection(context);
    if (!isNamedDefinitionsSection(section)) return null;
    const plan = planDefinitionInsert(section, texts, reference.term);
    if (!plan) return { applied: false, stale: true, reason: "already_listed" };

    const { tracked } = await withTrackingForced(context, async () => {
      const entryText = await insertDefinitionReference(context, paragraphs, texts, section, plan, reference);
      await insertCommentAndGetId(context, cc.getRange(), addedEntryComment(commentText, entryText));
    });
    return { applied: true, stale: false, tracked };
  });
  return added ?? flagWithComment(anchorTag, expected, commentText);
}

// ---------------------------------------------------------------------------
// used_before_defined — a defined term used in the body text before the point
// where the document defines it
// ---------------------------------------------------------------------------

/** One place in the document, as a paragraph and a character offset into
 *  that paragraph's own text — enough to order two positions. */
export interface DocumentPosition {
  paragraphIndex: number;
  offset: number;
}

export interface RawTermFirstUse {
  /** The term as declared ("Acquirer"). */
  term: string;
  /** Where the term's declaration puts it — the opening quote of `"X"`. */
  definition: DocumentPosition;
  /** Heading of the section the definition sits in, or "" when it sits
   *  under no heading at all. */
  definitionSection: string;
  /** AIVA-9259: the clause number the definition paragraph opens with ("3.1"),
   *  typed or Word's automatic one — set only when `definitionSection` is
   *  empty, so a document whose headings are plain typed text can still be
   *  told where the definition is. */
  definitionClause?: string;
  /** A short window of the definition paragraph's text around the term, so
   *  the comment can say exactly where it is defined, not just "later". */
  definitionExcerpt: string;
  /** The term's first real use anywhere in the document, or `null` when it
   *  is never used outside its own declaration (defined_not_used's concern,
   *  not this rule's). */
  firstUse: (DocumentPosition & { matchText: string; occurrenceIndex: number }) | null;
}

export interface UsedBeforeDefinedSweep {
  terms: RawTermFirstUse[];
}

// Headings, rendered contents entries and a document's title name sections;
// they do not use a term. "1. Services" sitting above `"Services" means ...`
// is a heading, and flagging it would be noise on every contract that titles
// a section after the term it defines.
const NON_BODY_STYLE = /^(?:TOC\s*\d|Title|Subtitle)\b/i;

function isBodyParagraph(style: string, outlineLevel: number | null): boolean {
  return headingLevelFrom(style, outlineLevel) === null && !NON_BODY_STYLE.test(style.trim());
}

function isBefore(a: DocumentPosition, b: DocumentPosition): boolean {
  return a.paragraphIndex < b.paragraphIndex || (a.paragraphIndex === b.paragraphIndex && a.offset < b.offset);
}

/** The innermost section a paragraph belongs to — the deepest heading whose
 *  range contains it — or "" when it sits before every heading. */
function innermostSectionTitle(toc: readonly TocEntry[], paragraphIndex: number): string {
  let best: TocEntry | undefined;
  for (const entry of toc) {
    if (entry.startParagraph > paragraphIndex || entry.endParagraph < paragraphIndex) continue;
    if (!best || entry.level > best.level) best = entry;
  }
  return best?.title.trim() ?? "";
}

const EXCERPT_BEFORE = 40;
const EXCERPT_AFTER = 70;

/** A window of `text` around `offset`, cut on word boundaries, with an
 *  ellipsis wherever it was cut. */
function excerptAround(text: string, offset: number): string {
  let start = Math.max(0, offset - EXCERPT_BEFORE);
  let end = Math.min(text.length, offset + EXCERPT_AFTER);
  if (start > 0) {
    const space = text.indexOf(" ", start);
    if (space !== -1 && space < offset) start = space + 1;
  }
  if (end < text.length) {
    const space = text.lastIndexOf(" ", end);
    if (space > offset) end = space;
  }
  const body = text.slice(start, end).replace(/\s+/g, " ").trim();
  return `${start > 0 ? "…" : ""}${body}${end < text.length ? "…" : ""}`;
}

/** Spans of every LONGER defined term that contains `key` as a whole-word
 *  run, in one paragraph — "Service" inside "Service Provider" is a use of
 *  "Service Provider", not of "Service". */
function longerTermSpans(key: string, longerTerms: readonly string[], text: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  for (const longer of longerTerms) {
    if (!` ${longer.toLowerCase()} `.includes(` ${key} `)) continue;
    const pattern = termOccurrencePattern(longer);
    if (!pattern) continue;
    for (const m of text.matchAll(pattern)) spans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
  }
  return spans;
}

// What sits between a term's own antecedent and its parenthetical declaration:
// `Master Agreement (this "Agreement")`, `the buyer ("Buyer")`. Nothing but the
// opening bracket and an optional article — a sentence in between means the
// earlier mention really is a separate, earlier use.
const DECLARATION_LEAD_IN = /^\s*\((?:(?:the|this|a|an)\s+)?"$/i;

/** Whether the match ending at `end` names the very thing its declaration is
 *  about to define — `This Master Agreement (this "Agreement")`. That is how
 *  nearly every contract opens, and it is the label being attached, not an
 *  early use of it. */
function isDeclarationAntecedent(text: string, end: number, definitionOffset: number): boolean {
  return end <= definitionOffset && DECLARATION_LEAD_IN.test(text.slice(end, definitionOffset + 1));
}

// One word of the name a declaration shortens — capitalised, or one of the
// small words a title joins them with ("Agreement and Plan of Merger").
const NAME_WORD = /^[A-Z][A-Za-z0-9'&.-]*$/;
const NAME_JOINER = /^(?:of|and|for|&)$/;
const MAX_NAME_WORDS = 8;

/** The span of the name a parenthetical declaration shortens, or `null`.
 *
 *  AIVA-9259: `This Master Services Agreement (this "Agreement")` names the
 *  document. "Services" in it is part of that name, not a use of the defined
 *  term "Services" — yet it sits before Services' definition on almost every
 *  contract, so counting it flagged the document's own title.
 *  `isDeclarationAntecedent` already spared "Agreement"; this spares the rest
 *  of the name.
 *
 *  Only a name that ENDS with the declared term and is longer than it counts:
 *  `the Services (the "Core Services")` shortens nothing, and its "Services"
 *  is a real use. */
function declaredNameSpan(straight: string, declared: DeclaredTerm): [number, number] | null {
  if (declared.shape !== "parenthetical") return null;
  const words = [...straight.slice(0, declared.offset).matchAll(/\S+/g)];
  let first = words.length;
  while (first > 0 && words.length - first < MAX_NAME_WORDS) {
    const word = words[first - 1]?.[0] ?? "";
    if (!NAME_WORD.test(word) && !NAME_JOINER.test(word)) break;
    first--;
  }
  while (first < words.length && NAME_JOINER.test(words[first]?.[0] ?? "")) first++;
  const name = words.slice(first);
  const termWords = straightenQuotes(declared.term).split(/\s+/);
  if (name.length <= termWords.length) return null;
  if (name.slice(-termWords.length).map((m) => m[0]).join(" ") !== termWords.join(" ")) return null;
  const last = name[name.length - 1];
  if (!last) return null;
  return [name[0]?.index ?? 0, (last.index ?? 0) + last[0].length];
}

const MAX_TITLE_WORDS = 8;

/** The document's own name as its title states it — the first paragraph
 *  styled Title, else an opening paragraph set in capitals ("MASTER SERVICES
 *  AGREEMENT") — or `null`. */
function documentTitle(texts: readonly string[], styles: readonly string[]): string | null {
  const titled = styles.findIndex((style) => /^Title\b/i.test(style.trim()));
  if (titled >= 0) return texts[titled]?.trim() || null;
  const opening = texts.find((text) => text.trim() !== "")?.trim() ?? "";
  return opening && !/[a-z]/.test(opening) ? opening : null;
}

/** Per paragraph, every place the body repeats the document's title —
 *  "This Master Services Agreement is made on…" under a MASTER SERVICES
 *  AGREEMENT title.
 *
 *  AIVA-9259: the same name `declaredNameSpan` spares, for the contract that
 *  opens without a `(this "Agreement")` after it. A title of one word, longer
 *  than a name runs, or that is itself a defined term names nothing to spare:
 *  sparing it would hide every use of that term. */
function titleNameSpans(
  straightened: readonly string[],
  title: string | null,
  declaredKeys: ReadonlySet<string>,
): Array<Array<[number, number]>> {
  const words = straightenQuotes(title ?? "").split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > MAX_TITLE_WORDS || declaredKeys.has(words.join(" ").toLowerCase())) {
    return straightened.map(() => []);
  }
  const pattern = new RegExp(String.raw`\b${words.map(escapeRegExp).join(TERM_WORD_GAP)}\b`, "gi");
  return straightened.map((text) =>
    [...text.matchAll(pattern)].map((m): [number, number] => [m.index ?? 0, (m.index ?? 0) + m[0].length]),
  );
}

/** Per paragraph, the spans of every name a declaration shortens. */
function declaredNameSpans(
  inventory: readonly DeclaredTerm[],
  straightened: readonly string[],
): Array<Array<[number, number]>> {
  const spans: Array<Array<[number, number]>> = straightened.map(() => []);
  for (const declared of inventory) {
    const span = declaredNameSpan(straightened[declared.paragraphIndex] ?? "", declared);
    if (span) spans[declared.paragraphIndex]?.push(span);
  }
  return spans;
}

/** Per paragraph, the offset of every declaration's opening quote. */
function declarationQuoteOffsets(inventory: readonly DeclaredTerm[], paragraphCount: number): Array<Set<number>> {
  const quotes = Array.from({ length: paragraphCount }, () => new Set<number>());
  for (const declared of inventory) quotes[declared.paragraphIndex]?.add(declared.termOffset);
  return quotes;
}

/** The swept document, as `firstUseOf` and `firstUseInParagraph` read it. */
interface SweptDocument {
  texts: readonly string[];
  straightened: readonly string[];
  bodyParagraph: readonly boolean[];
  /** Per paragraph, names a declaration shortens — see `declaredNameSpan`. */
  nameSpans: ReadonlyArray<ReadonlyArray<[number, number]>>;
  /** Per paragraph, where each declaration's opening quote sits — every
   *  shape, pointers included (`termOffset`). */
  declarationQuotes: ReadonlyArray<ReadonlySet<number>>;
  /** The real, named Definitions section, as alphabetical_order finds it, or
   *  null — see `firstUseOf`. */
  definitionsRange: { start: number; end: number } | null;
  counter: ReturnType<typeof documentOccurrenceCounter>;
}

// What follows a quoted term that only points at its definition:
// `"Work Product" (as defined in Section 7.2)`, `"Acquirer" as defined below`,
// and the Definitions-list form `"Deliverables" – Defined in Section 3.`, which
// the inventory does not read as a declaration.
const QUOTED_POINTER_AFTER =
  /^[.,]?["']?[.,]?\s*(?:[-–—:]\s*)?\(?\s*(?:as\s+)?(?:such\s+term\s+is\s+)?(?:defined|ha(?:s|ve)\s+the\s+(?:respective\s+)?meanings?)\b/i;

/** Whether a quoted mention at [offset, end) introduces its term — a
 *  declaration opens with that quote, or the words after it point to the
 *  definition — rather than using it.
 *
 *  AIVA-9259: a quoted term that does neither (`engage "Supplier" to provide`)
 *  is still the reader meeting the term before its definition, so it counts
 *  as a use. Before, every quoted mention was skipped. */
function introducesTerm(text: string, offset: number, end: number, declarationQuotes: ReadonlySet<number>): boolean {
  return declarationQuotes.has(offset - 1) || QUOTED_POINTER_AFTER.test(text.slice(end));
}

/** Whether the match at [offset, end) of one paragraph's (quote-straightened)
 *  text is a real use of its term, rather than one of the mentions
 *  `findUsedBeforeDefined` excludes. `antecedentOffset` is the definition's
 *  offset when this is the definition's own paragraph, else null. */
function isRealUse(
  text: string,
  offset: number,
  end: number,
  longerSpans: ReadonlyArray<[number, number]>,
  antecedentOffset: number | null,
  declarationQuotes: ReadonlySet<number>,
): boolean {
  const quoted = text[offset - 1] === '"' || text[offset - 1] === "'";
  if (quoted && introducesTerm(text, offset, end, declarationQuotes)) return false;
  if (longerSpans.some(([s, e]) => offset >= s && end <= e)) return false;
  return antecedentOffset === null || !isDeclarationAntecedent(text, end, antecedentOffset);
}

// The clause number a run-in clause title follows: "1.01", "4.03", "D.1".
const CLAUSE_NUMBER_LEAD = /^\s*(?:[A-Z]\.)?\d+(?:\.\d+)*\.?\s+/;

// A run-in title followed by a SINGLE space — `1.01 Defined Terms. As used…`.
// RUN_IN_HEADING_TITLE wants two, which is what tells a title from a sentence;
// with one, only a title-cased run counts (see `isTitleCased`).
const RUN_IN_TITLE_ONE_SPACE = /^([^.:]{1,79}[.:])\s\S/;
const TITLE_MINOR_WORDS = new Set(["a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "with"]);
const MAX_RUN_IN_TITLE_WORDS = 8;

/** Whether `title` reads as a clause title rather than a sentence: short, and
 *  every word capitalised except the small connecting ones ("Limitation of
 *  Liability"). `Provider shall perform the Services` is a sentence. */
function isTitleCased(title: string): boolean {
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > MAX_RUN_IN_TITLE_WORDS) return false;
  return words.every((w) => /^[A-Z0-9(&]/.test(w) || TITLE_MINOR_WORDS.has(w));
}

/** The run-in title a numbered clause opens with — `1.01  Defined Terms.  As
 *  used…`, or `1.01 Defined Terms. As used…` when it reads as a title — as a
 *  span of the paragraph, or `null` when it has none. */
function runInClauseTitle(text: string): { start: number; end: number; title: string } | null {
  const number = CLAUSE_NUMBER_LEAD.exec(text);
  if (!number) return null;
  const rest = text.slice(number[0].length);
  const twoSpaced = RUN_IN_HEADING_TITLE.exec(rest);
  const titled = twoSpaced ?? RUN_IN_TITLE_ONE_SPACE.exec(rest);
  if (!titled?.[1]) return null;
  const title = titled[1].slice(0, -1).trim();
  if (!twoSpaced && !isTitleCased(title)) return null;
  const start = number[0].length;
  return { start, end: start + titled[1].length, title };
}

/** Whether a match inside a clause's run-in title is a use. A title NAMES its
 *  clause, so "Term" inside "1.01  Defined Terms." is not one — unless the
 *  title is exactly the term ("4.03  Chronic Failure."), which puts the term in
 *  front of the reader before the clause defines it. */
function isTitleUse(
  title: ReturnType<typeof runInClauseTitle>,
  key: string,
  offset: number,
  end: number,
): boolean {
  if (!title || offset < title.start || end > title.end) return true;
  return title.title.toLowerCase() === key;
}

/** The first real use of a term within one paragraph, or `null`. */
function firstUseInParagraph(
  pattern: RegExp,
  key: string,
  longerTerms: readonly string[],
  doc: SweptDocument,
  paragraphIndex: number,
  definition: DocumentPosition,
): RawTermFirstUse["firstUse"] {
  const text = doc.straightened[paragraphIndex] ?? "";
  const spans = [...longerTermSpans(key, longerTerms, text), ...(doc.nameSpans[paragraphIndex] ?? [])];
  const antecedentOffset = paragraphIndex === definition.paragraphIndex ? definition.offset : null;
  const title = runInClauseTitle(text);
  for (const m of text.matchAll(pattern)) {
    const offset = m.index ?? 0;
    const end = offset + m[0].length;
    if (!isRealUse(text, offset, end, spans, antecedentOffset, doc.declarationQuotes[paragraphIndex] ?? new Set<number>())) continue;
    if (!isTitleUse(title, key, offset, end)) continue;
    const matchText = (doc.texts[paragraphIndex] ?? "").slice(offset, end);
    const occurrenceIndex = doc.counter.at(paragraphIndex, offset, matchText);
    if (occurrenceIndex !== null) return { paragraphIndex, offset, matchText, occurrenceIndex };
  }
  return null;
}

/** Whether a paragraph lies inside `range` (its heading excluded). */
function isWithin(range: { start: number; end: number } | null, paragraphIndex: number): boolean {
  return range !== null && paragraphIndex > range.start && paragraphIndex <= range.end;
}

/** The first real use of one term, in document order, or `null`. See
 *  `findUsedBeforeDefined` for what counts as a use.
 *
 *  AIVA-9259: a term defined in the Definitions section is not "used" by the
 *  rest of that section. The list is a glossary, read by lookup, and in
 *  alphabetical order: "Services" inside the "Client Data" entry, or the
 *  "In this Agreement the following terms…" lead-in, can never be fixed by
 *  moving a definition, so flagging them was noise on nearly every contract.
 *  A use before the section, or of a term defined in the body, still counts. */
function firstUseOf(
  term: string,
  definition: DocumentPosition,
  longerTerms: readonly string[],
  doc: SweptDocument,
): RawTermFirstUse["firstUse"] {
  const pattern = termOccurrencePattern(term);
  if (!pattern) return null;
  const key = term.toLowerCase();
  const definedInDefinitions = isWithin(doc.definitionsRange, definition.paragraphIndex);
  for (let paragraphIndex = 0; paragraphIndex < doc.texts.length; paragraphIndex++) {
    if (!doc.bodyParagraph[paragraphIndex]) continue;
    if (definedInDefinitions && isWithin(doc.definitionsRange, paragraphIndex)) continue;
    const use = firstUseInParagraph(pattern, key, longerTerms, doc, paragraphIndex, definition);
    if (use) return use;
  }
  return null;
}

/** Raw sweep only — for every defined term, where it is defined and where it
 *  is first USED. Deciding whether the use comes first is
 *  `evaluateUsedBeforeDefined`'s job.
 *
 *  A use is a case-sensitive, inflection-tolerant whole-word match (the same
 *  test defined_not_used counts with, via `termOccurrencePattern`), and is NOT:
 *  - the quoted term of a declaration, or of an explicit pointer such as
 *    `"Acquirer" (as defined below)`; neither is a use the reader trips over.
 *    Any other quoted mention IS a use (AIVA-9259, see `introducesTerm`);
 *  - in a heading, a contents entry or a title (see `NON_BODY_STYLE`);
 *  - part of a longer defined term ("Service" inside "Service Provider");
 *  - the name its own declaration labels — `Master Agreement (this "Agreement")`.
 *
 *  Reads text AND style per paragraph, like missing_defined_term, so it can
 *  both skip headings and name the section a definition lives in. */
export function findUsedBeforeDefined(): Promise<UsedBeforeDefinedSweep> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    // AIVA-8925: one targeted load, not "every property of every paragraph" and
    // then a second round trip for the three this reads.
    paragraphs.load("items/text,items/style,items/outlineLevel");
    await context.sync();

    const texts = paragraphs.items.map((p) => p.text ?? "");
    const styles = paragraphs.items.map((p) => p.style ?? "");
    const outlineLevels = paragraphs.items.map((p) => p.outlineLevel ?? null);
    const toc = tocFromParagraphs(texts, styles, outlineLevels);
    // AIVA-9190 follow-up (main): UK single-quoted terms read as quoted, and
    // bold-colon entries need Word's formatting. Computed once: every AIVA-9259
    // exclusion below reads these same positions.
    const straightened = texts.map(straightenTermQuotes);
    const inventory = await readDeclaredTerms(context, paragraphs, texts);
    const titleSpans = titleNameSpans(straightened, documentTitle(texts, styles), new Set(inventory.map((d) => d.key)));
    // The same Definitions section alphabetical_order works on. Only a real,
    // named one: a document with no headings would otherwise put the whole
    // body "inside" the section and silence the rule.
    const entries = definitionEntriesByParagraph(inventory, texts.length);
    const definitionFacts: DefinitionParagraphFacts[] = texts.map((text, i) => ({
      text,
      style: styles[i] ?? "",
      outlineLevel: outlineLevels[i] ?? null,
      entry: entries[i] ?? null,
      listLevel: null,
      deleted: false,
    }));
    const definitionsRange = definitionsTextRange(definitionFacts, selectDefinitionsSection(definitionFacts));
    const doc = {
      texts,
      straightened,
      bodyParagraph: styles.map((style, i) => isBodyParagraph(style, outlineLevels[i] ?? null)),
      nameSpans: declaredNameSpans(inventory, straightened).map((spans, i) => [...spans, ...(titleSpans[i] ?? [])]),
      declarationQuotes: declarationQuoteOffsets(inventory, texts.length),
      definitionsRange,
      counter: documentOccurrenceCounter(texts),
    };

    // A term's earliest declaration, of any shape, is where the reader first
    // learns what it means.
    const declarations = firstDeclarations(inventory).map(({ term, paragraphIndex, termOffset }) => ({
      term,
      position: { paragraphIndex, offset: termOffset },
      section: innermostSectionTitle(toc, paragraphIndex),
    }));
    const allTerms = declarations.map((d) => d.term);
    // Word's automatic number, read only for a definition no heading names and
    // whose paragraph has no number typed in its text — and no round trip at
    // all when there is none.
    const unnumbered = declarations
      .filter((d) => !d.section && !typedClauseNumber(texts[d.position.paragraphIndex] ?? ""))
      .map((d) => d.position.paragraphIndex);
    const listNumbers =
      unnumbered.length > 0
        ? await readListStrings(context, paragraphs, unnumbered, "docreview.used_before_defined_list_numbers")
        : new Map<number, string>();
    // A defined term is capitalised; a lower-case one ("control", "including")
    // is an ordinary word the document happens to pin down, and its every
    // earlier use in plain English would read as a finding.
    const terms: RawTermFirstUse[] = declarations.filter(({ term }) => /^[A-Z]/.test(term)).map(({ term, position, section }) => {
      const clause = section ? null : definitionClauseNumber(texts[position.paragraphIndex] ?? "", listNumbers.get(position.paragraphIndex));
      return {
        term,
        definition: position,
        definitionSection: section,
        ...(clause ? { definitionClause: clause } : {}),
        definitionExcerpt: excerptAround(texts[position.paragraphIndex] ?? "", position.offset),
        firstUse: firstUseOf(
          term,
          position,
          allTerms.filter((t) => t.length > term.length),
          doc,
        ),
      };
    });
    return { terms };
  });
}

/** The clause number a paragraph's text opens with — "3.1" from "3.1  The
 *  Parties may…", "3" from "3. Fees" — or `null`. A bare count is not one:
 *  "30 days after…" has no dot, so it never reads as Section 30. */
function typedClauseNumber(text: string): string | null {
  const lead = CLAUSE_NUMBER_LEAD.exec(text)?.[0].trim();
  if (!lead?.includes(".")) return null;
  return lead.replace(/\.$/, "");
}

/** The definition paragraph's clause number: typed in its text, else Word's
 *  automatic one. A list glyph that is no clause number ("(c)", a bullet)
 *  gives `null`, so the comment falls back to "later in the document" rather
 *  than naming a section it cannot. */
function definitionClauseNumber(text: string, listString: string | undefined): string | null {
  return typedClauseNumber(text) ?? (listString ? typedClauseNumber(`${listString} `) : null);
}

/** The comment the attorney reads — names the term and says where it is
 *  defined, so they can decide whether to move the definition up. */
function usedBeforeDefinedComment(term: RawTermFirstUse): string {
  let where = `It is defined later in the document: ${term.definitionExcerpt}`;
  if (term.definitionSection) {
    where = `It is defined later, under "${term.definitionSection}": ${term.definitionExcerpt}`;
  } else if (term.definitionClause) {
    where = `It is defined later, in Section ${term.definitionClause}: ${term.definitionExcerpt}`;
  }
  return `"${term.term}" is used here before it is defined. ${where} You may want to move the definition to this first use.`;
}

/** Deterministic evaluation — a defined term whose first use comes before its
 *  definition. One finding per term, anchored to that FIRST use only: every
 *  later early use is the same problem, and moving the definition fixes them
 *  all at once. */
export function evaluateUsedBeforeDefined(sweep: UsedBeforeDefinedSweep): FindingCreate[] {
  const findings: FindingCreate[] = [];
  for (const term of sweep.terms) {
    if (!term.firstUse || !isBefore(term.firstUse, term.definition)) continue;
    const commentText = usedBeforeDefinedComment(term);
    findings.push(
      toFindingCreate({
        ruleId: "used_before_defined",
        matchText: term.firstUse.matchText,
        occurrenceIndex: term.firstUse.occurrenceIndex,
        problem: `"${term.term}" is used before the point where it is defined.`,
        comment: commentText,
        fixHandler: "word.docreview.fix_used_before_defined",
        fixParams: { commentText },
      }),
    );
  }
  return findings;
}

/** Flag a defined term used before its definition. Comment-only — where a
 *  definition belongs is the attorney's call, so the fix offers them the
 *  choice rather than moving any text. */
export async function fixUsedBeforeDefined(
  anchorTag: string,
  expected: string,
  commentText: string,
): Promise<FixResult> {
  return flagWithComment(anchorTag, expected, commentText);
}
